(() => {
  "use strict";

  const CMG_RUNTIME_VERSION = "2026-05-21-desktop-video-url-1";
  const R = window.__VimeoVideoControlRuntime || (window.__VimeoVideoControlRuntime = {});
  if (R.__installed && R.__version === CMG_RUNTIME_VERSION) {
    // If this script is re-injected (Shopify editor / dynamic sections), only refresh the current section
    // when possible to avoid duplicate renders.
    try {
      if (typeof R.refresh === "function") {
        const cs = document.currentScript;
        const ctx = cs && cs.closest ? cs.closest(".shopify-section") : null;
        R.refresh(ctx || document);
      }
    } catch (e) {
      // no-op
    }
    return;
  }
  R.__installed = true;
  R.__version = CMG_RUNTIME_VERSION;

  function ready(fn){
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn, { once:true });
    else fn();
  }
  function safeJsonParse(text){ try { return JSON.parse(text); } catch { return null; } }

  function getSpecScripts(context){
    const scope = context || document;
    return Array.from(scope.querySelectorAll('script[data-vimeo-control-spec][type="application/json"]'));
  }

  /* ------------------------------
     Vimeo ended event router (global)
  ------------------------------ */
  const VIMEO_END_HANDLERS =
    window.__vimeoEndHandlers || (window.__vimeoEndHandlers = new WeakMap());

  const VIMEO_TIME_HANDLERS =
    window.__vimeoTimeHandlers || (window.__vimeoTimeHandlers = new WeakMap());

  // âœ… Track mute state (for iOS + "tap to unmute" UI)
  const VIMEO_VOLUME_HANDLERS =
    window.__vimeoVolumeHandlers || (window.__vimeoVolumeHandlers = new WeakMap());

  const VIMEO_METHOD_HANDLERS =
    window.__vimeoMethodHandlers || (window.__vimeoMethodHandlers = new WeakMap());

  if (!R.__vimeoMsgInstalled) {
    R.__vimeoMsgInstalled = true;
    window.addEventListener("message", (e) => {
      if (e.origin !== "https://player.vimeo.com") return;

      let data = e.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      if (!data || typeof data !== "object") return;

      const ev = data.event || data.type || "";
      if (ev === "ended" || ev === "finish") {
        const fn = VIMEO_END_HANDLERS.get(e.source);
        if (typeof fn === "function") fn(data);
        return;
      }
      if (ev === "timeupdate") {
        const fn = VIMEO_TIME_HANDLERS.get(e.source);
        if (typeof fn === "function") fn(data);
        return;
      }

      // âœ… Mute/volume state (Vimeo emits: {event:"volumechange", data:{volume, muted}})
      if (ev === "volumechange") {
        const fn = VIMEO_VOLUME_HANDLERS.get(e.source);
        if (typeof fn === "function") fn(data);
        return;
      }

      // âœ… Method responses (e.g., {method:"getMuted", value:true})
      const m = String(data.method || "").trim();
      if (m === "getMuted" || m === "getVolume") {
        const fn = VIMEO_METHOD_HANDLERS.get(e.source);
        if (typeof fn === "function") fn(data);
        return;
      }
    });
  }

  /* ------------------------------
     Composite set builder (import rows from global MEDIA_GALLERY_SETS)
     âœ… Supports mixed item types: Vimeo + Image + Product
     âœ… Accepts set shapes:
        - Rows: [{ heading, subheading, videos/items:[...] }, ...]
        - Single row object: { heading, videos/items:[...] }
        - Flat items: [{...}, {...}]  (treated as a single row)
        - Flat URLs: ["https://vimeo.com/..."] (treated as Vimeo video items)
  ------------------------------ */
  const pending = new Map();
  const IMPORT_WAIT_MAX_MS = 3000;

  function getSetStore(){
    const w = window;

    // âœ… Back-compat: allow window.VIMEO_VIDEO_SETS as an alias
    const candidate =
      (w.MEDIA_GALLERY_SETS && typeof w.MEDIA_GALLERY_SETS === "object")
        ? w.MEDIA_GALLERY_SETS
        : (w.VIMEO_VIDEO_SETS && typeof w.VIMEO_VIDEO_SETS === "object")
          ? w.VIMEO_VIDEO_SETS
          : null;

    if (candidate) {
      // Use a null-prototype object to prevent prototype-pollution edge cases (e.g. "__proto__" keys)
      if (Object.getPrototypeOf(candidate) === null) {
        w.MEDIA_GALLERY_SETS = candidate;
        w.VIMEO_VIDEO_SETS = candidate;
        return candidate;
      }

      const safe = Object.create(null);
      for (const k of Object.keys(candidate)) safe[k] = candidate[k];

      w.MEDIA_GALLERY_SETS = safe;
      w.VIMEO_VIDEO_SETS = safe;
      return safe;
    }

    // Create a shared store (and alias it to both keys)
    const safe = Object.create(null);
    w.MEDIA_GALLERY_SETS = safe;
    w.VIMEO_VIDEO_SETS = safe;
    return safe;
  }


  const VIMEO_RE = /(?:vimeo\.com|player\.vimeo\.com)/i;
  const YOUTUBE_RE = /(?:youtu\.be|youtube(?:-nocookie)?\.com)/i;
  const FILE_VIDEO_RE = /\.(?:mp4|m4v|webm|ogv|ogg|mov)(?:$|[?#])/i;

  function videoSourceFromUrl(raw){
    const s = String(raw || "").trim();
    if (!s) return "";
    if (VIMEO_RE.test(s)) return "vimeo";
    if (YOUTUBE_RE.test(s)) return "youtube";
    try {
      const u = new URL(s, window.location.href);
      const path = String(u.pathname || "");
      if (FILE_VIDEO_RE.test(path) || FILE_VIDEO_RE.test(s)) return "file";
    } catch {
      if (FILE_VIDEO_RE.test(s)) return "file";
    }
    return "";
  }

  function isSupportedVideoUrl(raw){
    return !!videoSourceFromUrl(raw);
  }

  function hasVideoOnlySignals(it){
    if (!it || typeof it !== "object") return false;

    const ctaThumbText = String(it?.cta?.thumb_text || it?.thumb_text || "").trim();
    if (ctaThumbText) return true;

    const markerValues = [
      it?.start_time,
      it?.startTime,
      it?.start_at,
      it?.startAt,
      it?.end_behavior,
      it?.endBehavior,
      it?.end_behavior_override,
      it?.end_of_video_override,
      it?.hero,
      it?.hero_video,
      it?.is_hero,
      it?.hero_autoplay_preview,
      it?.autoplay_video,
      it?.quick_load_video_url,
      it?.quickLoadVideoUrl,
      it?.tiny_video_url,
      it?.tinyVideoUrl,
      it?.prioritize_quick_load_video,
      it?.prioritizeQuickLoadVideo,
      it?.hero_show_play_badge,
      it?.hero_show_play_button,
      it?.show_play_badge,
      it?.show_play_button,
      it?.open_full_video_text,
      it?.title_wrap,
      it?.wrap_title
    ];

    if (markerValues.some((v) => {
      if (v == null) return false;
      if (typeof v === "boolean") return v;
      if (typeof v === "number") return isFinite(v) && v !== 0;
      return String(v).trim() !== "";
    })) return true;

    const poster = String(it?.thumbnail || it?.thumb || "").trim();
    const image = String(it?.image || it?.img || "").trim();
    const productUrl = String(it?.product_url || it?.productUrl || "").trim();
    const endBehavior = String(it?.end_behavior || it?.endBehavior || it?.end_behavior_override || it?.end_of_video_override || "").trim();
    if (poster && !image && !productUrl && endBehavior) return true;

    return false;
  }

  function guessItemKind(it){
    const tRaw = String(it?.type || it?.kind || it?.provider || "").trim().toLowerCase();
    if (tRaw === "image" || tRaw === "product" || tRaw === "video") return tRaw;
    if (tRaw === "hero_video" || tRaw === "hero-video" || tRaw === "hero") return "video";
    if (tRaw === "vimeo" || tRaw === "youtube" || tRaw === "file") return "video";
    if (hasVideoOnlySignals(it)) return "video";

    const productUrl = it?.product_url || it?.productUrl || "";
    if (String(productUrl || "").trim()) return "product";

    const u = String(it?.url || it?.href || "").trim();
    if (u && isSupportedVideoUrl(u)) return "video";

    const thumb = it?.thumbnail || it?.image || it?.img || "";
    if (String(thumb || "").trim()) return "image";

    return null;
  }

  function normalizeItem(it){
    // Allow plain URL strings inside sets for supported video providers.
    if (typeof it === "string") {
      const url = it.trim();
      if (!url) return null;
      if (!isSupportedVideoUrl(url)) return null;
      return { type: "video", url };
    }

    if (!it || typeof it !== "object") return null;

    const kind = guessItemKind(it);
    if (!kind) return null;

    // Normalize missing/alias types (or empty string) to a canonical "type"
    const tRaw = String(it.type || it.kind || "").trim().toLowerCase();
    if (!tRaw || tRaw !== kind) {
      const copy = Object.assign({}, it);
      copy.type = kind;
      return copy;
    }

    if (tRaw === "vimeo") {
      const copy = Object.assign({}, it);
      copy.type = "video";
      return copy;
    }

    return it;
  }

  function normalizeItems(list){
    const out = [];
    const arr = Array.isArray(list) ? list : [];
    for (const it of arr) {
      const n = normalizeItem(it);
      if (n) out.push(n);
    }
    return out;
  }

  function isRowLike(v){
    return !!(v && typeof v === "object" && (Array.isArray(v.videos) || Array.isArray(v.items) || Array.isArray(v.cards)));
  }

  function rowItems(row){
    return row?.videos || row?.items || row?.cards || [];
  }

  function coerceToRows(value){
    if (!value) return [];

    if (Array.isArray(value)) {
      // Already rows? Normalize their item arrays.
      if (value.length && isRowLike(value[0])) {
        return value.map((r) => {
          if (!r || typeof r !== "object") return null;
          const out = Object.assign({}, r);
          out.videos = normalizeItems(rowItems(r));
          return out;
        }).filter(Boolean);
      }

      // Flat list of items/urls -> single row
      const items = normalizeItems(value);
      if (!items.length) return [];
      return [{
        heading: "",
        subheading: "",
        img_ratio: "1:1",
        row_height: 0,
        fit_row: false,
        collapsed: false,
        show_more_text: "Show more",
        show_less_text: "Show less",
        videos: items
      }];
    }

    if (typeof value === "object") {
      if (isRowLike(value)) {
        const out = Object.assign({}, value);
        out.videos = normalizeItems(rowItems(value));
        return [out];
      }

      // Single item object -> single row
      const item = normalizeItem(value);
      if (!item) return [];
      return [{
        heading: "",
        subheading: "",
        img_ratio: "1:1",
        row_height: 0,
        fit_row: false,
        collapsed: false,
        show_more_text: "Show more",
        show_less_text: "Show less",
        videos: [item]
      }];
    }

    return [];
  }

  function extractRequired(spec){
    const req = [];
    const rows = Array.isArray(spec?.rows) ? spec.rows : [];
    for (const r of rows) {
      const k = String(r?.__import_set || "").trim();
      if (k && !req.includes(k)) req.push(k);
    }
    return req;
  }

  function importsReady(required){
    const BASE = getSetStore();
    for (const k of required) {
      if (typeof BASE[k] === "undefined") return false;
    }
    return true;
  }

  function buildCompositeSet(spec){
    const BASE = getSetStore();

    const target = String(spec?.targetSet || "").trim();
    if (!target) return false;

    const rows = Array.isArray(spec?.rows) ? spec.rows : [];
    const out = [];

    for (const rowSpec of rows) {
      const heading = rowSpec?.heading ?? "";
      const subheading = rowSpec?.subheading ?? "";
      const img_ratio = String(rowSpec?.img_ratio || "1:1").trim() || "1:1";
      const row_height = Number(rowSpec?.row_height || 0) || 0;
      const fit_row = !!rowSpec?.fit_row;
      const hide_cta_buttons = !!rowSpec?.hide_cta_buttons;
      const end_behavior_override = normalizeEndBehavior(rowSpec?.end_behavior_override || rowSpec?.end_of_video_override, "");
      const collapsed = !!rowSpec?.collapsed;
      const show_more_text = String(rowSpec?.show_more_text || "Show more");
      const show_less_text = String(rowSpec?.show_less_text || "Show less");
      const row_toggle_border_style = normalizeRowToggleBorderStyle(rowSpec?.row_toggle_border_style || rowSpec?.row_border_style, "solid");
      const custom_html = String(rowSpec?.custom_html || "");
      const custom_template_id = String(rowSpec?.custom_template_id || "");
      const accordion = !!rowSpec?.accordion;
      const accordion_title = String(rowSpec?.accordion_title || "");
      const accordion_subheading = String(rowSpec?.accordion_subheading || "");
      const accordion_content = String(rowSpec?.accordion_content || "");
      const accordion_hide_title_when_open = !!rowSpec?.accordion_hide_title_when_open;
      const accordion_hide_subheading_when_open = !!rowSpec?.accordion_hide_subheading_when_open;
      const accordion_hide_subheading_when_closed = !!rowSpec?.accordion_hide_subheading_when_closed;
      const accordion_title_center = !!rowSpec?.accordion_title_center;
      const accordion_start_open = !!rowSpec?.accordion_start_open;
      const accordion_allow_multiple = !!rowSpec?.accordion_allow_multiple;
      const accordion_bg_color = String(rowSpec?.accordion_bg_color || rowSpec?.accordion_bg || "");
      const accordion_shadow_pct = normalizeAccordionShadowPct(rowSpec?.accordion_shadow_pct, 8);
      const accordion_shadow_style = normalizeAccordionShadowStyle(rowSpec?.accordion_shadow_style || rowSpec?.accordion_shadow_effect, "raised");
      const accordion_max_width_px = normalizePresetButtonMaxWidthPx(rowSpec?.accordion_max_width_px, 0);
      const accordion_panel_width_override_percent = normalizeWidthOverridePercent(rowSpec?.accordion_panel_width_override_percent, 0);
      const accordion_panel_max_width_override_px = normalizePresetButtonMaxWidthPx(rowSpec?.accordion_panel_max_width_override_px, 0);
      const accordion_group = String(rowSpec?.accordion_group || "").trim();
      const accordion_always_open = !!rowSpec?.accordion_always_open;
      const accordion_marker = !!rowSpec?.accordion_marker;
      const collapse_group = String(rowSpec?.collapse_group || "");
      const preset_button = !!rowSpec?.preset_button;
      const preset_button_text = normalizePresetButtonTextHtml(rowSpec?.preset_button_text || rowSpec?.heading || rowSpec?.button_text || "", "Open Preset");
      const preset_button_set = String(rowSpec?.__preset_button_set || rowSpec?.preset_button_set || rowSpec?.__import_set || "").trim();
      const preset_button_style = normalizePresetButtonStyle(rowSpec?.preset_button_style || rowSpec?.button_style || "dropdown_box", "dropdown_box");
      const preset_button_image = String(rowSpec?.preset_button_image || rowSpec?.preset_button_image_url || rowSpec?.button_image || rowSpec?.button_image_url || "").trim();
      const preset_button_image_alt = String(rowSpec?.preset_button_image_alt || rowSpec?.button_image_alt || "").trim();
      const preset_button_width_percent = normalizePresetButtonWidthPercent(rowSpec?.preset_button_width_percent ?? rowSpec?.preset_button_width_pct ?? rowSpec?.button_width_percent ?? rowSpec?.button_width_pct, 0);
      const preset_button_max_width_px = normalizePresetButtonMaxWidthPx(rowSpec?.preset_button_max_width_px ?? rowSpec?.button_max_width_px, 0);
      const importKey = String(rowSpec?.__import_set || "").trim();
      const mode = String(rowSpec?.__import_mode || "flatten").trim();
      const passthrough = !!rowSpec?.__import_passthrough;

      const manualItems = normalizeItems(rowSpec?.items || rowSpec?.videos);

      if (!importKey) {
        out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_panel_width_override_percent, accordion_panel_max_width_override_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, preset_button, preset_button_text, preset_button_set, preset_button_style, preset_button_image, preset_button_image_alt, preset_button_width_percent, preset_button_max_width_px, __preset_button_set: preset_button_set, videos: manualItems });
        continue;
      }

      const srcVal = BASE[importKey];
      const srcRows = coerceToRows(srcVal);

      if (!srcRows.length) {
        console.warn("[vimeo-video-control] Missing set:", importKey);
        const missingImportHtml = appendMissingImportMessage(custom_html, importKey);
        out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html: missingImportHtml, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_panel_width_override_percent, accordion_panel_max_width_override_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, preset_button, preset_button_text, preset_button_set: preset_button_set || importKey, preset_button_style, preset_button_image, preset_button_image_alt, preset_button_width_percent, preset_button_max_width_px, __preset_button_set: preset_button_set || importKey, videos: manualItems });
        continue;
      }

      if (preset_button) {
        out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed: false, show_more_text, show_less_text, row_toggle_border_style, custom_html, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_panel_width_override_percent, accordion_panel_max_width_override_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, preset_button: true, preset_button_text, preset_button_set: preset_button_set || importKey, preset_button_style, preset_button_image, preset_button_image_alt, preset_button_width_percent, preset_button_max_width_px, __preset_button_set: preset_button_set || importKey, videos: [] });
        continue;
      }

      if (passthrough) {
        const forceCollapse = !!collapsed;
        const collapseGroupKey = forceCollapse ? (`preset:${importKey}:${out.length}`) : "";

        const nestedRows = srcRows.map((rr) => {
          const outRow = Object.assign({}, rr);
          outRow.videos = normalizeItems(rowItems(rr));
          if (forceCollapse) {
            outRow.collapsed = false;
            outRow.collapse_group = collapseGroupKey;
            outRow.show_more_text = show_more_text;
            outRow.show_less_text = show_less_text;
          }
          if (hide_cta_buttons) outRow.hide_cta_buttons = true;
          if (end_behavior_override) outRow.end_behavior_override = end_behavior_override;
          if (accordion_always_open) outRow.accordion_always_open = true;
          return outRow;
        });

        if (manualItems.length) {
          if (nestedRows.length) nestedRows[nestedRows.length - 1].videos = (nestedRows[nestedRows.length - 1].videos || []).concat(manualItems);
          else nestedRows.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: manualItems });
        }

        if (accordion_group) {
          const nestedSetKey = `__mg_nested_preset__${target}__${importKey}__${out.length}`;
          BASE[nestedSetKey] = nestedRows;
          out.push({
            heading,
            subheading,
            img_ratio,
            row_height,
            fit_row,
            hide_cta_buttons,
            end_behavior_override,
            collapsed: false,
            show_more_text,
            show_less_text,
            row_toggle_border_style,
            custom_html,
            custom_template_id,
            accordion: false,
            accordion_title: '',
            accordion_subheading: '',
            accordion_content: '',
            accordion_hide_subheading_when_closed: false,
            accordion_title_center: false,
            accordion_start_open: false,
            accordion_allow_multiple: false,
            accordion_bg_color: '',
            accordion_shadow_pct,
            accordion_shadow_style,
            accordion_panel_width_override_percent,
            accordion_panel_max_width_override_px,
            accordion_group,
            accordion_always_open,
            accordion_marker: false,
            collapse_group,
            __nested_preset_set: nestedSetKey,
            videos: []
          });
        } else {
          for (const outRow of nestedRows) out.push(outRow);
        }
        continue;
      }

      if (mode === "preserve" && accordion) {
        const importGroupKey = accordion_group || (`accordion-import:${target}:${importKey}:${out.length}`);
        const firstImported = srcRows[0] || {};
        out.push({
          heading: "",
          subheading: "",
          img_ratio,
          row_height,
          fit_row,
          hide_cta_buttons,
          end_behavior_override,
          collapsed: false,
          show_more_text,
          show_less_text,
          row_toggle_border_style,
          custom_html: "",
          accordion: true,
          accordion_title: String(accordion_title || deriveImportedAccordionTitle(firstImported, "Accordion Tab")),
          accordion_subheading: String(accordion_subheading || deriveImportedAccordionSubheading(firstImported, "")),
          accordion_content,
          accordion_hide_title_when_open,
          accordion_hide_subheading_when_open,
          accordion_hide_subheading_when_closed,
          accordion_title_center,
          accordion_start_open,
          accordion_allow_multiple,
          accordion_bg_color,
          accordion_shadow_pct,
          accordion_group: importGroupKey,
          accordion_always_open,
          accordion_marker: true,
          collapse_group: "",
          videos: []
        });
        for (let i = 0; i < srcRows.length; i++) {
          const rr = srcRows[i] || {};
          const outRow = Object.assign({}, rr);
          outRow.videos = normalizeItems(rowItems(rr));
          outRow.accordion = false;
          outRow.accordion_marker = false;
          outRow.accordion_group = importGroupKey;
          out.push(outRow);
        }
        if (manualItems.length) {
          if (out.length) out[out.length - 1].videos = (out[out.length - 1].videos || []).concat(manualItems);
          else out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: manualItems });
        }
      } else if ((mode === "accordion" && accordion) || (mode === "preserve" && !accordion)) {
        for (let i = 0; i < srcRows.length; i++) {
          const rr = srcRows[i] || {};
          const vids = normalizeItems(rowItems(rr));
          out.push({
            heading: heading,
            subheading: subheading,
            img_ratio,
            row_height,
            fit_row,
            hide_cta_buttons: hide_cta_buttons || !!rr?.hide_cta_buttons,
            end_behavior_override: end_behavior_override || normalizeEndBehavior(rr?.end_behavior_override || rr?.end_of_video_override, ""),
            collapsed,
            show_more_text,
            show_less_text,
            row_toggle_border_style,
            custom_html: (custom_html && custom_html.trim()) ? custom_html : String(rr?.custom_html || ""),
            custom_template_id: custom_template_id || String(rr?.custom_template_id || ""),
            accordion: accordion || !!rr?.accordion,
            accordion_title: accordion ? deriveImportedAccordionTitle(rr, accordion_title) : String(rr?.accordion_title || ""),
            accordion_subheading: accordion ? deriveImportedAccordionSubheading(rr, accordion_subheading) : String(rr?.accordion_subheading || ""),
            accordion_content: accordion ? accordion_content : String(rr?.accordion_content || ""),
            accordion_hide_title_when_open: accordion ? accordion_hide_title_when_open : !!rr?.accordion_hide_title_when_open,
            accordion_hide_subheading_when_open: accordion ? accordion_hide_subheading_when_open : !!rr?.accordion_hide_subheading_when_open,
            accordion_hide_subheading_when_closed: accordion ? accordion_hide_subheading_when_closed : !!rr?.accordion_hide_subheading_when_closed,
            accordion_title_center: accordion ? accordion_title_center : !!rr?.accordion_title_center,
            accordion_start_open: accordion ? accordion_start_open : !!rr?.accordion_start_open,
            accordion_allow_multiple: accordion ? accordion_allow_multiple : !!rr?.accordion_allow_multiple,
            accordion_bg_color: accordion ? accordion_bg_color : String(rr?.accordion_bg_color || rr?.accordion_bg || ""),
            accordion_shadow_pct: accordion ? accordion_shadow_pct : normalizeAccordionShadowPct(rr?.accordion_shadow_pct, 8),
            accordion_shadow_style: accordion ? accordion_shadow_style : normalizeAccordionShadowStyle(rr?.accordion_shadow_style || rr?.accordion_shadow_effect, "raised"),
            accordion_group: accordion_group || String(rr?.accordion_group || "").trim(),
            accordion_always_open: accordion_always_open || !!rr?.accordion_always_open,
            accordion_marker: !!rr?.accordion_marker,
            collapse_group: String(rr?.collapse_group || ""),
            videos: vids
          });
        }
        if (manualItems.length) {
          if (out.length) out[out.length - 1].videos = (out[out.length - 1].videos || []).concat(manualItems);
          else out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: manualItems });
        }
      } else {
        let flat = [];
        for (const rr of srcRows) flat = flat.concat(normalizeItems(rowItems(rr)));
        if (manualItems.length) flat = flat.concat(manualItems);
        const flatRowEndBehaviorOverride = end_behavior_override || (() => {
          const vals = srcRows
            .map((rr) => normalizeEndBehavior(rr?.end_behavior_override || rr?.end_of_video_override, ""))
            .filter(Boolean);
          return (vals.length && vals.every((v) => v === vals[0])) ? vals[0] : "";
        })();
        out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons: hide_cta_buttons || srcRows.some((rr) => !!rr?.hide_cta_buttons), end_behavior_override: flatRowEndBehaviorOverride, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, custom_template_id, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: flat });
      }
    }

    BASE[target] = out;
    document.dispatchEvent(new Event("vimeo_sets_ready"));
    return true;
  }

  function ensureBuilt(spec){
    const target = String(spec?.targetSet || "").trim();
    if (!target) return;

    const required = extractRequired(spec);
    const now = Date.now();
    const rec = pending.get(target) || { spec, timer: null, since: now };
    rec.spec = spec;
    if (!rec.since) rec.since = now;
    pending.set(target, rec);

    if (!importsReady(required)) {
      if ((now - rec.since) >= IMPORT_WAIT_MAX_MS) {
        buildCompositeSet(spec);
        if (rec?.timer) clearTimeout(rec.timer);
        pending.delete(target);
        return;
      }

      if (!rec.timer) {
        rec.timer = setTimeout(() => {
          rec.timer = null;
          ensureBuilt(rec.spec);
        }, 150);
      }
      return;
    }

    buildCompositeSet(spec);
    if (rec?.timer) clearTimeout(rec.timer);
    pending.delete(target);
  }

  function buildFromDom(context){
    const scripts = getSpecScripts(context);
    for (const s of scripts) {
      const spec = safeJsonParse(s.textContent || "");
      if (spec) ensureBuilt(spec);
    }
  }

  /* ------------------------------
     Shared helpers
  ------------------------------ */
  const GLOBAL = window.__vimeoSnippetGlobal || (window.__vimeoSnippetGlobal = {
    escInstalled: false,
    closeCurrent: null
  });

  if (!GLOBAL.escInstalled) {
    GLOBAL.escInstalled = true;
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && typeof GLOBAL.closeCurrent === "function") {
        GLOBAL.closeCurrent();
      }
    });
  }

  function addDnsPrefetchOnce(href, key) {
    if (!document.head) return;
    if (GLOBAL[key]) return;
    GLOBAL[key] = true;

    const dns = document.createElement("link");
    dns.rel = "dns-prefetch";
    dns.href = href;
    document.head.appendChild(dns);
  }

  function addPreconnectOnce(href, key) {
    if (!document.head) return;
    if (GLOBAL[key]) return;
    GLOBAL[key] = true;

    const pre = document.createElement("link");
    pre.rel = "preconnect";
    pre.href = href;
    pre.crossOrigin = "";
    document.head.appendChild(pre);
  }

  function warmVimeoConnections() {
    addPreconnectOnce("https://player.vimeo.com", "warmedVimeoPlayer");
  }

  function warmVimeoOembedConnection() {
    addDnsPrefetchOnce("https://vimeo.com", "prefetchedVimeoOembed");
  }

  function createLimiter(concurrency) {
    let active = 0;
    const queue = [];
    const next = () => {
      active--;
      const fn = queue.shift();
      if (fn) fn();
    };
    return (task) =>
      new Promise((resolve, reject) => {
        const run = async () => {
          active++;
          try { resolve(await task()); }
          catch (err) { reject(err); }
          finally { next(); }
        };
        if (active < concurrency) run();
        else queue.push(run);
      });
  }

  // Safe URL normalizer:
  // - Allows: http(s), mailto, tel, sms
  // - Converts Shopify editor links like shopify://collections/all to a storefront path
  function safeLink(raw) {
    const s = String(raw || "").trim();
    if (!s) return "";

    // Shopify's internal scheme (commonly produced by the theme editor link picker)
    // Examples: shopify://products/handle, shopify://collections/all, shopify://pages/about
    if (/^shopify:\/\//i.test(s)) {
      const path = "/" + s.replace(/^shopify:\/\//i, "").replace(/^\/+/, "");
      try { return new URL(path, window.location.origin).href; } catch { return ""; }
    }

    try {
      const u = new URL(s, window.location.href);
      if (
        u.protocol === "http:" ||
        u.protocol === "https:" ||
        u.protocol === "mailto:" ||
        u.protocol === "tel:" ||
        u.protocol === "sms:"
      ) return u.href;
      return "";
    } catch { return ""; }
  }

  // Stricter SRC sanitizer: only http(s) and same-origin paths (resolved to http/s)
  function safeSrc(raw) {
    const s = String(raw || "").trim();
    if (!s) return "";
    if (/^shopify:\/\//i.test(s)) {
      const path = "/" + s.replace(/^shopify:\/\//i, "").replace(/^\/+/, "");
      try { return new URL(path, window.location.origin).href; } catch { return ""; }
    }
    try {
      const u = new URL(s, window.location.href);
      if (u.protocol === "http:" || u.protocol === "https:") return u.href;
      return "";
    } catch { return ""; }
  }

  function absUrlMaybe(url){
    // Used for image/thumbnail URLs. Keep this strict to avoid javascript: / other schemes.
    return safeSrc(url);
  }

  function sanitizeHtml(html) {
    const tpl = document.createElement("template");
    tpl.innerHTML = String(html || "");

    // Strip high-risk / layout-breaking elements
    tpl.content
      .querySelectorAll(
        "script, iframe, object, embed, style, link, meta, base, form, input, button, textarea, select, option, noscript"
      )
      .forEach((n) => n.remove());

    tpl.content.querySelectorAll("*").forEach((el) => {
      const tag = String(el.tagName || "").toLowerCase();

      // SVG/MathML are hard to sanitize safely; drop them.
      if (tag === "svg" || tag === "math") {
        try { el.remove(); } catch {}
        return;
      }

      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        const val = attr.value || "";

        if (name.startsWith("on")) { el.removeAttribute(attr.name); continue; }
        if (name === "style") { el.removeAttribute(attr.name); continue; }

        // URL-bearing attributes
        if (name === "href") {
          const ok = safeLink(val);
          if (!ok) el.removeAttribute(attr.name);
          else el.setAttribute(attr.name, ok);
          continue;
        }
        if (name === "src") {
          const ok = safeSrc(val);
          if (!ok) el.removeAttribute(attr.name);
          else el.setAttribute(attr.name, ok);
          continue;
        }

        // srcset can contain multiple URLs; drop it unless parsed/sanitized
        if (name === "srcset") { el.removeAttribute(attr.name); continue; }

        // SVG legacy href attribute (belt + suspenders)
        if (name === "xlink:href") {
          const ok = safeLink(val);
          if (!ok) el.removeAttribute(attr.name);
          else el.setAttribute(attr.name, ok);
          continue;
        }

        // Prevent abusive targets (e.g. _top / _parent)
        if (name === "target") {
          const t = String(val || "").toLowerCase();
          if (t !== "_blank" && t !== "_self") el.removeAttribute(attr.name);
          continue;
        }
      }

      // Enforce rel protections for external tab opens
      if (String(el.tagName || "").toUpperCase() === "A") {
        const target = (el.getAttribute("target") || "").toLowerCase();
        if (target === "_blank") {
          const relRaw = String(el.getAttribute("rel") || "");
          const rel = relRaw.split(/\s+/).filter(Boolean).map((s) => s.toLowerCase());
          if (!rel.includes("noopener")) rel.push("noopener");
          if (!rel.includes("noreferrer")) rel.push("noreferrer");
          el.setAttribute("rel", rel.join(" "));
        }
      }
    });

    const div = document.createElement("div");
    div.appendChild(tpl.content.cloneNode(true));
    return div.innerHTML;
  }

  function activateClonedScripts(scope) {
    try {
      const root = scope && scope.querySelectorAll ? scope : null;
      if (!root) return;
      const scripts = Array.from(root.querySelectorAll("script"));
      for (const oldScript of scripts) {
        const newScript = document.createElement("script");
        for (const attr of Array.from(oldScript.attributes || [])) {
          try { newScript.setAttribute(attr.name, attr.value); } catch (_) {}
        }
        if (oldScript.textContent) newScript.textContent = oldScript.textContent;
        oldScript.parentNode && oldScript.parentNode.replaceChild(newScript, oldScript);
      }
    } catch (_) {}
  }

  function unwrapSingleParagraph(html) {
    const tpl = document.createElement("template");
    tpl.innerHTML = String(html || "");
    const nodes = Array.from(tpl.content.childNodes).filter(n => {
      if (n.nodeType === 3) return (n.textContent || "").trim().length > 0;
      return n.nodeType === 1;
    });
    if (nodes.length === 1 && nodes[0].nodeType === 1 && nodes[0].tagName === "P") {
      return nodes[0].innerHTML || "";
    }
    return html;
  }

  function truncate(text, max = 14) {
    const t = String(text || "").trim();
    if (!t) return "";
    if (t.length <= max) return t;
    return t.slice(0, Math.max(0, max - 3)) + "...";
  }


  function collapseWhitespace(s){
    return String(s || "").replace(/\s+/g, " ").trim();
  }

  function truncateLong(text, max = 160) {
    const t = collapseWhitespace(text);
    if (!t) return "";
    if (t.length <= max) return t;
    return t.slice(0, Math.max(0, max - 1)).trimEnd() + "â€¦";
  }

  const ALT_TEXT_MAX = 127;

  function clampAltText(raw){
    const s = collapseWhitespace(raw);
    if (!s) return "";
    if (s.length <= ALT_TEXT_MAX) return s;
    return s.slice(0, ALT_TEXT_MAX).trimEnd();
  }

  // Default alt text builder (requested): Title + Description (if available)
  function buildDefaultAlt(title, desc){
    const t = collapseWhitespace(title);
    const d = collapseWhitespace(desc);
    if (t && d) return clampAltText(`${t} â€” ${d}`);
    return clampAltText(t || d || "");
  }

  function parseStartTimeSeconds(raw){
    if (raw == null) return 0;
    if (typeof raw === "number" && isFinite(raw)) return Math.max(0, Math.floor(raw));
    const s = String(raw).trim();
    if (!s) return 0;
    if (/^\d+(?:\.\d+)?$/.test(s)) return Math.max(0, Math.floor(parseFloat(s)));

    const colon = s.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?$/);
    if (colon) {
      if (typeof colon[3] !== "undefined") {
        return (parseInt(colon[1], 10) * 3600) + (parseInt(colon[2], 10) * 60) + parseInt(colon[3], 10);
      }
      return (parseInt(colon[1], 10) * 60) + parseInt(colon[2], 10);
    }

    const hms = s.match(/^(?:(\d+)\s*h(?:ours?)?\s*)?(?:(\d+)\s*m(?:in(?:utes?)?)?\s*)?(?:(\d+)\s*s(?:ec(?:onds?)?)?\s*)?$/i);
    if (hms && (hms[1] || hms[2] || hms[3])) {
      return (parseInt(hms[1] || "0", 10) * 3600) + (parseInt(hms[2] || "0", 10) * 60) + parseInt(hms[3] || "0", 10);
    }

    return 0;
  }

  function formatTimecodeFragment(totalSeconds){
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    let out = "";
    if (h > 0) out += `${h}h`;
    if (m > 0 || h > 0) out += `${m}m`;
    out += `${sec}s`;
    return out;
  }

  function parseStartTimeFromUrl(rawUrl){
    try {
      const u = new URL(String(rawUrl || ""), window.location.href);
      const direct = u.searchParams.get("start") || u.searchParams.get("t") || u.searchParams.get("time_continue");
      const hash = String(u.hash || "").replace(/^#/, "");
      const hashValue = hash.replace(/^(?:.*&)?(?:t|start)=/i, "");
      const value = direct || ((hashValue && hashValue !== hash) ? hashValue : hash);
      return parseStartTimeSeconds(value);
    } catch {
      return 0;
    }
  }

  function startTimeOf(meta){
    const explicit = parseStartTimeSeconds(meta?.start_time ?? meta?.startTime ?? meta?.start_at ?? meta?.startAt);
    if (explicit > 0) return explicit;
    return parseStartTimeFromUrl(meta?.url);
  }

  function heroQuickVideoUrlOf(meta){
    return safeSrc(
      meta?.quick_load_video_url ??
      meta?.quickLoadVideoUrl ??
      meta?.tiny_video_url ??
      meta?.tinyVideoUrl ??
      ""
    );
  }

  function shouldUseHeroQuickVideo(meta, mainUrl){
    const quickUrl = heroQuickVideoUrlOf(meta);
    if (!quickUrl || quickUrl === safeSrc(mainUrl)) return false;
    if (videoSourceFromUrl(quickUrl) !== "file") return false;
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.saveData) return false;
      const effectiveType = String(conn && conn.effectiveType || "").toLowerCase();
      if (effectiveType === "slow-2g" || effectiveType === "2g") return false;
    } catch {}
    return true;
  }

  function heroPrioritizesQuickVideo(meta){
    return isTrueLike(meta?.prioritize_quick_load_video ?? meta?.prioritizeQuickLoadVideo);
  }

  function heroWantsAutoplayPreview(meta){
    return isTrueLike(meta?.hero_autoplay_preview ?? meta?.autoplay_video) || !!heroQuickVideoUrlOf(meta);
  }

  function parseImgRatio(raw) {
    const s = String(raw ?? "").trim().toLowerCase();
    if (!s) return null;
    const m = s.match(/^(\d+(?:\.\d+)?)\s*[:\/]\s*(\d+(?:\.\d+)?)$/);
    if (!m) return null;
    const w = parseFloat(m[1]), h = parseFloat(m[2]);
    if (!(w > 0) || !(h > 0)) return null;
    return { w, h };
  }

  function normalizeVJust(raw){
    const v = String(raw || "").trim().toLowerCase();
    if (v === "baseline") return "bottom";
    if (v === "top" || v === "center" || v === "bottom") return v;
    return "top";
  }

  function clampScalePct(raw, min = 10, max = 200){
    const n = Number(raw);
    if (!isFinite(n)) return 100;
    return Math.max(min, Math.min(max, Math.round(n)));
  }

  function isTrueLike(raw){
    if (raw === true) return true;
    const s = String(raw == null ? "" : raw).trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes" || s === "on";
  }

  function isFalseLike(raw){
    if (raw === false) return true;
    const s = String(raw == null ? "" : raw).trim().toLowerCase();
    return s === "false" || s === "0" || s === "no" || s === "off";
  }

  function wantsAutoplayWithSound(meta){
    return isTrueLike(
      meta?.autoplay_with_sound ??
      meta?.autoplayWithSound ??
      meta?.autoplay_sound ??
      meta?.autoplaySound ??
      meta?.unmuted_autoplay ??
      meta?.unmutedAutoplay
    );
  }

  function hasAutoplayWithSoundPreference(meta){
    if (!meta || typeof meta !== "object") return false;
    return (
      Object.prototype.hasOwnProperty.call(meta, "autoplay_with_sound") ||
      Object.prototype.hasOwnProperty.call(meta, "autoplayWithSound") ||
      Object.prototype.hasOwnProperty.call(meta, "autoplay_sound") ||
      Object.prototype.hasOwnProperty.call(meta, "autoplaySound") ||
      Object.prototype.hasOwnProperty.call(meta, "unmuted_autoplay") ||
      Object.prototype.hasOwnProperty.call(meta, "unmutedAutoplay")
    );
  }

  function providerOf(meta){
    const t = String(meta?.type || meta?.kind || "").trim().toLowerCase();
    if (t === "vimeo" || t === "youtube" || t === "file" || t === "video") return "video";
    if ((t === "image" || t === "product") && hasVideoOnlySignals(meta)) return "video";
    if (t === "image" || t === "product") return t;
    return hasVideoOnlySignals(meta) ? "video" : "video";
  }
  function videoSourceOf(meta){
    const t = String(meta?.provider || meta?.source || meta?.video_source || meta?.videoSource || meta?.type || meta?.kind || "").trim().toLowerCase();
    if (t === "vimeo" || t === "youtube" || t === "file") return t;
    return videoSourceFromUrl(meta?.url) || "vimeo";
  }
  function isVideoKind(k){ return k === "video"; }

  function htmlHasMeaningfulContent(raw){
    const html = String(raw == null ? "" : raw).trim();
    if (!html) return false;
    try {
      const tpl = document.createElement("template");
      tpl.innerHTML = html;
      const text = collapseWhitespace(tpl.content.textContent || "");
      if (text) return true;
      return !!tpl.content.querySelector("img,video,iframe,canvas,svg,table,ul,ol,li,blockquote");
    } catch {
      return collapseWhitespace(html.replace(/<[^>]*>/g, " ")).length > 0;
    }
  }

  function hasAnyDesc(meta){
    const h = htmlHasMeaningfulContent(meta?.html_desc);
    const t = collapseWhitespace(meta?.description || "");
    const a = htmlHasMeaningfulContent(meta?.desc_html);
    const imageDesc = htmlHasMeaningfulContent(meta?.image_desc_html);
    const imageMore = htmlHasMeaningfulContent(meta?.image_more_desc_html);
    return !!(h || t || a || imageDesc || imageMore);
  }

  function primaryHref(meta){
    const p = providerOf(meta);
    const ctaHref = safeLink(meta?.cta?.href);
    if (ctaHref) return ctaHref;
    if (p === "product") return safeLink(meta?.product_url);
    return "";
  }

  function primaryText(meta){
    const txt = String(meta?.cta?.text || "").trim();
    if (txt) return txt;
    return "Shop";
  }

  function normalizeEndBehavior(raw, fallback = "next"){
    const s = String(raw || "").trim().toLowerCase();
    if (!s || s === "inherit") return fallback;
    const compact = s.replace(/[\s_-]+/g, "");
    if (compact === "next" || compact === "nextvideo") return "next";
    if (compact === "loop") return "loop";
    if (compact === "default") return "default";
    return fallback;
  }

  function normalizeAccordionShadowPct(raw, fallback = 8){
    const fb = Math.max(0, Math.min(100, Math.round(Number(fallback) || 0)));
    const n = Number(raw);
    if (!isFinite(n)) return fb;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  function normalizeAccordionShadowStyle(raw, fallback = "raised"){
    const base = String(fallback || "raised").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s_-]+/g, "");
    if (compact === "raised" || compact === "dropshadow" || compact === "drop") return "raised";
    if (compact === "pressed" || compact === "innershadow" || compact === "inner") return "pressed";
    if (compact === "layered" || compact === "layeredshadows" || compact === "stacked") return "layered";
    return base;
  }

  function buildAccordionShadowCss(rawStyle, rawPct){
    const style = normalizeAccordionShadowStyle(rawStyle, "raised");
    const pct = normalizeAccordionShadowPct(rawPct, 8);
    if (!(pct > 0)) return 'none';
    if (style === "pressed") {
      const dark = Math.max(0.10, Math.min(0.26, pct / 100 * 0.38));
      const mid = Math.max(0.06, Math.min(0.18, pct / 100 * 0.22));
      return `inset 0 2px 4px rgba(255,255,255,.18), inset 0 10px 22px rgba(0,0,0,${dark}), inset 0 -2px 6px rgba(0,0,0,${mid})`;
    }
    if (style === "layered") {
      const alphaA = Math.max(0.04, Math.min(0.12, pct / 100 * 0.14));
      const alphaB = Math.max(0.06, Math.min(0.16, pct / 100 * 0.20));
      const alphaC = Math.max(0.10, Math.min(0.24, pct / 100 * 0.30));
      return `0 1px 2px rgba(0,0,0,${alphaA}), 0 8px 18px rgba(0,0,0,${alphaB}), 0 20px 48px rgba(0,0,0,${alphaC})`;
    }
    const alphaA = Math.max(0.08, Math.min(0.30, pct / 100 * 0.46));
    const alphaB = Math.max(0.04, Math.min(0.18, pct / 100 * 0.24));
    const y = Math.round(10 + (pct * 0.10));
    const blur = Math.round(22 + (pct * 0.60));
    const spread = Math.round(pct * 0.03);
    return `0 ${y}px ${blur}px ${spread}px rgba(0,0,0,${alphaA}), 0 ${Math.max(2, Math.round(y * 0.45))}px ${Math.max(10, Math.round(blur * 0.46))}px 0 rgba(0,0,0,${alphaB})`;
  }

  function normalizeRowToggleBorderStyle(raw, fallback = "solid"){
    const base = String(fallback || "solid").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s\/_-]+/g, "");
    if (compact === "solid") return "solid";
    if (compact === "inset") return "inset";
    if (compact === "outset") return "outset";
    if (compact === "groove" || compact === "beveled" || compact === "beveledgroove") return "groove";
    if (compact === "ridge") return "ridge";
    if (compact === "double") return "double";
    if (compact === "dasheddotted" || compact === "dashed" || compact === "dotted") return "dashed_dotted";
    return base;
  }

  function normalizePresetButtonStyle(raw, fallback = "dropdown_box"){
    const base = String(fallback || "dropdown_box").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s\/_-]+/g, "");
    if (compact === "button" || compact === "pill") return "button";
    if (compact === "dropdown" || compact === "dropdownbox" || compact === "selector" || compact === "select") return "dropdown_box";
    if (compact === "imagebutton" || compact === "image" || compact === "imagecard" || compact === "imagefill") return "image_button";
    if (compact === "flatsurface" || compact === "flat" || compact === "surface") return "image_button";
    return base;
  }

  function normalizeProductShopButtonCtaType(raw, fallback = "below_title"){
    const base = String(fallback || "below_title").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s/_-]+/g, "");
    if (compact === "belowtitle" || compact === "below") return "below_title";
    if (compact === "overimage" || compact === "overlay") return "over_image";
    if (compact === "titletopbuttonbottom" || compact === "titletop" || compact === "titleabovebuttonbelow") return "title_top_button_bottom";
    if (compact === "maketitlelink" || compact === "titlelink" || compact === "linktitle") return "make_title_link";
    if (compact === "maketitlebutton" || compact === "titlebutton" || compact === "buttontitle") return "make_title_button";
    if (compact === "hide" || compact === "hidden" || compact === "none") return "hide";
    return base;
  }

  function normalizeProductShopButtonStyle(raw, fallback = "theme_default"){
    const base = String(fallback || "theme_default").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s/_-]+/g, "");
    if (compact === "themedefault" || compact === "theme" || compact === "default") return "theme_default";
    if (compact === "themesecondary" || compact === "secondary") return "theme_secondary";
    if (compact === "glassbackground" || compact === "glass") return "glass_background";
    if (compact === "raisedbutton" || compact === "raised") return "raised_button";
    if (compact === "custombutton" || compact === "custom") return "custom_button";
    if (compact === "simplebutton" || compact === "simple") return "custom_button";
    return base;
  }

  function normalizeBackgroundImageStyle(raw, fallback = "center") {
    const base = String(fallback || "center").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    if (s === "center" || s === "stretch_to_fit" || s === "full_width" || s === "full_height") return s;
    return base;
  }

  function topLightShadowValue(shadowSize) {
    const s = Math.max(0, Math.min(60, Number(shadowSize) || 0));
    if (!s) return "none";
    const y = Math.max(2, Math.round(s * 0.55));
    const blurVal = Math.max(4, Math.round(s * 1.35));
    return `0 ${y}px ${blurVal}px rgba(128,128,128,0.45)`;
  }

  function parseCssColorToRgba(raw){
    const s = String(raw || "").trim();
    if (!s) return null;

    let m = s.match(/^#([0-9a-f]{3})$/i);
    if (m) {
      const hex = m[1];
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: 1
      };
    }

    m = s.match(/^#([0-9a-f]{4})$/i);
    if (m) {
      const hex = m[1];
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16),
        a: parseInt(hex[3] + hex[3], 16) / 255
      };
    }

    m = s.match(/^#([0-9a-f]{6})$/i);
    if (m) {
      const hex = m[1];
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: 1
      };
    }

    m = s.match(/^#([0-9a-f]{8})$/i);
    if (m) {
      const hex = m[1];
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: parseInt(hex.slice(6, 8), 16) / 255
      };
    }

    m = s.match(/^rgba?\(\s*([0-9.]+)\s*[, ]\s*([0-9.]+)\s*[, ]\s*([0-9.]+)(?:\s*[,/]\s*([0-9.]+))?\s*\)$/i);
    if (m) {
      return {
        r: Math.max(0, Math.min(255, Number(m[1]) || 0)),
        g: Math.max(0, Math.min(255, Number(m[2]) || 0)),
        b: Math.max(0, Math.min(255, Number(m[3]) || 0)),
        a: m[4] == null ? 1 : Math.max(0, Math.min(1, Number(m[4]) || 0))
      };
    }

    if (!document || !document.body || !window.getComputedStyle) return null;
    try {
      const probe = document.createElement("span");
      probe.style.color = s;
      if (!probe.style.color) return null;
      probe.style.position = "absolute";
      probe.style.left = "-99999px";
      probe.style.top = "0";
      document.body.appendChild(probe);
      const resolved = String(window.getComputedStyle(probe).color || "");
      probe.remove();
      const rm = resolved.match(/^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)$/i);
      if (!rm) return null;
      return {
        r: Math.max(0, Math.min(255, Number(rm[1]) || 0)),
        g: Math.max(0, Math.min(255, Number(rm[2]) || 0)),
        b: Math.max(0, Math.min(255, Number(rm[3]) || 0)),
        a: rm[4] == null ? 1 : Math.max(0, Math.min(1, Number(rm[4]) || 0))
      };
    } catch {
      return null;
    }
  }

  function colorWithOpacity(raw, opacityPct, fallback = ""){
    const parsed = parseCssColorToRgba(raw);
    if (!parsed) return fallback || String(raw || "");
    const opacity = Math.max(0, Math.min(1, (Number(opacityPct) || 0) / 100));
    const alpha = Math.max(0, Math.min(1, parsed.a * opacity));
    return `rgba(${Math.round(parsed.r)}, ${Math.round(parsed.g)}, ${Math.round(parsed.b)}, ${alpha})`;
  }

  function normalizePresetButtonWidthPercent(raw, fallback = 0){
    const baseNum = Number(fallback);
    const base = Number.isFinite(baseNum) ? Math.max(0, Math.min(100, baseNum)) : 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return base;
    return Math.max(0, Math.min(100, n));
  }

  function normalizeWidthOverridePercent(raw, fallback = 0){
    const baseNum = Number(fallback);
    const base = Number.isFinite(baseNum) ? Math.max(0, Math.min(200, Math.round(baseNum))) : 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return base;
    return Math.max(0, Math.min(200, Math.round(n)));
  }

  function normalizePresetButtonMaxWidthPx(raw, fallback = 0){
    const coerce = (value, fallbackValue = NaN) => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      const s = String(value ?? "").trim();
      if (!s) return fallbackValue;
      const match = s.match(/-?\d+(?:\.\d+)?/);
      if (!match) return fallbackValue;
      const num = Number(match[0]);
      return Number.isFinite(num) ? num : fallbackValue;
    };

    const baseNum = coerce(fallback, 0);
    const base = Number.isFinite(baseNum) ? Math.max(0, Math.min(2400, Math.round(baseNum))) : 0;
    const n = coerce(raw, NaN);
    if (!Number.isFinite(n)) return base;
    return Math.max(0, Math.min(2400, Math.round(n)));
  }

  function normalizePresetButtonTextHtml(raw, fallback = "Open Preset"){
    const safe = sanitizeHtml(String(raw || ""));
    if (plainTextFromMaybeHtml(safe)) return safe;
    return sanitizeHtml(String(fallback || "Open Preset"));
  }

  function normalizePresetButtonHintHtml(raw, fallback = ""){
    const safe = sanitizeHtml(String(raw || ""));
    if (plainTextFromMaybeHtml(safe)) return safe;
    const fb = sanitizeHtml(String(fallback || ""));
    if (plainTextFromMaybeHtml(fb)) return fb;
    return "";
  }

  function safeCssColor(raw, fallback = "rgba(255,255,255,.72)"){
    const s = String(raw || "").trim();
    if (!s) return fallback;

    if (/^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s)) return s;
    if (/^(?:rgb|hsl)a?\(/i.test(s)) return s;
    if (/^var\(--[A-Za-z0-9_-]+\)$/i.test(s)) return s;
    if (/^[A-Za-z]+$/i.test(s)) return s;

    try {
      if (window.CSS && typeof window.CSS.supports === "function" && window.CSS.supports("color", s)) return s;
    } catch {}

    try {
      const probe = document.createElement('span');
      probe.style.color = '';
      probe.style.color = s;
      if (probe.style.color) return s;
    } catch {}

    return fallback;
  }

  function escapeSvgText(input){
    return String(input || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function buildVideoThumbPlaceholderDataUrl(meta){
    const title = truncateLong(String(meta?.title || '').trim() || 'Video', 48);
    const safeTitle = escapeSvgText(title);
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720" role="img" aria-label="${safeTitle}">
  <rect width="1280" height="720" fill="#151515"/>
  <rect x="24" y="24" width="1232" height="672" rx="34" fill="#202020" stroke="rgba(255,255,255,.14)"/>
  <circle cx="640" cy="320" r="86" fill="rgba(0,0,0,.72)" stroke="rgba(255,255,255,.18)"/>
  <polygon points="615,270 615,370 700,320" fill="#ffffff"/>
  <text x="640" y="470" text-anchor="middle" font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif" font-size="46" font-weight="700" fill="#ffffff">${safeTitle}</text>
</svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  function captureFileVideoThumbnail(url, startTime = 0, timeoutMs = 9000){
    const src = safeSrc(url);
    if (!src) return Promise.resolve('');

    return new Promise((resolve) => {
      let settled = false;
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';

      const cleanup = () => {
        try { video.pause(); } catch {}
        try { video.removeAttribute('src'); video.load(); } catch {}
        try { video.remove(); } catch {}
      };
      const finish = (value) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(String(value || ''));
      };
      const tryCapture = () => {
        try {
          const w = Number(video.videoWidth || 0);
          const h = Number(video.videoHeight || 0);
          if (!(w > 0) || !(h > 0)) { finish(''); return; }
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) { finish(''); return; }
          ctx.drawImage(video, 0, 0, w, h);
          finish(canvas.toDataURL('image/jpeg', 0.84));
        } catch {
          finish('');
        }
      };
      const queueCapture = () => {
        const duration = Number(video.duration || 0);
        let t = Math.max(0, Math.floor(Number(startTime) || 0));
        if (duration && isFinite(duration)) t = Math.max(0, Math.min(t, Math.max(0, duration - 0.1)));
        if (t > 0.05) {
          const onSeeked = () => { tryCapture(); };
          video.addEventListener('seeked', onSeeked, { once:true });
          try {
            video.currentTime = t;
          } catch {
            setTimeout(tryCapture, 120);
          }
          setTimeout(tryCapture, 1200);
        } else {
          setTimeout(tryCapture, 90);
        }
      };

      video.addEventListener('loadeddata', queueCapture, { once:true });
      video.addEventListener('loadedmetadata', () => { setTimeout(queueCapture, 60); }, { once:true });
      video.addEventListener('error', () => finish(''), { once:true });
      setTimeout(() => finish(''), Math.max(2500, timeoutMs | 0));

      try {
        video.src = src;
        video.load();
      } catch {
        finish('');
      }
    });
  }

  function plainTextFromMaybeHtml(input){
    const raw = String(input || "");
    if (!raw) return "";
    if (raw.indexOf("<") === -1 && raw.indexOf("&") === -1) return String(raw).replace(/\s+/g, " ").trim();
    try {
      const tpl = document.createElement("template");
      tpl.innerHTML = raw;
      return String(tpl.content.textContent || "").replace(/\s+/g, " ").trim();
    } catch {
      return String(raw.replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();
    }
  }

  function deriveImportedAccordionTitle(rowLike, fallback = "Accordion Tab"){
    const accordionTitle = plainTextFromMaybeHtml(rowLike?.accordion_title || "");
    if (accordionTitle) return accordionTitle;

    const showMore = plainTextFromMaybeHtml(rowLike?.show_more_text || "");
    if (showMore && showMore.toLowerCase() !== "show more") return showMore;

    const headingText = plainTextFromMaybeHtml(rowLike?.heading || rowLike?.title || "");
    if (headingText) return headingText;

    const fb = plainTextFromMaybeHtml(fallback);
    return fb || "Accordion Tab";
  }

  function deriveImportedAccordionSubheading(rowLike, fallback = ""){
    const candidate = String(
      rowLike?.accordion_subheading ||
      rowLike?.subheading ||
      ""
    ).trim();
    return candidate || String(fallback || "");
  }

  function escapeHtmlString(input){
    return String(input ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function buildMissingImportHtml(importKey){
    const safeKey = escapeHtmlString(String(importKey || "").trim());
    return '<div style="margin:0 auto; width:min(920px,100%); padding:14px 16px; border-radius:14px; border:1px solid rgba(122,16,16,.18); background:rgba(255,245,245,.96); color:#7a1010; box-shadow:0 10px 30px rgba(0,0,0,.08); font:600 14px/1.45 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;">Media Gallery Preset key <code style="font:700 13px/1.3 ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace; background:rgba(0,0,0,.06); padding:2px 6px; border-radius:6px;">' + safeKey + '</code> does not exist.</div>';
  }

  function appendMissingImportMessage(customHtml, importKey){
    const msg = buildMissingImportHtml(importKey);
    const base = String(customHtml || "").trim();
    if (!base) return msg;
    return base + '<div style="height:12px" aria-hidden="true"></div>' + msg;
  }
  function deriveAccordionTitleHtmlFromElement(el, fallback = "Accordion Tab"){
    const direct = String(el && el.__accordionTitleHtml || "").trim();
    if (direct) return direct;

    try {
      const headingEl = el && el.querySelector ? el.querySelector('.row-heading') : null;
      const headingHtml = String(headingEl && headingEl.innerHTML || "").trim();
      if (headingHtml) return headingHtml;
    } catch {}

    const collapseMore = collapseWhitespace(el && el.dataset ? el.dataset.collapseMore || "" : "");
    if (collapseMore) return escapeHtml(collapseMore);

    return escapeHtml(collapseWhitespace(fallback || "Accordion Tab") || "Accordion Tab");
  }

  function findNearestScrollContainer(startEl){
    let el = startEl && startEl.parentElement ? startEl.parentElement : null;
    while (el && el !== document.body && el !== document.documentElement) {
      try {
        const cs = window.getComputedStyle(el);
        const oy = String(cs.overflowY || "").toLowerCase();
        if ((oy === "auto" || oy === "scroll") && el.scrollHeight > (el.clientHeight + 4)) return el;
      } catch {}
      el = el.parentElement;
    }
    return null;
  }

  function getStickyHeaderOffset(){
    let maxBottom = 0;
    const seen = new Set();
    const viewportW = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 0);
    const viewportH = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 0);

    const consider = (el) => {
      let node = el;
      while (node && node.nodeType === 1 && node !== document.body && node !== document.documentElement) {
        if (seen.has(node)) {
          node = node.parentElement;
          continue;
        }
        seen.add(node);
        try {
          const cs = window.getComputedStyle(node);
          const pos = String(cs.position || "").toLowerCase();
          if (pos !== "sticky" && pos !== "fixed") { node = node.parentElement; continue; }
          if (cs.display === "none" || cs.visibility === "hidden" || Number(cs.opacity || "1") <= 0.01) { node = node.parentElement; continue; }
          const rect = node.getBoundingClientRect();
          if (!rect || rect.height <= 0 || rect.width <= Math.min(240, viewportW * 0.3)) { node = node.parentElement; continue; }
          if (rect.bottom <= 0) { node = node.parentElement; continue; }
          if (rect.top > Math.max(12, viewportH * 0.18)) { node = node.parentElement; continue; }
          maxBottom = Math.max(maxBottom, rect.bottom);
        } catch {}
        node = node.parentElement;
      }
    };

    const selector = [
      '.shopify-section-group-header-group',
      '#shopify-section-header',
      '[id*="shopify-section-header"]',
      'header',
      '.header-wrapper',
      '.header',
      '[data-header]',
      '[data-sticky-header]',
      'sticky-header',
      '.sticky-header',
      '.announcement-bar',
      '.shopify-section[class*="announcement"]'
    ].join(',');

    try { document.querySelectorAll(selector).forEach(consider); } catch {}

    if (typeof document.elementsFromPoint === "function") {
      const x = Math.round(viewportW * 0.5);
      const ys = [1, 8, 16, 24, 32, 48, 64, 80, 96, 120, 144];
      for (const y of ys) {
        if (y >= viewportH) break;
        try {
          const els = document.elementsFromPoint(x, y) || [];
          els.forEach(consider);
        } catch {}
      }
    }

    return Math.max(0, Math.round(maxBottom));
  }

  function scrollAccordionTopIntoView(item, focusEl){
    const btn = focusEl || (item && item.querySelector ? item.querySelector('.accordion-row__toggle') : null);

    try { if (btn) btn.focus({ preventScroll: true }); }
    catch (_e) { try { if (btn) btn.focus(); } catch (_e2) {} }

    const panel = item && item.querySelector ? item.querySelector('.accordion-row__panel') : null;
    const viewportPadding = 12;

    const scroller = findNearestScrollContainer(item);
    if (scroller) {
      try {
        const btnRect = btn ? btn.getBoundingClientRect() : null;
        const panelRect = (panel && !panel.hidden) ? panel.getBoundingClientRect() : null;
        const scrollerRect = scroller.getBoundingClientRect();
        if (!btnRect) return;

        const viewportTop = scrollerRect.top + viewportPadding;
        const viewportBottom = scrollerRect.bottom - viewportPadding;
        let targetTop = scroller.scrollTop;

        if (btnRect.top < viewportTop || btnRect.bottom > viewportBottom) {
          targetTop = scroller.scrollTop + (btnRect.top - viewportTop);
        } else if (panelRect) {
          const visibleBelowTrigger = viewportBottom - Math.max(btnRect.bottom, viewportTop);
          const visiblePanelHeight = Math.max(0, viewportBottom - panelRect.top);
          if (visibleBelowTrigger < 120 || visiblePanelHeight < 120) {
            targetTop = scroller.scrollTop + (btnRect.top - viewportTop);
          }
        }

        scroller.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
        return;
      } catch {}
    }

    try {
      const btnRect = btn ? btn.getBoundingClientRect() : null;
      const panelRect = (panel && !panel.hidden) ? panel.getBoundingClientRect() : null;
      if (!btnRect) return;

      const headerOffset = getStickyHeaderOffset();
      const viewportTop = headerOffset + viewportPadding;
      const viewportBottom = Math.max(
        viewportTop + 1,
        (window.innerHeight || document.documentElement.clientHeight || 0) - viewportPadding
      );

      let targetTop = window.pageYOffset;

      if (btnRect.top < viewportTop || btnRect.bottom > viewportBottom) {
        targetTop = window.pageYOffset + (btnRect.top - viewportTop);
      } else if (panelRect) {
        const visibleBelowTrigger = viewportBottom - Math.max(btnRect.bottom, viewportTop);
        const visiblePanelHeight = Math.max(0, viewportBottom - panelRect.top);
        if (visibleBelowTrigger < 120 || visiblePanelHeight < 120) {
          targetTop = window.pageYOffset + (btnRect.top - viewportTop);
        }
      }

      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
      return;
    } catch {}

    try { if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }); }
    catch (_e) { try { if (btn) btn.scrollIntoView(true); } catch (_e2) {} }
  }

  function endBehaviorOf(meta, rowCtx){
    return normalizeEndBehavior(
      rowCtx?.endBehaviorOverride ||
      meta?.__end_behavior_override ||
      meta?.end_behavior ||
      meta?.end_of_video ||
      meta?.end_of_video_behavior,
      "next"
    );
  }

  function hideCtaButtons(meta, rowCtx){
    return !!(rowCtx?.hideCtaButtons || meta?.hide_cta_buttons || meta?.__hide_cta_buttons);
  }

  function parseRatioOverride(raw){
    const s = String(raw || "").trim();
    if (!s) return null;
    const low = s.toLowerCase();
    if (low === "row" || low === "row default" || low === "inherit") return null;
    return parseImgRatio(s);
  }

  function numCss(v){
    const n = parseFloat(String(v || "0"));
    return isFinite(n) ? n : 0;
  }

  function rowContentWidth(rowEl){
    const cs = getComputedStyle(rowEl);
    const padL = numCss(cs.paddingLeft);
    const padR = numCss(cs.paddingRight);
    return Math.max(0, rowEl.clientWidth - padL - padR);
  }

  function rowGapPx(rowEl){
    const cs = getComputedStyle(rowEl);
    return numCss(cs.columnGap || cs.gap || "0");
  }

  /* ------------------------------
     Vimeo oEmbed + player
  ------------------------------ */
  const VIMEO_OEMBED_CACHE = window.__vimeoOembedCache || (window.__vimeoOembedCache = new Map());
  const VIMEO_OEMBED_LIMIT = window.__vimeoOembedLimit || (window.__vimeoOembedLimit = createLimiter(4));

  const isGifUrl = (u) => /\.gif(\?|#|$)/i.test(String(u || ""));
  function pickVimeoThumbUrl(data) {
    if (!data) return "";
    const candidates = [
      data.thumbnail_url,
      data.thumbnail_url_with_play_button,
      data.thumbnail_url_with_play_button_url
    ].filter(Boolean).map(String);
    const gif = candidates.find(isGifUrl);
    return gif || candidates[0] || "";
  }

  function cacheVimeoSizing(meta, data){
    try{
      if (!meta || !data) return;
      // Use thumbnail dimensions first (they track the true video aspect ratio more reliably than oEmbed width/height).
      const tw = Number(data.thumbnail_width || 0);
      const th = Number(data.thumbnail_height || 0);
      const w = (tw > 0) ? tw : Number(data.width || 0);
      const h = (th > 0) ? th : Number(data.height || 0);
      if (w > 0) meta.__w = w;
      if (h > 0) meta.__h = h;
    } catch {}
  }

  async function fetchVimeoOEmbed(url) {
    if (!url) throw new Error("Missing url");
    if (VIMEO_OEMBED_CACHE.has(url)) return VIMEO_OEMBED_CACHE.get(url);

    const p = VIMEO_OEMBED_LIMIT(async () => {
      warmVimeoOembedConnection();
      const res = await fetch("https://vimeo.com/api/oembed.json?url=" + encodeURIComponent(url), { credentials: "omit" });
      if (!res.ok) throw new Error("Vimeo oEmbed failed");
      return res.json();
    });

    VIMEO_OEMBED_CACHE.set(url, p);
    try { return await p; }
    catch (e) { VIMEO_OEMBED_CACHE.delete(url); throw e; }
  }

  function getVimeoId(url) {
    try {
      const parts = new URL(url).pathname.split("/").filter(Boolean);
      for (let i = parts.length - 1; i >= 0; i--) if (/^\d+$/.test(parts[i])) return parts[i];
      return parts[parts.length - 1] || "";
    } catch {
      const m = String(url).match(/(\d+)(?:\D*$)/);
      return m ? m[1] : String(url).split("/").pop();
    }
  }

  function getYouTubeId(url) {
    try {
      const u = new URL(String(url || ""), window.location.href);
      const host = String(u.hostname || "").toLowerCase();
      if (host === "youtu.be") return u.pathname.replace(/^\/+/, "").split("/")[0] || "";
      if (u.searchParams.get("v")) return u.searchParams.get("v") || "";
      const parts = u.pathname.split("/").filter(Boolean);
      const embedIdx = parts.findIndex((p) => p === "embed" || p === "shorts" || p === "live");
      if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
      return parts.pop() || "";
    } catch {
      const m = String(url || "").match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
      return m ? m[1] : "";
    }
  }

  function pickYouTubeThumb(url) {
    const id = getYouTubeId(url);
    if (!id) return "";
    return `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
  }

  function buildVimeoSrc(url, autoplay, muted, endBehavior, startTime) {
    const id = getVimeoId(url);
    const mode = normalizeEndBehavior(endBehavior, "next");
    let params =
      "autoplay=" + (autoplay ? "1" : "0") +
      "&muted=" + (muted ? "1" : "0") +
      "&title=0&byline=0&portrait=0" +
      "&playsinline=1&dnt=1&api=1&autopause=0";
    if (mode === "loop") params += "&loop=1";
    else if (mode === "next") params += "&loop=0";
    let out = "https://player.vimeo.com/video/" + encodeURIComponent(id) + "?" + params;
    const startSecs = Math.max(0, Math.floor(Number(startTime) || 0));
    if (startSecs > 0) out += "#t=" + encodeURIComponent(formatTimecodeFragment(startSecs));
    return out;
  }

  function buildYouTubeSrc(url, autoplay, muted, startTime, endBehavior) {
    const id = getYouTubeId(url);
    const mode = normalizeEndBehavior(endBehavior, "next");
    const params = new URLSearchParams();
    params.set("autoplay", autoplay ? "1" : "0");
    params.set("playsinline", "1");
    params.set("rel", "0");
    params.set("enablejsapi", "1");
    params.set("origin", window.location.origin);
    params.set("mute", muted ? "1" : "0");
    const startSecs = Math.max(0, Math.floor(Number(startTime) || 0));
    if (startSecs > 0) params.set("start", String(startSecs));
    if (mode === "loop" && id) {
      params.set("loop", "1");
      params.set("playlist", id);
    }
    return "https://www.youtube.com/embed/" + encodeURIComponent(id) + "?" + params.toString();
  }

  function buildVimeoPreviewSrc(url, startTime) {
    const id = getVimeoId(url);
    const params = new URLSearchParams();
    params.set("background", "1");
    params.set("autoplay", "1");
    params.set("muted", "1");
    params.set("loop", "1");
    params.set("autopause", "0");
    params.set("playsinline", "1");
    params.set("dnt", "1");
    params.set("api", "1");
    let out = "https://player.vimeo.com/video/" + encodeURIComponent(id) + "?" + params.toString();
    const startSecs = Math.max(0, Math.floor(Number(startTime) || 0));
    if (startSecs > 0) out += "#t=" + encodeURIComponent(formatTimecodeFragment(startSecs));
    return out;
  }

  function buildYouTubePreviewSrc(url, startTime) {
    const id = getYouTubeId(url);
    const params = new URLSearchParams();
    params.set("autoplay", "1");
    params.set("playsinline", "1");
    params.set("rel", "0");
    params.set("mute", "1");
    params.set("controls", "0");
    params.set("modestbranding", "1");
    params.set("iv_load_policy", "3");
    params.set("disablekb", "1");
    params.set("fs", "0");
    params.set("loop", "1");
    params.set("enablejsapi", "1");
    params.set("origin", window.location.origin);
    if (id) params.set("playlist", id);
    const startSecs = Math.max(0, Math.floor(Number(startTime) || 0));
    if (startSecs > 0) params.set("start", String(startSecs));
    return "https://www.youtube.com/embed/" + encodeURIComponent(id) + "?" + params.toString();
  }

  function vimeoPost(iframe, method, value) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      const msg = (typeof value === "undefined") ? { method } : { method, value };
      iframe.contentWindow.postMessage(JSON.stringify(msg), "https://player.vimeo.com");
    } catch {}
  }

  function pauseVimeo(iframe){ vimeoPost(iframe, "pause"); }


  function applyVimeoStartTime(iframe, startTime, token) {
    const startSecs = Math.max(0, Math.floor(Number(startTime) || 0));
    if (!(startSecs > 0) || !iframe) return;
    const tok = token == null ? null : String(token);
    const ok = () => !tok || (iframe && iframe.dataset && iframe.dataset.mgToken === tok);
    const tries = [0, 180, 420, 900, 1400];
    for (const ms of tries) {
      setTimeout(() => {
        try {
          if (!ok()) return;
          vimeoPost(iframe, "setCurrentTime", startSecs);
        } catch {}
      }, ms);
    }
  }

  function configureVimeoLoopMode(iframe, endBehavior, token) {
    if (!iframe) return;
    const mode = normalizeEndBehavior(endBehavior, "next");
    if (mode === "default") return;
    const tok = token == null ? null : String(token);
    const ok = () => !tok || (iframe && iframe.dataset && iframe.dataset.mgToken === tok);
    const shouldLoop = mode === "loop";
    const tries = [0, 160, 420, 900];
    for (const ms of tries) {
      setTimeout(() => {
        try {
          if (!ok()) return;
          vimeoPost(iframe, "setLoop", shouldLoop);
        } catch {}
      }, ms);
    }
  }

  function restartVimeoFromStart(iframe, token, preferMuted) {
    if (!iframe) return;
    const tok = token == null ? null : String(token);
    try { vimeoPost(iframe, "setCurrentTime", 0); } catch {}
    if (preferMuted) kickPlayVimeoMuted(iframe, tok);
    else kickPlayVimeoUnmuted(iframe, tok);
  }

  function kickPlayVimeoUnmuted(iframe, token) {
    if (!iframe) return;

    // Guard against stale async retries when the modal has already moved on
    const tok = token == null ? null : String(token);
    const ok = () => !tok || (iframe && iframe.dataset && iframe.dataset.mgToken === tok);

    // âœ… First attempt synchronously (helps iOS treat it as user-initiated)
    try {
      if (!ok()) return;
      // small immediate burst so at least one lands while the player is ready
      for (let i = 0; i < 3; i++) {
        vimeoPost(iframe, "setMuted", false);
        vimeoPost(iframe, "setVolume", 1);
      }
      vimeoPost(iframe, "play");
      vimeoPost(iframe, "setMuted", false);
    } catch {}

    // Retry a few times while the iframe finishes booting
    const tries = [120, 350, 700, 1100];
    for (const ms of tries) {
      setTimeout(() => {
        try {
          if (!ok()) return;
          vimeoPost(iframe, "setMuted", false);
          vimeoPost(iframe, "setVolume", 1);
          vimeoPost(iframe, "play");
        } catch {}
      }, ms);
    }
  }
  function kickPlayVimeoMuted(iframe, token) {
    if (!iframe) return;

    // Guard against stale async retries when the modal has already moved on
    const tok = token == null ? null : String(token);
    const ok = () => !tok || (iframe && iframe.dataset && iframe.dataset.mgToken === tok);

    // âœ… First attempt synchronously
    try {
      if (!ok()) return;
      vimeoPost(iframe, "setMuted", true);
      vimeoPost(iframe, "play");
    } catch {}

    const tries = [120, 350, 700];
    for (const ms of tries) {
      setTimeout(() => {
        try {
          if (!ok()) return;
          vimeoPost(iframe, "setMuted", true);
          vimeoPost(iframe, "play");
        } catch {}
      }, ms);
    }
  }

  function playMediaPreferSound(mediaEl) {
    try {
      if (!mediaEl || typeof mediaEl.play !== "function") return;
      mediaEl.defaultMuted = false;
      mediaEl.muted = false;
      try { mediaEl.removeAttribute("muted"); } catch {}
      const p = mediaEl.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          try {
            mediaEl.defaultMuted = true;
            mediaEl.muted = true;
            mediaEl.setAttribute("muted", "");
            const retry = mediaEl.play();
            if (retry && typeof retry.catch === "function") retry.catch(() => {});
          } catch {}
        });
      }
    } catch {}
  }

  function unregisterVimeoEnded(iframe) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      VIMEO_END_HANDLERS.delete(iframe.contentWindow);
    } catch {}
  }

  function registerVimeoEnded(iframe, onEnded) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      if (typeof onEnded !== "function") return;
      VIMEO_END_HANDLERS.set(iframe.contentWindow, onEnded);
      vimeoPost(iframe, "addEventListener", "ended");
    } catch {}
  }


  function unregisterVimeoTimeUpdate(iframe) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      VIMEO_TIME_HANDLERS.delete(iframe.contentWindow);
    } catch {}
  }

  function registerVimeoTimeUpdate(iframe, onTimeUpdate) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      if (typeof onTimeUpdate !== "function") return;

      // Store handler for global message router
      VIMEO_TIME_HANDLERS.set(iframe.contentWindow, onTimeUpdate);

      // Ask Vimeo player to emit timeupdate events
      const tries = [0, 180, 420, 900];
      for (const ms of tries) {
        setTimeout(() => {
          try { vimeoPost(iframe, "addEventListener", "timeupdate"); } catch {}
        }, ms);
      }
    } catch {}
  }

  // ------------------------------
  // Vimeo mute/volume state
  // ------------------------------
  function unregisterVimeoVolumeChange(iframe) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      VIMEO_VOLUME_HANDLERS.delete(iframe.contentWindow);
    } catch {}
  }

  function registerVimeoVolumeChange(iframe, onVolumeChange) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      if (typeof onVolumeChange !== "function") return;

      VIMEO_VOLUME_HANDLERS.set(iframe.contentWindow, onVolumeChange);

      // Ask Vimeo to emit volumechange events.
      const tries = [0, 180, 420, 900];
      for (const ms of tries) {
        setTimeout(() => {
          try { vimeoPost(iframe, "addEventListener", "volumechange"); } catch {}
        }, ms);
      }
    } catch {}
  }

  function unregisterVimeoMethodResponses(iframe) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      VIMEO_METHOD_HANDLERS.delete(iframe.contentWindow);
    } catch {}
  }

  function registerVimeoMethodResponses(iframe, onMethod) {
    try {
      if (!iframe || !iframe.contentWindow) return;
      if (typeof onMethod !== "function") return;
      VIMEO_METHOD_HANDLERS.set(iframe.contentWindow, onMethod);
    } catch {}
  }

  function requestVimeoMuteState(iframe) {
    try {
      if (!iframe) return;
      vimeoPost(iframe, "getMuted");
      // getVolume isn't strictly required, but it's useful for some edge cases.
      vimeoPost(iframe, "getVolume");
    } catch {}
  }

  /* ------------------------------
     Init snippets
  ------------------------------ */
  function initAll(context) {
    const scope = context || document;
    const roots = [];
    if (scope && scope.nodeType === 1 && scope.matches && scope.matches(".vimeo-snippet")) roots.push(scope);
    if (scope && scope.querySelectorAll) roots.push(...scope.querySelectorAll(".vimeo-snippet"));
    for (const root of roots) {
      if (root.__vimeoInit) continue;
      root.__vimeoInit = true;
      initRoot(root);
    }
  }

  function initRoot(root) {
    const setName = (root.getAttribute("data-vimeo-set") || "").trim();
    const shopCurrency = String(root.getAttribute("data-shop-currency") || "").trim() || "USD";
    const SECTION_DEFAULT_PRODUCT_SHOP_BUTTON_STYLE = normalizeProductShopButtonStyle(
      root.getAttribute("data-mg-shop-button-style"),
      "theme_default"
    );
    const clampWidthPercent = (raw, fallback) => {
      const safe = isFinite(raw) ? raw : fallback;
      return Math.max(10, Math.min(200, Math.round(safe)));
    };
    const DEFAULT_BLOCK_WIDTH_DESKTOP_PCT = (() => {
      const raw = Number(
        root.getAttribute("data-block-width-pct-desktop") ||
        root.getAttribute("data-block-width-pct") ||
        root.style.getPropertyValue("--mg-block-width-pct-desktop") ||
        root.style.getPropertyValue("--mg-block-width-pct") ||
        100
      );
      return clampWidthPercent(raw, 100);
    })();
    const DEFAULT_BLOCK_WIDTH_MOBILE_PCT = (() => {
      const raw = Number(
        root.getAttribute("data-block-width-pct-mobile") ||
        root.style.getPropertyValue("--mg-block-width-pct-mobile") ||
        DEFAULT_BLOCK_WIDTH_DESKTOP_PCT
      );
      return clampWidthPercent(raw, DEFAULT_BLOCK_WIDTH_DESKTOP_PCT);
    })();
    const clampDesktopMaxWidthPx = (raw, fallback = 0) => {
      const n = Number(raw);
      if (!isFinite(n) || n <= 0) return Math.max(0, Math.round(fallback || 0));
      return Math.max(0, Math.round(n));
    };
    const DEFAULT_BLOCK_MAX_WIDTH_DESKTOP_PX = (() => {
      const raw = Number(
        root.getAttribute("data-block-max-width-desktop-px") ||
        root.style.getPropertyValue("--mg-block-max-width-desktop-px") ||
        0
      );
      return clampDesktopMaxWidthPx(raw, 0);
    })();
    try {
      root.style.setProperty("--mg-block-width-pct-desktop", String(DEFAULT_BLOCK_WIDTH_DESKTOP_PCT));
      root.style.setProperty("--mg-block-width-pct-mobile", String(DEFAULT_BLOCK_WIDTH_MOBILE_PCT));
      root.style.setProperty("--mg-block-max-width-desktop-px", DEFAULT_BLOCK_MAX_WIDTH_DESKTOP_PX > 0 ? (DEFAULT_BLOCK_MAX_WIDTH_DESKTOP_PX + "px") : "999999px");
    } catch {}

    const nestedAccordionPanel = !!root.closest(".accordion-row__panel");
    if (nestedAccordionPanel) {
      root.dataset.mgNestedAccordion = "true";
      if (!root.hasAttribute("data-mg-preserve-root-width")) {
        try {
          root.style.setProperty("--mg-block-width-pct-desktop", "100");
          root.style.setProperty("--mg-block-width-pct-mobile", "100");
          root.style.setProperty("--mg-block-max-width-desktop-px", "999999px");
        } catch {}
      }
    }

    const SET_SAFE = setName.replace(/[^A-Za-z0-9_-]/g, "") || "set";

    const IS_TOUCH =
      (navigator.maxTouchPoints || 0) > 0 ||
      "ontouchstart" in window;

    // iOS Safari (and iPadOS masquerading as Mac) has stricter autoplay/audio rules.
    // We keep a single persistent Vimeo iframe and swap videos via the Vimeo API
    // to preserve the user-initiated audio session (prevents iOS from re-muting on swaps).
    const IS_IOS = (() => {
      try {
        const ua = String(navigator.userAgent || "");
        const iOS = /iPad|iPhone|iPod/i.test(ua);
        const iPadOS = (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
        return iOS || iPadOS;
      } catch {
        return false;
      }
    })();

    // iOS note:
    // Mobile Safari blocks autoplay with sound. Previous "anti-auto-mute" tricks are fragile and can
    // break playback entirely on iOS. To keep iOS reliable, we default to muted autoplay on iOS and
    // rely on an explicit user action (the Unmute button / tap) to enable sound.
    const IOS_NO_AUTOUNMUTE = true; // set to false only if you want to re-enable experimental iOS audio hacks
    const IOS_PERSISTENT_PLAYER = IS_IOS && !IOS_NO_AUTOUNMUTE;
    const IOS_FORCE_MUTED_AUTOPLAY = IS_IOS; // keep autoplay reliable on iOS

    function resolveStoredImportRows(rows, sets, stack) {
      const input = Array.isArray(rows) ? rows : [];
      const out = [];

      for (const rowSpec of input) {
        const importKey = String(rowSpec?.__import_set || "").trim();
        if (!importKey) {
          out.push(rowSpec);
          continue;
        }

        if (rowSpec?.preset_button) {
          out.push(rowSpec);
          continue;
        }

        if (stack && stack.has(importKey)) {
          out.push(rowSpec);
          continue;
        }

        const sourceRows = coerceToRows(sets && sets[importKey]);
        if (!sourceRows.length) {
          out.push(rowSpec);
          continue;
        }

        if (stack) stack.add(importKey);
        const resolvedSourceRows = resolveStoredImportRows(sourceRows, sets, stack);
        if (stack) stack.delete(importKey);

        const collapseImportedRows = !!rowSpec?.collapsed;
        const collapseGroupKey = collapseImportedRows ? (`preset:${importKey}:${out.length}`) : "";
        const showMoreText = String(rowSpec?.show_more_text || "Show more");
        const showLessText = String(rowSpec?.show_less_text || "Show less");
        const hideCtaButtons = !!rowSpec?.hide_cta_buttons;
        const endBehaviorOverride = normalizeEndBehavior(rowSpec?.end_behavior_override || rowSpec?.end_of_video_override, "");
        const accordionGroup = String(rowSpec?.accordion_group || "").trim();
        const accordionAlwaysOpen = !!rowSpec?.accordion_always_open;

        const nestedRows = resolvedSourceRows.map((srcRow) => {
          const row = Object.assign({}, srcRow);
          row.videos = normalizeItems(rowItems(srcRow));
          if (collapseImportedRows) {
            row.collapsed = false;
            row.collapse_group = collapseGroupKey;
            row.show_more_text = showMoreText;
            row.show_less_text = showLessText;
          }
          if (hideCtaButtons) row.hide_cta_buttons = true;
          if (endBehaviorOverride) row.end_behavior_override = endBehaviorOverride;
          if (accordionAlwaysOpen) row.accordion_always_open = true;
          return row;
        });

        const manualItems = normalizeItems(rowItems(rowSpec));
        if (manualItems.length) {
          if (nestedRows.length) nestedRows[nestedRows.length - 1].videos = (nestedRows[nestedRows.length - 1].videos || []).concat(manualItems);
          else {
            const manualRow = Object.assign({}, rowSpec);
            delete manualRow.__import_set;
            delete manualRow.__import_mode;
            delete manualRow.__import_passthrough;
            manualRow.videos = manualItems;
            nestedRows.push(manualRow);
          }
        }

        if (accordionGroup) {
          const safeImportKey = importKey.replace(/[^A-Za-z0-9_-]/g, "") || "preset";
          const nestedSetKey = `__mg_nested_preset__${SET_SAFE}__${safeImportKey}__${out.length}`;
          sets[nestedSetKey] = nestedRows;
          out.push({
            heading: String(rowSpec?.heading || ""),
            subheading: String(rowSpec?.subheading || ""),
            img_ratio: String(rowSpec?.img_ratio || "1:1"),
            row_height: Number(rowSpec?.row_height || 0) || 0,
            fit_row: !!rowSpec?.fit_row,
            hide_cta_buttons: hideCtaButtons,
            end_behavior_override: endBehaviorOverride,
            margin_top: Number(rowSpec?.margin_top || 0) || 0,
            margin_bottom: Number(rowSpec?.margin_bottom || 0) || 0,
            heading_justify: String(rowSpec?.heading_justify || "center"),
            collapsed: false,
            show_more_text: showMoreText,
            show_less_text: showLessText,
            row_toggle_border_style: String(rowSpec?.row_toggle_border_style || "solid"),
            custom_html: (typeof rowSpec?.custom_html === "string") ? rowSpec.custom_html : "",
            custom_template_id: String(rowSpec?.custom_template_id || ""),
            accordion: false,
            accordion_title: "",
            accordion_subheading: "",
            accordion_content: "",
            accordion_hide_title_when_open: false,
            accordion_hide_subheading_when_open: false,
            accordion_hide_subheading_when_closed: false,
            accordion_title_center: false,
            accordion_start_open: false,
            accordion_allow_multiple: false,
            accordion_bg_color: "",
            accordion_shadow_pct: normalizeAccordionShadowPct(rowSpec?.accordion_shadow_pct, 8),
            accordion_shadow_style: normalizeAccordionShadowStyle(rowSpec?.accordion_shadow_style || rowSpec?.accordion_shadow_effect, "raised"),
            accordion_max_width_px: normalizePresetButtonMaxWidthPx(rowSpec?.accordion_max_width_px, 0),
            accordion_panel_width_override_percent: normalizeWidthOverridePercent(rowSpec?.accordion_panel_width_override_percent, 0),
            accordion_panel_max_width_override_px: normalizePresetButtonMaxWidthPx(rowSpec?.accordion_panel_max_width_override_px, 0),
            accordion_group: accordionGroup,
            accordion_always_open: accordionAlwaysOpen,
            accordion_marker: false,
            collapse_group: String(rowSpec?.collapse_group || ""),
            __nested_preset_set: nestedSetKey,
            videos: []
          });
        } else {
          for (const row of nestedRows) out.push(row);
        }
      }

      return out;
    }

    function getRows() {
      const sets = getSetStore();
      const val = sets[setName];
      const rows = coerceToRows(val);
      const hasImportRows = rows && rows.some((row) => row && typeof row === "object" && String(row.__import_set || "").trim());
      if (rows && rows.length && !hasImportRows) sets[setName] = rows;
      return hasImportRows ? resolveStoredImportRows(rows, sets, new Set([setName])) : rows;
    }

    function escapeHtml(s) {
      return String(s ?? "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    function showError() {
      root.innerHTML =
        '<p style="font:500 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#111;text-align:center;">' +
        "Preset <strong>" + escapeHtml(setName || "(missing)") +
        "</strong> not found on <code>window.MEDIA_GALLERY_SETS</code>." +
        "</p>";
    }

    const THEME_SHOP_BUTTON_SELECTORS = [
      '.featured-product form[action*="/cart/add"] button[type="submit"]',
      '.featured-product form[action*="/cart/add"] input[type="submit"]',
      '.featured-product .product-form__submit',
      '[id*="FeaturedProduct"] form[action*="/cart/add"] button[type="submit"]',
      '[id*="FeaturedProduct"] form[action*="/cart/add"] input[type="submit"]',
      '[id*="FeaturedProduct"] .product-form__submit',
      '[class*="featured-product"] form[action*="/cart/add"] button[type="submit"]',
      '[class*="featured-product"] .product-form__submit',
      'form[action*="/cart/add"] button[type="submit"]',
      'form[action*="/cart/add"] input[type="submit"]',
      'form[action*="/cart/add"] .button',
      'form[action*="/cart/add"] .btn',
      '[data-product-form] button[type="submit"]',
      '[data-product-form] input[type="submit"]',
      '[data-product-form] .button',
      '[data-product-form] .btn',
      '[id*="ProductSubmitButton"]',
      '[id*="product-submit"]',
      '[data-add-to-cart]',
      '[data-add-to-cart-button]',
      'button[name="add"]',
      '.featured-product .button',
      '.featured-product button',
      '.main-product form[action*="/cart/add"] button[type="submit"]',
      '.main-product form[action*="/cart/add"] input[type="submit"]',
      '.main-product .product-form__submit',
      '.main-product .button',
      '.main-product button',
      '.product__info-container form[action*="/cart/add"] button[type="submit"]',
      '.product__info-container .product-form__submit',
      '.product__info-container .button',
      '.product-form__buttons .button',
      '.product-form__buttons button',
      '.product-form__submit',
      '.shopify-payment-button__button--unbranded',
      '.shopify-payment-button__button',
      'a.button',
      'button.button',
      '.button--primary',
      '.button--secondary',
      '.button',
      '.btn'
    ];

    const THEME_SHOP_BUTTON_PROBE_CLASS_SETS = [
      ['button', 'button--primary'],
      ['button', 'button--secondary'],
      ['button'],
      ['btn', 'btn--primary'],
      ['btn', 'btn--secondary'],
      ['btn'],
      ['product-form__submit', 'button'],
      ['shopify-payment-button__button', 'shopify-payment-button__button--unbranded']
    ];

    const THEME_SHOP_BUTTON_TEXT_RE = /\b(add to cart|add to bag|buy now|shop now|purchase|pre-order|preorder|sold out|checkout)\b/i;
    const THEME_SHOP_NOW_TEXT_RE = /^\s*shop now\s*$/i;

    const THEME_SHOP_BUTTON_STYLE_PROPS = [
      'appearance',
      '-webkit-appearance',
      'background',
      'background-color',
      'background-image',
      'color',
      'border',
      'border-top',
      'border-right',
      'border-bottom',
      'border-left',
      'border-radius',
      'box-shadow',
      'display',
      'position',
      'overflow',
      'white-space',
      'vertical-align',
      'cursor',
      'user-select',
      'opacity',
      'visibility',
      'isolation',
      'pointer-events',
      'font',
      'font-family',
      'font-size',
      'font-style',
      'font-weight',
      'line-height',
      'letter-spacing',
      'text-transform',
      'text-decoration',
      'padding-top',
      'padding-right',
      'padding-bottom',
      'padding-left',
      'min-height',
      'min-width',
      'height',
      'gap',
      'text-align',
      'justify-content',
      'align-items',
      'transition',
      'outline',
      'outline-offset'
    ];

    let themeShopButtonProfiles = Object.create(null);
    let themeShopButtonSource = null;
    let themeShopButtonProfileIsFallback = Object.create(null);
    let themeShopButtonRefreshQueued = false;
    const themeShopButtonTargets = new Set();

    function isThemeShopButtonCandidate(el) {
      if (!el || !el.isConnected) return false;
      if (el.closest && el.closest('.vimeo-snippet')) return false;
      return true;
    }

    function isVisibleButtonCandidate(el) {
      if (!isThemeShopButtonCandidate(el)) return false;
      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;
      return !!(el.getClientRects && el.getClientRects().length);
    }

    function getThemeShopButtonCandidateText(el) {
      return collapseWhitespace(el && (el.textContent || el.value || (el.getAttribute && el.getAttribute('aria-label')) || ''));
    }

    function scoreThemeShopButtonCandidate(el, selectorIndex) {
      if (!isThemeShopButtonCandidate(el)) return -1;

      let score = 0;
      if (isVisibleButtonCandidate(el)) score += 1000;

      const text = getThemeShopButtonCandidateText(el);
      const className = collapseWhitespace(el && el.className || '');
      const tag = String(el && el.tagName || '').toUpperCase();
      const type = String(el && el.getAttribute && el.getAttribute('type') || '').toLowerCase();
      const inProductForm = !!(el && el.closest && (el.closest('form[action*="/cart/add"]') || el.closest('[data-product-form]') || el.closest('.product-form') || el.closest('.product-form__buttons') || el.closest('.featured-product') || el.closest('.featured-product__block') || el.closest('.product') || el.closest('.product__info-container') || el.closest('.main-product')));
      const looksButtony = /button|btn|submit|product-form/i.test(className);
      const textHit = THEME_SHOP_BUTTON_TEXT_RE.test(text);
      const exactShopNow = THEME_SHOP_NOW_TEXT_RE.test(text);
      const hasShopNow = /\bshop now\b/i.test(text);

      if (exactShopNow) score += 1600;
      else if (hasShopNow) score += 900;

      if (inProductForm) score += hasShopNow ? 140 : 320;
      if (textHit) score += 280;
      if (looksButtony) score += 120;
      if (tag === 'BUTTON') score += 70;
      if (tag === 'A' && hasShopNow) score += 140;
      if (type === 'submit') score += 110;
      if (el && el.closest && el.closest('.shopify-payment-button')) score -= 120;
      score += Math.max(0, 40 - Number(selectorIndex || 0));
      return score;
    }

    function findThemeShopButtonSource() {
      let bestVisible = null;
      let bestVisibleScore = -1;
      let bestAny = null;
      let bestAnyScore = -1;
      const seen = new Set();

      const consider = (el, idx) => {
        if (!el || seen.has(el) || !isThemeShopButtonCandidate(el)) return;
        seen.add(el);
        const score = scoreThemeShopButtonCandidate(el, idx);
        if (score > bestAnyScore) {
          bestAny = el;
          bestAnyScore = score;
        }
        if (isVisibleButtonCandidate(el) && score > bestVisibleScore) {
          bestVisible = el;
          bestVisibleScore = score;
        }
      };

      THEME_SHOP_BUTTON_SELECTORS.forEach((sel, idx) => {
        const candidates = Array.from(document.querySelectorAll(sel));
        for (const el of candidates) consider(el, idx);
      });

      const fallbackCandidates = Array.from(document.querySelectorAll('button, a, input[type="submit"], input[type="button"]'));
      for (const el of fallbackCandidates) consider(el, 999);

      return bestVisible || bestAny || null;
    }

    function getThemeShopButtonClasses(source) {
      const all = Array.from((source && source.classList) ? source.classList : []);
      return all.filter((cls) => {
        if (!cls) return false;
        if (/^mg-/.test(cls)) return false;
        if (/hidden|disabled|loading|spinner|visually-hidden|loading-overlay|is-loading|is-disabled/i.test(cls)) return false;
        if (/^shopify-payment-button__more-options$/i.test(cls)) return false;
        return true;
      });
    }

    function getThemeShopButtonVariables(cs) {
      const vars = {};
      if (!cs) return vars;
      for (let i = 0; i < cs.length; i++) {
        const name = cs[i];
        if (!name || name.indexOf('--') !== 0) continue;
        const value = cs.getPropertyValue(name);
        if (!value) continue;
        vars[name] = value;
      }
      return vars;
    }

    function buildThemeShopButtonProfileFromComputed(cs, classList) {
      if (!cs) return null;
      const profile = {
        classes: Array.isArray(classList) ? classList.slice() : [],
        vars: getThemeShopButtonVariables(cs),
        styles: {}
      };
      const blockedProps = new Set(['display', 'position', 'opacity', 'visibility', 'pointer-events']);
      for (const prop of THEME_SHOP_BUTTON_STYLE_PROPS) {
        if (blockedProps.has(prop)) continue;
        profile.styles[prop] = cs.getPropertyValue(prop);
      }
      return profile;
    }

    function buildThemeShopButtonProfileFromSource(source, classList) {
      if (!source) return null;
      const classes = Array.isArray(classList) ? classList : getThemeShopButtonClasses(source);
      return buildThemeShopButtonProfileFromComputed(getComputedStyle(source), classes);
    }

    function normalizeThemeButtonCompareValue(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    function themeShopButtonProfileDiffers(baseProfile, testProfile) {
      if (!baseProfile || !testProfile) return false;
      const keys = [
        'background',
        'background-color',
        'color',
        'border',
        'border-radius',
        'box-shadow',
        'font-size',
        'font-weight',
        'letter-spacing',
        'text-transform',
        'padding-top',
        'padding-right',
        'padding-bottom',
        'padding-left',
        'min-height'
      ];
      return keys.some((key) => normalizeThemeButtonCompareValue(baseProfile.styles && baseProfile.styles[key]) !== normalizeThemeButtonCompareValue(testProfile.styles && testProfile.styles[key]));
    }

    function getThemeShopButtonProbeClassSets(variant = 'primary') {
      if (variant === 'secondary') {
        return [
          ['button', 'button--secondary'],
          ['btn', 'btn--secondary'],
          ['button'],
          ['btn']
        ];
      }
      return THEME_SHOP_BUTTON_PROBE_CLASS_SETS;
    }

    function buildThemeShopButtonProfileFromClassSet(classList, tagName = 'button') {
      if (!document.body) return null;

      const host = document.createElement('div');
      host.setAttribute('aria-hidden', 'true');
      host.style.cssText = 'position:fixed;left:-99999px;top:0;opacity:0;pointer-events:none;visibility:hidden;z-index:-1;';
      document.body.appendChild(host);

      try {
        const base = document.createElement(tagName);
        if ('type' in base) base.type = 'button';
        base.textContent = 'Button';
        host.appendChild(base);
        const baseProfile = buildThemeShopButtonProfileFromComputed(getComputedStyle(base), []);

        const probe = document.createElement(tagName);
        if ('type' in probe) probe.type = 'button';
        probe.textContent = 'Button';
        for (const cls of Array.isArray(classList) ? classList : []) {
          if (cls) probe.classList.add(cls);
        }
        host.appendChild(probe);

        const profile = buildThemeShopButtonProfileFromComputed(getComputedStyle(probe), classList);
        if (themeShopButtonProfileDiffers(baseProfile, profile)) return profile;
      } catch (err) {
        // no-op
      } finally {
        try { host.remove(); } catch (err) {}
      }

      return null;
    }

    function deriveThemeShopButtonClassesForVariant(source, variant = 'primary') {
      const classes = getThemeShopButtonClasses(source);
      if (variant !== 'secondary') return classes;

      const out = [];
      let hasSecondary = false;
      let hasButtonBase = false;
      let hasBtnBase = false;

      for (const cls of classes) {
        if (cls === 'button--primary') {
          out.push('button--secondary');
          hasSecondary = true;
          continue;
        }
        if (cls === 'btn--primary') {
          out.push('btn--secondary');
          hasSecondary = true;
          continue;
        }
        if (cls === 'button--secondary' || cls === 'btn--secondary') hasSecondary = true;
        if (cls === 'button') hasButtonBase = true;
        if (cls === 'btn') hasBtnBase = true;
        out.push(cls);
      }

      if (!hasSecondary) {
        if (hasButtonBase) out.push('button--secondary');
        else if (hasBtnBase) out.push('btn--secondary');
        else out.push('button', 'button--secondary');
      }

      return out;
    }

    function probeThemeShopButtonProfile(variant = 'primary') {
      if (!document.body) return null;

      const host = document.createElement('div');
      host.setAttribute('aria-hidden', 'true');
      host.style.cssText = 'position:fixed;left:-99999px;top:0;opacity:0;pointer-events:none;visibility:hidden;z-index:-1;';
      document.body.appendChild(host);

      try {
        const base = document.createElement('button');
        base.type = 'button';
        base.textContent = 'Button';
        host.appendChild(base);
        const baseProfile = buildThemeShopButtonProfileFromComputed(getComputedStyle(base), []);

        for (const classSet of getThemeShopButtonProbeClassSets(variant)) {
          const probe = document.createElement('button');
          probe.type = 'button';
          probe.textContent = 'Button';
          for (const cls of classSet) {
            if (cls) probe.classList.add(cls);
          }
          host.appendChild(probe);

          const profile = buildThemeShopButtonProfileFromComputed(getComputedStyle(probe), classSet);
          probe.remove();

          if (themeShopButtonProfileDiffers(baseProfile, profile)) {
            return profile;
          }
        }
      } catch (err) {
        // no-op
      } finally {
        try { host.remove(); } catch (err) {}
      }

      return null;
    }

    function buildFallbackThemeShopButtonProfile(variant = 'primary') {
      if (variant === 'secondary') {
        return {
          classes: [],
          vars: {},
          styles: {
            'background': '#ffffff',
            'background-color': '#ffffff',
            'color': '#2f2f31',
            'border': '1px solid rgba(47,47,49,0.22)',
            'border-top': '1px solid rgba(47,47,49,0.22)',
            'border-right': '1px solid rgba(47,47,49,0.22)',
            'border-bottom': '1px solid rgba(47,47,49,0.22)',
            'border-left': '1px solid rgba(47,47,49,0.22)',
            'border-radius': '18px',
            'box-shadow': 'none',
            'font-family': 'inherit',
            'font-size': '16px',
            'font-weight': '700',
            'line-height': '1.1',
            'letter-spacing': '0.08em',
            'text-transform': 'uppercase',
            'text-decoration': 'none',
            'padding-top': '16px',
            'padding-right': '28px',
            'padding-bottom': '16px',
            'padding-left': '28px',
            'min-height': '52px',
            'min-width': '0px',
            'height': 'auto',
            'gap': '0px',
            'text-align': 'center',
            'justify-content': 'center',
            'align-items': 'center',
            'transition': 'none',
            'outline': '0px none rgb(47, 47, 49)',
            'outline-offset': '0px'
          }
        };
      }
      return {
        classes: [],
        vars: {},
        styles: {
          'background': '#2f2f31',
          'background-color': '#2f2f31',
          'color': '#ffffff',
          'border': '0px solid transparent',
          'border-top': '0px solid transparent',
          'border-right': '0px solid transparent',
          'border-bottom': '0px solid transparent',
          'border-left': '0px solid transparent',
          'border-radius': '18px',
          'box-shadow': 'none',
          'font-family': 'inherit',
          'font-size': '16px',
          'font-weight': '700',
          'line-height': '1.1',
          'letter-spacing': '0.08em',
          'text-transform': 'uppercase',
          'text-decoration': 'none',
          'padding-top': '16px',
          'padding-right': '28px',
          'padding-bottom': '16px',
          'padding-left': '28px',
          'min-height': '52px',
          'min-width': '0px',
          'height': 'auto',
          'gap': '0px',
          'text-align': 'center',
          'justify-content': 'center',
          'align-items': 'center',
          'transition': 'none',
          'outline': '0px none rgb(255, 255, 255)',
          'outline-offset': '0px'
        }
      };
    }

    function getThemeShopButtonProfile(forceRefresh, variant = 'primary') {
      const key = variant === 'secondary' ? 'secondary' : 'primary';
      const cachedSourceValid = !!(themeShopButtonSource && isThemeShopButtonCandidate(themeShopButtonSource));
      if (!forceRefresh && themeShopButtonProfiles[key] && !themeShopButtonProfileIsFallback[key] && (!themeShopButtonSource || cachedSourceValid)) {
        return themeShopButtonProfiles[key];
      }

      const source = findThemeShopButtonSource();
      let profile = null;
      if (source) {
        if (key === 'secondary') {
          const variantClasses = deriveThemeShopButtonClassesForVariant(source, 'secondary');
          const sourceTagName = String(source.tagName || 'button').toLowerCase();
          profile =
            buildThemeShopButtonProfileFromClassSet(variantClasses, sourceTagName) ||
            probeThemeShopButtonProfile('secondary') ||
            buildFallbackThemeShopButtonProfile('secondary');
        } else {
          profile = buildThemeShopButtonProfileFromSource(source);
        }
      } else {
        profile = probeThemeShopButtonProfile(key) || buildFallbackThemeShopButtonProfile(key);
      }

      if (!profile) {
        themeShopButtonSource = null;
        themeShopButtonProfiles[key] = null;
        themeShopButtonProfileIsFallback[key] = false;
        return null;
      }

      themeShopButtonSource = source || null;
      themeShopButtonProfiles[key] = profile;
      themeShopButtonProfileIsFallback[key] = !source;
      return themeShopButtonProfiles[key];
    }

    function clearThemeShopButtonClasses(el) {
      const prev = Array.isArray(el && el.__mgThemeBtnClasses) ? el.__mgThemeBtnClasses : [];
      for (const cls of prev) {
        try { el.classList.remove(cls); } catch {}
      }
      if (el) el.__mgThemeBtnClasses = [];
    }

    function clearThemeShopButtonProfileStyles(el) {
      const prevVars = Array.isArray(el && el.__mgThemeBtnVars) ? el.__mgThemeBtnVars : [];
      for (const name of prevVars) {
        try { el.style.removeProperty(name); } catch {}
      }
      const prevStyles = Array.isArray(el && el.__mgThemeBtnStyles) ? el.__mgThemeBtnStyles : [];
      for (const name of prevStyles) {
        try { el.style.removeProperty(name); } catch {}
      }
      if (el) {
        el.__mgThemeBtnVars = [];
        el.__mgThemeBtnStyles = [];
        try { el.removeAttribute('data-mg-theme-btn-ready'); } catch {}
      }
    }

    function scrubThemeShopButtonClone(rootEl) {
      if (!rootEl || !rootEl.querySelectorAll) return;
      const nodes = [rootEl].concat(Array.from(rootEl.querySelectorAll('*')));
      for (const el of nodes) {
        if (!el || !el.attributes) continue;
        for (const attr of Array.from(el.attributes)) {
          const name = String(attr && attr.name || '').toLowerCase();
          if (!name) continue;
          if (name === 'id' || name === 'name' || name === 'form' || name === 'for' || name === 'href' || name === 'target' || name === 'value' || name === 'type' || name === 'disabled') {
            try { el.removeAttribute(attr.name); } catch {}
            continue;
          }
          if (name.indexOf('on') === 0) {
            try { el.removeAttribute(attr.name); } catch {}
            continue;
          }
          if (name.indexOf('data-') === 0 && name.indexOf('data-mg-') !== 0) {
            try { el.removeAttribute(attr.name); } catch {}
            continue;
          }
        }
      }
    }

    function replaceThemeShopButtonTextNodes(rootEl, label) {
      if (!rootEl) return false;
      const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          return collapseWhitespace(node && node.nodeValue).length ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
      });
      let first = null;
      let node = null;
      while ((node = walker.nextNode())) {
        if (!first) {
          node.nodeValue = label;
          first = node;
        } else {
          node.nodeValue = '';
        }
      }
      return !!first;
    }

    function applyThemeShopButtonMarkup(el, source) {
      if (!el) return;
      const label = collapseWhitespace((el.dataset && el.dataset.mgThemeBtnLabel) || el.textContent || '');
      if (!label) return;

      const tag = String(el.tagName || '').toUpperCase();
      if (!source || String(source.tagName || '').toUpperCase() === 'INPUT') {
        if (tag === 'INPUT') el.value = label;
        else if (el.__mgThemeBtnMarkupApplied !== label) el.textContent = label;
        el.__mgThemeBtnMarkupApplied = label;
        return;
      }

      const clone = source.cloneNode(true);
      scrubThemeShopButtonClone(clone);

      const host = document.createElement('div');
      while (clone.firstChild) host.appendChild(clone.firstChild);
      if (!replaceThemeShopButtonTextNodes(host, label)) {
        host.textContent = label;
      }

      const html = host.innerHTML || escapeHtml(label);
      if (el.__mgThemeBtnMarkupApplied !== html) {
        el.innerHTML = html;
        el.__mgThemeBtnMarkupApplied = html;
      }
      try { el.setAttribute('aria-label', label); } catch {}
    }

    function applyThemeShopButtonProfile(el, profile) {
      if (!el) return;

      try { el.classList.add('mg-theme-shop-btn'); } catch {}
      clearThemeShopButtonClasses(el);
      clearThemeShopButtonProfileStyles(el);

      if (!profile) return;

      const classes = Array.isArray(profile.classes) ? profile.classes.slice() : [];
      for (const cls of classes) {
        try { el.classList.add(cls); } catch {}
      }
      el.__mgThemeBtnClasses = classes;

      const appliedVars = [];
      const vars = profile.vars || {};
      for (const name in vars) {
        const value = vars[name];
        if (!value) continue;
        el.style.setProperty(name, value);
        appliedVars.push(name);
      }
      el.__mgThemeBtnVars = appliedVars;

      const appliedStyles = [];
      const styles = profile.styles || {};
      for (const prop in styles) {
        const value = styles[prop];
        if (!value) continue;
        el.style.setProperty(prop, value);
        appliedStyles.push(prop);
      }
      el.__mgThemeBtnStyles = appliedStyles;

      el.style.setProperty('text-decoration', 'none');
      el.style.setProperty('cursor', 'pointer');
      el.style.setProperty('box-sizing', 'border-box');
      try { el.setAttribute('data-mg-theme-btn-ready', 'true'); } catch {}
    }

    function refreshThemeShopButtonTargets(forceRefresh) {
      const source = themeShopButtonSource && isThemeShopButtonCandidate(themeShopButtonSource)
        ? themeShopButtonSource
        : findThemeShopButtonSource();
      if (source) themeShopButtonSource = source;

      for (const el of Array.from(themeShopButtonTargets)) {
        if (!el || !el.isConnected) {
          themeShopButtonTargets.delete(el);
          continue;
        }
        const variant = String(el.dataset && el.dataset.mgThemeBtnVariant || 'primary').trim().toLowerCase() === 'secondary' ? 'secondary' : 'primary';
        const preserveInner = !!(el.dataset && el.dataset.mgThemeBtnPreserveInner === 'true');
        applyThemeShopButtonProfile(el, getThemeShopButtonProfile(!!forceRefresh, variant));
        if (!preserveInner) applyThemeShopButtonMarkup(el, source || null);
      }
    }

    function queueThemeShopButtonRefresh(forceRefresh) {
      if (forceRefresh) {
        themeShopButtonProfiles = Object.create(null);
        themeShopButtonSource = null;
        themeShopButtonProfileIsFallback = Object.create(null);
      }
      if (themeShopButtonRefreshQueued) return;
      themeShopButtonRefreshQueued = true;
      requestAnimationFrame(() => {
        themeShopButtonRefreshQueued = false;
        refreshThemeShopButtonTargets(forceRefresh);
      });
    }

    function applyThemeShopButtonLike(el, label, variant = 'primary') {
      if (!el) return;
      const text = collapseWhitespace(label || el.textContent || '');
      if (text && el.dataset) el.dataset.mgThemeBtnLabel = text;
      if (el.dataset) el.dataset.mgThemeBtnVariant = String(variant || '').toLowerCase() === 'secondary' ? 'secondary' : 'primary';
      if (el.dataset) delete el.dataset.mgThemeBtnPreserveInner;
      themeShopButtonTargets.add(el);
      const themeVariant = (el.dataset && el.dataset.mgThemeBtnVariant) || 'primary';
      const forceRefresh = !themeShopButtonSource || !!themeShopButtonProfileIsFallback[themeVariant];
      applyThemeShopButtonProfile(el, getThemeShopButtonProfile(forceRefresh, themeVariant));
      applyThemeShopButtonMarkup(el, themeShopButtonSource || null);
      queueThemeShopButtonRefresh(forceRefresh);
    }

    function applyThemeShopButtonStyleOnly(el, label, variant = 'primary') {
      if (!el) return;
      const text = collapseWhitespace(label || el.textContent || '');
      if (text && el.dataset) el.dataset.mgThemeBtnLabel = text;
      if (el.dataset) el.dataset.mgThemeBtnVariant = String(variant || '').toLowerCase() === 'secondary' ? 'secondary' : 'primary';
      if (el.dataset) el.dataset.mgThemeBtnPreserveInner = 'true';
      themeShopButtonTargets.add(el);
      const themeVariant = (el.dataset && el.dataset.mgThemeBtnVariant) || 'primary';
      const forceRefresh = !themeShopButtonSource || !!themeShopButtonProfileIsFallback[themeVariant];
      applyThemeShopButtonProfile(el, getThemeShopButtonProfile(forceRefresh, themeVariant));
      queueThemeShopButtonRefresh(forceRefresh);
    }

    function getCustomButtonGroupConfig() {
      const transparentValues = new Set(["", "rgba(0,0,0,0)", "rgba(0, 0, 0, 0)"]);
      const getAttr = (name) => String(root.getAttribute(name) || "").trim();
      const borderColorRaw = getAttr("data-mg-custom-button-border-color");
      const backgroundColorRaw = getAttr("data-mg-custom-button-background-color");
      const textColorRaw = getAttr("data-mg-custom-button-text-color");
      const backgroundOpacity = Math.max(0, Math.min(100, Number(getAttr("data-mg-custom-button-background-opacity")) || 100));
      const baseBackgroundColor = transparentValues.has(backgroundColorRaw)
        ? "rgb(var(--secondary-background))"
        : safeCssColor(backgroundColorRaw, "rgb(var(--secondary-background))");
      return {
        borderColor: transparentValues.has(borderColorRaw) ? "rgba(var(--text-color), 0.15)" : safeCssColor(borderColorRaw, "rgba(var(--text-color), 0.15)"),
        borderSize: Math.max(0, Math.min(20, Number(getAttr("data-mg-custom-button-border-size")) || 1)),
        shadowSize: Math.max(0, Math.min(60, Number(getAttr("data-mg-custom-button-shadow-size")) || 0)),
        borderRadiusPx: Math.max(0, Math.min(240, Number(getAttr("data-mg-custom-button-border-radius-px")) || 4)),
        paddingYPx: Math.max(0, Math.min(40, Number(getAttr("data-mg-custom-button-padding-y-px")) || 16)),
        paddingXPx: Math.max(0, Math.min(80, Number(getAttr("data-mg-custom-button-padding-x-px")) || 28)),
        backgroundColor: colorWithOpacity(baseBackgroundColor, backgroundOpacity, baseBackgroundColor),
        backgroundColorBase: baseBackgroundColor,
        backgroundOpacity,
        textColor: transparentValues.has(textColorRaw) ? "rgb(var(--text-color))" : safeCssColor(textColorRaw, "rgb(var(--text-color))"),
        gradientAngleDeg: Math.max(0, Math.min(360, Number(getAttr("data-mg-custom-button-gradient-angle")) || 180)),
        gradientColor: (() => {
          const raw = getAttr("data-mg-custom-button-gradient-color");
          if (transparentValues.has(raw)) return "";
          const safe = safeCssColor(raw, "");
          return colorWithOpacity(safe, backgroundOpacity, safe);
        })(),
        backgroundImage: safeSrc(getAttr("data-mg-custom-button-background-image")),
        backgroundImageStyle: normalizeBackgroundImageStyle(getAttr("data-mg-custom-button-background-image-style"), "center")
      };
    }

    function applyCustomButtonGroupStyle(el) {
      if (!el) return;
      const cfg = getCustomButtonGroupConfig();
      const shadowValue = topLightShadowValue(cfg.shadowSize);

      el.style.borderColor = cfg.borderColor;
      el.style.borderWidth = cfg.borderSize + "px";
      el.style.borderStyle = cfg.borderSize > 0 ? "solid" : "none";
      el.style.setProperty("border-radius", cfg.borderRadiusPx + "px", "important");
      el.style.padding = `${cfg.paddingYPx}px ${cfg.paddingXPx}px`;
      el.style.backgroundColor = cfg.backgroundColor;
      el.style.color = cfg.textColor;
      el.style.boxShadow = cfg.shadowSize > 0 ? shadowValue : "none";
      el.style.filter = "none";
      el.style.overflow = "hidden";
      el.style.backgroundRepeat = "no-repeat";
      el.style.backgroundBlendMode = "normal";

      if (cfg.backgroundImage) {
        const layers = [];
        if (cfg.backgroundOpacity < 100 || cfg.backgroundColorBase !== cfg.backgroundColor) {
          layers.push(`linear-gradient(${cfg.gradientAngleDeg}deg, ${cfg.backgroundColor}, ${cfg.backgroundColor})`);
        }
        layers.push(`url("${cfg.backgroundImage.replace(/"/g, '%22')}")`);
        el.style.backgroundImage = layers.join(", ");
        switch (cfg.backgroundImageStyle) {
          case "stretch_to_fit":
            el.style.backgroundSize = "100% 100%";
            el.style.backgroundPosition = "center center";
            break;
          case "full_width":
            el.style.backgroundSize = "100% auto";
            el.style.backgroundPosition = "center center";
            break;
          case "full_height":
            el.style.backgroundSize = "auto 100%";
            el.style.backgroundPosition = "center center";
            break;
          default:
            el.style.backgroundSize = "cover";
            el.style.backgroundPosition = "center center";
            break;
        }
      } else if (cfg.gradientColor) {
        el.style.backgroundImage = `linear-gradient(${cfg.gradientAngleDeg}deg, ${cfg.backgroundColor}, ${cfg.gradientColor})`;
        el.style.backgroundSize = "100% 100%";
        el.style.backgroundPosition = "center center";
      } else {
        el.style.backgroundImage = "none";
        el.style.backgroundSize = "";
        el.style.backgroundPosition = "";
      }
    }

    function setActionButtonHref(el, href) {
      if (!el) return;
      const safe = safeLink(href);
      if (safe) el.setAttribute('data-mg-href', safe);
      else el.removeAttribute('data-mg-href');

      if (String(el.tagName || '').toUpperCase() === 'A') {
        if (safe) {
          el.setAttribute('href', safe);
          el.setAttribute('target', '_self');
        } else {
          el.removeAttribute('href');
          el.removeAttribute('target');
        }
      }
    }

    function getActionButtonHref(el) {
      if (!el) return '';
      return String(el.getAttribute('data-mg-href') || el.getAttribute('href') || '').trim();
    }

    function setActionButtonVisible(el, show) {
      if (!el) return;
      if (show) {
        try { el.removeAttribute('hidden'); } catch {}
        try { el.setAttribute('aria-hidden', 'false'); } catch {}
        try { if ('disabled' in el) el.disabled = false; } catch {}
      } else {
        try { el.setAttribute('hidden', ''); } catch {}
        try { el.setAttribute('aria-hidden', 'true'); } catch {}
        try { if ('disabled' in el) el.disabled = true; } catch {}
      }
    }

    function collectReviewWidgetText(el) {
      if (!el) return '';
      const parts = [];
      const push = (value) => {
        const t = collapseWhitespace(value || '');
        if (t) parts.push(t);
      };

      push(el.textContent || '');
      try {
        const nodes = el.querySelectorAll('[aria-label],[title]');
        for (const node of nodes) {
          push(node.getAttribute('aria-label'));
          push(node.getAttribute('title'));
        }
      } catch {}

      return collapseWhitespace(parts.join(' '));
    }

    function parseReviewCountFromWidgetText(text) {
      const t = collapseWhitespace(text || '');
      if (!t) return 0;

      let m = t.match(/\((\d+)\)/);
      if (m) return parseInt(m[1], 10);

      m = t.match(/\b(\d+)\s+reviews?\b/i);
      if (m) return parseInt(m[1], 10);

      m = t.match(/\b(\d+)\s+ratings?\b/i);
      if (m) return parseInt(m[1], 10);

      return null;
    }

    function bazaarvoiceInlineHasReviews(el) {
      const text = collectReviewWidgetText(el);
      if (!text) return false;
      if (/\b(no reviews?|be the first|not yet rated|unrated)\b/i.test(text)) return false;

      const count = parseReviewCountFromWidgetText(text);
      if (count === 0) return false;
      if (typeof count === 'number' && count > 0) return true;

      return true;
    }

    function setBazaarvoiceInlineVisible(el, show) {
      if (!el) return;
      if (show) {
        try { el.removeAttribute('hidden'); } catch {}
        try { el.setAttribute('aria-hidden', 'false'); } catch {}
      } else {
        try { el.setAttribute('hidden', ''); } catch {}
        try { el.setAttribute('aria-hidden', 'true'); } catch {}
      }
    }

    function applyProductReviewMobileScale(el) {
      if (!el) return;
      const isMobileLike = !window.matchMedia("(min-width: 769px)").matches;
      try {
        el.style.removeProperty('transform');
        el.style.removeProperty('transform-origin');
      } catch {}
      const targets = [];
      try {
        targets.push(...el.querySelectorAll('.scm-reviews-rate, .mg-review-inline-fallback'));
      } catch {}

      for (const target of targets) {
        if (!target || !target.style) continue;
        if (isMobileLike) {
          target.style.display = 'inline-flex';
          target.style.alignItems = 'center';
          target.style.justifyContent = 'flex-start';
          target.style.flexWrap = 'nowrap';
          target.style.whiteSpace = 'nowrap';
          target.style.maxWidth = 'none';

          try {
            const children = target.children ? Array.from(target.children) : [];
            for (const child of children) {
              if (!child || !child.style) continue;
              child.style.display = 'inline-flex';
              child.style.alignItems = 'center';
              child.style.flex = '0 0 auto';
              child.style.whiteSpace = 'nowrap';
            }
          } catch {}

          target.style.transform = '';
          target.style.transformOrigin = '';
          const naturalWidth = Math.max(0, target.scrollWidth || 0);
          const hostWidth = Math.max(0, el.clientWidth || (el.getBoundingClientRect ? el.getBoundingClientRect().width : 0) || 0);
          const available = Math.max(24, hostWidth - 4);
          const scale = naturalWidth > available && available > 0
            ? Math.max(0.5, Math.min(1, available / naturalWidth))
            : 1;
          if (scale < 0.999) {
            target.style.transform = `scale(${scale.toFixed(4)})`;
            target.style.transformOrigin = 'left top';
          }
        } else {
          target.style.removeProperty('display');
          target.style.removeProperty('align-items');
          target.style.removeProperty('justify-content');
          target.style.removeProperty('flex-wrap');
          target.style.removeProperty('white-space');
          target.style.removeProperty('max-width');
          target.style.removeProperty('transform');
          target.style.removeProperty('transform-origin');
          try {
            const children = target.children ? Array.from(target.children) : [];
            for (const child of children) {
              if (!child || !child.style) continue;
              child.style.removeProperty('display');
              child.style.removeProperty('align-items');
              child.style.removeProperty('flex');
              child.style.removeProperty('white-space');
            }
          } catch {}
        }
      }
    }

    function watchBazaarvoiceInline(el, enabled, onChange) {
      if (!el) return;

      if (el.__mgBvObserver) {
        try { el.__mgBvObserver.disconnect(); } catch {}
        el.__mgBvObserver = null;
      }

      const notify = (visible) => {
        if (typeof onChange === 'function') onChange(!!visible);
      };

      if (!enabled) {
        setBazaarvoiceInlineVisible(el, false);
        notify(false);
        return;
      }

      const sync = () => {
        const visible = bazaarvoiceInlineHasReviews(el);
        setBazaarvoiceInlineVisible(el, visible);
        applyProductReviewMobileScale(el);
        notify(visible);
      };

      setBazaarvoiceInlineVisible(el, false);

      try {
        const mo = new MutationObserver(() => {
          requestAnimationFrame(sync);
        });
        mo.observe(el, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['aria-label', 'title', 'class', 'style']
        });
        el.__mgBvObserver = mo;
      } catch {}

      [0, 250, 700, 1500, 3000, 5000].forEach((ms) => {
        setTimeout(sync, ms);
      });
    }

    function parseReviewDataJson(raw) {
      if (raw == null || raw === "") return null;
      if (typeof raw === "object") return raw;
      try { return JSON.parse(String(raw)); } catch {}
      return null;
    }

    function findNumericReviewValue(input, keys, depth = 0) {
      if (depth > 4 || input == null) return NaN;

      if (typeof input === "number") return isFinite(input) ? input : NaN;

      if (typeof input === "string") {
        const s = String(input).trim();
        if (!s) return NaN;
        if (/^-?\d+(?:\.\d+)?$/.test(s)) return Number(s);
        const parsed = parseReviewDataJson(s);
        if (parsed && parsed !== input) return findNumericReviewValue(parsed, keys, depth + 1);
        return NaN;
      }

      if (Array.isArray(input)) {
        for (const item of input) {
          const found = findNumericReviewValue(item, keys, depth + 1);
          if (isFinite(found)) return found;
        }
        return NaN;
      }

      if (typeof input === "object") {
        const wanted = new Set((Array.isArray(keys) ? keys : []).map((k) => String(k || "").trim().toLowerCase()));
        for (const key of Object.keys(input)) {
          const compact = String(key || "").trim().toLowerCase();
          if (!wanted.has(compact)) continue;
          const found = findNumericReviewValue(input[key], keys, depth + 1);
          if (isFinite(found)) return found;
        }
        for (const key of Object.keys(input)) {
          const found = findNumericReviewValue(input[key], keys, depth + 1);
          if (isFinite(found)) return found;
        }
      }

      return NaN;
    }

    function extractLaiReviewSummary(raw) {
      const data = parseReviewDataJson(raw) ?? raw;
      const averageKeys = [
        "averagerating",
        "average_rating",
        "average",
        "avg",
        "ratingaverage",
        "rating_average",
        "rating",
        "rate",
        "star",
        "starscore",
        "star_score"
      ];
      const countKeys = [
        "reviewcount",
        "review_count",
        "reviews",
        "review",
        "count",
        "total",
        "totalreviews",
        "total_reviews",
        "numberreviews",
        "number_reviews",
        "n"
      ];

      let average = findNumericReviewValue(data, averageKeys);
      let count = findNumericReviewValue(data, countKeys);

      if (isFinite(average)) average = Math.max(0, Math.min(5, Number(average)));
      else average = NaN;

      if (isFinite(count)) count = Math.max(0, Math.round(Number(count)));
      else count = NaN;

      if (!isFinite(average) && isFinite(count) && count > 0) average = 5;
      if (!isFinite(count) && isFinite(average) && average > 0) {
        const text = collectReviewWidgetText({ textContent: JSON.stringify(data || "") });
        const parsed = parseReviewCountFromWidgetText(text);
        if (typeof parsed === "number") count = parsed;
      }

      return {
        average: isFinite(average) ? average : 0,
        count: isFinite(count) ? count : 0,
        hasReviews: !!(isFinite(count) && count > 0)
      };
    }

    function buildInlineReviewStars(average) {
      const safeAverage = Math.max(0, Math.min(5, Number(average) || 0));
      const fillPct = Math.max(0, Math.min(100, (safeAverage / 5) * 100));
      return `<span class="mg-review-stars" aria-hidden="true"><span class="mg-review-stars__base">&#9733;&#9733;&#9733;&#9733;&#9733;</span><span class="mg-review-stars__fill" style="width:${fillPct.toFixed(2)}%;">&#9733;&#9733;&#9733;&#9733;&#9733;</span></span>`;
    }

    function renderLaiInlineFallback(host, summary, redirectUrl) {
      if (!host) return false;
      const count = Math.max(0, Number(summary?.count) || 0);
      if (!(count > 0)) {
        host.innerHTML = "";
        host.hidden = true;
        return false;
      }

      const average = Math.max(0, Math.min(5, Number(summary?.average) || 0));
      const averageLabel = average.toFixed(1).replace(/\.0$/, "");
      const countLabel = count === 1 ? "1 review" : `${count} reviews`;
      const tag = redirectUrl ? "a" : "span";
      const attrs = redirectUrl
        ? ` class="mg-review-inline-fallback is-link" href="${escapeHtml(redirectUrl)}" target="_self" aria-label="${escapeHtml(`${averageLabel} out of 5 stars, ${countLabel}`)}"`
        : ` class="mg-review-inline-fallback" aria-label="${escapeHtml(`${averageLabel} out of 5 stars, ${countLabel}`)}"`;

      host.innerHTML = `<${tag}${attrs}>${buildInlineReviewStars(average)}<span class="mg-review-inline-fallback__text">${escapeHtml(averageLabel)} <span class="mg-review-inline-fallback__count">(${escapeHtml(String(count))})</span></span></${tag}>`;
      host.hidden = false;
      return true;
    }

    function laiInlineHasReviews(el) {
      if (!el) return false;
      const text = collectReviewWidgetText(el);
      if (text && !/\b(no reviews?|be the first|not yet rated|unrated)\b/i.test(text)) return true;
      try {
        return !!el.querySelector('svg, img, [class*="star"], [class*="rating"], i');
      } catch {}
      return false;
    }

    function requestLaiInlineRefresh(scope) {
      const root = scope && scope.nodeType === 1 ? scope : document;
      const calls = [
        () => { if (window.SCM && typeof window.SCM.loadStarRating === "function") window.SCM.loadStarRating(root); },
        () => { if (window.SCM && typeof window.SCM.loadStarRatings === "function") window.SCM.loadStarRatings(root); },
        () => { if (window.SCM && typeof window.SCM.refresh === "function") window.SCM.refresh(root); },
        () => { if (window.LAI && typeof window.LAI.refresh === "function") window.LAI.refresh(root); }
      ];
      for (const call of calls) {
        try { call(); } catch {}
      }
    }

    function setLaiInlineVisible(el, show) {
      if (!el) return;
      if (show) {
        try { el.removeAttribute('hidden'); } catch {}
        try { el.setAttribute('aria-hidden', 'false'); } catch {}
      } else {
        try { el.setAttribute('hidden', ''); } catch {}
        try { el.setAttribute('aria-hidden', 'true'); } catch {}
      }
    }

    function watchLaiInline(el, widgetEl, fallbackEl, enabled, onChange) {
      if (!el) return;

      if (el.__mgLaiObserver) {
        try { el.__mgLaiObserver.disconnect(); } catch {}
        el.__mgLaiObserver = null;
      }

      const notify = (visible) => {
        if (typeof onChange === 'function') onChange(!!visible);
      };

      if (!enabled) {
        setLaiInlineVisible(el, false);
        notify(false);
        return;
      }

      const sync = () => {
        const widgetVisible = laiInlineHasReviews(widgetEl);
        const fallbackVisible = !!(fallbackEl && !fallbackEl.hidden && collapseWhitespace(fallbackEl.textContent || ""));
        if (fallbackEl) fallbackEl.hidden = !!widgetVisible;
        const visible = widgetVisible || fallbackVisible;
        setLaiInlineVisible(el, visible);
        applyProductReviewMobileScale(el);
        notify(visible);
      };

      setLaiInlineVisible(el, false);

      try {
        const mo = new MutationObserver(() => {
          requestAnimationFrame(sync);
        });
        mo.observe(el, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['aria-label', 'title', 'class', 'style', 'hidden']
        });
        el.__mgLaiObserver = mo;
      } catch {}

      [0, 250, 700, 1500, 3000, 5000].forEach((ms) => {
        setTimeout(sync, ms);
      });
    }

    function normalizeProductBelowActionButton(el, role) {
      if (!el) return;
      try { themeShopButtonTargets.delete(el); } catch {}
      try { clearThemeShopButtonClasses(el); } catch {}
      try { clearThemeShopButtonProfileStyles(el); } catch {}
      try { el.removeAttribute('data-mg-theme-btn-ready'); } catch {}
      try { el.removeAttribute('style'); } catch {}
      try {
        el.classList.remove(
          'card-btn',
          'card-btn--primary',
          'card-btn--more',
          'mg-theme-shop-btn',
          'mg-product-action-btn',
          'mg-product-action-btn--primary',
          'mg-product-action-btn--more',
          'mg-product-below-btn',
          'mg-product-below-btn--primary',
          'mg-product-below-btn--more',
          'mg-product-overlay-btn',
          'mg-product-overlay-btn--primary',
          'mg-product-overlay-btn--more',
          'button',
          'button--primary',
          'button--secondary',
          'btn',
          'btn--primary',
          'btn--secondary'
        );
      } catch {}
      try { el.classList.add('mg-product-below-btn'); } catch {}
      try { el.classList.add(role === 'more' ? 'mg-product-below-btn--more' : 'mg-product-below-btn--primary'); } catch {}
      try { el.type = 'button'; } catch {}
    }

    function getProductThemeButtonClasses(shopButtonStyle) {
      const normalized = normalizeProductShopButtonStyle(shopButtonStyle, "theme_default");
      if (normalized === "theme_secondary") return ["button", "button--secondary"];
      if (normalized === "theme_default") return ["button", "button--primary"];
      return [];
    }

    function applyProductThemeButtonClasses(el, shopButtonStyle) {
      if (!el) return;
      try {
        el.classList.remove(
          "button",
          "button--primary",
          "button--secondary",
          "btn",
          "btn--primary",
          "btn--secondary",
          "mg-theme-shop-btn"
        );
      } catch {}
      for (const cls of getProductThemeButtonClasses(shopButtonStyle)) {
        try { el.classList.add(cls); } catch {}
      }
    }

    function normalizeProductOverlayActionButton(el, role) {
      if (!el) return;
      try { themeShopButtonTargets.delete(el); } catch {}
      try { clearThemeShopButtonClasses(el); } catch {}
      try { clearThemeShopButtonProfileStyles(el); } catch {}
      try { el.removeAttribute('data-mg-theme-btn-ready'); } catch {}
      try { el.removeAttribute('style'); } catch {}
      try {
        el.classList.remove(
          'card-btn',
          'card-btn--primary',
          'card-btn--more',
          'mg-theme-shop-btn',
          'mg-product-action-btn',
          'mg-product-action-btn--primary',
          'mg-product-action-btn--more',
          'mg-product-below-btn',
          'mg-product-below-btn--primary',
          'mg-product-below-btn--more',
          'mg-product-overlay-btn',
          'mg-product-overlay-btn--primary',
          'mg-product-overlay-btn--more',
          'button',
          'button--primary',
          'button--secondary',
          'btn',
          'btn--primary',
          'btn--secondary'
        );
      } catch {}
      try { el.classList.add('mg-product-overlay-btn'); } catch {}
      try { el.classList.add(role === 'more' ? 'mg-product-overlay-btn--more' : 'mg-product-overlay-btn--primary'); } catch {}
      try { el.type = 'button'; } catch {}
    }

    function mountVideoLightbox() {
      if (root.querySelector(".lightbox")) return;

      const lb = document.createElement("div");
      lb.className = "lightbox";
      lb.setAttribute("aria-hidden", "true");
      lb.setAttribute("role", "dialog");
      lb.setAttribute("aria-modal", "true");

      lb.innerHTML = `
        <div class="lightbox__backdrop" data-lb-backdrop></div>

        <button class="edge-nav edge-nav--prev" type="button" data-lb-edge-prev aria-label="Previous video" aria-hidden="true" tabindex="-1"><span class="mg-chevron mg-chevron--left" aria-hidden="true"></span></button>
        <button class="edge-nav edge-nav--next" type="button" data-lb-edge-next aria-label="Next video" aria-hidden="true" tabindex="-1"><span class="mg-chevron mg-chevron--right" aria-hidden="true"></span></button>

        <div class="lightbox__scroll" data-lb-scroll>
          <div class="lightbox__panel">
            <button class="lb-close" type="button" data-lb-close aria-label="Close"><span class="mg-close-icon" aria-hidden="true"></span></button>

            <div class="lb-content" data-lb-content>
              <div class="lb-video-col" data-lb-video-col>
                <div class="video-wrap" data-lb-video-wrap>
                  <div class="video-frame" data-lb-frame>
                    <div class="player-stage is-loading" data-lb-player>
                      <div class="loading-spinner" aria-hidden="true"></div>
                      <div data-lb-player-slot></div>
                    </div>

                    <div class="gesture-layer" data-lb-gesture aria-hidden="true"></div>

                    <button class="unmute-btn" type="button" data-lb-unmute aria-label="Unmute audio" aria-hidden="true" tabindex="-1">
                      <span class="mg-speaker-icon" aria-hidden="true"></span><span>Tap to unmute</span>
                    </button>

                    <button class="nav-btn nav-overlay nav-overlay--desc" type="button" data-lb-desc-btn aria-hidden="true" tabindex="-1">
                      More Info
                    </button>

                    <div class="shop-cta" data-lb-cta-wrap>
                      <a class="shop-btn" data-lb-cta target="_self">Shop Now</a>
                    </div>
                  </div>

                  <div class="shop-cta-below" data-lb-cta-below-wrap>
                    <a class="shop-btn" data-lb-cta-below target="_self">Shop Now</a>
                  </div>
                </div>
              </div>

              <div class="lb-desc-col" data-lb-desc-col>
                <div class="desc-wrap" data-lb-desc-wrap>
                  <button class="desc-tab" type="button" data-desc-tab aria-label="More">
                    More <span class="mg-chevron mg-chevron--down" aria-hidden="true"></span>
                  </button>
                  <div class="desc-title is-hidden" data-lb-desc-title></div>
                  <div class="desc is-empty" data-lb-desc></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
      root.appendChild(lb);
    }

    function mountInfoLightbox() {
      if (root.querySelector(".info-lightbox")) return;

      const ib = document.createElement("div");
      ib.className = "info-lightbox";
      ib.setAttribute("aria-hidden", "true");
      ib.setAttribute("role", "dialog");
      ib.setAttribute("aria-modal", "true");

      ib.innerHTML = `
        <div class="lightbox__backdrop" data-info-backdrop></div>

        <div class="lightbox__scroll" data-info-scroll>
          <div class="lightbox__panel">
            <button class="lb-close" type="button" data-info-close aria-label="Close"><span class="mg-close-icon" aria-hidden="true"></span></button>

            <div class="desc-wrap is-visible">
              <div class="thumb-row info-preview-row is-centered" data-info-preview-row style="--row-aspect: 1 / 1;"></div>
              <div class="info-divider"></div>
              <div class="info-desc-block" data-info-desc-block>
                <div class="desc" data-info-desc></div>
              </div>
            </div>
          </div>
        </div>
      `;
      root.appendChild(ib);
    }

    function mountPresetLightbox() {
      // Skip in mini mode (used for preset bottom sheet content)
      if (root.hasAttribute("data-mg-mini")) return;

      if (root.querySelector(".preset-lightbox")) return;

      const presetLb = document.createElement("div");
      presetLb.className = "preset-lightbox";
      presetLb.setAttribute("aria-hidden", "true");
      presetLb.setAttribute("role", "dialog");
      presetLb.setAttribute("aria-modal", "true");

      const panel = document.createElement("div");
      panel.className = "preset-panel";
      panel.setAttribute("data-preset-panel", "");

      const header = document.createElement("div");
      header.className = "preset-header";

      const handle = document.createElement("div");
      handle.className = "preset-handle";
      handle.setAttribute("aria-hidden", "true");

      const closeBtn = document.createElement("button");
      closeBtn.className = "preset-close";
      closeBtn.type = "button";
      closeBtn.setAttribute("data-preset-close", "");
      closeBtn.setAttribute("aria-label", "Close");
      closeBtn.innerHTML = '<span class="mg-close-icon" aria-hidden="true"></span>';

      header.appendChild(handle);
      header.appendChild(closeBtn);

      const body = document.createElement("div");
      body.className = "preset-body";
      body.setAttribute("data-preset-body", "");

      panel.appendChild(header);
      panel.appendChild(body);

      const backdrop = document.createElement("div");
      backdrop.className = "preset-backdrop";
      backdrop.setAttribute("data-preset-backdrop", "");

      presetLb.appendChild(backdrop);
      presetLb.appendChild(panel);

      root.appendChild(presetLb);
    }

    async function render(ROWS) {
      const rowsRoot = document.createElement("div");
      rowsRoot.className = "video-rows";
      root.replaceChildren(rowsRoot);
      root.setAttribute("data-mg-rendering", "true");

      mountVideoLightbox();
      mountInfoLightbox();
      mountPresetLightbox();

      const yieldForPaint = () =>
        new Promise((resolve) => {
          requestAnimationFrame(() => resolve());
        });

      const lazyCustomLiquidEntries = [];
      let lazyCustomLiquidCursor = 0;
      let lazyCustomLiquidObserver = null;
      let lazyCustomLiquidPumpQueued = false;

      const isElementNearViewport = (el, pad = 0) => {
        if (!el || !el.isConnected || !el.getBoundingClientRect) return false;
        try {
          const cs = getComputedStyle(el);
          if (cs.display === "none" || cs.visibility === "hidden") return false;
          if (el.offsetParent === null && cs.position !== "fixed") return false;
        } catch {}
        const rect = el.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        return rect.bottom >= -pad && rect.top <= (viewportH + pad);
      };

      const prepareCustomLiquidMedia = (scope) => {
        try {
          const rootEl = scope && scope.querySelectorAll ? scope : null;
          if (!rootEl) return;
          rootEl.querySelectorAll("img").forEach((img) => {
            try { if (!img.getAttribute("loading")) img.loading = "lazy"; } catch {}
            try { img.decoding = "async"; } catch {}
            try { img.fetchPriority = "low"; } catch {}
          });
          rootEl.querySelectorAll("iframe").forEach((iframe) => {
            try { if (!iframe.getAttribute("loading")) iframe.loading = "lazy"; } catch {}
          });
          rootEl.querySelectorAll("video").forEach((video) => {
            try {
              if (!video.hasAttribute("autoplay") && !video.getAttribute("preload")) video.preload = "metadata";
            } catch {}
          });
        } catch {}
      };

      const hydrateLazyCustomLiquid = (entry) => {
        const customEl = entry && entry.el;
        if (!customEl || customEl.dataset.mgLazyCustom === "loaded" || customEl.dataset.mgLazyCustom === "loading") return false;

        customEl.dataset.mgLazyCustom = "loading";
        try {
          const templateId = String(entry.templateId || "").trim();
          const rawHtml = String(entry.rawHtml || "");
          let injected = false;

          if (templateId) {
            const tpl = document.getElementById(templateId);
            if (tpl) {
              if (tpl.tagName === "TEMPLATE" && tpl.content) {
                customEl.appendChild(tpl.content.cloneNode(true));
              } else {
                const src = tpl.cloneNode(true);
                try { src.removeAttribute("id"); } catch {}
                try { src.removeAttribute("hidden"); } catch {}
                try { src.removeAttribute("aria-hidden"); } catch {}
                while (src.firstChild) customEl.appendChild(src.firstChild);
              }
              injected = true;
            }
          }

          if (!injected && rawHtml.trim()) {
            const wrap = document.createElement("div");
            wrap.className = "mg-custom-liquid-wrap";
            wrap.style.setProperty("--mg-custom-width-mobile", String(Number(entry.widthMobile || 100) || 100) + "%");
            wrap.style.setProperty("--mg-custom-width-desktop", String(Number(entry.widthDesktop || 100) || 100) + "%");
            const maxWidth = Number(entry.maxWidth || 0) || 0;
            wrap.style.setProperty("--mg-custom-max-width", maxWidth > 0 ? (maxWidth + "px") : "none");
            wrap.innerHTML = rawHtml;
            customEl.appendChild(wrap);
          }

          prepareCustomLiquidMedia(customEl);
          activateClonedScripts(customEl);
          customEl.dataset.mgLazyCustom = "loaded";

          requestAnimationFrame(() => {
            try { mountInlinePresetInstances(customEl); } catch {}
            try { refreshRowLayoutsWithin(customEl.closest(".video-row") || customEl); } catch {}
            try { queueThemeShopButtonRefresh(true); } catch {}
          });
          return true;
        } catch {
          customEl.dataset.mgLazyCustom = "error";
          return false;
        }
      };

      const scheduleLazyCustomLiquidPump = () => {
        if (lazyCustomLiquidPumpQueued) return;
        lazyCustomLiquidPumpQueued = true;
        const run = () => {
          lazyCustomLiquidPumpQueued = false;
          pumpLazyCustomLiquidQueue();
        };
        if (typeof window.requestIdleCallback === "function") {
          window.requestIdleCallback(run, { timeout: 500 });
        } else {
          setTimeout(run, 60);
        }
      };

      function pumpLazyCustomLiquidQueue() {
        while (
          lazyCustomLiquidCursor < lazyCustomLiquidEntries.length &&
          lazyCustomLiquidEntries[lazyCustomLiquidCursor]?.el?.dataset?.mgLazyCustom === "loaded"
        ) {
          lazyCustomLiquidCursor++;
        }

        const entry = lazyCustomLiquidEntries[lazyCustomLiquidCursor];
        if (!entry) return;

        const target = entry.el;
        if (!entry.ready && !isElementNearViewport(target, 900)) return;
        entry.ready = true;

        if (lazyCustomLiquidObserver) {
          try { lazyCustomLiquidObserver.unobserve(target); } catch {}
        }

        hydrateLazyCustomLiquid(entry);
        lazyCustomLiquidCursor++;

        if (lazyCustomLiquidCursor < lazyCustomLiquidEntries.length) {
          scheduleLazyCustomLiquidPump();
        }
      }

      const markLazyCustomRowsReady = (scope) => {
        if (!scope) return;
        const nodes = [];
        if (scope.matches && scope.matches('.row-custom-html[data-mg-lazy-custom="pending"]')) nodes.push(scope);
        if (scope.querySelectorAll) nodes.push(...Array.from(scope.querySelectorAll('.row-custom-html[data-mg-lazy-custom="pending"]')));
        if (!nodes.length) return;

        for (const node of nodes) {
          const entry = node.__mgLazyCustomEntry;
          if (entry) entry.ready = true;
        }
        scheduleLazyCustomLiquidPump();
      };

      const queueLazyCustomLiquid = (customEl, rowSpec, templateId, rawHtml) => {
        if (!customEl) return;
        const entry = {
          el: customEl,
          templateId,
          rawHtml,
          ready: false,
          widthDesktop: rowSpec?.custom_width_desktop_percent,
          widthMobile: rowSpec?.custom_width_mobile_percent,
          maxWidth: rowSpec?.custom_max_width_px
        };
        customEl.dataset.mgLazyCustom = "pending";
        customEl.__mgLazyCustomEntry = entry;
        lazyCustomLiquidEntries.push(entry);
      };

      const startLazyCustomLiquidQueue = () => {
        if (!lazyCustomLiquidEntries.length) return;

        if ("IntersectionObserver" in window) {
          lazyCustomLiquidObserver = new IntersectionObserver((entries) => {
            for (const observed of entries) {
              if (!observed.isIntersecting && observed.intersectionRatio <= 0) continue;
              const entry = observed.target && observed.target.__mgLazyCustomEntry;
              if (!entry) continue;
              entry.ready = true;
            }
            scheduleLazyCustomLiquidPump();
          }, {
            rootMargin: "900px 0px 900px 0px",
            threshold: 0.01
          });
          cleanupFns.push(() => {
            try { lazyCustomLiquidObserver && lazyCustomLiquidObserver.disconnect(); } catch {}
          });
        }

        for (const entry of lazyCustomLiquidEntries) {
          if (!entry || !entry.el || !entry.el.isConnected) continue;
          if (isElementNearViewport(entry.el, 900)) entry.ready = true;
          if (lazyCustomLiquidObserver) {
            try { lazyCustomLiquidObserver.observe(entry.el); } catch {}
          } else {
            entry.ready = true;
          }
        }

        scheduleLazyCustomLiquidPump();
      };

      // Video modal refs
      const lightbox   = root.querySelector(".lightbox");
      const backdrop   = root.querySelector("[data-lb-backdrop]");
      const closeBtn   = root.querySelector("[data-lb-close]");
      const frameEl    = root.querySelector("[data-lb-frame]");
      const playerStage = root.querySelector("[data-lb-player]");
      const playerSlot  = root.querySelector("[data-lb-player-slot]");
      const scrollEl   = root.querySelector("[data-lb-scroll]");
      const gestureLayer = root.querySelector("[data-lb-gesture]");

      const unmuteBtn = root.querySelector("[data-lb-unmute]");

      const edgePrev = root.querySelector("[data-lb-edge-prev]");
      const edgeNext = root.querySelector("[data-lb-edge-next]");

      const descWrap   = root.querySelector("[data-lb-desc-wrap]");
      const descTitleEl = root.querySelector("[data-lb-desc-title]");
      const descEl     = root.querySelector("[data-lb-desc]");
      const descCol    = root.querySelector("[data-lb-desc-col]");
      const descTab    = root.querySelector("[data-desc-tab]");

      const ctaWrap    = root.querySelector("[data-lb-cta-wrap]");
      const ctaBtn     = root.querySelector("[data-lb-cta]");
      const ctaBelowWrap = root.querySelector("[data-lb-cta-below-wrap]");
      const ctaBelowBtn  = root.querySelector("[data-lb-cta-below]");

      const descBtn    = root.querySelector("[data-lb-desc-btn]");

      // Info modal refs
      const infoLb       = root.querySelector(".info-lightbox");
      const infoBackdrop = root.querySelector("[data-info-backdrop]");
      const infoClose    = root.querySelector("[data-info-close]");
      const infoScroll   = root.querySelector("[data-info-scroll]");
      const infoPreviewRow = root.querySelector("[data-info-preview-row]");
      const infoDescBlock = root.querySelector("[data-info-desc-block]");
      const infoDesc     = root.querySelector("[data-info-desc]");

      // Data
      const VIDEO_PLAYLIST = [];       // Video playlist
      const CARD_META = new WeakMap(); // all cards

      let currentIndex = -1;
      let currentUrl = null;
      let currentW = 16;
      let currentH = 9;
      let currentEndBehavior = "next";

      let hasDesc = false;
      let hasDescBody = false;
      let currentVideoTitle = "";
      let currentDescAppendHtml = ""; // per-video HTML add-on appended after base description
      let isSideDesc = false;
      let sideDescW = 0;
      let audioUnlocked = false; // iOS: once audio is unlocked, keep trying to preserve it across swipes
      let autoUnmuteToken = "";
      let autoUnmuteTries = 0;

      // CTA placement state
      let hasCtaLink = false;
      let ctaPlaceRaf = 0;

      let infoMetaActive = null;
      let infoSizingRaf = 0;

      // Single Vimeo player state
      let playerToken = 0;
      let playerLoadTimer = 0;

      // iOS audio stability: keep a single persistent Vimeo iframe and park it off-screen when closed.
      let playerIframe = null;
      let playerParking = null;
      let playerReady = false;
      let lastLoadedVimeoId = "";
      let currentVideoSource = "vimeo";
      let activeAuxPlayer = null;
      let activeAuxPlayerCleanup = null;

      // Swipe state (simple: decide prev/next; no track animation)
      const SWIPE = {
        armed: false,
        id: null,
        startX: 0,
        startY: 0,
        dx: 0,
        dy: 0,
        axis: null,
        animating: false
      };
      const SWIPE_LOCK_THRESH = 18;
      const SWIPE_COMMIT_MIN  = 80;
      const SWIPE_COMMIT_PCT  = 0.18;

      function isDesktop() {
        // Desktop layout is ONLY for non-phone screens, and NOT for touch devices held vertically.
        // This makes portrait tablets behave like mobile (stacked video + description).
        const wide = window.matchMedia("(min-width: 769px)").matches;
        if (!wide) return false;

        if (IS_TOUCH) {
          const portrait = window.matchMedia("(orientation: portrait)").matches;
          if (portrait) return false;

          // Also treat smaller touch screens in landscape (phones) as mobile UI.
          if (window.matchMedia("(max-width: 900px)").matches) return false;
        }

        return true;
      }
      function desktopVideoUrlOf(meta) {
        return String(meta?.desktop_video_url ?? meta?.desktopVideoUrl ?? meta?.desktop_url ?? meta?.desktopUrl ?? "").trim();
      }
      function playbackUrlOf(meta) {
        const desktopUrl = desktopVideoUrlOf(meta);
        if (desktopUrl && isDesktop()) return desktopUrl;
        return String(meta?.url || "").trim();
      }
      function playbackSourceOf(meta) {
        return videoSourceFromUrl(playbackUrlOf(meta)) || videoSourceOf(meta);
      }
      function playbackStartTimeOf(meta, url) {
        const explicit = parseStartTimeSeconds(meta?.start_time ?? meta?.startTime ?? meta?.start_at ?? meta?.startAt);
        if (explicit > 0) return explicit;
        return parseStartTimeFromUrl(url);
      }
      function isVideoOpen() { return lightbox.getAttribute("aria-hidden") === "false"; }
      function isInfoOpen() { return infoLb.getAttribute("aria-hidden") === "false"; }

      function setGestureLayerActive(on) {
        gestureLayer.classList.toggle("is-active", !!on);
        gestureLayer.setAttribute("aria-hidden", on ? "false" : "true");
      }

      function setAriaAndTab(el, shown) {
        if (!el) return;
        el.setAttribute("aria-hidden", shown ? "false" : "true");
        el.tabIndex = shown ? 0 : -1;
      }

      function updateEdgeNav() {
        const show = isVideoOpen() && isDesktop() && VIDEO_PLAYLIST.length > 1;
        edgePrev.classList.toggle("is-visible", show);
        edgeNext.classList.toggle("is-visible", show);
        setAriaAndTab(edgePrev, show);
        setAriaAndTab(edgeNext, show);
      }

      // ------------------------------
      // Unmute button UI
      // ------------------------------
      let lastMutedState = null; // null = unknown

      function updateUnmuteBtnUI(){
        const show = isVideoOpen() && currentVideoSource === "vimeo" && lastMutedState === true;
        if (!unmuteBtn) return;
        unmuteBtn.classList.toggle("is-visible", show);
        setAriaAndTab(unmuteBtn, show);
      }

      function setMutedState(muted){
        if (typeof muted !== "boolean") return;
        lastMutedState = muted;

        // Once audio has been successfully unmuted at least once, iOS *should* allow
        // subsequent videos to play with sound, but it can still flip back to muted.
        if (muted === false) {
          audioUnlocked = true;
          autoUnmuteTries = 0;
        }

        // iOS quirk: after `loadVideo()` (swipe), Vimeo may come up muted again.
        // If audio is already unlocked, keep nudging it back to unmuted.
        if (muted === true && audioUnlocked && IOS_PERSISTENT_PLAYER) {
          autoUnmuteCurrent();
        }

        updateUnmuteBtnUI();
      }

      function autoUnmuteCurrent(){
        const iframe = getPlayerIframe();
        if (!iframe) return;
        const token = String(iframe.dataset.mgToken || "");
        if (!token) return;

        if (autoUnmuteToken !== token) {
          autoUnmuteToken = token;
          autoUnmuteTries = 0;
        }
        if (autoUnmuteTries >= 8) return;
        autoUnmuteTries += 1;

        try {
          // Burst a couple commands immediately (helps when the player is mid-transition)
          vimeoPost(iframe, "setMuted", false);
          vimeoPost(iframe, "setVolume", 1);
          vimeoPost(iframe, "play");
          vimeoPost(iframe, "setMuted", false);
        } catch {}

        setTimeout(() => {
          if (!iframe || String(iframe.dataset.mgToken || "") !== token) return;
          try { requestVimeoMuteState(iframe); } catch {}
        }, 180);
      }

      function resetMutedState(){
        lastMutedState = null;
        updateUnmuteBtnUI();
      }

      function wrapIndex(i) {
        const n = VIDEO_PLAYLIST.length || 0;
        if (!n) return 0;
        return ((i % n) + n) % n;
      }
      function metaAt(i) { return VIDEO_PLAYLIST[wrapIndex(i)]; }

      function panelContentBox(){
        try{
          const panel = frameEl && frameEl.closest ? frameEl.closest(".lightbox__panel") : null;
          const vpW = Math.max(0, Math.round((window.visualViewport && window.visualViewport.width) || window.innerWidth || 0));
          const vpH = Math.max(0, Math.round((window.visualViewport && window.visualViewport.height) || window.innerHeight || 0));
          if (!panel) {
            return {
              w: Math.max(0, vpW - 24),
              h: Math.max(0, vpH - 24)
            };
          }
          const cs = getComputedStyle(panel);
          const padL = numCss(cs.paddingLeft);
          const padR = numCss(cs.paddingRight);
          const padT = numCss(cs.paddingTop);
          const padB = numCss(cs.paddingBottom);

          const w = Math.max(0, vpW - padL - padR);
          const h = Math.max(0, vpH - padT - padB);
          return { w, h };
        } catch {
          return {
            w: Math.max(0, window.innerWidth - 24),
            h: Math.max(0, window.innerHeight - 24)
          };
        }
      }

      function showMobileTitleDescBox() {
        return !isDesktop() && !!String(currentVideoTitle || "").trim();
      }

      function visibleDescBox() {
        return !!hasDescBody || showMobileTitleDescBox();
      }

      function syncVideoDescChrome() {
        hasDesc = visibleDescBox();

        if (descTitleEl) {
          const titleText = String(currentVideoTitle || "").trim();
          descTitleEl.textContent = titleText;
          descTitleEl.classList.toggle("is-hidden", !titleText);
        }

        if (descEl) {
          descEl.classList.toggle("is-empty", !hasDescBody);
        }
      }

      function mobileDescPeekPx() {
        if (!visibleDescBox() || isSideDesc || isDesktop()) return 0;

        let titleLinePx = 20;
        let bodyLinePx = 0;
        let padTop = 14;
        let padBottom = 14;

        try {
          if (descTitleEl) {
            const titleCs = getComputedStyle(descTitleEl);
            titleLinePx = numCss(titleCs.lineHeight);
            if (!(titleLinePx > 0)) {
              const fs = numCss(titleCs.fontSize || "16");
              titleLinePx = fs > 0 ? (fs * 1.25) : 20;
            }
          }
          if (hasDescBody && descEl) {
            const descCs = getComputedStyle(descEl);
            bodyLinePx = numCss(descCs.lineHeight);
            if (!(bodyLinePx > 0)) {
              const fs = numCss(descCs.fontSize || "15");
              bodyLinePx = fs > 0 ? (fs * 1.55) : 24;
            }
          }
          if (descWrap) {
            const wrapCs = getComputedStyle(descWrap);
            padTop = numCss(wrapCs.paddingTop || "14");
            padBottom = numCss(wrapCs.paddingBottom || "14");
          }
        } catch {}

        const extraBody = hasDescBody ? (Math.min(bodyLinePx || 24, 24) + 8) : Math.min(8, padBottom * 0.35);
        const peek = Math.round(padTop + titleLinePx + extraBody + Math.min(12, padBottom * 0.5));
        return Math.max(58, Math.min(118, peek));
      }

      function maximize(w, h) {
        const layout = computeDescLayout();
        applyDescLayout(layout.mode, layout.descW, layout.isReel);

        const box = panelContentBox();
        const vw = box.w;
        const vh = box.h;

        const W = Number(w) || 16;
        const H = Number(h) || 9;
        const r = W / H;
        if (!(r > 0) || !isFinite(r)) return;

        const reel = !!layout.isReel;
        const desktop = isDesktop();

        const GAP = 18;
        const usableW = isSideDesc ? Math.max(240, vw - (sideDescW || 0) - GAP) : vw;
        const descPeek = (!desktop && !isSideDesc) ? mobileDescPeekPx() : 0;
        const usableH = Math.max(180, vh - descPeek);

        let width;
        let height;

        /*
          Reels (portrait videos):
          - Mobile: size is naturally width-first, but we still reserve a visible description peek.
          - Desktop: prefer full height (top-to-bottom) with width auto.
        */
        if (reel && desktop) {
          height = vh;
          width = height * r;
          if (width > usableW) {
            width = usableW;
            height = width / r;
          }
        } else {
          width = usableW;
          height = usableW / r;
          if (height > usableH) { height = usableH; width = usableH * r; }
        }
        if (isSideDesc && (width + (sideDescW || 0) + GAP) > vw) {
          width = Math.max(240, vw - (sideDescW || 0) - GAP);
          height = width / r;
        }

        frameEl.style.width = Math.floor(width) + "px";
        frameEl.style.height = Math.floor(height) + "px";
        try { lightbox.style.setProperty("--lb-frame-h", Math.floor(height) + "px"); } catch {}

        // Keep the below-video description aligned with the video width.
        // On mobile/below-video layouts, the height reservation above guarantees
        // that at least one visible line of the description peeks below the video.
        if (descWrap) {
          if (isSideDesc) descWrap.style.width = "";
          else descWrap.style.width = Math.floor(width) + "px";
        }

        updateDescTabVisibility();
      }

      function showCtaOverlay(){
        ctaWrap.classList.add("is-visible");
        if (ctaBelowWrap) {
          ctaBelowWrap.classList.remove("is-visible", "is-measuring");
        }
      }

      function showCtaBelow(){
        if (!ctaBelowWrap) { showCtaOverlay(); return; }
        ctaWrap.classList.remove("is-visible");
        ctaBelowWrap.classList.add("is-visible");
        ctaBelowWrap.classList.remove("is-measuring");
      }

      function updateCtaPlacement(){
        if (!isVideoOpen()) return;

        if (!hasCtaLink) {
          ctaWrap.classList.remove("is-visible");
          if (ctaBelowWrap) ctaBelowWrap.classList.remove("is-visible", "is-measuring");
          return;
        }

        // Always compute a sensible overlay offset (~5% of the video height, clamped).
        if (frameEl) {
          const fr = frameEl.getBoundingClientRect();
          const px = Math.round(Math.max(12, Math.min(80, fr.height * 0.05)));
          frameEl.style.setProperty("--cta-overlay-offset", px + "px");
        }

        if (!ctaBelowWrap || !ctaBelowBtn) {
          showCtaOverlay();
          return;
        }

        // Mobile: keep the description flush with the bottom of the video.
        // Side-by-side description: keep the video height stable so only the description scrolls.
        if (!isDesktop() || isSideDesc || hasDesc) {
          ctaBelowWrap.classList.remove("is-visible", "is-measuring");
          showCtaOverlay();
          return;
        }

        ctaBelowWrap.classList.add("is-visible", "is-measuring");
        const btnH = ctaBelowBtn.getBoundingClientRect().height || 0;
        ctaBelowWrap.classList.remove("is-measuring");

        const needed = Math.max(0, btnH) + 14;
        const frameRect = frameEl.getBoundingClientRect();
        const avail = window.innerHeight - frameRect.bottom - 18;

        if (avail >= needed) {
          showCtaBelow();
        } else {
          ctaBelowWrap.classList.remove("is-visible");
          showCtaOverlay();
        }
      }

      function scheduleCtaPlacement(){
        if (ctaPlaceRaf) return;
        ctaPlaceRaf = requestAnimationFrame(() => {
          ctaPlaceRaf = 0;
          updateCtaPlacement();
        });
      }

      function setCTA(meta) {
        const hide = !!(meta?.hide_cta_buttons || meta?.__hide_cta_buttons);
        const cta = meta?.cta;
        const href = hide ? "" : safeLink(cta?.href);
        if (!href) {
          hasCtaLink = false;

          ctaWrap.classList.remove("is-visible");
          if (ctaBelowWrap) ctaBelowWrap.classList.remove("is-visible", "is-measuring");

          ctaBtn.removeAttribute("href");
          ctaBtn.textContent = "Shop Now";
          ctaBtn.target = "_self";

          if (ctaBelowBtn) {
            ctaBelowBtn.removeAttribute("href");
            ctaBelowBtn.textContent = "Shop Now";
            ctaBelowBtn.target = "_self";
          }
          return;
        }

        hasCtaLink = true;

        const text = (String(cta?.text || "").trim() || "Shop Now");

        ctaBtn.href = href;
        ctaBtn.target = "_self";
        ctaBtn.textContent = text;

        if (ctaBelowBtn) {
          ctaBelowBtn.href = href;
          ctaBelowBtn.target = "_self";
          ctaBelowBtn.textContent = text;
        }

        showCtaOverlay();
        scheduleCtaPlacement();
      }

      function clearVideoDesc() {
        hasDescBody = false;
        descEl.classList.remove("is-text", "is-html");
        descEl.textContent = "";
        descEl.innerHTML = "";
        syncVideoDescChrome();
      }
      function setVideoDescTitle(meta) {
        currentVideoTitle = collapseWhitespace(meta?.title) || "Video";
        syncVideoDescChrome();
      }
      function setVideoDescHTML(html) {
        // Sanitize first, then linkify any plain URLs that appear inside HTML,
        // then sanitize again (linkify introduces <a> tags).
        const cleaned = sanitizeHtml(html);
        if (!cleaned.trim()) { clearVideoDesc(); return; }
        const linked = linkifyHtmlString(cleaned);
        const finalHtml = sanitizeHtml(linked);
        if (!finalHtml.trim()) { clearVideoDesc(); return; }
        hasDescBody = true;
        descEl.classList.remove("is-text");
        descEl.classList.add("is-html");
        descEl.innerHTML = finalHtml;
        syncVideoDescChrome();
      }

      const LINK_DETECT_RE = /(https?:\/\/|www\.|mailto:|tel:|sms:|\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b|\b[a-z0-9-]+\.[a-z]{2,}(?:\/[\w\-./?%&=+#@:]*)?)/i;

      // Linkify plain-text URLs that appear *inside* HTML (e.g., a richtext field where the user typed
      // a URL but didn't explicitly create a link). Skips existing <a> tags and code-ish containers.
      function linkifyHtmlString(html) {
        const input = String(html ?? "");
        if (!input.trim() || !LINK_DETECT_RE.test(input)) return input;

        const tpl = document.createElement("template");
        tpl.innerHTML = input;

        const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        while (walker.nextNode()) textNodes.push(walker.currentNode);

        for (const node of textNodes) {
          if (!node || !node.parentElement) continue;
          // Don't touch text that's already inside a link or code-ish blocks.
          if (node.parentElement.closest("a,script,style,textarea,code,pre")) continue;

          const raw = String(node.nodeValue ?? "");
          if (!raw.trim() || !LINK_DETECT_RE.test(raw)) continue;

          const linked = linkifyInlineText(raw);
          if (!linked || linked.indexOf("<a") === -1) continue;

          const fragTpl = document.createElement("template");
          fragTpl.innerHTML = linked;
          node.parentNode.replaceChild(fragTpl.content, node);
        }

        return tpl.innerHTML;
      }

      // Convert a plain-text description into safe HTML that preserves newlines
      // and makes URLs/emails clickable.
      function richHtmlFromPlainText(input) {
        const raw = String(input ?? "").replace(/\r\n/g, "\n").trim();
        if (!raw) return "";

        const blocks = raw.split(/\n{2,}/);
        const out = blocks
          .map((block) => {
            const lines = block.split("\n");
            const inner = lines.map((line) => linkifyInlineText(line)).join("<br>");
            return `<p>${inner}</p>`;
          })
          .join("");

        // Extra safety: strips scripts/unsafe hrefs if anything slipped in.
        return sanitizeHtml(out);
      }

      // Inline-only linkifier used by both richHtmlFromPlainText() and linkifyHtmlString().
      // NOTE: This runs on *raw text* (not escaped text), so it can correctly handle
      // Vimeo-style autolinks like <https://example.com>.
      function linkifyInlineText(text) {
        const s = String(text ?? "");
        if (!s) return "";

        // Order matters:
        //  1) Markdown links: [text](url)
        //  2) Angle-bracket autolinks: <https://example.com>
        //  3) Full URLs / www URLs
        //  4) Emails
        //  5) Bare domains (example.com/path)
        const RE =
          /\[([^\]]+)\]\(([^)\s]+)\)|<((?:https?:\/\/|www\.)[^>\s]+)>|((?:https?:\/\/|www\.)[^\s<]+)|([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})|(\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}(?::\d{2,5})?(?:\/[\S]*)?)/gi;

        let out = "";
        let lastIndex = 0;

        // Peel trailing punctuation that often follows links in prose.
        const peelTrailing = (val) => {
          let core = val;
          let trailing = "";
          while (core && /[)\],.!?:;>\"'â€â€™]+$/.test(core)) {
            trailing = core.slice(-1) + trailing;
            core = core.slice(0, -1);
          }
          return { core, trailing };
        };

        s.replace(RE, (match, mdText, mdHref, angleUrl, url, email, domain, offset) => {
          out += escapeHtml(s.slice(lastIndex, offset));
          lastIndex = offset + match.length;

          // Markdown: [text](url)
          if (mdText && mdHref) {
            const { core, trailing } = peelTrailing(String(mdHref));
            const okHref = safeLink(core);
            if (!okHref) {
              out += escapeHtml(match);
              return match;
            }
            out += `<a href="${escapeHtml(okHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(mdText)}</a>${escapeHtml(trailing)}`;
            return match;
          }

          // Angle-autolinks, full URLs, and bare domains
          const candidate = String(angleUrl || url || domain || "");
          if (candidate) {
            const kind = angleUrl || url ? "url" : "domain";
            let { core, trailing } = peelTrailing(candidate);

            let href = core;

            // Autocomplete scheme for "www." and bare domains.
            if (/^www\./i.test(href)) href = "https://" + href;
            if (kind === "domain") href = "https://" + href;

            const okHref = safeLink(href);
            if (!okHref) {
              out += escapeHtml(match);
              return match;
            }

            out += `<a href="${escapeHtml(okHref)}" target="_blank" rel="noopener noreferrer">${escapeHtml(core)}</a>${escapeHtml(trailing)}`;
            return match;
          }

          // Emails
          if (email) {
            const addr = String(email);
            const okHref = safeLink("mailto:" + addr);
            if (!okHref) {
              out += escapeHtml(match);
              return match;
            }
            out += `<a href="${escapeHtml(okHref)}">${escapeHtml(addr)}</a>`;
            return match;
          }

          out += escapeHtml(match);
          return match;
        });

        out += escapeHtml(s.slice(lastIndex));
        return out;
      }

      function setVideoDescText(text) {
        const raw = String(text || "");
        if (!raw.trim()) { clearVideoDesc(); return; }
        hasDescBody = true;
        descEl.classList.remove("is-text");
        descEl.classList.add("is-html");
        descEl.innerHTML = richHtmlFromPlainText(raw);
        syncVideoDescChrome();
      }

      const DESC_APPEND_SEPARATOR = '<div class="mg-desc-spacer" aria-hidden="true"></div>';

      function setVideoDescFromMeta(meta) {
        // `desc_html` is an *add-on* HTML block that should be appended AFTER the base description.
        // If there is no base override, we will append it to the Vimeo-fetched description.
        currentDescAppendHtml = String(meta?.desc_html || "").trim();
        const append = currentDescAppendHtml;

        const baseHtml = String(meta?.html_desc || "").trim();
        if (baseHtml) {
          setVideoDescHTML(append ? (baseHtml + DESC_APPEND_SEPARATOR + append) : baseHtml);
          return true;
        }

        const baseTxt = String(meta?.description || "").trim();
        if (baseTxt) {
          const baseConverted = richHtmlFromPlainText(baseTxt);
          setVideoDescHTML(append ? (baseConverted + DESC_APPEND_SEPARATOR + append) : baseConverted);
          return true;
        }

        clearVideoDesc();
        return false;
      }

      function updateVideoDescVisibility() {
        // On mobile, keep a visible description box by showing the title as the first line.
        // On desktop, only show the box when there is actual description content.
        try {
          descBtn.classList.remove("is-visible");
          setAriaAndTab(descBtn, false);
        } catch {}

        syncVideoDescChrome();

        descWrap.classList.toggle("is-visible", !!hasDesc);
        if (descCol) descCol.style.display = hasDesc ? "flex" : "none";

        if (isVideoOpen()) maximize(currentW, currentH);
        updateEdgeNav();
        scheduleCtaPlacement();
        updateDescTabVisibility();
      }

      function computeDescLayout() {
        const aspect = (Number(currentW)||0) && (Number(currentH)||0)
          ? (Number(currentW) / Number(currentH))
          : 0;
        const isReel = aspect > 0 && aspect <= 0.75;

        // Only attempt side-by-side on desktop when there is actual description content.
        if (!hasDescBody || !isDesktop()) return { mode: "below", descW: 0, isReel };

        const box = panelContentBox();
        const GAP = 18;
        const MIN_DESC = 340;
        const MAX_DESC = 520;

        // Reels (portrait): only place description to the side if the video can still run
        // from top-to-bottom (full available height) AND the description column keeps a readable width.
        if (isReel) {
          const videoWAtFullH = box.h * aspect; // width when height = box.h
          const sideOK = (box.h >= 420) && (box.w >= (videoWAtFullH + MIN_DESC + GAP));
          if (!sideOK) return { mode: "below", descW: 0, isReel };
          const leftoverForDesc = box.w - videoWAtFullH - GAP;
          const descW = Math.round(Math.max(MIN_DESC, Math.min(MAX_DESC, leftoverForDesc)));
          return { mode: "side", descW, isReel };
        }

        // Non-reels: keep the existing "if there's room" behavior.
        const MIN_VIDEO = 360;
        if ((box.h < 420) || (box.w < (MIN_DESC + MIN_VIDEO + GAP))) return { mode: "below", descW: 0, isReel };
        const ideal = Math.round(Math.max(MIN_DESC, Math.min(MAX_DESC, box.w * 0.34)));
        if (box.w - ideal - GAP < MIN_VIDEO) return { mode: "below", descW: 0, isReel };
        return { mode: "side", descW: ideal, isReel };
      }

      function applyDescLayout(mode, descW, isReel) {
        isSideDesc = (mode === "side");
        sideDescW = isSideDesc ? (descW || 0) : 0;
        try { lightbox.classList.toggle("is-side-desc", isSideDesc); } catch {}
        try { lightbox.classList.toggle("is-reel", !!isReel); } catch {}
        try { lightbox.classList.toggle("is-mobilelike", !isDesktop()); } catch {}
        if (descCol) {
          if (isSideDesc && sideDescW) {
            descCol.style.width = sideDescW + "px";
          } else {
            descCol.style.width = "";
          }
        }
        if (isSideDesc) {
          try { scrollEl.scrollTop = 0; } catch {}
          try { if (descCol) descCol.scrollTop = 0; } catch {}
        }
      }

      function scrollDescDown() {
        if (!scrollEl) return;
        const max = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (max <= 2) return;
        const remaining = max - scrollEl.scrollTop;
        if (remaining <= 6) return;
        const step = Math.round(Math.min(remaining, scrollEl.clientHeight * 0.65));
        scrollEl.scrollTo({ top: scrollEl.scrollTop + Math.max(40, step), behavior: "smooth" });
      }

      function updateDescTabVisibility() {
        if (!descTab) return;
        // Only show the tab when the description is below the video (mobile / narrow desktop)
        // and there is actually more content to scroll.
        if (!hasDescBody || isSideDesc) { descTab.classList.add("is-hidden"); return; }
        const max = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (max <= 12) { descTab.classList.add("is-hidden"); return; }
        const atBottom = scrollEl.scrollTop >= (max - 4);
        descTab.classList.toggle("is-hidden", atBottom);
      }

      if (descTab) {
        descTab.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          scrollDescDown();
        });
      }
      // Keep the tab visibility in sync while the user scrolls the modal.
      if (scrollEl) {
        scrollEl.addEventListener("scroll", () => {
          updateDescTabVisibility();
        }, { passive: true });
      }

      function getPlayerIframe(){
        if (playerIframe && playerIframe.isConnected) return playerIframe;
        const found = root.querySelector("iframe[data-mg-vimeo-iframe]") || null;
        if (found) playerIframe = found;
        return playerIframe;
      }

      function ensurePlayerParking(){
        if (playerParking && playerParking.isConnected) return playerParking;
        const el = document.createElement("div");
        el.className = "player-parking";
        el.setAttribute("aria-hidden", "true");
        root.appendChild(el);
        playerParking = el;
        return playerParking;
      }

      function dockPlayerIntoModal(){
        const iframe = getPlayerIframe();
        if (!iframe || !playerSlot) return null;
        if (iframe.parentElement !== playerSlot) {
          try { playerSlot.appendChild(iframe); } catch {}
        }
        return iframe;
      }

      function parkPlayerOffscreen(){
        const iframe = getPlayerIframe();
        if (!iframe) return;
        const park = ensurePlayerParking();
        if (iframe.parentElement !== park) {
          try { park.appendChild(iframe); } catch {}
        }
      }

      function clearAuxPlayer(){
        try {
          if (activeAuxPlayerCleanup) activeAuxPlayerCleanup();
        } catch {}
        activeAuxPlayerCleanup = null;
        try {
          const el = activeAuxPlayer;
          if (el) {
            if (el.tagName === "VIDEO") {
              try { el.pause(); } catch {}
              try { el.removeAttribute("src"); el.load(); } catch {}
            }
            if (el.tagName === "IFRAME") {
              try { el.src = "about:blank"; } catch {}
            }
            try { el.remove(); } catch {}
          }
        } catch {}
        activeAuxPlayer = null;
      }

      function clearPlayer({ unload = true } = {}){
        clearAuxPlayer();
        currentVideoSource = "vimeo";
        try {
          const iframe = getPlayerIframe();
          if (iframe) {
            try { unregisterVimeoEnded(iframe); } catch {}
            try { unregisterVimeoTimeUpdate(iframe); } catch {}

            // Best-effort stop (prevents background audio)
            try { pauseVimeo(iframe); } catch {}

            if (unload) {
              try { iframe.src = "about:blank"; } catch {}
              try { iframe.removeAttribute("src"); } catch {}
              try { iframe.remove(); } catch {}
              playerIframe = null;
              playerReady = false;
              lastLoadedVimeoId = "";
            } else {
              // Keep iframe alive (iOS)
              parkPlayerOffscreen();
            }
          }
        } catch {}
        try { if (playerSlot && unload) playerSlot.innerHTML = ""; } catch {}
        try { if (playerStage) playerStage.classList.add("is-loading"); } catch {}
        try {
          if (playerLoadTimer) {
            clearTimeout(playerLoadTimer);
            playerLoadTimer = 0;
          }
        } catch {}
      }

      function ensurePlayerIframe(meta){
        // Create the single persistent Vimeo iframe once.
        let iframe = getPlayerIframe();
        if (iframe) return iframe;

        const baseUrl = String(meta?.url || VIDEO_PLAYLIST?.[0]?.url || "").trim();
        if (!baseUrl) return null;

        iframe = document.createElement("iframe");
        iframe.setAttribute("data-mg-vimeo-iframe", "1");
        iframe.allow = "autoplay; fullscreen; picture-in-picture";
        iframe.setAttribute("allowfullscreen", "");
        iframe.setAttribute("loading", "eager");
        iframe.title = String(meta?.title || "Video");

        // Boot the player without autoplay (avoids iOS forced-muted autoplay).
        // iOS: boot once and keep alive; non-iOS: keep blank until opened (saves network).
        iframe.src = IOS_PERSISTENT_PLAYER ? buildVimeoSrc(baseUrl, false, false, endBehaviorOf(meta), startTimeOf(meta)) : "about:blank";

        iframe.addEventListener("load", () => {
          playerReady = true;
        });

        // Park off-screen on iOS so it stays loaded even while the modal is display:none.
        if (IOS_PERSISTENT_PLAYER) {
          ensurePlayerParking().appendChild(iframe);
        } else if (playerSlot) {
          playerSlot.appendChild(iframe);
        }

        playerIframe = iframe;
        return iframe;
      }

      function setPlayerLoading(on){
        if (!playerStage) return;
        playerStage.classList.toggle("is-loading", !!on);
      }

      function pauseCurrent(){
        if (currentVideoSource === "file" && activeAuxPlayer && activeAuxPlayer.tagName === "VIDEO") {
          try { activeAuxPlayer.pause(); } catch {}
          return;
        }
        const iframe = getPlayerIframe();
        if (iframe) pauseVimeo(iframe);
      }

      function isModalOpen(){ return isVideoOpen(); }

      function goToIndex(i, opts = {}) { loadIndex(i, opts); }
      function goNext(opts = {}) { if (VIDEO_PLAYLIST.length > 1) goToIndex(currentIndex + 1, opts); }
      function goPrev(opts = {}) { if (VIDEO_PLAYLIST.length > 1) goToIndex(currentIndex - 1, opts); }

      edgePrev.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); goPrev({ focus:false, fromArrow:true }); });
      edgeNext.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); goNext({ focus:false, fromArrow:true }); });

      function registerEndedForCurrent(urlToken, endBehaviorInput) {
        const iframe = getPlayerIframe();
        const meta = metaAt(currentIndex);
        if (!iframe || !meta) return;

        try { unregisterVimeoEnded(iframe); } catch {}
        try { unregisterVimeoTimeUpdate(iframe); } catch {}

        const behavior = normalizeEndBehavior(endBehaviorInput || endBehaviorOf(meta), "next");
        let actionLock = false;

        const isCurrent = () => {
          if (!isVideoOpen()) return false;
          if (String(currentUrl || "") !== String(urlToken || "")) return false;
          return true;
        };

        const lockFor = (ms = 650) => {
          actionLock = true;
          setTimeout(() => {
            if (!isCurrent()) return;
            actionLock = false;
          }, Math.max(180, ms | 0));
        };

        const advance = () => {
          if (actionLock) return;
          if (!isCurrent()) return;
          if (VIDEO_PLAYLIST.length <= 1) return;
          actionLock = true;
          goNext({ focus:false, fromEnded:true });
        };

        const loopCurrent = () => {
          if (actionLock) return;
          if (!isCurrent()) return;
          const token = String(iframe.dataset.mgToken || "");
          const preferMuted = (lastMutedState === true) || (IOS_FORCE_MUTED_AUTOPLAY && !audioUnlocked);
          lockFor(820);
          restartVimeoFromStart(iframe, token, preferMuted);
          setTimeout(() => {
            try {
              if (!isCurrent()) return;
              requestVimeoMuteState(iframe);
            } catch {}
          }, 200);
        };

        const handleAtEnd = () => {
          if (behavior === "loop") {
            loopCurrent();
            return;
          }
          advance();
        };

        // Primary: ended event.
        // - next: always advance.
        // - loop: restart same video.
        // - default: let provider defaults win; if it actually ends, advance to next.
        registerVimeoEnded(iframe, () => {
          if (!isCurrent()) return;
          handleAtEnd();
        });

        let started = false;
        let spinnerCleared = false;
        let lastSeconds = 0;
        let nearCount = 0;

        registerVimeoTimeUpdate(iframe, (msg) => {
          if (!isCurrent()) return;

          const d = (msg && typeof msg === "object") ? (msg.data || msg) : {};
          const seconds  = Number(d.seconds);
          const duration = Number(d.duration);
          const pct      = Number(d.percent);

          if (!isFinite(seconds) || !isFinite(duration)) return;
          if (!(duration > 1)) return;

          if (!started) {
            if (seconds >= 0.6 || (isFinite(pct) && pct > 0.02)) started = true;
            else return;
          }

          if (started && !spinnerCleared) {
            spinnerCleared = true;
            try { setPlayerLoading(false); } catch {}
          }

          const resetNearEnd = (seconds + 2 < lastSeconds) && (lastSeconds >= Math.max(1, duration - 1));
          if (resetNearEnd) {
            if (behavior === "next") {
              advance();
              return;
            }
            nearCount = 0;
          }
          lastSeconds = seconds;

          if (behavior === "default") return;

          const remaining = duration - seconds;
          const nearByPct  = (isFinite(pct) && pct >= 0.999 && seconds > 1);
          const nearByTime = (isFinite(remaining) && remaining <= 0.35 && seconds > 1);

          if (!(nearByPct || nearByTime)) {
            nearCount = 0;
            return;
          }

          if (remaining <= 0.18) {
            handleAtEnd();
            return;
          }

          nearCount++;
          if (nearCount >= 2) handleAtEnd();
        });
      }

      function registerMuteStateForCurrent(urlToken) {
        const iframe = getPlayerIframe();
        if (!iframe) return;

        let seen = false;

        // Replace handlers each time we load a new video.
        try { unregisterVimeoVolumeChange(iframe); } catch {}
        try { unregisterVimeoMethodResponses(iframe); } catch {}

        registerVimeoVolumeChange(iframe, (msg) => {
          try {
            if (!isModalOpen()) return;
            if (String(currentUrl || "") !== String(urlToken || "")) return;

            const payload = (msg && typeof msg === "object") ? (msg.data || msg) : {};
            if (payload && typeof payload.muted === "boolean") {
              seen = true;
              setMutedState(payload.muted);
            }
          } catch {}
        });

        registerVimeoMethodResponses(iframe, (msg) => {
          try {
            if (!isModalOpen()) return;
            if (String(currentUrl || "") !== String(urlToken || "")) return;

            const method = String(msg && msg.method ? msg.method : "");
            if (method === "getMuted" && typeof msg.value === "boolean") {
              seen = true;
              setMutedState(msg.value);
            }
          } catch {}
        });

        const probe = () => {
          try {
            if (!isModalOpen()) return;
            if (String(currentUrl || "") !== String(urlToken || "")) return;
            requestVimeoMuteState(iframe);
          } catch {}
        };

        // Probe a few times as the player/video swaps.
        [0, 180, 420, 900, 1400].forEach((ms) => setTimeout(probe, ms));

        // iOS fallback: if we can't read state quickly, show the button so users can force sound.
        setTimeout(() => {
          try {
            if (!IOS_PERSISTENT_PLAYER) return;
            if (seen) return;
            if (!isModalOpen()) return;
            if (String(currentUrl || "") !== String(urlToken || "")) return;
            setMutedState(true);
          } catch {}
        }, 900);
      }

      function setAuxVideoInModal(meta, opts = {}) {
        const url = playbackUrlOf(meta);
        const source = playbackSourceOf(meta);
        if (!url || source === "vimeo") return;

        const autoplay = !!opts.autoplay;
        const muted = !!opts.muted;
        const endBehavior = normalizeEndBehavior(opts.endBehavior || endBehaviorOf(meta), "next");
        const startSecs = playbackStartTimeOf(meta, url);

        currentVideoSource = source;
        clearPlayer({ unload: true });
        currentVideoSource = source;
        resetMutedState();
        setPlayerLoading(true);

        const token = ++playerToken;
        let playerEl = null;
        let cleanup = null;

        if (source === "youtube") {
          const iframe = document.createElement("iframe");
          iframe.allow = "autoplay; fullscreen; picture-in-picture";
          iframe.setAttribute("allowfullscreen", "");
          iframe.setAttribute("loading", "eager");
          iframe.title = String(meta?.title || "Video");
          iframe.src = buildYouTubeSrc(url, autoplay, muted, startSecs, endBehavior);
          iframe.addEventListener("load", () => {
            if (playerToken !== token) return;
            setPlayerLoading(false);
            scheduleCtaPlacement();
          }, { once:true });
          playerEl = iframe;
        } else {
          const video = document.createElement("video");
          video.playsInline = true;
          video.controls = true;
          video.preload = "metadata";
          video.setAttribute("playsinline", "");
          video.src = safeSrc(url);
          video.loop = (endBehavior === "loop");
          video.muted = muted;

          const applyStart = () => {
            if (!(startSecs > 0)) return;
            try {
              const dur = Number(video.duration || 0);
              const target = (dur > 0) ? Math.min(startSecs, Math.max(0, dur - 0.25)) : startSecs;
              video.currentTime = target;
            } catch {}
          };

          video.addEventListener("loadedmetadata", () => {
            if (playerToken !== token) return;
            applyStart();
            setPlayerLoading(false);
            if (autoplay) {
              playMediaPreferSound(video);
            }
            scheduleCtaPlacement();
          }, { once:true });

          const onEnded = () => {
            if (playerToken !== token || !isVideoOpen()) return;
            if (endBehavior === "loop") {
              applyStart();
              playMediaPreferSound(video);
              return;
            }
            if (VIDEO_PLAYLIST.length > 1) goNext({ focus:false, fromEnded:true });
          };
          video.addEventListener("ended", onEnded);
          cleanup = () => {
            try { video.removeEventListener("ended", onEnded); } catch {}
          };
          playerEl = video;
        }

        activeAuxPlayer = playerEl;
        activeAuxPlayerCleanup = cleanup;
        try { if (playerSlot) playerSlot.replaceChildren(playerEl); } catch {}
        updateUnmuteBtnUI();
      }

      function setVimeoInModal(meta, opts = {}) {
        const url = playbackUrlOf(meta);
        if (!url) return;

        const autoplay = !!opts.autoplay;
        let muted = !!opts.muted;
        const endBehavior = normalizeEndBehavior(opts.endBehavior || endBehaviorOf(meta), "next");
        const startSecs = playbackStartTimeOf(meta, url);
        currentVideoSource = "vimeo";

        // iOS Safari blocks autoplay with sound. Force muted autoplay on iOS so playback works reliably.
        if (IOS_FORCE_MUTED_AUTOPLAY && autoplay) muted = true;

        clearAuxPlayer();
        const iframe = ensurePlayerIframe(meta);
        if (!iframe) return;

        // On iOS we park the iframe off-screen while closed; make sure it's in the modal now.
        if (IOS_PERSISTENT_PLAYER) dockPlayerIntoModal();

        // Cancel any previous fallback spinner timer
        try {
          if (playerLoadTimer) {
            clearTimeout(playerLoadTimer);
            playerLoadTimer = 0;
          }
        } catch {}

        // Unregister old listeners
        try { unregisterVimeoEnded(iframe); } catch {}
        try { unregisterVimeoTimeUpdate(iframe); } catch {}

        const token = ++playerToken;
        iframe.dataset.mgToken = String(token);
        iframe.title = String(meta?.title || "Video");

        setPlayerLoading(true);

        // We'll re-detect mute state for each new video.
        resetMutedState();

        // Watch for mute state changes and show/hide the Unmute overlay.
        registerMuteStateForCurrent(url);
        configureVimeoLoopMode(iframe, endBehavior, token);

        // Ended/advance registration:
        // - iOS: register immediately (we keep the same iframe/player and swap via loadVideo)
        // - non-iOS: register after the iframe load event (src swap)

        // Never leave spinner up forever
        playerLoadTimer = setTimeout(() => {
          try {
            if (iframe.dataset.mgToken === String(token)) setPlayerLoading(false);
          } catch {}
          playerLoadTimer = 0;
        }, 2200);

        if (IOS_PERSISTENT_PLAYER) {
          // iOS fix:
          // - DON'T swap iframe.src (fresh autoplay sessions tend to re-mute on iOS).
          // - Swap videos inside the same Vimeo player using loadVideo().
          const vid = getVimeoId(url);

          // Register ended/timeupdate now (same iframe, no reload)
          registerEndedForCurrent(url, endBehavior);

          // If the player isn't booted yet, fall back to a one-time src swap.
          // (Still try to unmute+play immediately in the same user gesture.)
          if (!playerReady || !vid) {
            const src = buildVimeoSrc(url, false, false, endBehavior, startSecs);
            iframe.addEventListener("load", () => {
              if (iframe.dataset.mgToken !== String(token)) return;
              try { playerReady = true; } catch {}
              try { setPlayerLoading(false); } catch {}
              if (autoplay) {
                const tok = String(token);
                if (muted) kickPlayVimeoMuted(iframe, tok);
                else kickPlayVimeoUnmuted(iframe, tok);
              }
              try { requestVimeoMuteState(iframe); } catch {}
              applyVimeoStartTime(iframe, startSecs, token);
              registerEndedForCurrent(url, endBehavior);
              scheduleCtaPlacement();
            }, { once:true });

            try { iframe.src = src; } catch {}
            if (autoplay) {
              const tok = String(token);
              if (muted) kickPlayVimeoMuted(iframe, tok);
              else kickPlayVimeoUnmuted(iframe, tok);
            }
            try { requestVimeoMuteState(iframe); } catch {}
          } else {
            const shouldLoad = String(vid || "") !== String(lastLoadedVimeoId || "");
            lastLoadedVimeoId = String(vid || "");

            if (shouldLoad) {
              // Fire loadVideo immediately (gesture) + a couple retries while the player switches
              try { vimeoPost(iframe, "pause"); } catch {}
              try { vimeoPost(iframe, "loadVideo", vid); } catch {}
              const tries = [140, 420];
              for (const ms of tries) {
                setTimeout(() => {
                  try {
                    if (iframe.dataset.mgToken !== String(token)) return;
                    vimeoPost(iframe, "loadVideo", vid);
                  } catch {}
                }, ms);
              }
            }

            if (autoplay) {
              // Keep sound on across swaps.
              // Important for iOS: don't force-mute on every swap (it can "stick" after a few swipes).
              const swapToken = String(token);
              if (muted) kickPlayVimeoMuted(iframe, swapToken);
              else kickPlayVimeoUnmuted(iframe, swapToken);

              // iOS: loadVideo() sometimes flips back to muted after the swap.
              // If the user already heard audio (unlocked), aggressively re-apply unmute for a short window.
              autoUnmuteToken = swapToken;
              autoUnmuteTries = 0;
              if (!muted && audioUnlocked) {
                const steps = [180, 520, 900, 1400];
                for (const ms of steps) {
                  setTimeout(() => {
                    try {
                      if (iframe.dataset.mgToken !== swapToken) return;
                      vimeoPost(iframe, "setMuted", false);
                      vimeoPost(iframe, "setVolume", 1);
                      if (ms === 520) vimeoPost(iframe, "play");
                    } catch {}
                  }, ms);
                }
              }
            } else {
              try { pauseVimeo(iframe); } catch {}
            }

            // Refresh mute state after swaps (some iOS devices force mute on loadVideo)
            applyVimeoStartTime(iframe, startSecs, token);
            try { requestVimeoMuteState(iframe); } catch {}
            setTimeout(() => {
              try {
                if (iframe.dataset.mgToken !== String(token)) return;
                requestVimeoMuteState(iframe);
              } catch {}
            }, 300);

            scheduleCtaPlacement();
          }

          return;
        }

        // Non-iOS: keep the simpler src swap behavior.
        const src = buildVimeoSrc(url, autoplay ? 1 : 0, muted ? 1 : 0, endBehavior, startSecs);

        iframe.addEventListener("load", () => {
          if (iframe.dataset.mgToken !== String(token)) return;

          try {
            if (playerLoadTimer) {
              clearTimeout(playerLoadTimer);
              playerLoadTimer = 0;
            }
          } catch {}

          setPlayerLoading(false);

          // Best-effort play
          if (autoplay) {
            const tok = String(token);
            if (muted) kickPlayVimeoMuted(iframe, tok);
            else kickPlayVimeoUnmuted(iframe, tok);
          }

          // Refresh mute state once the iframe has loaded.
          applyVimeoStartTime(iframe, startSecs, token);
          try { requestVimeoMuteState(iframe); } catch {}
          setTimeout(() => {
            try {
              if (iframe.dataset.mgToken !== String(token)) return;
              requestVimeoMuteState(iframe);
            } catch {}
          }, 300);

          registerEndedForCurrent(url, endBehavior);
          scheduleCtaPlacement();
        }, { once:true });

        if (iframe.src !== src) {
          iframe.src = src;
        } else {
          // Same video already loaded; just play/unmute
          setPlayerLoading(false);
          if (autoplay) {
            const tok = String(token);
            if (muted) kickPlayVimeoMuted(iframe, tok);
            else kickPlayVimeoUnmuted(iframe, tok);
          }

          // Ensure the Unmute overlay stays in sync with actual player state.
          applyVimeoStartTime(iframe, startSecs, token);
          try { requestVimeoMuteState(iframe); } catch {}

          registerEndedForCurrent(url, endBehavior);
        }
      }

      function setVideoInModal(meta, opts = {}) {
        const source = playbackSourceOf(meta);
        if (source === "vimeo") return setVimeoInModal(meta, opts);
        return setAuxVideoInModal(meta, opts);
      }

      function openFirstVideo(opts = {}) {
        if (!VIDEO_PLAYLIST.length) return false;
        root.__mgCloseParentPresetOnVideoClose = !!opts.closeParentPresetOnClose;
        if (opts.hideRows) {
          try {
            const visibleRows = root.querySelector(".video-rows");
            if (visibleRows) visibleRows.hidden = true;
          } catch {}
        }
        loadIndex(0, { focus: opts.focus !== false });
        return true;
      }

      async function loadIndex(i, opts = {}) {
        if (!VIDEO_PLAYLIST.length) return;

        const idx = wrapIndex(i);
        const meta = metaAt(idx);
        const url = playbackUrlOf(meta);
        if (!url) return;

        if (playbackSourceOf(meta) === "vimeo") warmVimeoConnections();

        currentIndex = idx;
        currentUrl = url;

        if (!isModalOpen()) {
          scrollEl.scrollTop = 0;
          lightbox.setAttribute("aria-hidden", "false");
          infoLb.setAttribute("aria-hidden", "true");
          document.body.style.overflow = "hidden";
          GLOBAL.closeCurrent = closeVideo;
          if (opts.focus) requestAnimationFrame(() => closeBtn.focus());

          setGestureLayerActive(IS_TOUCH && VIDEO_PLAYLIST.length > 1);
        } else {
          setGestureLayerActive(IS_TOUCH && VIDEO_PLAYLIST.length > 1);
        }

        updateEdgeNav();

        currentEndBehavior = endBehaviorOf(meta);
        setVideoDescTitle(meta);
        setCTA(meta);
        const metaHasDesc = setVideoDescFromMeta(meta);

        currentW = Number(meta.__w || 0) || 16;
        currentH = Number(meta.__h || 0) || 9;
        maximize(currentW, currentH);
        scheduleCtaPlacement();
        updateVideoDescVisibility();

        // Load video into the modal (provider-specific autoplay + start time handling)
        setVideoInModal(meta, {
          autoplay: true,
          muted: IOS_FORCE_MUTED_AUTOPLAY ? true : false,
          endBehavior: currentEndBehavior
        });

        if (playbackSourceOf(meta) !== "vimeo") {
          updateVideoDescVisibility();
          return;
        }

        // Fetch oEmbed for sizing + default description
        try {
          const data = await fetchVimeoOEmbed(url);
          if (String(currentUrl || "") !== url) return;

          cacheVimeoSizing(meta, data);

          currentW = Number(meta.__w || 0) || currentW;
          currentH = Number(meta.__h || 0) || currentH;
          maximize(currentW, currentH);
          scheduleCtaPlacement();

          if (!metaHasDesc) {
            const vDesc = String(data?.description || "").trim();
            // Vimeo descriptions are plain text; convert to HTML and append optional HTML add-on.
            const baseHtml = vDesc ? richHtmlFromPlainText(vDesc) : "";
            const append = String(currentDescAppendHtml || "").trim();
            const combined = append
              ? (baseHtml ? (baseHtml + DESC_APPEND_SEPARATOR + append) : append)
              : baseHtml;

            if (combined && String(combined).trim()) setVideoDescHTML(combined);
            else clearVideoDesc();
          }
          updateVideoDescVisibility();
        } catch {
          updateVideoDescVisibility();
        }

        updateVideoDescVisibility();
      }

      function closeVideo() {
        // Stop playback ASAP
        try {
          const iframe = getPlayerIframe();
          if (iframe) {
            try { pauseVimeo(iframe); } catch {}
            try { unregisterVimeoEnded(iframe); } catch {}
            try { unregisterVimeoTimeUpdate(iframe); } catch {}
            // On iOS we keep the iframe alive so audio does not reset/mute on the next open.
            if (!IOS_PERSISTENT_PLAYER) {
              try { iframe.src = "about:blank"; } catch {}
              try { iframe.removeAttribute("src"); } catch {}
            }
          }
        } catch {}

        // Clear player slot
        try { clearPlayer({ unload: !IOS_PERSISTENT_PLAYER }); } catch {}

        // Close UI
        try { lightbox.setAttribute("aria-hidden", "true"); } catch {}
        try {
          document.body.style.overflow =
            (typeof isPresetOpen === "function" && isPresetOpen()) ? "hidden" : "";
        } catch {
          document.body.style.overflow = "";
        }

        currentUrl = null;
        currentIndex = -1;
        currentEndBehavior = "next";
        currentVideoSource = "vimeo";
        applyDescLayout("below", 0, false);

        try { setGestureLayerActive(false); } catch {}
        try { setCTA(null); } catch {}
        try { clearVideoDesc(); } catch {}
        try {
          currentVideoTitle = "";
          syncVideoDescChrome();
        } catch {}
        try { resetMutedState(); } catch {}

        try {
          edgePrev.classList.remove("is-visible");
          edgeNext.classList.remove("is-visible");
          setAriaAndTab(edgePrev, false);
          setAriaAndTab(edgeNext, false);
        } catch {}

        if (GLOBAL.closeCurrent === closeVideo) {
          GLOBAL.closeCurrent = closeAny;
        }

        try {
          if (root.__mgCloseParentPresetOnVideoClose && typeof root.__mgCloseParentPresetModal === "function") {
            root.__mgCloseParentPresetOnVideoClose = false;
            const closeParentPreset = root.__mgCloseParentPresetModal;
            root.__mgCloseParentPresetModal = null;
            closeParentPreset();
            if (root.classList && root.classList.contains("mg-preset-mini")) {
              try { root.remove(); } catch {}
            }
          }
        } catch {}
      }

      closeBtn.addEventListener("click", closeVideo);
      backdrop.addEventListener("click", closeVideo);

      // Unmute overlay button
      if (unmuteBtn) {
        unmuteBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          const iframe = getPlayerIframe();
          if (!iframe) return;

          // Mark audio as user-unlocked so we keep trying to preserve sound on iOS swipes.
          audioUnlocked = true;
          autoUnmuteToken = String(iframe.dataset.mgToken || "");
          autoUnmuteTries = 0;

          // User gesture â†’ maximize chances of unmuting on iOS.
          try { kickPlayVimeoUnmuted(iframe, String(iframe.dataset.mgToken || "")); } catch {}

          // Re-check mute state a few times (player may respond after a short delay)
          const token = String(iframe.dataset.mgToken || "");
          const probe = () => {
            try {
              if (!isModalOpen()) return;
              if (String(iframe.dataset.mgToken || "") !== token) return;
              requestVimeoMuteState(iframe);
            } catch {}
          };
          probe();
          setTimeout(probe, 180);
          setTimeout(probe, 450);
          setTimeout(probe, 900);
        });
      }

      lightbox.addEventListener("click", (e) => {
        if (!isModalOpen()) return;

        // âœ… Don't close when interacting with the video frame OR the description area.
        // This allows links + text selection inside the description without dismissing the modal.
        if (e.target.closest("[data-lb-frame]")) return;
        if (e.target.closest("[data-lb-desc-wrap]")) return;

        // Don't close when clicking on any interactive elements (links/buttons/inputs)
        if (e.target.closest("a,button,input,textarea,select,label")) return;

        closeVideo();
      });

      /* ==========================
         Swipe handling (simple, Vimeo-only)
         ========================== */
      function swipeAllowed(e) {
        if (!IS_TOUCH) return false;
        if (!isModalOpen()) return false;
        if (VIDEO_PLAYLIST.length <= 1) return false;
        if (SWIPE.animating) return false;

        const mobile = !isDesktop();
        // Don't hijack taps/clicks.
        if (e.target && e.target.closest && e.target.closest("a,button,input,textarea,select,label,[data-lb-close],[data-lb-prev],[data-lb-next]")) return false;
        // Desktop/tablet: only swipe when starting on the video.
        // Mobile: allow swiping from the description area too.
        if (!mobile && e.target && e.target.closest && !e.target.closest("[data-lb-frame]")) return false;

        return true;
      }

      function getScrollContainerForTarget(target) {
        // Prefer nested scroll regions (e.g., side-description column), otherwise
        // fall back to the main lightbox scroll container.
        let el = target && target.nodeType === 1 ? target : null;
        while (el && el !== root && el !== document.body) {
          if (el === scrollEl) return scrollEl;
          try {
            const cs = window.getComputedStyle(el);
            const oy = cs.overflowY;
            if ((oy === "auto" || oy === "scroll") && (el.scrollHeight > el.clientHeight + 4)) {
              return el;
            }
          } catch (_e) {}
          el = el.parentElement;
        }
        return scrollEl;
      }

      function getScrollMetrics(el) {
        const st = el ? (el.scrollTop || 0) : 0;
        const max = el ? Math.max(0, (el.scrollHeight || 0) - (el.clientHeight || 0)) : 0;
        return { st, max };
      }

      function cancelSwipe() {
        SWIPE.armed = false;
        SWIPE.axis = null;
        SWIPE.fromGestureLayer = false;
      }

      function swipeStart(e) {
        if (!e.touches || e.touches.length !== 1) return;
        if (!swipeAllowed(e)) return;

        const t = e.touches[0];
        SWIPE.armed = true;
        // If the swipe starts on the gesture overlay (video), do NOT treat vertical movement
        // as scroll. This keeps "swipe up on the video" advancing to the next video on iOS.
        SWIPE.fromGestureLayer = (e.currentTarget === gestureLayer);
        SWIPE.id = t.identifier;
        SWIPE.startX = t.clientX;
        SWIPE.startY = t.clientY;
        SWIPE.dx = 0;
        SWIPE.dy = 0;
        SWIPE.axis = null;
      }

      function swipeMove(e) {
        if (!SWIPE.armed) return;

        let t = null;
        for (let i = 0; i < e.touches.length; i++) {
          if (e.touches[i].identifier === SWIPE.id) { t = e.touches[i]; break; }
        }
        if (!t) return;

        const dx = t.clientX - SWIPE.startX;
        const dy = t.clientY - SWIPE.startY;
        SWIPE.dx = dx;
        SWIPE.dy = dy;

        const absX = Math.abs(dx);
        const absY = Math.abs(dy);

        if (!SWIPE.axis) {
          if (Math.max(absX, absY) < SWIPE_LOCK_THRESH) return;
          SWIPE.axis = absX > absY ? "x" : "y";

          // If this is a vertical gesture and the user can still scroll (reading the description),
          // let native scrolling happen. Only hijack vertical swipes at scroll boundaries so
          // "scroll down" at the end can advance to the next video.
          if (SWIPE.axis === "y" && !SWIPE.fromGestureLayer) {
            const scroller = getScrollContainerForTarget(e && e.target);
            const m = getScrollMetrics(scroller);
            const wantsScrollDown = dy < 0; // finger moves up
            const wantsScrollUp = dy > 0;   // finger moves down
            const canScrollDown = m.st < (m.max - 2);
            const canScrollUp = m.st > 2;
            if ((wantsScrollDown && canScrollDown) || (wantsScrollUp && canScrollUp)) {
              cancelSwipe();
              return;
            }
          }
        }

        e.preventDefault();
        e.stopPropagation();
      }

      function tapToPlayCurrent() {
        try {
          if (currentVideoSource === "file" && activeAuxPlayer && activeAuxPlayer.tagName === "VIDEO") {
            const p = activeAuxPlayer.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
            return;
          }
          const iframe = getPlayerIframe();
          if (iframe) kickPlayVimeoUnmuted(iframe, String(iframe.dataset.mgToken || ""));
        } catch {}
      }

      function swipeEnd(e) {
        if (!SWIPE.armed) return;
        if (!e.changedTouches) { SWIPE.armed = false; return; }

        let ended = false;
        for (let i = 0; i < e.changedTouches.length; i++) {
          if (e.changedTouches[i].identifier === SWIPE.id) { ended = true; break; }
        }
        if (!ended) return;

        const axis = SWIPE.axis;
        SWIPE.armed = false;

        if (!axis) {
          // Tap fallback
          tapToPlayCurrent();
          return;
        }

        const rect = frameEl.getBoundingClientRect();
        const size = axis === "x" ? rect.width : rect.height;
        const dist = axis === "x" ? SWIPE.dx : SWIPE.dy;

        const commit = Math.abs(dist) >= Math.max(SWIPE_COMMIT_MIN, size * SWIPE_COMMIT_PCT);
        if (!commit) return;

        SWIPE.animating = true;

        // Mobile: once the user has scrolled down into the description, allow left OR right
        // swipe to advance to the next video (so browsing keeps flowing). Vertical scrolling
        // up should just return to the video (native scroll handled earlier).
        const mobile = !isDesktop();
        const scroller = getScrollContainerForTarget(e && e.target);
        const m = getScrollMetrics(scroller);
        const isInDescScroll = mobile && (m.st > 6);
        let toNext = dist < 0;
        if (axis === "x" && isInDescScroll) toNext = true;

        // Stop current quickly
        pauseCurrent();

        if (toNext) goNext({ focus:false, fromSwipe:true });
        else goPrev({ focus:false, fromSwipe:true });

        // Small debounce to prevent double-fires
        setTimeout(() => { SWIPE.animating = false; }, 280);
      }

      function swipeCancel() {
        if (!SWIPE.armed) return;
        SWIPE.armed = false;
      }

      gestureLayer.addEventListener("touchstart", (e) => { swipeStart(e); e.stopPropagation(); }, { passive: true });
      gestureLayer.addEventListener("touchmove",  (e) => { swipeMove(e);  e.stopPropagation(); }, { passive: false });
      gestureLayer.addEventListener("touchend",   (e) => { swipeEnd(e);   e.stopPropagation(); }, { passive: true });
      gestureLayer.addEventListener("touchcancel",(e) => { swipeCancel(); e.stopPropagation(); }, { passive: true });

      scrollEl.addEventListener("touchstart", swipeStart, { passive: true });
      scrollEl.addEventListener("touchmove",  swipeMove,  { passive: false });
      scrollEl.addEventListener("touchend",   swipeEnd,   { passive: true });
      scrollEl.addEventListener("touchcancel",swipeCancel,{ passive: true });

      /* ------------------------------
         Info modal (Image + Product Learn More)
      ------------------------------ */
      function closeInfo() {
        infoLb.setAttribute("aria-hidden", "true");
        document.body.style.overflow = isPresetOpen() ? "hidden" : "";
        infoMetaActive = null;

        infoPreviewRow.innerHTML = "";
        infoDesc.classList.remove("is-text", "is-html");
        infoDesc.textContent = "";
        infoDesc.innerHTML = "";

        infoDescBlock.style.removeProperty("width");

        if (GLOBAL.closeCurrent === closeInfo) {
          GLOBAL.closeCurrent = closeAny;
        }
      }

      function fillInfoDesc(meta) {
        const imageDesc = String(meta?.image_desc_html || "").trim();
        const htmlFirst = String(meta?.html_desc || "").trim();
        const htmlAlt   = String(meta?.desc_html || meta?.image_more_desc_html || "").trim();
        const text = String(meta?.description || "").trim();
        const justify = String(meta?.image_desc_justify || "left").trim().toLowerCase() || "left";

        infoDesc.style.textAlign = justify;

        if (imageDesc || htmlFirst) {
          infoDesc.classList.remove("is-text");
          infoDesc.classList.add("is-html");
          const pieces = [];
          if (imageDesc) {
            const cleanedImage = sanitizeHtml(imageDesc);
            pieces.push(sanitizeHtml(linkifyHtmlString(cleanedImage)));
          }
          if (htmlFirst) {
            const cleaned = sanitizeHtml(htmlFirst);
            pieces.push(sanitizeHtml(linkifyHtmlString(cleaned)));
          }
          if (htmlAlt) {
            const cleanedAlt = sanitizeHtml(htmlAlt);
            pieces.push(sanitizeHtml(linkifyHtmlString(cleanedAlt)));
          }
          infoDesc.innerHTML = pieces.join("");
        } else if (htmlAlt) {
          infoDesc.classList.remove("is-text");
          infoDesc.classList.add("is-html");
          const cleaned = sanitizeHtml(htmlAlt);
          infoDesc.innerHTML = sanitizeHtml(linkifyHtmlString(cleaned));
        } else {
          const rich = richHtmlFromPlainText(text);
          infoDesc.classList.remove("is-text");
          infoDesc.classList.add("is-html");
          infoDesc.innerHTML = rich;
        }
      }

      /* ------------------------------
         Preset Set modal (bottom sheet)
      ------------------------------ */

      const presetLb = root.querySelector(".preset-lightbox");
      const presetPanel = presetLb ? presetLb.querySelector("[data-preset-panel]") : null;
      const presetBody = presetLb ? presetLb.querySelector("[data-preset-body]") : null;
      const presetBackdrop = presetLb ? presetLb.querySelector("[data-preset-backdrop]") : null;
      const presetCloseBtn = presetLb ? presetLb.querySelector("[data-preset-close]") : null;

      const presetCache = presetLb ? new Map() : null;
      let activeBulletPopup = null;

      function isPresetOpen() {
        return !!(presetLb && presetLb.getAttribute("aria-hidden") === "false");
      }

      function closePreset() {
        if (!presetLb) return;
        try { presetLb.style.display = ""; } catch {}
        if (activeBulletPopup && activeBulletPopup.panel && activeBulletPopup.body) {
          try {
            while (activeBulletPopup.body.firstChild) {
              const child = activeBulletPopup.body.firstChild;
              if (child.classList && child.classList.contains("bullet-popup-title")) child.remove();
              else activeBulletPopup.panel.appendChild(child);
            }
          } catch {}
          activeBulletPopup = null;
        }
        presetLb.setAttribute("aria-hidden", "true");

        // Restore scroll lock only if other modals are open
        document.body.style.overflow = (isVideoOpen() || isInfoOpen()) ? "hidden" : "";

        // Restore default ESC handler
        GLOBAL.closeCurrent = closeAny;
      }

      const openBulletPopup = presetLb ? function openBulletPopup(panel, meta) {
        if (!panel || !presetBody) return;
        if (isVideoOpen()) closeVideo();
        if (isInfoOpen()) closeInfo();
        if (isPresetOpen()) closePreset();

        const body = document.createElement("div");
        body.className = "bullet-popup-body";
        const titleHtml = String(meta?.titleHtml || "").trim();
        if (titleHtml) {
          const title = document.createElement("div");
          title.className = "bullet-popup-title rte";
          title.innerHTML = sanitizeHtml(titleHtml);
          body.appendChild(title);
        }
        while (panel.firstChild) body.appendChild(panel.firstChild);
        activeBulletPopup = { panel, body };
        presetBody.replaceChildren(body);

        presetLb.setAttribute("aria-hidden", "false");
        try { presetLb.style.display = ""; } catch {}
        document.body.style.overflow = "hidden";
        GLOBAL.closeCurrent = closePreset;
        if (presetPanel) presetPanel.scrollTop = 0;
        requestAnimationFrame(() => {
          refreshRowLayoutsWithin(body);
          try {
            body.querySelectorAll(".thumb-row").forEach((row) => { row.scrollLeft = 0; });
          } catch {}
        });
      } : null;

      function presetVideoItemsForKey(setKey) {
        try {
          const key = String(setKey || "").trim();
          if (!key) return [];
          const sets = getSetStore();
          const rows = coerceToRows(sets && sets[key]);
          if (!rows.length) return [];
          const hasImportRows = rows.some((row) => row && typeof row === "object" && String(row.__import_set || "").trim());
          const resolvedRows = hasImportRows ? resolveStoredImportRows(rows, sets, new Set([key])) : rows;
          const videos = [];
          for (const row of resolvedRows) {
            for (const item of normalizeItems(rowItems(row))) {
              if (isVideoKind(providerOf(item))) videos.push(item);
            }
          }
          return videos;
        } catch {
          return [];
        }
      }

      function presetShouldAutoOpenFirstVideo(setKey) {
        const videos = presetVideoItemsForKey(setKey);
        return videos.length === 1 && wantsAutoplayWithSound(videos[0]);
      }

      const openPresetSet = presetLb ? function openPresetSet(setKey, opts = {}) {
        const key = String(setKey || "").trim();
        if (!key) return;
        const openFirstVideo = !!opts.openFirstVideo && presetShouldAutoOpenFirstVideo(key);

        // Close other modals first (they manage their own focus/scroll lock)
        if (isVideoOpen()) closeVideo();
        if (isInfoOpen()) closeInfo();
        if (isPresetOpen()) closePreset();

        presetLb.setAttribute("aria-hidden", "false");
        try { presetLb.style.display = openFirstVideo ? "none" : ""; } catch {}
        document.body.style.overflow = "hidden";
        GLOBAL.closeCurrent = closePreset;

        if (presetPanel) presetPanel.scrollTop = 0;

        // Reuse an initialized mini root per key (avoids repeated global listeners)
        let mini = presetCache.get(key);
        if (!mini) {
          mini = document.createElement("div");
          mini.className = "vimeo-snippet mg-preset-mini";
          mini.setAttribute("data-mg-mini", "1");
          mini.setAttribute("data-vimeo-set", key);
          mini.setAttribute("data-shop-currency", shopCurrency);
          presetCache.set(key, mini);
        }

        // Ensure attributes stay in sync
        mini.setAttribute("data-vimeo-set", key);
        mini.setAttribute("data-shop-currency", shopCurrency);
        mini.setAttribute("data-block-width-pct-desktop", "100");
        mini.setAttribute("data-block-width-pct-mobile", "100");
        mini.setAttribute("data-block-max-width-desktop-px", "0");
        try {
          mini.style.setProperty("--mg-block-width-pct-desktop", "100");
          mini.style.setProperty("--mg-block-width-pct-mobile", "100");
          mini.style.setProperty("--mg-block-max-width-desktop-px", "999999px");
          mini.style.setProperty("--mg-margin-top", "0px");
          mini.style.setProperty("--mg-margin-bottom", "0px");
        } catch {}

        if (openFirstVideo) {
          if (!mini.isConnected || mini.parentElement !== root) {
            try { root.appendChild(mini); } catch {}
          }
        } else if (presetBody) {
          try {
            const visibleRows = mini.querySelector(".video-rows");
            if (visibleRows) visibleRows.hidden = false;
          } catch {}
          presetBody.replaceChildren(mini);
        }

        // Initialize (once) when first opened
        if (!mini.__vimeoInit) {
          mini.__vimeoInit = true;
          initRoot(mini);
        } else {
          // Best-effort layout refresh (e.g., after orientation changes)
          requestAnimationFrame(() => {
            try { window.dispatchEvent(new Event("resize")); } catch (e) {}
          });
        }
        requestAnimationFrame(() => {
          try {
            if (!openFirstVideo) {
              mini.querySelectorAll(".thumb-row").forEach((row) => { row.scrollLeft = 0; });
            }
          } catch {}
          if (openFirstVideo && typeof mini.__mgOpenFirstVideo === "function") {
            try {
              mini.__mgCloseParentPresetModal = closePreset;
              mini.__mgOpenFirstVideo({ focus:true, closeParentPresetOnClose:true, hideRows:true });
            } catch {}
          }
        });
      } : null;
      if (openPresetSet) {
        root.__mgOpenPresetSet = openPresetSet;
      }
      root.__mgOpenFirstVideo = openFirstVideo;

      if (presetLb && presetBackdrop && presetCloseBtn) {
        presetBackdrop.addEventListener("click", closePreset);
        presetCloseBtn.addEventListener("click", closePreset);

        presetLb.addEventListener("click", (e) => {
          // Clicking outside the panel closes
          if (e.target === presetLb) closePreset();
        });
      }

      function buildInlinePresetRoot(setKey, opts = {}) {
        const mini = document.createElement("div");
        mini.className = "vimeo-snippet mg-inline-preset";
        mini.setAttribute("data-mg-mini", "1");
        mini.setAttribute("data-mg-preserve-root-width", "true");
        mini.setAttribute("data-vimeo-set", String(setKey || "").trim());
        mini.setAttribute("data-shop-currency", shopCurrency);
        const desktopWidthOverride = normalizeWidthOverridePercent(opts.desktopWidthOverridePercent, 0);
        const desktopMaxWidthOverride = normalizePresetButtonMaxWidthPx(opts.desktopMaxWidthOverridePx, 0);
        const effectiveDesktopWidth = desktopWidthOverride > 0 ? desktopWidthOverride : DEFAULT_BLOCK_WIDTH_DESKTOP_PCT;
        const effectiveDesktopMaxWidth = desktopMaxWidthOverride > 0 ? desktopMaxWidthOverride : DEFAULT_BLOCK_MAX_WIDTH_DESKTOP_PX;
        mini.setAttribute("data-block-width-pct-desktop", String(effectiveDesktopWidth));
        mini.setAttribute("data-block-width-pct-mobile", String(DEFAULT_BLOCK_WIDTH_MOBILE_PCT));
        mini.setAttribute("data-block-max-width-desktop-px", String(effectiveDesktopMaxWidth));

        const copyVar = (name, fallback) => {
          const inlineValue = String(root.style.getPropertyValue(name) || "").trim();
          if (inlineValue) {
            mini.style.setProperty(name, inlineValue);
            return;
          }
          let computedValue = "";
          try { computedValue = String(getComputedStyle(root).getPropertyValue(name) || "").trim(); } catch {}
          if (computedValue) {
            mini.style.setProperty(name, computedValue);
            return;
          }
          if (fallback != null) mini.style.setProperty(name, String(fallback));
        };

        mini.style.setProperty("--mg-block-width-pct-desktop", String(effectiveDesktopWidth));
        mini.style.setProperty("--mg-block-width-pct-mobile", String(DEFAULT_BLOCK_WIDTH_MOBILE_PCT));
        mini.style.setProperty("--mg-block-max-width-desktop-px", effectiveDesktopMaxWidth > 0 ? (effectiveDesktopMaxWidth + "px") : "999999px");
        copyVar("--mg-media-radius", "12px");
        copyVar("--mg-media-border-width", "0px");
        copyVar("--mg-media-border-color", "transparent");
        mini.style.setProperty("--mg-margin-top", "0px");
        mini.style.setProperty("--mg-margin-bottom", "0px");
        return mini;
      }

      function mountInlinePresetInstances(scope, opts = {}) {
        const searchRoot = scope && scope.querySelectorAll ? scope : rowsRoot;
        const includeHidden = !!opts.includeHidden;
        const hosts = Array.from(searchRoot.querySelectorAll("[data-inline-preset-host]"));
        for (const host of hosts) {
          if (!host || host.__mgInlinePresetMounted) continue;
          if (!includeHidden) {
            const hiddenPanel = host.closest('.accordion-row__panel[hidden]');
            if (hiddenPanel) continue;
          }

          const setKey = String(host.getAttribute("data-inline-preset-host") || "").trim();
          if (!setKey) continue;

          host.__mgInlinePresetMounted = true;
          host.textContent = "";

          const mini = buildInlinePresetRoot(setKey, {
            desktopWidthOverridePercent: host.closest(".accordion-row__panel")?.getAttribute("data-accordion-panel-width-override-percent"),
            desktopMaxWidthOverridePx: host.closest(".accordion-row__panel")?.getAttribute("data-accordion-panel-max-width-override-px")
          });
          host.appendChild(mini);

          try {
            if (!mini.__vimeoInit) {
              mini.__vimeoInit = true;
              initRoot(mini);
            }
          } catch (err) {
            host.__mgInlinePresetMounted = false;
            host.innerHTML = '<p style="font:500 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#111;text-align:center;">Unable to render preset <strong>' + escapeHtml(setKey) + '</strong>.</p>';
          }
        }
      }

      function refreshAfterExternalVisibilityChange(scope) {
        const targetScope = scope && scope.querySelectorAll ? scope : root;
        try { mountInlinePresetInstances(targetScope, { includeHidden: true }); } catch {}
        try { markLazyCustomRowsReady(targetScope); } catch {}
        try { refreshRowLayoutsWithin(targetScope); } catch {}
        try { primeAllVideoThumbsThenSwapOverrides(targetScope, { force: true, visibleOnly: true }); } catch {}
        requestAnimationFrame(() => {
          try { mountInlinePresetInstances(targetScope, { includeHidden: true }); } catch {}
          try { refreshRowLayoutsWithin(targetScope); } catch {}
          try { primeAllVideoThumbsThenSwapOverrides(targetScope, { force: true, visibleOnly: true }); } catch {}
        });
      }

      function bindExternalVisibilityRefresh() {
        const schedule = (scope, delay = 0) => {
          const run = () => refreshAfterExternalVisibilityChange(scope);
          if (delay > 0) setTimeout(run, delay);
          else requestAnimationFrame(run);
        };

        const onOpenable = (event) => {
          if (!event || !event.target || !event.target.contains || !event.target.contains(root)) return;
          const scope = event.target;
          schedule(scope);
          schedule(scope, 550);
        };
        document.addEventListener('openable-element:open', onOpenable);
        cleanupFns.push(() => document.removeEventListener('openable-element:open', onOpenable));

        const onTabsChanged = (event) => {
          const button = event && event.detail ? event.detail.button : null;
          const panelId = button && button.getAttribute ? button.getAttribute('aria-controls') : '';
          const panel = panelId ? document.getElementById(panelId) : null;
          if (panel && panel.contains && panel.contains(root)) {
            schedule(panel, 280);
            schedule(panel, 560);
          } else if (root.closest && root.closest('[hidden]')) {
            schedule(root, 560);
          }
        };
        document.addEventListener('tabs-nav:changed', onTabsChanged);
        cleanupFns.push(() => document.removeEventListener('tabs-nav:changed', onTabsChanged));

        const detailsEl = root.closest ? root.closest('details') : null;
        if (detailsEl) {
          const onDetailsToggle = () => {
            if (detailsEl.open) {
              schedule(detailsEl);
              schedule(detailsEl, 300);
            }
          };
          detailsEl.addEventListener('toggle', onDetailsToggle);
          cleanupFns.push(() => detailsEl.removeEventListener('toggle', onDetailsToggle));
        }
      }

      function scheduleInfoSizing() {
        if (!isInfoOpen()) return;
        if (infoSizingRaf) return;
        infoSizingRaf = requestAnimationFrame(() => {
          infoSizingRaf = 0;
          sizeInfoDescBlock();
        });
      }

      function sizeInfoDescBlock() {
        if (!isInfoOpen()) return;

        const pad = 24;
        const maxW = Math.min(920, Math.max(280, window.innerWidth - pad));
        let w = 0;

        const previewCard = infoPreviewRow.querySelector(".thumb");
        if (previewCard) {
          const r = previewCard.getBoundingClientRect();
          w = Math.round(r.width || 0);
        }

        if (!(w > 0)) w = Math.min(760, maxW);

        w = Math.max(280, Math.min(maxW, w));
        infoDescBlock.style.width = w + "px";
      }

      function openInfo(meta, sourceCard) {
        if (!meta || !hasAnyDesc(meta)) return;

        infoMetaActive = meta;

        const rowAspect = String(meta.__row_aspect || "1 / 1");
        const rowHeight = Number(meta.__row_height || 0) || 0;

        infoPreviewRow.style.setProperty("--row-aspect", rowAspect);
        infoPreviewRow.style.setProperty("--row-thumb-h", rowHeight > 0 ? (rowHeight + "px") : "0px");
        infoPreviewRow.classList.toggle("has-row-height", rowHeight > 0);
        infoPreviewRow.classList.remove("has-auto-thumb-h");

        infoPreviewRow.innerHTML = "";
        if (sourceCard && sourceCard.cloneNode) {
          const clone = sourceCard.cloneNode(true);
          clone.tabIndex = -1;
          clone.querySelectorAll("a,button,[role='button'],[tabindex]").forEach((el) => {
            try { el.tabIndex = -1; } catch {}
            if (el.tagName === "A") el.setAttribute("tabindex","-1");
          });

          clone.querySelectorAll("[data-more-badge],[data-card-more]").forEach((el) => {
            try { el.remove(); } catch {}
          });
          clone.querySelectorAll("[data-image-inline-desc],[data-image-inline-read-more],[data-image-inline-more-desc]").forEach((el) => {
            try { el.remove(); } catch {}
          });

          const cloneActions = clone.querySelector("[data-card-actions]");
          if (cloneActions) {
            const clonePrimary = cloneActions.querySelector("[data-card-primary]");
            const primaryHref = String(clonePrimary?.getAttribute("data-mg-href") || clonePrimary?.getAttribute("href") || "").trim();
            if (primaryHref) {
              cloneActions.classList.add("is-single");
            } else {
              cloneActions.remove();
            }
          }

          infoPreviewRow.appendChild(clone);
        }

        fillInfoDesc(meta);

        infoScroll.scrollTop = 0;
        infoLb.setAttribute("aria-hidden", "false");
        lightbox.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "hidden";
        GLOBAL.closeCurrent = closeInfo;

        requestAnimationFrame(() => {
          sizeInfoDescBlock();
          infoClose.focus();
        });
      }

      infoPreviewRow.addEventListener("click", (e) => {
        if (!infoMetaActive) return;
        if (e.target.closest("a")) return;
        const href = String(infoMetaActive.__primaryHref || "").trim();
        if (href) window.location.href = href;
      });

      infoClose.addEventListener("click", closeInfo);
      infoBackdrop.addEventListener("click", closeInfo);
      infoLb.addEventListener("click", (e) => {
        if (!isInfoOpen()) return;
        if (e.target.closest(".desc-wrap")) return;
        if (e.target.closest("a,button,input,textarea,select,label")) return;
        closeInfo();
      });

      /* ------------------------------
         Card template
      ------------------------------ */
      const cardTpl = document.createElement("template");
      cardTpl.innerHTML = `
        <div class="thumb" tabindex="0">
          <div class="thumb-block" data-thumb-block>
            <div class="title card-top-title" data-card-top-title hidden></div>
            <div class="thumb-slot">
              <div class="img-wrap" data-hit>
                <img loading="lazy" decoding="async" width="240" height="240" alt="" />
                <div class="hero-preview-stage" data-hero-preview aria-hidden="true"></div>

                <div class="play-badge" data-badge-play><span class="play-badge__icon" aria-hidden="true"></span><span>Play</span></div>

                <a class="primary-badge" data-primary-badge href="#" target="_self" aria-label="Open link"></a>
                <button class="more-badge" data-more-badge type="button" tabindex="-1" aria-label="Learn more">Learn More</button>

                <a class="cta-badge" data-cta-badge href="#" target="_self" aria-label="Open CTA"></a>
              </div>
            </div>

            <div class="card-bottom" data-card-bottom>
              <div class="meta" data-meta>
                <div class="card-heading" data-card-heading></div>
                <div class="card-subheading" data-card-subheading></div>
                <div class="bv-inline-rating" data-product-review-inline aria-hidden="true"></div>
                <div class="title" data-card-title></div>
                <div class="image-inline-desc" data-image-inline-desc></div>
                <button class="image-inline-read-more" data-image-inline-read-more type="button" hidden></button>
                <div class="image-inline-more-desc" data-image-inline-more-desc></div>
                <a class="hero-open-link" data-hero-open-link href="#" target="_self"></a>
                <div class="price" data-price></div>
              </div>

              <div class="card-actions" data-card-actions aria-hidden="true">
                <button class="card-btn card-btn--primary" data-card-primary type="button"></button>
                <button class="card-btn card-btn--more" data-card-more type="button">Learn More</button>
              </div>
            </div>
          </div>
        </div>
      `;

      function setButtonPlacement(thumb, placeBelow){
        thumb.dataset.btnPlace = placeBelow ? "below" : "overlay";
      }

      function setRowAspect(rowEl, ratioObj){
        const aspect = ratioObj ? `${ratioObj.w} / ${ratioObj.h}` : "1 / 1";
        rowEl.style.setProperty("--row-aspect", aspect);
        return aspect;
      }

      function applyRowHeight(rowEl, rowHeight){
        const h = Number(rowHeight || 0) || 0;
        if (h > 0) {
          rowEl.classList.add("has-row-height");
          rowEl.classList.remove("has-auto-thumb-h");
          rowEl.style.setProperty("--row-thumb-h", h + "px");
        } else {
          rowEl.classList.remove("has-row-height");
          rowEl.style.setProperty("--row-thumb-h", "0px");
        }
      }

      function applyAutoThumbH(rowEl, thumbH){
        const h = Number(thumbH || 0) || 0;
        if (h > 0) {
          rowEl.classList.add("has-auto-thumb-h");
          rowEl.classList.remove("has-row-height");
          rowEl.style.setProperty("--row-thumb-h", h + "px");
        } else {
          rowEl.classList.remove("has-auto-thumb-h");
          rowEl.style.setProperty("--row-thumb-h", "0px");
        }
      }

      /* -------------------------------------------------
         âœ… Vimeo thumbnail strategy (requested)
         1) Fetch + download Vimeo oEmbed thumbnails for ALL Vimeo cards first.
         2) After those thumbnails are downloaded, swap to any custom thumbnail overrides.
      -------------------------------------------------- */
      const MG_IMG_PRELOAD_CACHE = window.__mgImgPreloadCache || (window.__mgImgPreloadCache = new Map());
      const MG_IMG_PRELOAD_LIMIT = window.__mgImgPreloadLimit || (window.__mgImgPreloadLimit = createLimiter(6));
      const MG_CARD_IMAGE_MIN_WIDTH = 220;
      const MG_CARD_IMAGE_DEFAULT_WIDTH = 360;
      const MG_CARD_IMAGE_ROOT_MARGIN = "280px 0px 420px 0px";
      const MG_CARD_IMAGE_MAX_WIDTHS = Object.freeze({
        product: 560,
        image: 720,
        video: 720,
        hero: 1280
      });

      function preloadImageOnce(url, timeoutMs = 15000) {
        const u = String(url || "").trim();
        if (!u) return Promise.resolve(false);

        const cached = MG_IMG_PRELOAD_CACHE.get(u);
        if (cached) return cached;

        const p = MG_IMG_PRELOAD_LIMIT(() => new Promise((resolve) => {
          let done = false;
          const img = new Image();

          const finish = (ok) => {
            if (done) return;
            done = true;
            try { img.onload = null; img.onerror = null; } catch {}
            resolve(!!ok);
          };

          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          try { img.decoding = "async"; } catch {}
          img.src = u;

          setTimeout(() => finish(false), Math.max(3000, timeoutMs | 0));
        }));

        MG_IMG_PRELOAD_CACHE.set(u, p);
        return p;
      }

      function cardImageMaxWidthForMeta(meta) {
        const kind = providerOf(meta);
        const isHeroVideo = kind === "video" && isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
        if (isHeroVideo) return MG_CARD_IMAGE_MAX_WIDTHS.hero;
        return MG_CARD_IMAGE_MAX_WIDTHS[kind] || MG_CARD_IMAGE_MAX_WIDTHS.image;
      }

      function isShopifyCdnImageUrl(rawUrl) {
        try {
          const u = new URL(String(rawUrl || ""), window.location.href);
          const host = String(u.hostname || "").toLowerCase();
          const path = String(u.pathname || "");
          return /\/cdn\/shop\//i.test(path) || (/cdn\.shopify\.com$/i.test(host) && /\/s\/files\//i.test(path));
        } catch {
          return false;
        }
      }

      function sizedShopifyImageUrl(rawUrl, targetWidth) {
        const src = String(rawUrl || "").trim();
        const width = Math.max(MG_CARD_IMAGE_MIN_WIDTH, Math.round(Number(targetWidth) || 0));
        if (!src || !(width > 0) || !isShopifyCdnImageUrl(src)) return src;

        try {
          const u = new URL(src, window.location.href);
          const path = String(u.pathname || "");
          const sizeMatch = path.match(/_(\d{2,5})(?:x\d{0,5})?(?=\.[a-z0-9]+$)/i);
          const currentQueryWidth = parseInt(u.searchParams.get("width") || "", 10);
          const currentWidth = sizeMatch
            ? parseInt(sizeMatch[1], 10)
            : (Number.isFinite(currentQueryWidth) ? currentQueryWidth : 0);

          if (currentWidth > 0 && currentWidth <= width) return u.href;

          if (sizeMatch) {
            u.pathname = path.replace(/_(\d{2,5})(?:x\d{0,5})?(?=\.[a-z0-9]+$)/i, `_${width}x`);
            u.searchParams.delete("width");
            return u.href;
          }

          if (Number.isFinite(currentQueryWidth) && currentQueryWidth > 0 && currentQueryWidth <= width) return u.href;
          u.searchParams.set("width", String(width));
          return u.href;
        } catch {
          return src;
        }
      }

      function cardImageTargetWidth(card, img, meta) {
        const dpr = Math.max(1, Math.min(2, Number(window.devicePixelRatio || 1) || 1));
        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        const wrapRect = wrap && wrap.getBoundingClientRect ? wrap.getBoundingClientRect() : null;
        const imgRect = img && img.getBoundingClientRect ? img.getBoundingClientRect() : null;
        const cardRect = card && card.getBoundingClientRect ? card.getBoundingClientRect() : null;
        const baseWidth = Math.max(
          Number(wrapRect && wrapRect.width) || 0,
          Number(imgRect && imgRect.width) || 0,
          Number(cardRect && cardRect.width) || 0,
          MG_CARD_IMAGE_DEFAULT_WIDTH
        );
        const rawTarget = Math.round(baseWidth * dpr);
        return Math.max(
          MG_CARD_IMAGE_MIN_WIDTH,
          Math.min(cardImageMaxWidthForMeta(meta), rawTarget || MG_CARD_IMAGE_DEFAULT_WIDTH)
        );
      }

      function optimizedCardImageUrl(rawUrl, card, img, meta) {
        const src = absUrlMaybe(rawUrl);
        if (!src) return "";
        return sizedShopifyImageUrl(src, cardImageTargetWidth(card, img, meta)) || src;
      }

      function isCardNearViewport(card, pad = 0) {
        if (!card || !card.getBoundingClientRect) return false;
        const rect = card.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        return rect.bottom >= -pad && rect.top <= (viewportH + pad);
      }

      function cardImageObserver() {
        if (!("IntersectionObserver" in window)) return null;
        if (window.__mgCardImageObserver) return window.__mgCardImageObserver;

        window.__mgCardImageObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.intersectionRatio <= 0) return;
            loadDeferredCardImage(entry.target);
          });
        }, {
          rootMargin: MG_CARD_IMAGE_ROOT_MARGIN,
          threshold: 0.01
        });

        return window.__mgCardImageObserver;
      }

      function stopObservingCardImage(img) {
        const observer = window.__mgCardImageObserver;
        if (observer && img) {
          try { observer.unobserve(img); } catch {}
        }
      }

      function loadDeferredCardImage(img) {
        try {
          if (!img || img.dataset.mgImageLoaded === "true" || img.dataset.mgImageLoading === "true") return;

          const rawUrl = String(img.dataset.mgSrc || "").trim();
          if (!rawUrl) return;

          const card = img.closest ? img.closest(".thumb") : null;
          const meta = (card && CARD_META.get(card)) || null;
          const resolvedUrl = optimizedCardImageUrl(rawUrl, card, img, meta) || rawUrl;
          const fallbackUrl = absUrlMaybe(rawUrl) || rawUrl;
          const currentSrc = String(img.getAttribute("src") || "").trim();

          img.dataset.mgImageLoading = "true";
          try { img.fetchPriority = isCardNearViewport(card, 160) ? "high" : "low"; } catch {}

          const cleanup = () => {
            img.removeEventListener("load", onLoad);
            img.removeEventListener("error", onError);
            delete img.dataset.mgImageLoading;
            stopObservingCardImage(img);
          };

          const onLoad = () => {
            img.dataset.mgImageLoaded = "true";
            cleanup();
          };

          const onError = () => {
            if (fallbackUrl && fallbackUrl !== resolvedUrl && img.dataset.mgImageRetried !== "true") {
              img.dataset.mgImageRetried = "true";
              img.src = fallbackUrl;
              return;
            }
            cleanup();
          };

          if (currentSrc === resolvedUrl && img.complete && img.naturalWidth > 0) {
            img.dataset.mgImageLoaded = "true";
            cleanup();
            return;
          }

          img.addEventListener("load", onLoad);
          img.addEventListener("error", onError);
          img.src = resolvedUrl;
        } catch {}
      }

      function scheduleCardImageLoad(card, img, rawUrl) {
        const src = absUrlMaybe(rawUrl);
        if (!card || !img || !src) return;

        img.dataset.mgSrc = src;
        delete img.dataset.mgImageLoaded;
        delete img.dataset.mgImageLoading;
        delete img.dataset.mgImageRetried;
        img.removeAttribute("src");
        img.removeAttribute("srcset");
        try { img.loading = "lazy"; } catch {}
        try { img.decoding = "async"; } catch {}
        try { img.fetchPriority = "low"; } catch {}

        requestAnimationFrame(() => {
          if (!img.isConnected) return;
          const observer = cardImageObserver();
          if (!observer || isCardNearViewport(card, 180)) {
            loadDeferredCardImage(img);
            return;
          }
          observer.observe(img);
        });
      }

      function applyVideoPlaceholderThumb(card, meta) {
        try {
          const img = card && card.querySelector ? card.querySelector('img') : null;
          if (!img) return false;
          const placeholder = buildVideoThumbPlaceholderDataUrl(meta || CARD_META.get(card) || {});
          img.src = placeholder;
          img.setAttribute('data-mg-placeholder', 'true');
          return true;
        } catch {
          return false;
        }
      }

      function primeVideoCard(card, opts = {}) {
        try {
          const meta = CARD_META.get(card);
          if (!meta) return Promise.resolve(false);

          const kind = providerOf(meta);
          if (kind !== "video") return Promise.resolve(false);

          const url = String(meta?.url || "").trim();
          if (!url) {
            applyVideoPlaceholderThumb(card, meta);
            return Promise.resolve(false);
          }

          const img = card.querySelector("img");
          if (!img) return Promise.resolve(false);

          const force = !!opts.force;
          const preferProviderThumb = !!opts.preferProviderThumb;
          const isHeroVideo = isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
          if (isHeroVideo) {
            try { img.loading = "eager"; } catch {}
            try { img.fetchPriority = "high"; } catch {}
          }

          if (typeof meta.__thumbOverride === "undefined") {
            meta.__thumbOverride = absUrlMaybe(meta?.thumbnail) || "";
          }

          if (!String(meta?.alt || "").trim()) {
            const t = String(meta?.title || "").trim() || "Video";
            const d = String(meta?.description || "").trim();
            img.alt = clampAltText(buildDefaultAlt(t, d) || t);
          }

          const currentSrc = String(img.getAttribute("src") || img.src || "").trim();
          if (!force && card.dataset.videoPrime === "1" && currentSrc && currentSrc !== 'about:blank') {
            return Promise.resolve(true);
          }
          card.dataset.videoPrime = "1";

          const source = videoSourceOf(meta);
          if (source === "youtube") {
            const providerThumb = absUrlMaybe(pickYouTubeThumb(url));
            const fallbackThumb = String(meta.__thumbOverride || '').trim();
            const useProviderFirst = (typeof opts.preferProviderThumb === "undefined") ? true : !!opts.preferProviderThumb;
            const thumb = useProviderFirst ? (providerThumb || fallbackThumb) : (fallbackThumb || providerThumb);
            if (!thumb) {
              applyVideoPlaceholderThumb(card, meta);
              return Promise.resolve(false);
            }
            if (providerThumb && !meta.__thumbOverride && !String(meta?.thumbnail || "").trim()) meta.thumbnail = providerThumb;
            img.src = thumb;
            img.removeAttribute('data-mg-placeholder');
            return preloadImageOnce(thumb).catch(() => false);
          }

          if (source === "file") {
            const override = String(meta.__thumbOverride || '').trim();
            const useProviderFirst = preferProviderThumb || !override;
            if (useProviderFirst) {
              return captureFileVideoThumbnail(url, Number(meta?.start_time || meta?.start || 0) || 0).then((thumb) => {
                const out = String(thumb || '').trim();
                if (out) {
                  meta.__fileThumb = out;
                  if (!String(meta?.thumbnail || '').trim()) meta.thumbnail = out;
                  img.src = out;
                  img.removeAttribute('data-mg-placeholder');
                  return true;
                }
                if (override) {
                  img.src = override;
                  img.removeAttribute('data-mg-placeholder');
                  return preloadImageOnce(override).then((ok) => {
                    if (!ok) applyVideoPlaceholderThumb(card, meta);
                    return !!ok;
                  }).catch(() => {
                    applyVideoPlaceholderThumb(card, meta);
                    return false;
                  });
                }
                applyVideoPlaceholderThumb(card, meta);
                return false;
              }).catch(() => {
                if (override) {
                  img.src = override;
                  img.removeAttribute('data-mg-placeholder');
                  return preloadImageOnce(override).then((ok) => {
                    if (!ok) applyVideoPlaceholderThumb(card, meta);
                    return !!ok;
                  }).catch(() => {
                    applyVideoPlaceholderThumb(card, meta);
                    return false;
                  });
                }
                applyVideoPlaceholderThumb(card, meta);
                return false;
              });
            }

            img.src = override;
            img.removeAttribute('data-mg-placeholder');
            return preloadImageOnce(override).then((ok) => {
              if (!ok) applyVideoPlaceholderThumb(card, meta);
              return !!ok;
            }).catch(() => {
              applyVideoPlaceholderThumb(card, meta);
              return false;
            });
          }

          return fetchVimeoOEmbed(url).then((data) => {
            const providerThumb = pickVimeoThumbUrl(data);
            const fallbackThumb = String(meta.__thumbOverride || '').trim();
            const useProviderFirst = (typeof opts.preferProviderThumb === "undefined") ? true : !!opts.preferProviderThumb;
            const thumb = useProviderFirst ? (providerThumb || fallbackThumb) : (fallbackThumb || providerThumb);
            cacheVimeoSizing(meta, data);

            const vDesc = String(data?.description || "").trim();
            const hasOverrideDesc =
              !!String(meta?.html_desc || "").trim() ||
              !!String(meta?.desc_html || "").trim() ||
              !!String(meta?.description || "").trim();
            if (!hasOverrideDesc && vDesc) meta.description = vDesc;

            if (!String(meta?.alt || "").trim()) {
              const t = String(meta?.title || "").trim() || "Video";
              const d = String(meta?.description || vDesc || "").trim();
              img.alt = clampAltText(buildDefaultAlt(t, d) || t);
            }

            if (!thumb) {
              applyVideoPlaceholderThumb(card, meta);
              return false;
            }

            if (providerThumb) meta.__vimeoThumb = providerThumb;
            if (providerThumb && !meta.__thumbOverride && !String(meta?.thumbnail || "").trim()) {
              meta.thumbnail = providerThumb;
            }
            img.src = thumb;
            img.removeAttribute('data-mg-placeholder');
            return preloadImageOnce(thumb).catch(() => false);
          }).catch(() => {
            const override = String(meta.__thumbOverride || '').trim();
            if (override) {
              img.src = override;
              img.removeAttribute('data-mg-placeholder');
              return preloadImageOnce(override).then((ok) => {
                if (!ok) applyVideoPlaceholderThumb(card, meta);
                return !!ok;
              }).catch(() => {
                applyVideoPlaceholderThumb(card, meta);
                return false;
              });
            }
            applyVideoPlaceholderThumb(card, meta);
            return false;
          });
        } catch {
          return Promise.resolve(false);
        }
      }

      function swapVideoOverrideOnCard(card, opts = {}) {
        try {
          const meta = CARD_META.get(card);
          const img = card && card.querySelector ? card.querySelector('img') : null;
          if (!meta || !img) return Promise.resolve(false);
          const override = String(meta.__thumbOverride || absUrlMaybe(meta?.thumbnail) || '').trim();
          if (!override) return Promise.resolve(false);

          const force = !!opts.force;
          const currentSrc = String(img.getAttribute('src') || img.src || '').trim();
          if (!force && currentSrc === override) {
            applyHeroMediaFit(card);
            return Promise.resolve(true);
          }

          return preloadImageOnce(override).then((ok) => {
            if (!ok) return false;
            if (!card.isConnected) return false;
            img.src = override;
            img.removeAttribute('data-mg-placeholder');
            img.classList.remove('is-hidden');
            card.dataset.thumbMode = 'override';
            applyHeroMediaFit(card);
            return true;
          }).catch(() => false);
        } catch {
          return Promise.resolve(false);
        }
      }

      function waitForMs(ms){
        return new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0)));
      }

      const HERO_VIDEO_METADATA_WARMS = new Map();

      function waitForHeroThumbReady(card, timeoutMs = 5000) {
        return new Promise((resolve) => {
          try {
            const img = card && card.querySelector ? card.querySelector('.img-wrap > img') : null;
            if (!img) {
              resolve(false);
              return;
            }
            if (img.complete && (img.naturalWidth || img.getAttribute('src'))) {
              resolve(true);
              return;
            }

            let done = false;
            const finish = (ok) => {
              if (done) return;
              done = true;
              try { img.removeEventListener('load', onLoad); } catch {}
              try { img.removeEventListener('error', onError); } catch {}
              resolve(!!ok);
            };
            const onLoad = () => finish(true);
            const onError = () => finish(false);
            img.addEventListener('load', onLoad, { once:true });
            img.addEventListener('error', onError, { once:true });
            setTimeout(() => finish(false), Math.max(800, Number(timeoutMs) || 5000));
          } catch {
            resolve(false);
          }
        });
      }

      function warmHeroFileVideoMetadata(card) {
        try {
          const meta = CARD_META.get(card);
          if (!meta || videoSourceOf(meta) !== 'file') return false;
          const url = safeSrc(String(meta?.url || '').trim());
          if (!url) return false;
          if (HERO_VIDEO_METADATA_WARMS.has(url)) return true;

          const video = document.createElement('video');
          video.preload = 'metadata';
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.setAttribute('preload', 'metadata');
          video.setAttribute('muted', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.setAttribute('aria-hidden', 'true');
          video.tabIndex = -1;
          video.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:auto;';
          video.src = url;
          HERO_VIDEO_METADATA_WARMS.set(url, video);
          try { (document.body || root).appendChild(video); } catch {}
          try { video.load(); } catch {}

          setTimeout(() => {
            try { video.removeAttribute('src'); } catch {}
            try { video.load(); } catch {}
            try { video.remove(); } catch {}
            try { HERO_VIDEO_METADATA_WARMS.delete(url); } catch {}
          }, 45000);
          return true;
        } catch {
          return false;
        }
      }

      function scheduleHeroVideoMetadataWarmup(card) {
        try {
          if (!card || card.dataset.heroMetadataWarmScheduled === 'true') return;
          const meta = CARD_META.get(card);
          if (!meta || videoSourceOf(meta) !== 'file') return;
          card.dataset.heroMetadataWarmScheduled = 'true';

          const isMobileViewport = !!(
            window.matchMedia && window.matchMedia("(max-width: 768px)").matches
          );
          const delay = isMobileViewport ? 2500 : 900;

          waitForHeroThumbReady(card).then(() => {
            if (!card.isConnected) return;
            setTimeout(() => {
              if (!card.isConnected) return;
              const run = () => warmHeroFileVideoMetadata(card);
              if ('requestIdleCallback' in window) {
                window.requestIdleCallback(run, { timeout: 3000 });
              } else {
                run();
              }
            }, delay);
          });
        } catch {}
      }

      async function sequenceHeroVideoCard(card, opts = {}) {
        try {
          const meta = CARD_META.get(card);
          if (!meta || providerOf(meta) !== 'video') return false;
          const isHero = isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
          const wantsPreview = heroWantsAutoplayPreview(meta);
          if (!isHero || !wantsPreview) return false;

          const force = !!opts.force;
          const seqToken = 'hero-seq-' + Date.now() + '-' + Math.random().toString(36).slice(2);
          if (!force && card.dataset.heroSequencePending === 'true') return false;
          card.dataset.heroSequencePending = 'true';
          card.dataset.heroSequenceToken = seqToken;

          clearHeroPreview(card);
          syncHeroVideoCardLayout(card);
          applyHeroMediaFit(card);

          const hasExplicitThumb = !!String(meta.__thumbOverride || absUrlMaybe(meta?.thumbnail) || '').trim();
          await primeVideoCard(card, { force:true, preferProviderThumb:!hasExplicitThumb });
          if (!card.isConnected || card.dataset.heroSequenceToken !== seqToken) return false;
          syncHeroVideoCardLayout(card);
          applyHeroMediaFit(card);
          await waitForMs(140);

          if (!card.isConnected || card.dataset.heroSequenceToken !== seqToken) return false;
          const overrideShown = await swapVideoOverrideOnCard(card, { force:true });
          if (!card.isConnected || card.dataset.heroSequenceToken !== seqToken) return false;
          syncHeroVideoCardLayout(card);
          applyHeroMediaFit(card);
          if (overrideShown) await waitForMs(160);

          if (!card.isConnected || card.dataset.heroSequenceToken !== seqToken) return false;
          const ok = await primeHeroVideoPreview(card, { force:true });
          if (!card.isConnected || card.dataset.heroSequenceToken !== seqToken) return false;
          syncHeroVideoCardLayout(card);
          applyHeroMediaFit(card);
          return !!ok;
        } catch {
          return false;
        } finally {
          try { delete card.dataset.heroSequencePending; } catch {}
        }
      }

      function heroVideoPreviewStartDelayMs(card) {
        try {
          const meta = CARD_META.get(card);
          const hasExplicitThumb = !!String(meta?.__thumbOverride || absUrlMaybe(meta?.thumbnail) || '').trim();
          const isMobileViewport = !!(
            window.matchMedia && window.matchMedia("(max-width: 768px)").matches
          );

          if (heroPrioritizesQuickVideo(meta) && shouldUseHeroQuickVideo(meta, meta?.url)) return 0;
          if (isMobileViewport && hasExplicitThumb) return 12000;
          if (isMobileViewport) return 8000;
          if (hasExplicitThumb) return 5500;
          return 2800;
        } catch {
          return 5000;
        }
      }

      function scheduleHeroVideoCard(card, opts = {}) {
        try {
          if (!card || card.dataset.heroAutoplayScheduled === 'true') return;
          card.dataset.heroAutoplayScheduled = 'true';
          scheduleHeroVideoMetadataWarmup(card);

          const force = !!opts.force;
          const attempt = () => {
            try {
              if (!card.isConnected) return;
              if (card.dataset.heroPreviewReady === 'true') return;
              if (card.dataset.heroSequencePending === 'true') return;
              sequenceHeroVideoCard(card, { force });
            } catch {}
          };

          const runAttempts = () => {
            attempt();
            [2600, 6200].forEach((ms) => {
              setTimeout(() => {
                try {
                  if (card.isConnected && card.dataset.heroPreviewReady !== 'true') attempt();
                } catch {}
              }, ms);
            });
          };

          const afterLoad = () => {
            const start = () => setTimeout(runAttempts, heroVideoPreviewStartDelayMs(card));
            if ('requestIdleCallback' in window) {
              window.requestIdleCallback(start, { timeout: 2500 });
            } else {
              setTimeout(start, 1800);
            }
          };

          if (document.readyState === 'complete') afterLoad();
          else window.addEventListener('load', afterLoad, { once: true });
        } catch {}
      }

      async function primeAllVideoThumbsThenSwapOverrides(scope, opts = {}) {
        const searchRoot = scope && scope.querySelectorAll ? scope : rowsRoot;
        const visibleOnly = !!opts.visibleOnly;
        const allCards = Array.from(searchRoot.querySelectorAll('.thumb[data-kind="video"]')).filter((card) => {
          try {
            if (card.closest('.vimeo-snippet') !== root) return false;
          } catch {}
          return !visibleOnly || !isInsideClosedAccordionPanel(card);
        });
        if (!allCards.length) return;

        const heroCards = [];
        const normalCards = [];
        for (const card of allCards) {
          const meta = CARD_META.get(card);
          const isHeroAutoplay = !!(meta && providerOf(meta) === 'video' && isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero) && heroWantsAutoplayPreview(meta));
          if (isHeroAutoplay) heroCards.push(card);
          else normalCards.push(card);
        }

        if (normalCards.length) {
          const stage1 = normalCards.map((c) => primeVideoCard(c, opts));
          await Promise.allSettled(stage1);

          const swaps = [];
          for (const card of normalCards) {
            const meta = CARD_META.get(card);
            const img = card.querySelector("img");
            if (!meta || !img) continue;

            const override = String(meta.__thumbOverride || "").trim();
            if (!override) continue;

            if (String(img.getAttribute("src") || "") === override) continue;

            swaps.push(
              preloadImageOnce(override).then((ok) => {
                if (!ok) return;
                if (!card.isConnected) return;
                img.src = override;
                img.removeAttribute('data-mg-placeholder');
              })
            );
          }
          await Promise.allSettled(swaps);
        }

        if (heroCards.length) {
          heroCards.forEach((card) => scheduleHeroVideoCard(card, { force:!!opts.force }));
        }
      }

      function applyHeroMediaFit(card){
        if (!card || !card.querySelector) return;
        const meta = CARD_META.get(card);
        const wrap = card.querySelector('.img-wrap');
        const img = card.querySelector('.img-wrap > img');
        const stage = card.querySelector('[data-hero-preview]');
        if (!wrap) return;

        const setImp = (el, prop, value) => {
          try { if (el && el.style) el.style.setProperty(prop, value, 'important'); } catch {}
        };
        const clearProp = (el, prop) => {
          try { if (el && el.style) el.style.removeProperty(prop); } catch {}
        };

        wrap.style.width = '100%';
        wrap.style.maxWidth = '100%';
        wrap.style.overflow = 'hidden';

        const boxW = Math.max(0, wrap.clientWidth || wrap.getBoundingClientRect().width || 0);
        const boxH = Math.max(0, wrap.clientHeight || wrap.getBoundingClientRect().height || 0);
        const fallbackAspect = (() => {
          const stable = heroStableAspectForMeta(meta);
          if (stable && stable.w > 0 && stable.h > 0) return stable.w / stable.h;
          const w = Number(meta?.__w || 0);
          const h = Number(meta?.__h || 0);
          if (w > 0 && h > 0) return w / h;
          const source = videoSourceOf(meta);
          if (source === 'youtube') return 16 / 9;
          if (source === 'vimeo') return 16 / 9;
          return 16 / 9;
        })();

        const fitCoverBox = (el, aspect) => {
          if (!el || !el.style) return;
          const r = (Number(aspect) > 0 && isFinite(Number(aspect))) ? Number(aspect) : fallbackAspect;
          if (!(boxW > 0) || !(boxH > 0)) {
            setImp(el, 'position', 'absolute');
            setImp(el, 'left', '0');
            setImp(el, 'top', '0');
            setImp(el, 'right', '0');
            setImp(el, 'bottom', '0');
            setImp(el, 'width', '100%');
            setImp(el, 'height', '100%');
            setImp(el, 'transform', 'none');
            return;
          }
          const boxRatio = boxW / boxH;
          let width = boxW;
          let height = boxH;
          if (boxRatio > r) {
            width = boxW;
            height = boxW / r;
          } else {
            height = boxH;
            width = boxH * r;
          }
          setImp(el, 'position', 'absolute');
          setImp(el, 'left', '50%');
          setImp(el, 'top', '50%');
          setImp(el, 'right', 'auto');
          setImp(el, 'bottom', 'auto');
          setImp(el, 'width', Math.max(1, Math.ceil(width)) + 'px');
          setImp(el, 'height', Math.max(1, Math.ceil(height)) + 'px');
          setImp(el, 'min-width', '0');
          setImp(el, 'min-height', '0');
          setImp(el, 'max-width', 'none');
          setImp(el, 'max-height', 'none');
          setImp(el, 'transform', 'translate(-50%, -50%)');
        };

        if (img && img.style) {
          setImp(img, 'position', 'absolute');
          setImp(img, 'left', '0');
          setImp(img, 'right', '0');
          setImp(img, 'top', '0');
          setImp(img, 'bottom', '0');
          setImp(img, 'width', '100%');
          setImp(img, 'height', '100%');
          setImp(img, 'min-width', '100%');
          setImp(img, 'min-height', '100%');
          setImp(img, 'max-width', 'none');
          setImp(img, 'max-height', 'none');
          setImp(img, 'object-fit', 'cover');
          setImp(img, 'object-position', 'center center');
          setImp(img, 'transform', 'none');
        }
        if (stage && stage.style) {
          setImp(stage, 'position', 'absolute');
          setImp(stage, 'left', '0');
          setImp(stage, 'right', '0');
          setImp(stage, 'top', '0');
          setImp(stage, 'bottom', '0');
          setImp(stage, 'width', '100%');
          setImp(stage, 'height', '100%');
          setImp(stage, 'min-width', '100%');
          setImp(stage, 'min-height', '100%');
          setImp(stage, 'max-width', 'none');
          setImp(stage, 'max-height', 'none');
          setImp(stage, 'transform', 'none');
          setImp(stage, 'background', 'transparent');
          Array.from(stage.querySelectorAll('iframe,video')).forEach((el) => {
            const tag = String(el.tagName || '').toUpperCase();
            if (tag === 'IFRAME') {
              fitCoverBox(el, fallbackAspect);
              setImp(el, 'background', 'transparent');
            } else {
              const videoAspect = (() => {
                const vw = Number(el.videoWidth || 0);
                const vh = Number(el.videoHeight || 0);
                if (vw > 0 && vh > 0) return vw / vh;
                return fallbackAspect;
              })();
              fitCoverBox(el, videoAspect);
              setImp(el, 'background', 'transparent');
              setImp(el, 'object-fit', 'cover');
              setImp(el, 'object-position', 'center center');
            }
          });
        }
      }

      function clearHeroPreview(card, opts = {}){
        if (!card || !card.querySelector) return;
        const keepState = !!opts.keepState;
        const stage = card.querySelector('[data-hero-preview]');
        const img = card.querySelector('.img-wrap > img');
        if (stage) {
          try {
            Array.from(stage.querySelectorAll('iframe')).forEach((iframe) => {
              unregisterHeroVimeoPreview(iframe);
              unregisterHeroYouTubePreview(iframe);
            });
          } catch {}
          try { stage.innerHTML = ''; } catch {}
          stage.classList.remove('is-ready');
          stage.setAttribute('aria-hidden', 'true');
        }
        if (img) img.classList.remove('is-hidden');
        if (!keepState) {
          delete card.dataset.heroPreviewReady;
          delete card.dataset.heroPreviewPending;
          delete card.dataset.heroPreviewToken;
          try { card.__heroPreviewPromise = null; } catch {}
        }
      }

      function primeHeroVideoPreview(card, opts = {}) {
        try {
          const meta = CARD_META.get(card);
          if (!meta || providerOf(meta) !== 'video') return Promise.resolve(false);
          const wantsPreview = heroWantsAutoplayPreview(meta);
          const isHero = isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
          if (!isHero || !wantsPreview) return Promise.resolve(false);
          if (!opts.force && card.offsetParent === null) return Promise.resolve(false);

          const stage = card.querySelector('[data-hero-preview]');
          const img = card.querySelector('.img-wrap > img');
          if (!stage || !img) return Promise.resolve(false);

          const url = String(meta?.url || '').trim();
          const source = videoSourceOf(meta);
          if (!url || !source) return Promise.resolve(false);

          if (card.dataset.heroPreviewReady === 'true') {
            applyHeroMediaFit(card);
            return Promise.resolve(true);
          }
          if (card.dataset.heroPreviewPending === 'true' && card.__heroPreviewPromise) {
            return card.__heroPreviewPromise;
          }
          clearHeroPreview(card, { keepState:true });
          applyHeroMediaFit(card);
          const startSecs = startTimeOf(meta);
          const token = 'hero-' + Date.now() + '-' + Math.random().toString(36).slice(2);
          card.dataset.heroPreviewPending = 'true';
          card.dataset.heroPreviewToken = token;
          const revealGate = ensureHeroVisualSequence(card, meta, token);
          const quickUrl = shouldUseHeroQuickVideo(meta, url) ? heroQuickVideoUrlOf(meta) : "";
          const prioritizeQuickVideo = !!(quickUrl && heroPrioritizesQuickVideo(meta));

          const promise = new Promise((resolve) => {
            let settled = false;
            let timer = 0;
            const removePreviewEl = (el) => {
              try {
                if (!el) return;
                const tag = String(el.tagName || '').toUpperCase();
                if (tag === 'IFRAME') {
                  unregisterHeroVimeoPreview(el);
                  unregisterHeroYouTubePreview(el);
                }
                if (tag === 'VIDEO') {
                  try { el.pause(); } catch {}
                  try { el.removeAttribute('src'); el.load(); } catch {}
                }
                if (el.parentNode) el.parentNode.removeChild(el);
              } catch {}
            };
            const revealPreviewEl = (nextEl, previousEl) => {
              try {
                if (card.dataset.heroPreviewToken !== token) return;
                if (nextEl && nextEl.style) nextEl.style.opacity = '1';
                applyHeroMediaFit(card);
                if (previousEl && previousEl !== nextEl) {
                  setTimeout(() => removePreviewEl(previousEl), 180);
                }
              } catch {}
            };
            const finish = (ok) => {
              if (settled) return;
              settled = true;
              if (timer) clearTimeout(timer);
              const finalize = () => {
                if (card.dataset.heroPreviewToken !== token) {
                  resolve(false);
                  return;
                }
                delete card.dataset.heroPreviewPending;
                if (ok) {
                  stage.setAttribute('aria-hidden', 'false');
                  stage.classList.add('is-ready');
                  requestAnimationFrame(() => {
                    try { img.classList.add('is-hidden'); } catch {}
                  });
                  card.dataset.heroPreviewReady = 'true';
                  applyHeroMediaFit(card);
                } else {
                  stage.setAttribute('aria-hidden', 'true');
                  stage.classList.remove('is-ready');
                  img.classList.remove('is-hidden');
                  delete card.dataset.heroPreviewReady;
                }
                try { card.__heroPreviewPromise = null; } catch {}
                resolve(!!ok);
              };
              if (ok) {
                Promise.resolve(revealGate).catch(() => false).then(() => { finalize(); });
                return;
              }
              finalize();
            };
            timer = setTimeout(() => finish(false), 12000);

            const warmMainPreviewAfterQuick = (previousEl) => {
              try {
                if (card.dataset.heroPreviewToken !== token) return;
                if (source === 'file') {
                  const mainVideo = document.createElement('video');
                  mainVideo.defaultMuted = true;
                  mainVideo.muted = true;
                  mainVideo.loop = true;
                  mainVideo.autoplay = true;
                  mainVideo.playsInline = true;
                  mainVideo.preload = 'metadata';
                  mainVideo.style.opacity = '0';
                  mainVideo.style.transition = 'opacity .22s ease';
                  mainVideo.setAttribute('muted', '');
                  mainVideo.setAttribute('playsinline', '');
                  mainVideo.setAttribute('webkit-playsinline', '');
                  mainVideo.setAttribute('preload', 'metadata');
                  mainVideo.setAttribute('autoplay', '');
                  mainVideo.setAttribute('loop', '');
                  mainVideo.src = safeSrc(url);

                  const revealMain = () => {
                    try {
                      if (startSecs > 0) {
                        const dur = Number(mainVideo.duration || 0);
                        const target = (dur > 0) ? Math.min(startSecs, Math.max(0, dur - 0.25)) : startSecs;
                        mainVideo.currentTime = target;
                      }
                    } catch {}
                    try {
                      const p = mainVideo.play();
                      if (p && typeof p.then === 'function') {
                        p.then(() => { revealPreviewEl(mainVideo, previousEl); finish(true); }).catch(() => { revealPreviewEl(mainVideo, previousEl); finish(true); });
                      } else {
                        revealPreviewEl(mainVideo, previousEl);
                        finish(true);
                      }
                    } catch {
                      revealPreviewEl(mainVideo, previousEl);
                      finish(true);
                    }
                  };

                  mainVideo.addEventListener('loadedmetadata', revealMain, { once:true });
                  mainVideo.addEventListener('canplay', revealMain, { once:true });
                  mainVideo.addEventListener('loadeddata', revealMain, { once:true });
                  mainVideo.addEventListener('playing', () => { revealPreviewEl(mainVideo, previousEl); finish(true); }, { once:true });
                  mainVideo.addEventListener('error', () => removePreviewEl(mainVideo), { once:true });
                  stage.appendChild(mainVideo);
                  applyHeroMediaFit(card);
                  return;
                }

                const iframe = document.createElement('iframe');
                iframe.setAttribute('tabindex', '-1');
                iframe.setAttribute('aria-hidden', 'true');
                iframe.allow = 'autoplay; fullscreen; picture-in-picture';
                iframe.setAttribute('allowfullscreen', '');
                iframe.loading = 'eager';
                iframe.title = String(meta?.title || 'Video preview');
                iframe.style.opacity = '0';
                iframe.style.transition = 'opacity .22s ease';
                iframe.src = (source === 'youtube')
                  ? buildYouTubePreviewSrc(url, startSecs)
                  : buildVimeoPreviewSrc(url, startSecs);
                iframe.addEventListener('load', () => {
                  try {
                    if (source === 'vimeo') {
                      [80, 260, 620].forEach((ms) => {
                        setTimeout(() => {
                          try { vimeoPost(iframe, 'play'); } catch {}
                        }, ms);
                      });
                    } else {
                      registerHeroYouTubePreview(iframe, () => {}, startSecs);
                    }
                  } catch {}
                  revealPreviewEl(iframe, previousEl);
                  finish(true);
                }, { once:true });
                iframe.addEventListener('error', () => removePreviewEl(iframe), { once:true });
                stage.appendChild(iframe);
                applyHeroMediaFit(card);
              } catch {}
            };

            if (quickUrl) {
              const quickVideo = document.createElement('video');
              quickVideo.defaultMuted = true;
              quickVideo.muted = true;
              quickVideo.loop = true;
              quickVideo.autoplay = true;
              quickVideo.playsInline = true;
              quickVideo.preload = 'auto';
              quickVideo.style.transition = 'opacity .22s ease';
              quickVideo.setAttribute('muted', '');
              quickVideo.setAttribute('playsinline', '');
              quickVideo.setAttribute('webkit-playsinline', '');
              quickVideo.setAttribute('preload', 'auto');
              quickVideo.setAttribute('autoplay', '');
              quickVideo.setAttribute('loop', '');
              try { quickVideo.fetchPriority = prioritizeQuickVideo ? 'high' : 'low'; } catch {}
              quickVideo.setAttribute('fetchpriority', prioritizeQuickVideo ? 'high' : 'low');
              quickVideo.src = quickUrl;
              let quickStarted = false;
              const revealQuick = () => {
                if (quickStarted) return;
                quickStarted = true;
                try {
                  const p = quickVideo.play();
                  if (p && typeof p.then === 'function') {
                    p.then(() => finish(true)).catch(() => finish(quickVideo.readyState >= 2));
                  } else {
                    finish(true);
                  }
                } catch {
                  finish(quickVideo.readyState >= 2);
                }
                setTimeout(() => warmMainPreviewAfterQuick(quickVideo), 120);
              };
              quickVideo.addEventListener('loadedmetadata', revealQuick, { once:true });
              quickVideo.addEventListener('canplay', revealQuick, { once:true });
              quickVideo.addEventListener('loadeddata', revealQuick, { once:true });
              quickVideo.addEventListener('playing', () => finish(true), { once:true });
              quickVideo.addEventListener('error', () => {
                try { removePreviewEl(quickVideo); } catch {}
                warmMainPreviewAfterQuick(null);
              }, { once:true });
              stage.appendChild(quickVideo);
              applyHeroMediaFit(card);
              return;
            }

            if (source === 'file') {
              const video = document.createElement('video');
              video.defaultMuted = true;
              video.muted = true;
              video.loop = true;
              video.autoplay = true;
              video.playsInline = true;
              video.preload = 'metadata';
              video.setAttribute('muted', '');
              video.setAttribute('playsinline', '');
              video.setAttribute('webkit-playsinline', '');
              video.setAttribute('preload', 'metadata');
              video.setAttribute('autoplay', '');
              video.setAttribute('loop', '');
              video.src = safeSrc(url);
              applyHeroMediaFit(card);
              const ready = () => {
                try {
                  if (startSecs > 0) {
                    const dur = Number(video.duration || 0);
                    const target = (dur > 0) ? Math.min(startSecs, Math.max(0, dur - 0.25)) : startSecs;
                    video.currentTime = target;
                  }
                } catch {}
                try {
                  const p = video.play();
                  if (p && typeof p.then === 'function') {
                    p.then(() => setTimeout(() => finish(true), 140)).catch(() => finish(video.readyState >= 2));
                  } else {
                    setTimeout(() => finish(true), 140);
                  }
                } catch {
                  finish(video.readyState >= 2);
                }
              };

              video.addEventListener('loadedmetadata', ready, { once:true });
              video.addEventListener('canplay', ready, { once:true });
              video.addEventListener('loadeddata', ready, { once:true });
              video.addEventListener('playing', () => finish(true), { once:true });
              video.addEventListener('timeupdate', () => finish(true), { once:true });
              video.addEventListener('error', () => finish(false), { once:true });
              stage.appendChild(video);
              applyHeroMediaFit(card);
              return;
            }

            const iframe = document.createElement('iframe');
            iframe.setAttribute('tabindex', '-1');
            iframe.setAttribute('aria-hidden', 'true');
            iframe.allow = 'autoplay; fullscreen; picture-in-picture';
            iframe.setAttribute('allowfullscreen', '');
            iframe.loading = 'eager';
            iframe.title = String(meta?.title || 'Video preview');
            iframe.src = (source === 'youtube')
              ? buildYouTubePreviewSrc(url, startSecs)
              : buildVimeoPreviewSrc(url, startSecs);
            iframe.addEventListener('load', () => {
              applyHeroMediaFit(card);
              if (source === 'vimeo') {
                registerHeroVimeoPreview(iframe, (msg) => {
                  try {
                    const ev = String((msg && (msg.event || msg.type)) || '').trim().toLowerCase();
                    const payload = (msg && typeof msg === 'object') ? (msg.data || msg) : {};
                    const seconds = Number(payload && payload.seconds);
                    if (ev === 'play' || (ev === 'timeupdate' && isFinite(seconds) && seconds >= 0)) finish(true);
                  } catch {}
                });
                [80, 260, 620].forEach((ms) => {
                  setTimeout(() => {
                    try {
                      vimeoPost(iframe, 'play');
                      vimeoPost(iframe, 'addEventListener', 'play');
                      vimeoPost(iframe, 'addEventListener', 'timeupdate');
                    } catch {}
                  }, ms);
                });
              } else {
                registerHeroYouTubePreview(iframe, (msg) => {
                  try {
                    const ev = String((msg && msg.event) || '').trim();
                    const info = msg && typeof msg === 'object' ? msg.info : null;
                    if (ev === 'onStateChange' && Number(info) === 1) finish(true);
                    if (ev === 'infoDelivery' && msg.info && Number(msg.info.currentTime || 0) > 0) finish(true);
                  } catch {}
                }, startSecs);
              }
            }, { once:true });
            iframe.addEventListener('error', () => finish(false), { once:true });
            stage.appendChild(iframe);
            applyHeroMediaFit(card);

            // Fallback: if the provider iframe attached without error, reveal it after a short delay.
            setTimeout(() => {
              try {
                if (card.dataset.heroPreviewToken === token && stage.contains(iframe)) finish(true);
              } catch {}
            }, 3200);
          });
          card.__heroPreviewPromise = promise;
          return promise;
        } catch {
          return Promise.resolve(false);
        }
      }

      function primeHeroVideoPreviews(scope, opts = {}) {
        const searchRoot = scope && scope.querySelectorAll ? scope : rowsRoot;
        const cards = Array.from(searchRoot.querySelectorAll('.thumb[data-hero-video="true"][data-hero-autoplay="true"]'));
        if (!cards.length) return Promise.resolve();
        return Promise.allSettled(cards.map((card) => primeHeroVideoPreview(card, opts))).then(() => {});
      }

      function makeCard(meta, rowCtx){
        const kind = providerOf(meta);
        const node = cardTpl.content.firstElementChild.cloneNode(true);
        const itemMarginTop = Number(meta?.margin_top || 0) || 0;
        const itemMarginBottom = Number(meta?.margin_bottom || 0) || 0;
        if (itemMarginTop > 0) node.style.marginTop = itemMarginTop + "px";
        if (itemMarginBottom > 0) node.style.marginBottom = itemMarginBottom + "px";

        node.dataset.kind = kind;
        // Product media always rests on the bottom of the shared row track.
        // This keeps differently scaled products on the same visual baseline.
        const itemVJust = kind === "product" ? "bottom" : normalizeVJust(meta?.vjust || "top");
        node.setAttribute("data-vjust", itemVJust);

        const imgWrap = node.querySelector(".img-wrap");
        const img = node.querySelector("img");
        const titleEl = node.querySelector("[data-card-title]");
        const topTitleEl = node.querySelector("[data-card-top-title]");
        const headingEl = node.querySelector("[data-card-heading]");
        const subheadingEl = node.querySelector("[data-card-subheading]");
        const imageInlineDescEl = node.querySelector("[data-image-inline-desc]");
        const imageInlineReadMoreEl = node.querySelector("[data-image-inline-read-more]");
        const imageInlineMoreDescEl = node.querySelector("[data-image-inline-more-desc]");
        const heroOpenLink = node.querySelector("[data-hero-open-link]");
        const heroPreviewStage = node.querySelector("[data-hero-preview]");
        const priceEl = node.querySelector("[data-price]");
        const reviewInline = node.querySelector("[data-product-review-inline]");
        const playBadge = node.querySelector("[data-badge-play]");
        const ctaBadge = node.querySelector("[data-cta-badge]");
        const primaryBadge = node.querySelector("[data-primary-badge]");
        const moreBadge = node.querySelector("[data-more-badge]");

        const actionsRow = node.querySelector("[data-card-actions]");
        const primaryBtn = node.querySelector("[data-card-primary]");
        const moreBtn = node.querySelector("[data-card-more]");

        const title = String(meta?.title || "").trim();
        const productShopButtonCtaType = (kind === "product")
          ? normalizeProductShopButtonCtaType(meta?.shop_button_cta_type, "below_title")
          : "";
        const productShopButtonStyle = (kind === "product")
          ? normalizeProductShopButtonStyle(meta?.shop_button_style, SECTION_DEFAULT_PRODUCT_SHOP_BUTTON_STYLE)
          : "";
        const productButtonsOverImage = (kind === "product") && productShopButtonCtaType === "over_image";
        const productTitleTopButtonBottom = (kind === "product") && productShopButtonCtaType === "title_top_button_bottom";
        const productTitleIsLink = (kind === "product") && productShopButtonCtaType === "make_title_link";
        const productTitleIsButton = (kind === "product") && productShopButtonCtaType === "make_title_button";
        const productPrimaryHidden = (kind === "product") && productShopButtonCtaType === "hide";
        const isHeroVideo = isVideoKind(kind) && isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
        const heroAutoplayPreview = isHeroVideo && heroWantsAutoplayPreview(meta);
        const heroShowPlayBadge = isHeroVideo && isTrueLike(meta?.hero_show_play_badge ?? meta?.hero_show_play_button ?? meta?.show_play_badge ?? meta?.show_play_button ?? meta?.hero_play_badge);
        const heroHeadingHtml = String(meta?.heading_html || "").trim();
        const heroSubheadingHtml = String(meta?.subheading_html || "").trim();
        const heroOpenText = String(meta?.open_full_video_text || "").trim();
        const heroCtaText = String(meta?.cta?.thumb_text || meta?.cta?.text || "").trim();
        const heroCtaHref = safeLink(meta?.cta?.href);
        titleEl.textContent = (productTitleTopButtonBottom || isHeroVideo) ? "" : (title || "");
        titleEl.hidden = !!isHeroVideo || productTitleTopButtonBottom;
        if (topTitleEl) {
          topTitleEl.textContent = (productTitleTopButtonBottom && !isHeroVideo) ? (title || "") : "";
          topTitleEl.hidden = !(productTitleTopButtonBottom && !isHeroVideo && title);
        }

        if (headingEl) {
          if (isHeroVideo && heroHeadingHtml) {
            headingEl.innerHTML = sanitizeHtml(heroHeadingHtml);
            headingEl.classList.add("is-visible");
          } else {
            headingEl.innerHTML = "";
            headingEl.classList.remove("is-visible");
          }
        }
        if (subheadingEl) {
          if (isHeroVideo && heroSubheadingHtml) {
            subheadingEl.innerHTML = sanitizeHtml(heroSubheadingHtml);
            subheadingEl.classList.add("is-visible");
          } else {
            subheadingEl.innerHTML = "";
            subheadingEl.classList.remove("is-visible");
          }
        }

        if (kind === "image") {
          const imageHeadingHtml = String(meta?.heading_html || "").trim();
          const imageSubheadingHtml = String(meta?.title_html || "").trim();
          const imageDescHtml = String(meta?.image_desc_html || "").trim();
          const imageMoreDescHtml = String(meta?.image_more_desc_html || "").trim();
          const imageDescJustify = String(meta?.image_desc_justify || "left").trim().toLowerCase() || "left";
          const learnMoreLabel = plainTextFromMaybeHtml(meta?.learn_more_html || "") || "Learn More";
          const showLearnMoreButton = isTrueLike(meta?.show_learn_more_button);
          const showLearnMoreLink = isTrueLike(meta?.show_learn_more_link);
          const hasMoreDescription = !!imageMoreDescHtml;
          node.dataset.showLearnMoreButton = showLearnMoreButton ? "true" : "false";
          moreBadge.classList.remove("is-visible");
          moreBadge.hidden = true;
          moreBadge.tabIndex = -1;
          moreBtn.hidden = true;
          moreBtn.setAttribute("aria-hidden", "true");

          if (headingEl) {
            if (imageHeadingHtml) {
              headingEl.innerHTML = sanitizeHtml(imageHeadingHtml);
              headingEl.classList.add("is-visible");
            } else {
              headingEl.innerHTML = "";
              headingEl.classList.remove("is-visible");
            }
          }

          if (subheadingEl) {
            if (imageSubheadingHtml) {
              subheadingEl.innerHTML = sanitizeHtml(imageSubheadingHtml);
              subheadingEl.classList.add("is-visible");
            } else {
              subheadingEl.innerHTML = "";
              subheadingEl.classList.remove("is-visible");
            }
          }

          titleEl.textContent = "";
          titleEl.hidden = true;

          if (imageInlineDescEl) {
            imageInlineDescEl.style.textAlign = imageDescJustify;
            if (imageDescHtml) {
              imageInlineDescEl.innerHTML = sanitizeHtml(linkifyHtmlString(sanitizeHtml(imageDescHtml)));
              imageInlineDescEl.classList.add("is-visible");
            } else {
              imageInlineDescEl.innerHTML = "";
              imageInlineDescEl.classList.remove("is-visible");
            }
          }

          if (imageInlineMoreDescEl) {
            imageInlineMoreDescEl.style.textAlign = imageDescJustify;
            imageInlineMoreDescEl.dataset.expanded = "false";
            imageInlineMoreDescEl.innerHTML = hasMoreDescription
              ? sanitizeHtml(linkifyHtmlString(sanitizeHtml(imageMoreDescHtml)))
              : "";
            imageInlineMoreDescEl.classList.remove("is-visible");
          }

          if (imageInlineReadMoreEl) {
            imageInlineReadMoreEl.textContent = learnMoreLabel;
            imageInlineReadMoreEl.hidden = !(showLearnMoreLink && hasMoreDescription && !showLearnMoreButton);
            imageInlineReadMoreEl.onclick = null;
            if (showLearnMoreLink && hasMoreDescription && !showLearnMoreButton) {
              imageInlineReadMoreEl.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (imageInlineMoreDescEl) {
                  imageInlineMoreDescEl.classList.add("is-visible");
                  imageInlineMoreDescEl.dataset.expanded = "true";
                }
                imageInlineReadMoreEl.hidden = true;
              };
            }
          }

          if (showLearnMoreButton && hasMoreDescription) {
            moreBadge.textContent = learnMoreLabel;
            moreBadge.classList.add("is-visible");
            moreBadge.hidden = false;
            moreBadge.tabIndex = 0;
          }

          requestAnimationFrame(() => {
            fitSingleLineSqueeze(headingEl, 0.72);
            fitSingleLineSqueeze(subheadingEl, 0.72);
          });
        } else {
          delete node.dataset.showLearnMoreButton;
          if (imageInlineDescEl) {
            imageInlineDescEl.innerHTML = "";
            imageInlineDescEl.classList.remove("is-visible");
          }
          if (imageInlineMoreDescEl) {
            imageInlineMoreDescEl.innerHTML = "";
            imageInlineMoreDescEl.classList.remove("is-visible");
            imageInlineMoreDescEl.dataset.expanded = "false";
          }
          if (imageInlineReadMoreEl) {
            imageInlineReadMoreEl.hidden = true;
            imageInlineReadMoreEl.onclick = null;
          }
        }

        const wrapVideoTitle = !isHeroVideo && isVideoKind(kind) && isTrueLike(meta?.title_wrap ?? meta?.wrap_title);
        node.dataset.titleWrap = wrapVideoTitle ? "true" : "false";
        node.dataset.heroVideo = isHeroVideo ? "true" : "false";
        node.dataset.heroAutoplay = heroAutoplayPreview ? "true" : "false";
        node.dataset.heroShowPlayBadge = heroShowPlayBadge ? "true" : "false";
        node.dataset.heroShowPlayButton = heroShowPlayBadge ? "true" : "false";
        node.dataset.heroClickable = (isHeroVideo && heroShowPlayBadge) ? "true" : "false";
        node.dataset.thumbMode = isHeroVideo ? "override" : (String(meta?.thumbnail || "").trim() ? "override" : "oembed");
        node.tabIndex = (isHeroVideo && !heroShowPlayBadge) ? -1 : 0;

        const showProductReviews = (kind === "product") && !isFalseLike(meta?.show_reviews);
        node.dataset.showReviews = showProductReviews ? "true" : "false";
        node.dataset.productButtonsOverImage = productButtonsOverImage ? "true" : "false";
        node.dataset.shopButtonCtaType = productShopButtonCtaType;
        node.dataset.shopButtonStyle = productShopButtonStyle;
        if (kind === "product") node.dataset.productFit = "width";

        const moreText = kind === "image"
          ? (plainTextFromMaybeHtml(meta?.learn_more_html || "") || "Learn More")
          : (String(meta?.learn_more_text || "").trim() || "Learn More");
        moreBadge.textContent = moreText;
        moreBtn.textContent = moreText;
        if (heroOpenLink) {
          if (isHeroVideo && String(meta?.url || '').trim() && heroOpenText) {
            heroOpenLink.textContent = heroOpenText;
            heroOpenLink.href = safeLink(meta?.url) || '#';
            heroOpenLink.classList.add('is-visible');
          } else {
            heroOpenLink.textContent = '';
            heroOpenLink.removeAttribute('href');
            heroOpenLink.classList.remove('is-visible');
          }
        }
        if (heroPreviewStage) {
          if (!heroAutoplayPreview) clearHeroPreview(node);
          heroPreviewStage.setAttribute('aria-hidden', heroAutoplayPreview ? 'false' : 'true');
        }
        if (isHeroVideo) {
          const heroDesktopWidthRaw = Number(meta?.hero_width_desktop_pct ?? meta?.heroWidthDesktopPct ?? 0);
          const heroMobileWidthRaw = Number(meta?.hero_width_mobile_pct ?? meta?.heroWidthMobilePct ?? 0);
          const heroDesktopWidthPct = (isFinite(heroDesktopWidthRaw) && heroDesktopWidthRaw > 0)
            ? clampWidthPercent(heroDesktopWidthRaw, DEFAULT_BLOCK_WIDTH_DESKTOP_PCT)
            : DEFAULT_BLOCK_WIDTH_DESKTOP_PCT;
          const heroMobileWidthPct = (isFinite(heroMobileWidthRaw) && heroMobileWidthRaw > 0)
            ? clampWidthPercent(heroMobileWidthRaw, DEFAULT_BLOCK_WIDTH_MOBILE_PCT)
            : DEFAULT_BLOCK_WIDTH_MOBILE_PCT;
          const heroMaxDesktopPxRaw = Number(meta?.hero_max_width_desktop_px ?? meta?.heroMaxWidthDesktopPx ?? 0);
          const heroMaxDesktopPx = (isFinite(heroMaxDesktopPxRaw) && heroMaxDesktopPxRaw > 0)
            ? clampDesktopMaxWidthPx(heroMaxDesktopPxRaw, DEFAULT_BLOCK_MAX_WIDTH_DESKTOP_PX)
            : DEFAULT_BLOCK_MAX_WIDTH_DESKTOP_PX;
          node.style.setProperty('--mg-hero-width-pct-desktop', String(heroDesktopWidthPct));
          node.style.setProperty('--mg-hero-width-pct-mobile', String(heroMobileWidthPct));
          node.style.setProperty('--mg-hero-max-width-desktop-px', heroMaxDesktopPx > 0 ? (heroMaxDesktopPx + 'px') : '999999px');
          applyHeroMediaFit(node);
        } else {
          node.style.removeProperty('--mg-hero-width-pct-desktop');
          node.style.removeProperty('--mg-hero-width-pct-mobile');
          node.style.removeProperty('--mg-hero-max-width-desktop-px');
        }

        // âœ… Default alt (requested): Title + Description (truncated) when no explicit alt is provided
        let alt = clampAltText(meta?.alt || "");
        if (!alt) {
          const descForAlt = String(meta?.description || "").trim();
          if (isVideoKind(kind)) {
            const baseTitle = title || "Video";
            alt = buildDefaultAlt(baseTitle, descForAlt) || baseTitle;
          } else if (kind === "product") {
            alt = title || "Product image";
          } else if (kind === "image") {
            alt = title || "Image";
          } else {
            alt = title || "Media";
          }
        }

        // Track row context for info modal sizing
        meta.__row_aspect = rowCtx.rowAspectCss;
        meta.__row_height = rowCtx.rowHeightPx;

        // Product cards can either overlay the image edge or sit below the text.
        // Non-product cards only use below-placement when older imported data still carries that flag.
        const usesProductActionRow = (kind === "product");
        const wantsBelow = usesProductActionRow ? !productButtonsOverImage : isTrueLike(meta?.buttons_below);
        const useActionRow = usesProductActionRow || wantsBelow;
        setButtonPlacement(node, wantsBelow);

        // Which elements show based on placement
        if (useActionRow) {
          actionsRow.classList.add("is-visible");
          actionsRow.removeAttribute("aria-hidden");
        } else {
          actionsRow.classList.remove("is-visible");
          actionsRow.setAttribute("aria-hidden", "true");
        }
        // Set thumbnail
        let thumbUrl = absUrlMaybe(meta?.thumbnail);
        const hasImmediateHeroThumb = isHeroVideo && !!thumbUrl;

        // Video cards show provider thumbnails first, but hero override thumbnails should paint immediately.
        if (kind === "video") {
          if (typeof meta.__thumbOverride === "undefined") {
            meta.__thumbOverride = thumbUrl || "";
          }
          if (!hasImmediateHeroThumb) thumbUrl = "";
        }

        if (thumbUrl) {
          if (kind === "product" || kind === "image") scheduleCardImageLoad(node, img, thumbUrl);
          else {
            if (isHeroVideo) {
              try { img.loading = "eager"; } catch {}
              try { img.fetchPriority = "high"; } catch {}
            }
            img.src = thumbUrl;
            img.removeAttribute('data-mg-placeholder');
          }
          if (!String(meta?.thumbnail || "").trim()) meta.thumbnail = thumbUrl;
        } else if (kind === "video") {
          applyVideoPlaceholderThumb(node, meta);
        }

        img.alt = clampAltText(alt);
        if (kind === "video") {
          img.addEventListener('error', () => {
            const currentMeta = CARD_META.get(node) || meta;
            const override = String(currentMeta?.__thumbOverride || '').trim();
            const currentSrc = String(img.getAttribute('src') || img.src || '').trim();
            if (override && currentSrc !== override) {
              img.src = override;
              return;
            }
            applyVideoPlaceholderThumb(node, currentMeta);
          });
        }

        const syncCardFromImage = () => {
          const row = node.closest(".thumb-row");
          if (!row) return;
          if (row.__wantFitRow) fitRowIfNeeded(row, true);
          else syncRowCardLayout(row, { applyVideoScale: true });
          if (kind === "image") {
            fitSingleLineSqueeze(headingEl, 0.72);
            fitSingleLineSqueeze(subheadingEl, 0.72);
          }
        };
        img.addEventListener("load", () => {
          requestAnimationFrame(() => {
            syncCardFromImage();
            if (node.dataset.heroVideo === "true") applyHeroMediaFit(node);
          });
        });

        // Handle scaling
        const scalePct = isVideoKind(kind) ? clampScalePct(meta?.scale, 0, 200) : clampScalePct(meta?.scale, 10, 200);
        node.dataset.scalePct = String(scalePct);
        if (isVideoKind(kind)) {
          imgWrap.style.removeProperty("transform");
          imgWrap.style.removeProperty("transformOrigin");
        } else if (kind === "product") {
          // Scale only the product image. Scaling imgWrap would also visually
          // displace metadata that must remain below the image slot.
          imgWrap.style.removeProperty("transform");
          imgWrap.style.removeProperty("transformOrigin");
          img.style.setProperty("--mg-product-image-scale", String(scalePct / 100));
        } else {
          imgWrap.style.transform = `scale(${scalePct / 100})`;
          imgWrap.style.transformOrigin = (kind === "product") ? "center center" : "center top";
        }

        // Padding top for image blocks
        const padTop = Number(meta?.pad_t || 0) || 0;
        imgWrap.style.setProperty("--pad-top", padTop + "px");

        // Per item aspect overrides for videos/images only.
        // Products now use natural image height so row vertical alignment
        // is controlled by the row slot instead of a forced aspect box.
        const rOverride = parseRatioOverride(meta?.ratio_override);
        if (kind !== "product" && rOverride) {
          imgWrap.style.setProperty("--item-aspect", `${rOverride.w} / ${rOverride.h}`);
        } else {
          imgWrap.style.removeProperty("--item-aspect");
        }

        // Price display for product
        if (kind === "product" && meta?.show_price && String(meta?.price || "").trim()) {
          const currentPrice = String(meta.price).trim();
          const compareAtPrice = String(meta?.compare_at_price || "").trim();
          const currentPriceNum = Number(meta?.price_num);
          const compareAtPriceNum = Number(meta?.compare_at_price_num);
          const hasNumericPrices = meta?.price_num != null && meta?.compare_at_price_num != null &&
            Number.isFinite(currentPriceNum) && Number.isFinite(compareAtPriceNum);
          const hasDifferentListPrice = compareAtPrice && (
            hasNumericPrices
              ? compareAtPriceNum > currentPriceNum
              : compareAtPrice !== currentPrice
          );

          priceEl.textContent = "";
          if (hasDifferentListPrice) {
            const listPriceEl = document.createElement("span");
            listPriceEl.className = "price-list";
            listPriceEl.textContent = compareAtPrice;
            listPriceEl.setAttribute("aria-label", `List price ${compareAtPrice}`);
            priceEl.appendChild(listPriceEl);
          }

          const currentPriceEl = document.createElement("span");
          currentPriceEl.className = "price-current";
          currentPriceEl.textContent = currentPrice;
          currentPriceEl.setAttribute("aria-label", hasDifferentListPrice ? `Sale price ${currentPrice}` : `Price ${currentPrice}`);
          priceEl.appendChild(currentPriceEl);

          if (hasDifferentListPrice && hasNumericPrices && compareAtPriceNum > 0) {
            const savingsPercent = Math.round(((compareAtPriceNum - currentPriceNum) / compareAtPriceNum) * 100);
            if (savingsPercent > 0) {
              const savingsEl = document.createElement("span");
              savingsEl.className = "price-save";
              savingsEl.textContent = `SAVE ${savingsPercent}%`;
              savingsEl.setAttribute("aria-label", `Save ${savingsPercent} percent`);
              priceEl.appendChild(savingsEl);
            }
          }
          priceEl.classList.add("is-visible");
        } else {
          priceEl.textContent = "";
          priceEl.classList.remove("is-visible");
        }

        // LAI Reviews inline rating snippet (product only)
        if (reviewInline) {
          const clearReviewInline = () => {
            if (reviewInline.__mgBvObserver) {
              try { reviewInline.__mgBvObserver.disconnect(); } catch {}
              reviewInline.__mgBvObserver = null;
            }
            if (reviewInline.__mgLaiObserver) {
              try { reviewInline.__mgLaiObserver.disconnect(); } catch {}
              reviewInline.__mgLaiObserver = null;
            }
            reviewInline.textContent = "";
            reviewInline.removeAttribute("data-bv-show");
            reviewInline.removeAttribute("data-bv-product-id");
            reviewInline.removeAttribute("data-bv-redirect-url");
            reviewInline.removeAttribute("data-bv-seo");
            setLaiInlineVisible(reviewInline, false);
          };

          if (showProductReviews) {
            const pid = meta?.product_id ?? meta?.productId ?? meta?.id;
            const pidStr = String(pid ?? "").trim();
            const redirect = safeLink(meta?.product_url) || safeLink(meta?.cta?.href) || "";
            const laiReviewInfo = parseReviewDataJson(meta?.lai_review_count_info);
            const laiSummary = extractLaiReviewSummary(laiReviewInfo);

            if (pidStr && (laiSummary.hasReviews || laiReviewInfo != null)) {
              const widgetEl = document.createElement("div");
              widgetEl.className = "scm-reviews-rate";
              widgetEl.setAttribute("data-product-id", pidStr);
              if (laiReviewInfo != null) {
                try {
                  widgetEl.setAttribute("data-rate-version2", JSON.stringify(laiReviewInfo));
                } catch {}
              }

              const fallbackEl = document.createElement("div");
              fallbackEl.className = "mg-review-inline-fallback-wrap";
              renderLaiInlineFallback(fallbackEl, laiSummary, redirect);

              reviewInline.replaceChildren(widgetEl, fallbackEl);
              setLaiInlineVisible(reviewInline, true);
              watchLaiInline(reviewInline, widgetEl, fallbackEl, true, () => {
                requestAnimationFrame(syncCardFromImage);
              });
              requestLaiInlineRefresh(reviewInline);
            } else {
              clearReviewInline();
            }
          } else {
            clearReviewInline();
          }
        }

        // Play button only for videos; hero videos can opt in explicitly.
        if (isVideoKind(kind) && (!isHeroVideo || heroShowPlayBadge)) playBadge.classList.add("is-visible");
        else playBadge.classList.remove("is-visible");

        const hideRowCtas = hideCtaButtons(meta, rowCtx);

        // CTA badge for videos (thumbnail badge)
        const ctaThumbText = heroCtaText || String(meta?.cta?.thumb_text || "").trim();
        const ctaHref = heroCtaHref;
        const showHeroCta = isHeroVideo && ctaHref && ctaThumbText;
        if (showHeroCta || (!hideRowCtas && isVideoKind(kind) && !isHeroVideo && ctaHref && ctaThumbText)) {
          ctaBadge.href = ctaHref || '#';
          ctaBadge.textContent = ctaThumbText;
          ctaBadge.classList.add("is-visible");
          ctaBadge.setAttribute("aria-hidden", "false");
        } else {
          ctaBadge.textContent = "";
          ctaBadge.removeAttribute("href");
          ctaBadge.classList.remove("is-visible");
          ctaBadge.setAttribute("aria-hidden", "true");
        }

        // Primary link logic (image/product)
        const pHref = primaryHref(meta);
        meta.__primaryHref = pHref;
        meta.__cardPrimaryHref = pHref;
        meta.__openSetKey = String(meta.open_set_key || meta.open_set || "").trim();
        const hasPresetLearnMore = (kind === "product") && !!meta.__openSetKey && typeof openPresetSet === "function";
        const showPrimaryAction = !!pHref && !hideRowCtas && (kind !== "product" || (!productTitleIsLink && !productTitleIsButton && !productPrimaryHidden));

        const showActionBadges = !useActionRow && !hideRowCtas && !isVideoKind(kind);
        if (pHref && !isVideoKind(kind)) {
          if (showActionBadges && showPrimaryAction) {
            primaryBadge.href = pHref;
            primaryBadge.textContent = primaryText(meta);
            primaryBadge.classList.add("is-visible");
          } else {
            primaryBadge.removeAttribute("href");
            primaryBadge.textContent = "";
            primaryBadge.classList.remove("is-visible");
          }

          if (showPrimaryAction) {
            setActionButtonHref(primaryBtn, pHref);
            primaryBtn.textContent = primaryText(meta);
          } else {
            setActionButtonHref(primaryBtn, "");
            primaryBtn.textContent = "";
          }

          // Make image wrapper clickable same as primary
          imgWrap.dataset.primaryHref = pHref;
        } else {
          primaryBadge.removeAttribute("href");
          primaryBadge.textContent = "";
          primaryBadge.classList.remove("is-visible");

          setActionButtonHref(primaryBtn, "");
          primaryBtn.textContent = "";
          imgWrap.dataset.primaryHref = "";
        }

        if (kind === "product" && productTitleTopButtonBottom) {
          if (titleEl) {
            titleEl.textContent = "";
            titleEl.innerHTML = "";
            titleEl.hidden = true;
          }
          if (topTitleEl) {
            topTitleEl.textContent = title || "";
            topTitleEl.hidden = !title;
          }
        } else if (kind === "product" && titleEl && !titleEl.hidden) {
          if (productTitleIsButton && pHref) {
            const titleButtonClasses = ["mg-product-title-action", "mg-product-below-btn"].concat(getProductThemeButtonClasses(productShopButtonStyle)).join(" ");
            titleEl.innerHTML = `<a href="${escapeHtml(pHref)}" target="_self" data-title-link="true" data-title-button="true" class="${escapeHtml(titleButtonClasses)}">${escapeHtml(title || "")}</a>`;
          } else if (productTitleIsLink && pHref) {
            titleEl.innerHTML = `<a href="${escapeHtml(pHref)}" target="_self" data-title-link="true">${escapeHtml(title || "")}</a>`;
          } else {
            titleEl.textContent = title || "";
          }
        }

        // Learn more shows for non-video cards when there is modal content,
        // or for product cards when a preset modal key is configured.
        const imageHasMoreDescription = kind === "image" && String(meta?.image_more_desc_html || "").trim() !== "";
        const canMore = !isVideoKind(kind) && kind !== "image" && (hasAnyDesc(meta) || hasPresetLearnMore);
        const showMoreAction = canMore && !hideRowCtas;
        if (showMoreAction && !useActionRow) {
          moreBadge.classList.add("is-visible");
          moreBadge.hidden = false;
          moreBadge.tabIndex = 0;
        } else {
          moreBadge.classList.remove("is-visible");
          moreBadge.hidden = true;
          moreBadge.tabIndex = -1;
        }

        if (kind === "image") {
          moreBadge.classList.remove("is-visible");
          moreBadge.hidden = true;
          moreBadge.tabIndex = -1;
          setActionButtonVisible(moreBtn, false);
          moreBtn.hidden = true;
          moreBtn.setAttribute("aria-hidden", "true");
          if (isTrueLike(meta?.show_learn_more_button) && imageHasMoreDescription && !useActionRow) {
            moreBadge.textContent = plainTextFromMaybeHtml(meta?.learn_more_html || "") || "Learn More";
            moreBadge.classList.add("is-visible");
            moreBadge.hidden = false;
            moreBadge.tabIndex = 0;
          }
        }

        if (useActionRow) {
          const hasPrimaryAction = showPrimaryAction;
          const hasMoreAction = showMoreAction;

          actionsRow.classList.toggle("is-single", (hasPrimaryAction && !hasMoreAction) || (!hasPrimaryAction && hasMoreAction));
          setActionButtonVisible(primaryBtn, hasPrimaryAction);
          setActionButtonVisible(moreBtn, hasMoreAction);

          if (!hasPrimaryAction && !hasMoreAction) {
            actionsRow.classList.remove("is-visible");
            actionsRow.setAttribute("aria-hidden", "true");
          } else {
            actionsRow.classList.add("is-visible");
            actionsRow.removeAttribute("aria-hidden");
          }

          if (kind === "product") {
            if (productButtonsOverImage) {
              normalizeProductOverlayActionButton(primaryBtn, "primary");
              normalizeProductOverlayActionButton(moreBtn, "more");
            } else {
              normalizeProductBelowActionButton(primaryBtn, "primary");
              normalizeProductBelowActionButton(moreBtn, "more");
            }
            setActionButtonVisible(primaryBtn, hasPrimaryAction);
            setActionButtonVisible(moreBtn, hasMoreAction);
            if (productShopButtonStyle === "theme_default" || productShopButtonStyle === "theme_secondary") {
              if (hasPrimaryAction) applyProductThemeButtonClasses(primaryBtn, productShopButtonStyle);
              if (hasMoreAction) applyProductThemeButtonClasses(moreBtn, productShopButtonStyle);
            } else if (productShopButtonStyle === "custom_button") {
              if (hasPrimaryAction) applyCustomButtonGroupStyle(primaryBtn);
              if (hasMoreAction) applyCustomButtonGroupStyle(moreBtn);
            }
          }
        } else {
          setActionButtonVisible(primaryBtn, false);
          setActionButtonVisible(moreBtn, false);
        }

        if (kind === "product" && productTitleIsButton && titleEl) {
          const titleActionEl = titleEl.querySelector("[data-title-button]");
          if (titleActionEl) {
            if (productShopButtonStyle === "theme_default" || productShopButtonStyle === "theme_secondary") {
              applyProductThemeButtonClasses(titleActionEl, productShopButtonStyle);
            } else if (productShopButtonStyle === "custom_button") {
              applyCustomButtonGroupStyle(titleActionEl);
            }
          }
        }

        if (img.complete) {
          requestAnimationFrame(syncCardFromImage);
        }

        // Save meta
        CARD_META.set(node, meta);
        if (isHeroVideo && !String(meta.__thumbOverride || "").trim()) {
          requestAnimationFrame(() => {
            try { primeVideoCard(node, { force:true, preferProviderThumb:true }); } catch {}
          });
        }
        if (heroAutoplayPreview) {
          scheduleHeroVideoCard(node, { force:true });
        }

        function openVideo(){
          // Always open in modal for videos
          goToIndex(meta.__videoIndex, { focus:true });
        }

        function clickPrimary(){
          if (pHref) window.location.href = pHref;
        }

        // Click handlers
        node.addEventListener("click", (e) => {
          const target = e.target;

          // CTA badge should behave like a real link/button and never trigger the video modal.
          if (isVideoKind(kind) && target.closest("[data-cta-badge]")) {
            return;
          }

          // Hero open-full-video link
          if (isVideoKind(kind) && target.closest("[data-hero-open-link]")) {
            e.preventDefault(); e.stopPropagation();
            openVideo();
            return;
          }

          // Learn more (overlay)
          if (target.closest("[data-more-badge]")) {
            e.preventDefault(); e.stopPropagation();
            if (hasPresetLearnMore) {
              openPresetSet(meta.__openSetKey);
              return;
            }
            if (canMore) openInfo(meta, node);
            return;
          }

          // Primary badge anchor
          if (target.closest("[data-primary-badge]")) {
            // allow native link
            return;
          }
          if (target.closest("[data-title-link]")) {
            return;
          }

          // Below buttons
          if (target.closest("[data-card-more]")) {
            e.preventDefault(); e.stopPropagation();
            if (hasPresetLearnMore) {
              openPresetSet(meta.__openSetKey);
              return;
            }
            if (canMore) openInfo(meta, node);
            return;
          }
          if (target.closest("[data-card-primary]")) {
            const actionEl = target.closest("[data-card-primary]");
            const href = getActionButtonHref(actionEl) || pHref;
            if (href) {
              e.preventDefault(); e.stopPropagation();
              window.location.href = href;
            }
            return;
          }

          // Clicking image area/card for product/image goes to primary
          if (!isVideoKind(kind)) {
            const hit = target.closest("[data-hit]");
            if (hit) {
              if (kind === "image" && String(meta?.image_more_desc_html || "").trim()) {
                e.preventDefault();
                openInfo(meta, node);
                return;
              }
              if (meta.__openSetKey && typeof openPresetSet === "function") {
                e.preventDefault();
                openPresetSet(meta.__openSetKey);
                return;
              }
              if (meta.__cardPrimaryHref) {
                e.preventDefault();
                window.location.href = meta.__cardPrimaryHref;
              }
              return;
            }
          }

          // Hero videos only open from the play state (thumbnail click) or the explicit open link.
          if (isHeroVideo) {
            if (heroShowPlayBadge) {
              const heroAction = target.closest("[data-badge-play], [data-hero-open-link]");
              if (!heroAction) {
                e.preventDefault();
                openVideo();
              }
            }
            return;
          }

          // Otherwise: open video
          if (isVideoKind(kind)) {
            e.preventDefault();
            openVideo();
          }
        });

        node.addEventListener("keydown", (e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          const meta = CARD_META.get(node);
          if (!meta) return;

          const kind = providerOf(meta);
          if (isHeroVideo) {
            if (heroShowPlayBadge) {
              e.preventDefault();
              goToIndex(meta.__videoIndex, { focus:true });
            }
          } else if (isVideoKind(kind)) {
            e.preventDefault();
            goToIndex(meta.__videoIndex, { focus:true });
          } else {
            if (kind === "image" && String(meta?.image_more_desc_html || "").trim()) {
              e.preventDefault();
              openInfo(meta, node);
              return;
            }
            if (meta.__openSetKey && typeof openPresetSet === "function") {
              e.preventDefault();
              openPresetSet(meta.__openSetKey);
            } else {
              const href = String(meta.__primaryHref || "").trim();
              if (href) {
                e.preventDefault();
                window.location.href = href;
              }
            }
          }
        });

        // Learn more keyboard
        moreBadge.addEventListener("keydown", (e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault(); e.stopPropagation();
          if (hasPresetLearnMore) {
            openPresetSet(meta.__openSetKey);
            return;
          }
          if (canMore) openInfo(meta, node);
        });

        // Desktop pointer: show play badge on hover? (already visible)
        return node;
      }

      function buildRowHeading(rowSpec){
        const heading = String(rowSpec?.heading || "").trim();
        const sub = String(rowSpec?.subheading || "").trim();
        const wrap = document.createElement("div");

        if (heading) {
          const h = document.createElement("div");
          h.className = "row-heading";
          h.innerHTML = sanitizeHtml(heading);
          wrap.appendChild(h);
        }
        if (sub) {
          const s = document.createElement("div");
          s.className = "row-subheading";
          s.innerHTML = sanitizeHtml(sub);
          wrap.appendChild(s);
        }
        return wrap;
      }

      function computeAutoRowHeight(rowEl){
        if (!rowEl || rowEl.offsetParent === null) return 0;
        // Estimate: measure first card's img-wrap height
        const firstImgWrap = rowEl.querySelector(".img-wrap");
        if (!firstImgWrap) return 0;
        const r = firstImgWrap.getBoundingClientRect();
        return Math.round(r.height || 0);
      }

  function fitTextDown(el, maxWidth, minPx = 10){
        if (!el) return;
        const cap = Math.max(0, Number(maxWidth || 0));
        if (!(cap > 0)) return;

        const cs = getComputedStyle(el);
        const basePx = parseFloat(el.dataset.baseFontPx || "") || numCss(cs.fontSize || "0") || 14;
        if (!el.dataset.baseFontPx) el.dataset.baseFontPx = String(basePx);

        el.style.fontSize = basePx + "px";
        if (el.scrollWidth <= cap + 1) return;

        let lo = Math.max(8, Number(minPx || 10));
        let hi = basePx;
        let best = lo;

        while ((hi - lo) > 0.25) {
          const mid = (hi + lo) / 2;
          el.style.fontSize = mid.toFixed(2) + "px";
          if (el.scrollWidth <= cap + 1) {
            best = mid;
            lo = mid;
          } else {
            hi = mid;
          }
        }

        el.style.fontSize = Math.max(Math.min(basePx, best), Math.max(8, Number(minPx || 10))).toFixed(2) + "px";
      }

      function fitSingleLineSqueeze(el, minScale = 0.72) {
        if (!el) return;
        const scaleFloor = Math.max(0.4, Math.min(1, Number(minScale || 0.72)));
        el.style.transform = "";
        el.style.transformOrigin = "";
        el.style.whiteSpace = "";
        el.style.display = "";
        el.style.width = "";

        const text = plainTextFromMaybeHtml(el.innerHTML || "").trim();
        if (!text) return;

        el.style.whiteSpace = "nowrap";
        el.style.display = "block";
        el.style.width = "100%";

        const available = Math.max(0, el.clientWidth || el.getBoundingClientRect().width || 0);
        const natural = Math.max(0, el.scrollWidth || 0);
        if (!(available > 0) || !(natural > available + 1)) {
          el.style.transform = "";
          el.style.transformOrigin = "";
          return;
        }

        const scale = Math.max(scaleFloor, Math.min(1, available / natural));
        el.style.transformOrigin = "center top";
        el.style.transform = scale < 0.999 ? `scaleX(${scale.toFixed(4)})` : "";
      }

      function waitMs(ms){
        return new Promise((resolve) => setTimeout(resolve, Math.max(0, Number(ms) || 0)));
      }

      const HERO_VIMEO_PREVIEW_HANDLERS = window.__heroVimeoPreviewHandlers || (window.__heroVimeoPreviewHandlers = new WeakMap());
      const HERO_YT_PREVIEW_HANDLERS = window.__heroYouTubePreviewHandlers || (window.__heroYouTubePreviewHandlers = new WeakMap());

      if (!R.__heroPreviewMsgInstalled) {
        R.__heroPreviewMsgInstalled = true;
        window.addEventListener("message", (e) => {
          try {
            const origin = String(e.origin || "");
            let data = e.data;
            if (typeof data === "string") {
              try { data = JSON.parse(data); } catch {}
            }
            if (origin === "https://player.vimeo.com") {
              const fn = HERO_VIMEO_PREVIEW_HANDLERS.get(e.source);
              if (typeof fn === "function") fn(data);
              return;
            }
            if (/^https:\/\/(?:www\.)?youtube(?:-nocookie)?\.com$/i.test(origin)) {
              const fn = HERO_YT_PREVIEW_HANDLERS.get(e.source);
              if (typeof fn === "function") fn(data);
            }
          } catch {}
        });
      }

      function unregisterHeroVimeoPreview(iframe){
        try {
          if (!iframe || !iframe.contentWindow) return;
          HERO_VIMEO_PREVIEW_HANDLERS.delete(iframe.contentWindow);
        } catch {}
      }

      function registerHeroVimeoPreview(iframe, onUpdate){
        try {
          if (!iframe || !iframe.contentWindow || typeof onUpdate !== 'function') return;
          HERO_VIMEO_PREVIEW_HANDLERS.set(iframe.contentWindow, onUpdate);
          [0, 160, 420, 900].forEach((ms) => {
            setTimeout(() => {
              try {
                vimeoPost(iframe, 'addEventListener', 'play');
                vimeoPost(iframe, 'addEventListener', 'timeupdate');
              } catch {}
            }, ms);
          });
        } catch {}
      }

      function ytPreviewPost(iframe, payload){
        try {
          if (!iframe || !iframe.contentWindow) return;
          iframe.contentWindow.postMessage(JSON.stringify(payload), '*');
        } catch {}
      }

      function unregisterHeroYouTubePreview(iframe){
        try {
          if (!iframe || !iframe.contentWindow) return;
          HERO_YT_PREVIEW_HANDLERS.delete(iframe.contentWindow);
        } catch {}
      }

      function registerHeroYouTubePreview(iframe, onUpdate, startSecs){
        try {
          if (!iframe || !iframe.contentWindow || typeof onUpdate !== 'function') return;
          HERO_YT_PREVIEW_HANDLERS.set(iframe.contentWindow, onUpdate);
          const widgetId = String(iframe.dataset.heroYtWidgetId || ('hero-yt-' + Math.random().toString(36).slice(2)));
          iframe.dataset.heroYtWidgetId = widgetId;
          const commands = [
            { event:'listening', id: widgetId, channel:'widget' },
            { event:'command', func:'mute', args:[], id: widgetId, channel:'widget' },
            { event:'command', func:'playVideo', args:[], id: widgetId, channel:'widget' },
            { event:'command', func:'addEventListener', args:['onStateChange'], id: widgetId, channel:'widget' },
            { event:'command', func:'addEventListener', args:['onReady'], id: widgetId, channel:'widget' }
          ];
          if ((Number(startSecs) || 0) > 0) {
            commands.splice(3, 0, { event:'command', func:'seekTo', args:[Math.max(0, Math.floor(Number(startSecs) || 0)), true], id: widgetId, channel:'widget' });
          }
          [0, 160, 420, 900].forEach((ms) => {
            setTimeout(() => {
              try { commands.forEach((cmd) => ytPreviewPost(iframe, cmd)); } catch {}
            }, ms);
          });
        } catch {}
      }

      function ensureHeroVisualSequence(card, meta, token){
        const img = card && card.querySelector ? card.querySelector('.img-wrap > img') : null;
        if (!card || !img) return Promise.resolve(false);

        return Promise.resolve()
          .then(() => primeVideoCard(card, { force:true }))
          .catch(() => false)
          .then(() => {
            if (String(card.dataset.heroPreviewToken || '') !== String(token || '')) return false;
            const override = String(meta?.__thumbOverride || '').trim();
            if (!override) return waitMs(80).then(() => true);
            return preloadImageOnce(override).catch(() => false).then((ok) => {
              if (String(card.dataset.heroPreviewToken || '') !== String(token || '')) return false;
              if (ok) {
                try {
                  img.src = override;
                  img.removeAttribute('data-mg-placeholder');
                } catch {}
                try { applyHeroMediaFit(card); } catch {}
                return waitMs(140).then(() => true);
              }
              return waitMs(80).then(() => true);
            });
          });
      }

      function heroStableAspectForMeta(meta){
        const ratioOverride = parseRatioOverride(meta?.ratio_override);
        if (ratioOverride && ratioOverride.w > 0 && ratioOverride.h > 0) return ratioOverride;
        const rowRatio = parseRatioOverride(meta?.__row_ratio || meta?.row_ratio || '');
        if (rowRatio && rowRatio.w > 0 && rowRatio.h > 0) return rowRatio;
        return { w:16, h:6 };
      }

      function heroHeightPxForCard(meta){
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        const raw = isMobile
          ? (meta?.hero_height_mobile_px ?? meta?.heroHeightMobilePx ?? 0)
          : (meta?.hero_height_desktop_px ?? meta?.heroHeightDesktopPx ?? 0);
        const n = Number(raw);
        if (!isFinite(n) || n <= 0) return 0;
        return Math.max(0, Math.round(n));
      }

      function syncHeroVideoCardLayout(card){
        const meta = CARD_META.get(card);
        const isHero = providerOf(meta) === "video" && isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        if (!wrap) return;

        if (!isHero) {
          card.dataset.heroFixedHeight = "false";
          wrap.style.removeProperty("height");
          wrap.style.removeProperty("aspect-ratio");
          return;
        }

        card.dataset.thumbMode = "override";
        const fixedH = heroHeightPxForCard(meta);
        if (fixedH > 0) {
          card.dataset.heroFixedHeight = "true";
          wrap.style.height = fixedH + "px";
          wrap.style.setProperty("aspect-ratio", "auto");
        } else {
          const stableRatio = heroStableAspectForMeta(meta);
          const aspectCss = `${stableRatio.w} / ${stableRatio.h}`;
          card.dataset.heroFixedHeight = "false";
          wrap.style.removeProperty("height");
          wrap.style.setProperty("aspect-ratio", aspectCss);
        }
        applyHeroMediaFit(card);
      }

      function applyVideoCardScale(card){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "video") return;
        if (isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero)) {
          syncHeroVideoCardLayout(card);
          return;
        }

        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        if (!wrap) return;

        const scalePct = clampScalePct(card.dataset.scalePct || meta?.scale, 0, 200);
        const scale = scalePct / 100;

        wrap.style.removeProperty("transform");
        wrap.style.removeProperty("transformOrigin");
        wrap.style.removeProperty("width");
        wrap.style.removeProperty("height");

        if (Math.abs(scale - 1) < 0.001) return;

        const base = wrap.getBoundingClientRect();
        if (!(base.width > 0) || !(base.height > 0)) return;

        wrap.style.width = Math.max(1, Math.round(base.width * scale)) + "px";
        wrap.style.height = Math.max(1, Math.round(base.height * scale)) + "px";
      }

      function syncProductImageFit(card){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "product") {
          if (card && card.dataset) delete card.dataset.productFit;
          return;
        }

        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        const img = card && card.querySelector ? card.querySelector(".img-wrap > img") : null;
        if (!wrap || !img) {
          card.dataset.productFit = "width";
          return;
        }

        const wrapRect = wrap.getBoundingClientRect();
        const wrapW = Number(wrapRect.width || wrap.clientWidth || 0);
        const wrapH = Number(wrapRect.height || wrap.clientHeight || 0);
        const naturalW = Number(img.naturalWidth || 0);
        const naturalH = Number(img.naturalHeight || 0);

        if (!(wrapW > 0) || !(wrapH > 0) || !(naturalW > 0) || !(naturalH > 0)) {
          card.dataset.productFit = "width";
          return;
        }

        const scaledHeightAtFullWidth = wrapW * (naturalH / naturalW);
        card.dataset.productFit = (scaledHeightAtFullWidth > (wrapH + 0.5)) ? "height" : "width";
      }

      function syncProductScaleForRow(card){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "product") return;

        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        const img = card && card.querySelector ? card.querySelector(".img-wrap > img") : null;
        if (!wrap || !img) return;

        const row = card.closest ? card.closest(".thumb-row") : null;
        const forceFitRow = !!(row && row.__fitScaleProducts);
        const allowScaleUp = !(row && row.__allowProductScaleUp === false);
        const scalePct = clampScalePct(card.dataset.scalePct || meta?.scale, 10, 200);

        if (forceFitRow || (!allowScaleUp && scalePct > 100)) {
          wrap.style.transform = "none";
          wrap.style.transformOrigin = "center center";
          img.style.setProperty("--mg-product-image-scale", "1");
          return;
        }

        wrap.style.removeProperty("transform");
        wrap.style.removeProperty("transformOrigin");
        img.style.setProperty("--mg-product-image-scale", String(scalePct / 100));
      }

      function updateRowProductScaleAllowance(rowEl){
        if (!rowEl) return true;
        if (rowEl.__fitScaleProducts) {
          rowEl.__allowProductScaleUp = false;
          return false;
        }

        const thumbs = Array.from(rowEl.querySelectorAll(":scope > .thumb"));
        if (!thumbs.length) {
          rowEl.__allowProductScaleUp = true;
          return true;
        }

        const innerW = Math.max(0, Math.floor(rowContentWidth(rowEl)));
        if (!(innerW > 0)) {
          rowEl.__allowProductScaleUp = true;
          return true;
        }

        const gap = rowGapPx(rowEl);
        let hasScaledProduct = false;
        let total = 0;

        for (const thumb of thumbs) {
          const meta = CARD_META.get(thumb);
          const wrap = thumb && thumb.querySelector ? thumb.querySelector(".img-wrap") : null;
          if (!wrap) continue;

          let width = 0;
          if (providerOf(meta) === "product") {
            const scalePct = clampScalePct(thumb.dataset.scalePct || meta?.scale, 10, 200);
            const baseW = Math.max(
              1,
              Number(wrap.offsetWidth || wrap.clientWidth || wrap.getBoundingClientRect().width || 0)
            );
            if (scalePct > 100) hasScaledProduct = true;
            width = baseW * (scalePct > 100 ? (scalePct / 100) : 1);
          } else {
            width = Math.max(
              1,
              Number(thumb.offsetWidth || thumb.clientWidth || thumb.getBoundingClientRect().width || 0)
            );
          }
          total += width;
        }

        if (thumbs.length > 1) total += gap * (thumbs.length - 1);

        const allow = !hasScaledProduct || (total <= innerW + 1);
        rowEl.__allowProductScaleUp = allow;
        return allow;
      }

      function getProductVisualOverflowBelowWrap(card){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "product") return 0;

        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        const img = card && card.querySelector ? card.querySelector(".img-wrap > img") : null;
        if (!wrap || !img) return 0;

        const wrapRect = wrap.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        if (!(wrapRect.width > 0) || !(wrapRect.height > 0) || !(imgRect.width > 0) || !(imgRect.height > 0)) {
          return 0;
        }

        return Math.max(0, Math.round(imgRect.bottom - wrapRect.bottom));
      }

      function applyMobileFitRowProductOverflow(rowEl, items, preserveConfiguredHeight){
        const isMobileLike = !window.matchMedia("(min-width: 769px)").matches;
        if (!isMobileLike || !rowEl || !items || !items.length) return;

        let maxWrapH = 0;
        let maxOverflow = 0;
        for (const it of items) {
          if (it && it.wrap) {
            const wrapRect = it.wrap.getBoundingClientRect();
            maxWrapH = Math.max(maxWrapH, wrapRect.height || 0);
          }
          maxOverflow = Math.max(maxOverflow, getProductVisualOverflowBelowWrap(it && it.thumb));
        }

        const roundedWrapH = Math.max(0, Math.round(maxWrapH));
        if (!(roundedWrapH > 0)) return;

        const currentThumbH = parseFloat(rowEl.style.getPropertyValue("--row-thumb-h")) || 0;
        const baseThumbH = preserveConfiguredHeight
          ? Math.max(Math.round(currentThumbH), roundedWrapH)
          : roundedWrapH;

        if (!preserveConfiguredHeight) {
          rowEl.classList.add("has-auto-thumb-h");
          rowEl.classList.remove("has-row-height");
        }

        rowEl.style.setProperty("--row-thumb-h", Math.max(1, baseThumbH + Math.round(maxOverflow)) + "px");
        syncRowCardLayout(rowEl, { applyVideoScale: false });
      }

      function syncProductCardBottomGap(card){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "product") {
          card.style.removeProperty("--product-bottom-gap");
          return;
        }

        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        const img = card && card.querySelector ? card.querySelector(".img-wrap > img") : null;
        if (!wrap || !img) {
          card.style.setProperty("--product-bottom-gap", "0px");
          return;
        }

        const bottomOffset = getProductVisualOverflowBelowWrap(card);
        card.style.setProperty("--product-bottom-gap", bottomOffset + "px");
      }

      function syncProductCardBottomSpacing(card){
        const bottom = card && card.querySelector ? card.querySelector("[data-card-bottom]") : null;
        const meta = CARD_META.get(card);
        const row = card && card.closest ? card.closest(".thumb-row") : null;
        const isMobileLike = !window.matchMedia("(min-width: 769px)").matches;
        const shouldOffset = !!(
          bottom &&
          row &&
          row.classList.contains("is-fit") &&
          providerOf(meta) === "product" &&
          isMobileLike
        );

        if (!bottom || !shouldOffset) {
          if (bottom) bottom.style.removeProperty("margin-top");
          return;
        }

        const extra = getProductVisualOverflowBelowWrap(card);
        bottom.style.marginTop = Math.max(0, Math.round(extra)) + "px";
      }

      function syncProductActionLayout(card){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "product") {
          card.style.removeProperty("--product-actions-h");
          return;
        }

        const actionsRow = card && card.querySelector ? card.querySelector("[data-card-actions]") : null;
        if (!actionsRow || !actionsRow.classList.contains("is-visible") || actionsRow.hasAttribute("aria-hidden")) {
          card.style.removeProperty("--product-actions-h");
          return;
        }

        const visibleButtons = Array.from(actionsRow.querySelectorAll("[data-card-primary],[data-card-more]")).filter((el) => !el.hasAttribute("hidden"));
        if (!visibleButtons.length) {
          card.style.removeProperty("--product-actions-h");
          return;
        }

        const isOverlay = String(card.dataset.btnPlace || "") === "overlay";

        for (const btn of visibleButtons) {
          if (btn.dataset && btn.dataset.baseFontPx) btn.style.fontSize = parseFloat(btn.dataset.baseFontPx) + "px";
          else btn.style.removeProperty("font-size");
        }

        if (isOverlay) {
          for (const btn of visibleButtons) {
            const cap = Math.max(24, Math.round(btn.getBoundingClientRect().width || btn.clientWidth || 0) - 2);
            fitTextDown(btn, cap, 9);
          }
        }

        const rowRect = actionsRow.getBoundingClientRect();
        if (rowRect.height > 0) card.style.setProperty("--product-actions-h", Math.round(rowRect.height) + "px");
        else card.style.removeProperty("--product-actions-h");
      }

      function syncProductFitRowText(card, cardWidth = 0){
        const meta = CARD_META.get(card);
        if (providerOf(meta) !== "product") return;

        const row = card && card.closest ? card.closest(".thumb-row") : null;
        const isMobileLike = !window.matchMedia("(min-width: 769px)").matches;
        const shouldFit = !!(row && row.classList.contains("is-fit") && isMobileLike);
        const width = Math.max(
          0,
          Math.round(
            Number(cardWidth || 0) ||
            Number(card && (card.clientWidth || (card.getBoundingClientRect && card.getBoundingClientRect().width)) || 0) ||
            0
          )
        );

        const titleEl = card && card.querySelector ? card.querySelector("[data-card-title]") : null;
        if (titleEl) {
          titleEl.style.maxWidth = "100%";
          const titleAction = titleEl.querySelector ? titleEl.querySelector("[data-title-button]") : null;
          if (titleAction) {
            titleAction.style.maxWidth = "100%";
            if (shouldFit && width > 0) {
              fitTextDown(titleAction, Math.max(24, width - 8), 9);
            } else if (titleAction.dataset && titleAction.dataset.baseFontPx) {
              titleAction.style.fontSize = parseFloat(titleAction.dataset.baseFontPx) + "px";
            } else {
              titleAction.style.removeProperty("font-size");
            }
          }
        }

        const visibleButtons = Array.from(
          card && card.querySelectorAll ? card.querySelectorAll("[data-card-primary],[data-card-more]") : []
        ).filter((el) => !el.hasAttribute("hidden"));

        if (!visibleButtons.length) return;

        const gap = visibleButtons.length > 1 ? 12 : 0;
        const cap = visibleButtons.length > 1
          ? Math.max(24, Math.floor((width - gap) / visibleButtons.length) - 2)
          : Math.max(24, width - 8);

        for (const btn of visibleButtons) {
          if (shouldFit && width > 0) {
            btn.style.maxWidth = cap + "px";
            fitTextDown(btn, cap, 9);
          } else {
            btn.style.removeProperty("max-width");
            if (btn.dataset && btn.dataset.baseFontPx) btn.style.fontSize = parseFloat(btn.dataset.baseFontPx) + "px";
            else btn.style.removeProperty("font-size");
          }
        }
      }

      function syncCardWidthToThumbnail(card){
        syncProductScaleForRow(card);
        syncHeroVideoCardLayout(card);
        const wrap = card && card.querySelector ? card.querySelector(".img-wrap") : null;
        if (!wrap) return;
        const row = card.closest ? card.closest(".thumb-row") : null;
        const preserveFitRowWidth = !!(row && row.__fitScaleProducts);
        const meta = CARD_META.get(card);
        const isSingleStandardProductRow = !!(
          row &&
          row.classList.contains("is-single") &&
          !row.classList.contains("has-row-height") &&
          !row.classList.contains("has-auto-thumb-h") &&
          !row.classList.contains("is-fit") &&
          providerOf(meta) === "product"
        );

        if (isSingleStandardProductRow) {
          wrap.style.width = "min(calc(240px * var(--mg-block-width-pct, 100) / 100), var(--mg-block-max-width-effective, 999999px))";
        } else if (wrap.style.width && wrap.style.width.indexOf("240px * var(--mg-block-width-pct") !== -1) {
          wrap.style.removeProperty("width");
        }

        const wrapRect = wrap.getBoundingClientRect();
        const wrapW = Math.max(1, Math.round(wrapRect.width || 0));
        const wrapH = Math.max(1, Math.round(wrapRect.height || 0));
        const isHeroVideoCard = providerOf(meta) === "video" && isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero);
        if (isHeroVideoCard) {
          card.style.removeProperty("width");
          card.style.setProperty("max-width", "100%");
          card.style.setProperty("min-width", "0");
        } else if (!preserveFitRowWidth) {
          card.style.width = wrapW + "px";
        }

        if (providerOf(meta) === "product") {
          syncProductImageFit(card);
          syncProductCardBottomGap(card);
          syncProductCardBottomSpacing(card);
          syncProductActionLayout(card);
          syncProductFitRowText(card, wrapW);
        } else {
          delete card.dataset.productFit;
          card.style.removeProperty("--product-bottom-gap");
          card.style.removeProperty("--product-actions-h");
          const cardBottom = card && card.querySelector ? card.querySelector("[data-card-bottom]") : null;
          if (cardBottom) cardBottom.style.removeProperty("margin-top");
        }

        if (providerOf(meta) === "video") {
          card.style.setProperty("--thumb-visual-h", wrapH + "px");

          const block = card.querySelector("[data-thumb-block]");
          const blockRect = block ? block.getBoundingClientRect() : null;
          const extraH = blockRect ? Math.max(0, Math.round(blockRect.height - wrapRect.height)) : 0;
          card.style.setProperty("--thumb-extra-h", extraH + "px");

          const titleEl = card.querySelector("[data-card-title]");
          if (titleEl && !titleEl.hidden) {
            titleEl.style.maxWidth = "100%";
            if (card.dataset.titleWrap === "true") {
              if (titleEl.dataset.baseFontPx) titleEl.style.fontSize = parseFloat(titleEl.dataset.baseFontPx) + "px";
              else titleEl.style.removeProperty("font-size");
            } else {
              fitTextDown(titleEl, Math.max(24, wrapW - 4), 10);
            }
          }

          const ctaEl = card.querySelector("[data-cta-badge]");
          if (ctaEl && ctaEl.classList.contains("is-visible")) {
            fitTextDown(ctaEl, Math.max(24, wrapW - 20), 10);
          }
        } else {
          card.style.removeProperty("--thumb-visual-h");
          card.style.removeProperty("--thumb-extra-h");
        }
      }

      function syncRowCardLayout(rowEl, opts = {}){
        if (!rowEl) return;
        const applyVideoScale = opts.applyVideoScale !== false;
        const cards = Array.from(rowEl.querySelectorAll(":scope > .thumb"));
        if (!cards.length) return;

        updateRowProductScaleAllowance(rowEl);

        if (applyVideoScale) {
          for (const card of cards) applyVideoCardScale(card);
        }

        for (const card of cards) syncCardWidthToThumbnail(card);
      }

      function updateRowCentering(rowEl){
        if (!rowEl) return;

        const isDesktop = window.matchMedia("(min-width: 769px)").matches;

        // Always keep single rows centered
        if (rowEl.classList.contains("is-single")) {
          rowEl.classList.add("is-centered");
          return;
        }

        // Fit-row should be centered (no scroll)
        if (rowEl.classList.contains("is-fit")) {
          rowEl.classList.add("is-centered");
          return;
        }

        // Desktop: wrap rows and center them
        if (isDesktop) {
          rowEl.classList.add("is-centered");
          return;
        }

        // Mobile: only center if it fully fits (otherwise allow natural left-start scroll)
        // Note: If element is not yet laid out, scrollWidth/clientWidth can be 0.
        const cw = rowEl.clientWidth || 0;
        const sw = rowEl.scrollWidth || 0;
        const fits = (cw > 0 && sw > 0) ? (sw <= cw + 2) : false;

        rowEl.classList.toggle("is-centered", fits);
      }

      function rememberSingleFitRowState(rowEl){
        if (!rowEl || rowEl.__singleFitStored) return;
        rowEl.__singleFitStored = true;
        rowEl.__singleFitPrevThumbH = rowEl.style.getPropertyValue("--row-thumb-h") || "";
        rowEl.__singleFitPrevHasRowH = rowEl.classList.contains("has-row-height");
        rowEl.__singleFitPrevHasAutoH = rowEl.classList.contains("has-auto-thumb-h");
      }

      function restoreSingleFitRowState(rowEl){
        if (!rowEl || !rowEl.__singleFitStored) return;

        if (rowEl.__singleFitPrevThumbH) rowEl.style.setProperty("--row-thumb-h", rowEl.__singleFitPrevThumbH);
        else rowEl.style.removeProperty("--row-thumb-h");

        rowEl.classList.toggle("has-row-height", !!rowEl.__singleFitPrevHasRowH);
        rowEl.classList.toggle("has-auto-thumb-h", !!rowEl.__singleFitPrevHasAutoH);

        delete rowEl.__singleFitStored;
        delete rowEl.__singleFitPrevThumbH;
        delete rowEl.__singleFitPrevHasRowH;
        delete rowEl.__singleFitPrevHasAutoH;
      }

      function fitSingleVideoRowIfNeeded(rowEl, opts = {}){
        if (!rowEl) return false;

        const thumbs = Array.from(rowEl.querySelectorAll(":scope > .thumb"));
        if (thumbs.length !== 1) return false;

        const thumb = thumbs[0];
        const meta = CARD_META.get(thumb);
        if (providerOf(meta) !== "video") return false;

        const forceFitClass = opts.forceFitClass !== false;
        const onlyIfOverflow = opts.onlyIfOverflow !== false;

        if (isTrueLike(meta?.hero ?? meta?.hero_video ?? meta?.is_hero)) {
          rowEl.classList.toggle("is-fit", !!forceFitClass);
          syncHeroVideoCardLayout(thumb);
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          updateRowCentering(rowEl);
          return true;
        }

        const wrap = thumb.querySelector(".img-wrap");
        if (!wrap) return false;

        const viewportW = Math.max(0, window.innerWidth || document.documentElement.clientWidth || 0);
        const viewportInset = viewportW > 0 ? Math.max(12, Math.round(viewportW * 0.02)) : 12;
        const innerW = Math.max(1, Math.floor(rowContentWidth(rowEl)));
        const targetMaxW = Math.max(1, Math.floor(Math.min(innerW, viewportW > 0 ? (viewportW - (viewportInset * 2)) : innerW)));
        if (!(targetMaxW > 0)) return false;

        let wrapW = 0;
        let wrapH = 0;
        const rect = wrap.getBoundingClientRect();
        wrapW = Number(rect.width || 0);
        wrapH = Number(rect.height || 0);

        if (!(wrapW > 0) || !(wrapH > 0)) {
          const ratio = parseRatioOverride(meta?.ratio_override) || parseImgRatio(String(rowEl.style.getPropertyValue("--row-aspect") || ""));
          if (ratio && ratio.w > 0 && ratio.h > 0) {
            wrapW = ratio.w;
            wrapH = ratio.h;
          }
        }

        if (!(wrapW > 0) || !(wrapH > 0)) {
          wrapW = Number(meta?.__w || 16) || 16;
          wrapH = Number(meta?.__h || 9) || 9;
        }

        if (!(wrapW > 0) || !(wrapH > 0)) return false;
        if (onlyIfOverflow && wrapW <= (targetMaxW + 1)) return false;

        rememberSingleFitRowState(rowEl);

        const aspect = wrapW / wrapH;
        const newW = Math.max(1, targetMaxW);
        const newH = Math.max(1, Math.round(newW / (aspect > 0 ? aspect : (16 / 9))));

        rowEl.classList.toggle("is-fit", !!forceFitClass);
        rowEl.classList.add("has-auto-thumb-h");
        rowEl.classList.remove("has-row-height");
        rowEl.style.setProperty("--row-thumb-h", newH + "px");

        wrap.style.width = newW + "px";
        wrap.style.height = newH + "px";
        thumb.style.width = newW + "px";

        syncRowCardLayout(rowEl, { applyVideoScale: false });
        updateRowCentering(rowEl);
        return true;
      }

      function fitRowIfNeeded(rowEl, wantFit){
        // Reset any previous "fit row" sizing so we can measure true base sizes.
        rowEl.__fitScaleProducts = false;
        rowEl.style.removeProperty("gap");
        rowEl.style.removeProperty("--fit-gap");
        rowEl.style.removeProperty("--fit-scale");
        rowEl.querySelectorAll(":scope > .thumb").forEach((thumb) => {
          thumb.style.removeProperty("transform");
          thumb.style.removeProperty("transformOrigin");
          thumb.style.removeProperty("width");
          thumb.style.removeProperty("height");
          thumb.style.removeProperty("flex-basis");
        });
        rowEl.querySelectorAll(":scope > .thumb [data-thumb-block]").forEach((block) => {
          block.style.removeProperty("transform");
          block.style.removeProperty("transformOrigin");
          block.style.removeProperty("width");
          block.style.removeProperty("height");
          block.style.removeProperty("max-width");
        });
        rowEl.querySelectorAll(":scope > .thumb .img-wrap").forEach((wrap) => {
          wrap.style.removeProperty("width");
          wrap.style.removeProperty("height");
        });

        syncRowCardLayout(rowEl, { applyVideoScale: true });

        if (!wantFit) {
          rowEl.__fitScaleProducts = false;
          if (fitSingleVideoRowIfNeeded(rowEl, { forceFitClass: false, onlyIfOverflow: true })) return;
          restoreSingleFitRowState(rowEl);
          rowEl.classList.remove("is-fit");
          updateRowCentering(rowEl);
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          return;
        }

        const thumbs = Array.from(rowEl.querySelectorAll(":scope > .thumb"));
        if (thumbs.length === 1) {
          rowEl.__fitScaleProducts = false;
          if (fitSingleVideoRowIfNeeded(rowEl, { forceFitClass: true, onlyIfOverflow: false })) return;

          restoreSingleFitRowState(rowEl);
          rowEl.classList.remove("is-fit");
          updateRowCentering(rowEl);
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          return;
        }

        restoreSingleFitRowState(rowEl);

        if (thumbs.length <= 1) {
          rowEl.__fitScaleProducts = false;
          rowEl.classList.remove("is-fit");
          updateRowCentering(rowEl);
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          return;
        }

        // Remember prior slot-track sizing so we can restore if we decide not to force-fit.
        const prevThumbH = rowEl.style.getPropertyValue("--row-thumb-h");
        const prevHasRowH = rowEl.classList.contains("has-row-height");
        const prevHasAutoH = rowEl.classList.contains("has-auto-thumb-h");

        const csRow = getComputedStyle(rowEl);
        const padL = parseFloat(csRow.paddingLeft) || 0;
        const padR = parseFloat(csRow.paddingRight) || 0;
        const innerW = Math.max(0, rowEl.clientWidth - padL - padR);
        if (innerW <= 0) {
          rowEl.__fitScaleProducts = false;
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          return;
        }

        const gap = parseFloat(csRow.columnGap || csRow.gap) || 0;

        const items = [];
        for (const thumb of thumbs) {
          const wrap = thumb.querySelector(".img-wrap");
          const block = thumb.querySelector("[data-thumb-block]");
          if (!wrap) continue;
          const meta = CARD_META.get(thumb);
          const kind = providerOf(meta);
          const wrapRect = wrap.getBoundingClientRect();
          const thumbRect = thumb.getBoundingClientRect();
          items.push({
            thumb,
            block,
            wrap,
            meta,
            kind,
            w: Math.max(1, wrapRect.width),
            h: Math.max(1, wrapRect.height),
            thumbW: Math.max(1, thumbRect.width || wrapRect.width),
            thumbH: Math.max(1, thumbRect.height || wrapRect.height),
          });
        }

        if (items.length <= 1) {
          rowEl.__fitScaleProducts = false;
          rowEl.classList.remove("is-fit");
          updateRowCentering(rowEl);
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          return;
        }

        const sumW = items.reduce((acc, it) => acc + Math.max(it.thumbW || 0, it.w || 0), 0);
        const total = sumW + gap * (items.length - 1);

        // Fits already â€” no scaling needed.
        if (total <= innerW + 1) {
          rowEl.__fitScaleProducts = false;
          rowEl.classList.add("is-fit"); // keep "no scroll" behavior consistent
          updateRowCentering(rowEl);
          syncRowCardLayout(rowEl, { applyVideoScale: false });
          applyMobileFitRowProductOverflow(rowEl, items, !!prevHasRowH);
          return;
        }

        let factor = (innerW - 1) / total;
        factor = Math.max(0.35, Math.min(1, factor));
        let appliedFactor = factor;
        const isMobileLike = !window.matchMedia("(min-width: 769px)").matches;

        const applyFactor = (f) => {
          appliedFactor = f;
          const newGap = Math.max(8, Math.floor(gap * f));
          rowEl.style.gap = newGap + "px";
          for (const it of items) {
            const thumbNW = Math.max(1, Math.floor((Math.max(it.thumbW || 0, it.w || 0)) * f));
            const thumbNH = Math.max(1, Math.floor((Math.max(it.thumbH || 0, it.h || 0)) * f));
            const wrapNW = Math.max(1, Math.floor((it.w || 0) * f));
            const wrapNH = Math.max(1, Math.floor((it.h || 0) * f));
            const preserveMobileProductBottom = isMobileLike && it.kind === "product";
            it.thumb.style.width = thumbNW + "px";
            it.thumb.style.flexBasis = thumbNW + "px";
            if (preserveMobileProductBottom) {
              it.thumb.style.removeProperty("height");
              it.wrap.style.width = wrapNW + "px";
              it.wrap.style.height = wrapNH + "px";
              syncProductFitRowText(it.thumb, thumbNW);
            } else {
              it.thumb.style.height = thumbNH + "px";
              it.wrap.style.removeProperty("width");
              it.wrap.style.removeProperty("height");
            }
            if (it.block) {
              if (preserveMobileProductBottom) {
                it.block.style.removeProperty("width");
                it.block.style.removeProperty("height");
                it.block.style.removeProperty("max-width");
                it.block.style.removeProperty("transform");
                it.block.style.removeProperty("transformOrigin");
              } else {
                it.block.style.width = Math.max(it.thumbW || 0, it.w || 0) + "px";
                it.block.style.height = Math.max(it.thumbH || 0, it.h || 0) + "px";
                it.block.style.maxWidth = "none";
                it.block.style.transform = `scale(${f})`;
                it.block.style.transformOrigin = "top center";
              }
            }
          }
        };

        applyFactor(factor);

        // One correction pass for rounding / long titles.
        if (rowEl.scrollWidth > rowEl.clientWidth + 2) {
          const f2 = appliedFactor * ((rowEl.clientWidth - 1) / rowEl.scrollWidth);
          if (f2 < appliedFactor) applyFactor(f2);
        }

        const fitsNow = rowEl.scrollWidth <= rowEl.clientWidth + 2;

        // Final fallback: if it still overflows, don't force-fit (avoid cut off).
        if (!fitsNow) {
          rowEl.__fitScaleProducts = false;
          rowEl.style.removeProperty("gap");
          rowEl.querySelectorAll(":scope > .thumb").forEach((thumb) => {
            thumb.style.removeProperty("width");
            thumb.style.removeProperty("height");
            thumb.style.removeProperty("flex-basis");
          });
          rowEl.querySelectorAll(":scope > .thumb [data-thumb-block]").forEach((block) => {
            block.style.removeProperty("transform");
            block.style.removeProperty("transformOrigin");
            block.style.removeProperty("width");
            block.style.removeProperty("height");
            block.style.removeProperty("max-width");
          });
          rowEl.querySelectorAll(":scope > .thumb .img-wrap").forEach((wrap) => {
            wrap.style.removeProperty("width");
            wrap.style.removeProperty("height");
          });
          rowEl.classList.remove("is-fit");

          // Restore prior slot-track sizing
          if (prevThumbH) rowEl.style.setProperty("--row-thumb-h", prevThumbH);
          else rowEl.style.removeProperty("--row-thumb-h");
          rowEl.classList.toggle("has-row-height", !!prevHasRowH);
          rowEl.classList.toggle("has-auto-thumb-h", !!prevHasAutoH);

          syncRowCardLayout(rowEl, { applyVideoScale: true });
        } else {
          rowEl.__fitScaleProducts = true;
          rowEl.classList.add("is-fit");

          // âœ… IMPORTANT:
          // When we shrink by setting wrap width/height, the thumb-slot track height must also shrink,
          // otherwise the titles/actions stay where they would have been pre-fit (big vertical gaps).
          if (prevHasRowH) {
            const oldH = parseFloat(prevThumbH) || 0;
            if (oldH > 0) {
              const scaled = Math.max(1, Math.round(oldH * appliedFactor));
              rowEl.style.setProperty("--row-thumb-h", scaled + "px");
            }
          } else {
            let maxH = 0;
            for (const it of items) {
              const r = it.wrap.getBoundingClientRect();
              maxH = Math.max(maxH, r.height || 0);
            }
            if (maxH > 0) {
              rowEl.classList.add("has-auto-thumb-h");
              rowEl.classList.remove("has-row-height");
              rowEl.style.setProperty("--row-thumb-h", Math.round(maxH) + "px");
            }
          }

          syncRowCardLayout(rowEl, { applyVideoScale: false });
          applyMobileFitRowProductOverflow(rowEl, items, !!prevHasRowH);
        }

        updateRowCentering(rowEl);
      }

      function attachRowFitObserver(rowEl, wantFit){
        if (!wantFit) return () => {};
        const ro = new ResizeObserver(() => fitRowIfNeeded(rowEl, true));
        ro.observe(rowEl);
        return () => { try { ro.disconnect(); } catch {} };
      }

      function refreshRowLayoutForVisibility(rowWrap) {
        const rowEl = rowWrap && rowWrap.querySelector ? rowWrap.querySelector('.thumb-row') : null;
        if (!rowEl) return;
        const wantFitRow = !!rowEl.__wantFitRow;
        const rowHeight = Number(rowEl.__rowHeight || 0) || 0;
        requestAnimationFrame(() => {
          syncRowCardLayout(rowEl, { applyVideoScale: true });
          if (!rowHeight && !rowEl.classList.contains('is-single')) {
            const h = computeAutoRowHeight(rowEl);
            applyAutoThumbH(rowEl, h);
            syncRowCardLayout(rowEl, { applyVideoScale: true });
          }
          fitRowIfNeeded(rowEl, wantFitRow);
        });
      }

      function refreshRowLayoutsWithin(container) {
        if (!container || !container.querySelectorAll) return;
        const rows = Array.from(container.querySelectorAll('.video-row')).filter((rowWrap) => {
          try { return rowWrap.closest('.vimeo-snippet') === root; }
          catch { return true; }
        });
        for (const rowWrap of rows) refreshRowLayoutForVisibility(rowWrap);
      }

      function isInsideClosedAccordionPanel(el) {
        if (!el || !el.closest) return false;
        const panel = el.closest('.accordion-row__panel');
        const item = el.closest('.accordion-row');
        if (!panel || !item) return false;
        return !!panel.hidden || !item.classList.contains('is-open');
      }

      function resetAccordionPanelVideoCards(panel) {
        if (!panel || !panel.querySelectorAll) return;
        const cards = Array.from(panel.querySelectorAll('.thumb[data-kind="video"]'));
        for (const card of cards) {
          try {
            delete card.dataset.videoPrime;
            delete card.dataset.heroSequencePending;
            delete card.dataset.heroSequenceToken;
          } catch {}
          try { clearHeroPreview(card); } catch {}
          try {
            const img = card.querySelector('.img-wrap > img');
            if (img) {
              img.classList.remove('is-hidden');
              img.style.removeProperty('opacity');
              img.style.removeProperty('visibility');
            }
          } catch {}
          try {
            const meta = CARD_META.get(card);
            if (meta) applyVideoPlaceholderThumb(card, meta);
          } catch {}
        }
      }

      function copyAccordionDescriptor(fromEl, toEl) {
        if (!fromEl || !toEl || !fromEl.dataset || !toEl.dataset) return;
        if (fromEl.dataset.accordion === 'true') toEl.dataset.accordion = 'true';
        if (fromEl.dataset.accordionGroup) toEl.dataset.accordionGroup = fromEl.dataset.accordionGroup;
        if (fromEl.dataset.hostAccordionGroup) toEl.dataset.hostAccordionGroup = fromEl.dataset.hostAccordionGroup;
        if (fromEl.dataset.accordionStartOpen) toEl.dataset.accordionStartOpen = fromEl.dataset.accordionStartOpen;
        if (fromEl.dataset.accordionAllowMultiple) toEl.dataset.accordionAllowMultiple = fromEl.dataset.accordionAllowMultiple;
        if (fromEl.dataset.accordionHideTitleWhenOpen) toEl.dataset.accordionHideTitleWhenOpen = fromEl.dataset.accordionHideTitleWhenOpen;
        if (fromEl.dataset.accordionHideSubheadingWhenOpen) toEl.dataset.accordionHideSubheadingWhenOpen = fromEl.dataset.accordionHideSubheadingWhenOpen;
        if (fromEl.dataset.accordionHideSubheadingWhenClosed) toEl.dataset.accordionHideSubheadingWhenClosed = fromEl.dataset.accordionHideSubheadingWhenClosed;
        if (fromEl.dataset.accordionAlwaysOpen) toEl.dataset.accordionAlwaysOpen = fromEl.dataset.accordionAlwaysOpen;
        if (fromEl.dataset.bulletPoint) toEl.dataset.bulletPoint = fromEl.dataset.bulletPoint;
        if (fromEl.dataset.accordionVariant) toEl.dataset.accordionVariant = fromEl.dataset.accordionVariant;
        if (fromEl.dataset.accordionMoreEnabled) toEl.dataset.accordionMoreEnabled = fromEl.dataset.accordionMoreEnabled;
        if (fromEl.dataset.accordionMoreText) toEl.dataset.accordionMoreText = fromEl.dataset.accordionMoreText;
        if (fromEl.dataset.accordionPopup) toEl.dataset.accordionPopup = fromEl.dataset.accordionPopup;
        if (fromEl.dataset.accordionBulletPaddingTop) toEl.dataset.accordionBulletPaddingTop = fromEl.dataset.accordionBulletPaddingTop;
        if (fromEl.dataset.accordionBulletPaddingBottom) toEl.dataset.accordionBulletPaddingBottom = fromEl.dataset.accordionBulletPaddingBottom;
        if (fromEl.dataset.accordionBgColor) toEl.dataset.accordionBgColor = fromEl.dataset.accordionBgColor;
        if (fromEl.dataset.accordionShadowPct) toEl.dataset.accordionShadowPct = fromEl.dataset.accordionShadowPct;
        if (fromEl.dataset.accordionShadowStyle) toEl.dataset.accordionShadowStyle = fromEl.dataset.accordionShadowStyle;
        if (fromEl.dataset.accordionRowImgRatio) toEl.dataset.accordionRowImgRatio = fromEl.dataset.accordionRowImgRatio;
        if (fromEl.dataset.accordionRowHeight) toEl.dataset.accordionRowHeight = fromEl.dataset.accordionRowHeight;
        if (fromEl.dataset.accordionFitRow) toEl.dataset.accordionFitRow = fromEl.dataset.accordionFitRow;
        toEl.__accordionTitleHtml = String(fromEl.__accordionTitleHtml || '');
        toEl.__accordionSubheadingHtml = String(fromEl.__accordionSubheadingHtml || '');
        toEl.__accordionContentHtml = String(fromEl.__accordionContentHtml || '');
        toEl.__accordionVariant = fromEl.dataset.bulletPoint === 'true' ? 'bullet' : String(fromEl.dataset.accordionVariant || fromEl.__accordionVariant || '');
        toEl.__accordionMoreEnabled = fromEl.dataset.accordionMoreEnabled !== 'false' && fromEl.__accordionMoreEnabled !== false;
        toEl.__accordionMoreText = String(fromEl.dataset.accordionMoreText || fromEl.__accordionMoreText || '');
        toEl.__accordionPopup = fromEl.dataset.accordionPopup === 'true' || !!fromEl.__accordionPopup;
        toEl.__accordionBulletPaddingTop = Number(fromEl.dataset.accordionBulletPaddingTop || fromEl.__accordionBulletPaddingTop || 2) || 0;
        toEl.__accordionBulletPaddingBottom = Number(fromEl.dataset.accordionBulletPaddingBottom || fromEl.__accordionBulletPaddingBottom || 2) || 0;
        toEl.__accordionTitleCenter = !!fromEl.__accordionTitleCenter;
        toEl.__accordionBgColor = String(fromEl.dataset.accordionBgColor || fromEl.__accordionBgColor || '');
        toEl.__accordionShadowPct = normalizeAccordionShadowPct(fromEl.dataset.accordionShadowPct || fromEl.__accordionShadowPct, 8);
        toEl.__accordionShadowStyle = normalizeAccordionShadowStyle(fromEl.dataset.accordionShadowStyle || fromEl.__accordionShadowStyle, 'raised');
        toEl.__accordionRowImgRatio = String(fromEl.dataset.accordionRowImgRatio || fromEl.__accordionRowImgRatio || '');
        toEl.__accordionRowHeight = Number(fromEl.dataset.accordionRowHeight || fromEl.__accordionRowHeight || 0) || 0;
        toEl.__accordionFitRow = fromEl.dataset.accordionFitRow === 'true' || !!fromEl.__accordionFitRow;
        toEl.__accordionHostGroup = String(fromEl.dataset.hostAccordionGroup || fromEl.__accordionHostGroup || '');
      }

      function applyRowToggleBorderStyle(btn, rawStyle) {
        if (!btn) return;
        btn.dataset.rowToggleBorderStyle = normalizeRowToggleBorderStyle(rawStyle, 'solid');
      }

      function applyRowToggleTheme(btn) {
        if (!btn) return;
        applyThemeShopButtonLike(btn, collapseWhitespace(btn.textContent || ''));
        applyRowToggleBorderStyle(btn, btn.dataset.rowToggleBorderStyle || 'solid');
      }

      function readAccordionDescriptor(el) {
        if (!el || !el.dataset) return null;
        const isAccordion = el.dataset.accordion === 'true';
        const groupKey = String(el.dataset.accordionGroup || '').trim();
        if (!isAccordion && !groupKey) return null;
        return {
          isAccordion,
          groupKey,
          titleHtml: String(el.__accordionTitleHtml || '').trim(),
          subHtml: String(el.__accordionSubheadingHtml || '').trim(),
          contentHtml: String(el.__accordionContentHtml || '').trim(),
          variant: el.dataset.bulletPoint === 'true' ? 'bullet' : String(el.dataset.accordionVariant || el.__accordionVariant || '').trim().toLowerCase(),
          moreEnabled: el.dataset.accordionMoreEnabled !== 'false' && el.__accordionMoreEnabled !== false,
          moreText: String(el.dataset.accordionMoreText || el.__accordionMoreText || 'more').trim() || 'more',
          openAsPopup: el.dataset.accordionPopup === 'true' || !!el.__accordionPopup,
          bulletPaddingTop: Math.max(0, Number(el.dataset.accordionBulletPaddingTop || el.__accordionBulletPaddingTop || 2) || 0),
          bulletPaddingBottom: Math.max(0, Number(el.dataset.accordionBulletPaddingBottom || el.__accordionBulletPaddingBottom || 2) || 0),
          titleCenter: !!el.__accordionTitleCenter,
          allowMultiple: el.dataset.accordionAllowMultiple === 'true',
          startOpen: el.dataset.accordionStartOpen === 'true',
          hideTitleWhenOpen: el.dataset.accordionHideTitleWhenOpen === 'true',
          hideSubheadingWhenOpen: el.dataset.accordionHideSubheadingWhenOpen === 'true',
          hideSubheadingWhenClosed: el.dataset.accordionHideSubheadingWhenClosed === 'true',
          alwaysOpen: el.dataset.accordionAlwaysOpen === 'true',
          bgColor: safeCssColor(el.dataset.accordionBgColor || el.__accordionBgColor, 'rgba(255,255,255,.72)'),
          shadowPct: normalizeAccordionShadowPct(el.dataset.accordionShadowPct || el.__accordionShadowPct, 8),
          shadowStyle: normalizeAccordionShadowStyle(el.dataset.accordionShadowStyle || el.__accordionShadowStyle, 'raised'),
          maxWidthPx: normalizePresetButtonMaxWidthPx(el.dataset.accordionMaxWidthPx || el.__accordionMaxWidthPx, 0),
          panelWidthOverridePercent: normalizeWidthOverridePercent(el.dataset.accordionPanelWidthOverridePercent || el.__accordionPanelWidthOverridePercent, 0),
          panelMaxWidthOverridePx: normalizePresetButtonMaxWidthPx(el.dataset.accordionPanelMaxWidthOverridePx || el.__accordionPanelMaxWidthOverridePx, 0),
          rowImgRatio: String(el.dataset.accordionRowImgRatio || el.__accordionRowImgRatio || '').trim(),
          rowHeight: Number(el.dataset.accordionRowHeight || el.__accordionRowHeight || 0) || 0,
          fitRow: el.dataset.accordionFitRow === 'true' || !!el.__accordionFitRow
        };
      }

      function readAccordionHostGroup(el) {
        if (!el || !el.dataset) return '';
        return String(el.dataset.hostAccordionGroup || el.__accordionHostGroup || '').trim();
      }

      function hasAccordionAffinity(el) {
        return !!readAccordionDescriptor(el) || !!readAccordionHostGroup(el);
      }

      function rowBelongsToAccordionItem(el, ownerGroupKey) {
        const meta = readAccordionDescriptor(el);
        const hostGroup = readAccordionHostGroup(el);
        if (!ownerGroupKey) return !!meta;
        if (hostGroup && hostGroup === ownerGroupKey) return true;
        if (meta && meta.groupKey === ownerGroupKey) return true;
        return false;
      }

      function readBulletDescriptor(el) {
        if (!el || !el.dataset || el.dataset.bulletMarker !== 'true') return null;
        const groupKey = String(el.dataset.bulletGroup || '').trim();
        if (!groupKey) return null;
        return {
          groupKey,
          textHtml: String(el.__bulletTextHtml || '').trim(),
          contentHtml: String(el.__bulletContentHtml || '').trim(),
          presetSet: String(el.__bulletPresetSet || '').trim(),
          moreEnabled: el.__bulletMoreEnabled !== false,
          moreText: String(el.__bulletMoreText || 'more').trim() || 'more',
          openAsPopup: !!el.__bulletPopup,
          startOpen: el.dataset.accordionStartOpen === 'true',
          paddingTop: Math.max(0, Number(el.__bulletPaddingTop || 2) || 0),
          paddingBottom: Math.max(0, Number(el.__bulletPaddingBottom || 2) || 0),
          widthDesktopPercent: normalizeWidthOverridePercent(el.__bulletWidthDesktopPercent, 100) || 100,
          widthMobilePercent: normalizeWidthOverridePercent(el.__bulletWidthMobilePercent, 100) || 100,
          maxWidthPx: normalizePresetButtonMaxWidthPx(el.__bulletMaxWidthPx, 0)
        };
      }

      function readBulletHostGroup(el) {
        if (!el || !el.dataset) return '';
        return String(el.dataset.hostBulletGroup || '').trim();
      }

      function groupBulletRows(container) {
        if (!container) return;
        const children = Array.from(container.children);
        const buildBulletItem = (meta, members) => {
          const item = document.createElement('div');
          item.className = 'bullet-point-item';
          item.style.setProperty('--bullet-padding-top', `${meta.paddingTop}px`);
          item.style.setProperty('--bullet-padding-bottom', `${meta.paddingBottom}px`);

          const line = document.createElement('div');
          line.className = 'bullet-point-item__line';
          const text = document.createElement('span');
          text.className = 'bullet-point-item__text rte';
          text.innerHTML = sanitizeHtml(meta.textHtml || 'Bullet point');
          line.appendChild(text);

          const panel = document.createElement('div');
          panel.className = 'bullet-point-item__panel';
          if (meta.contentHtml) {
            const content = document.createElement('div');
            content.className = 'bullet-point-item__content rte';
            content.innerHTML = sanitizeHtml(meta.contentHtml);
            panel.appendChild(content);
          }
          if (meta.presetSet) {
            const presetHost = document.createElement('div');
            presetHost.className = 'row-inline-preset-host bullet-point-item__preset';
            presetHost.setAttribute('data-inline-preset-host', meta.presetSet);
            presetHost.style.width = '100%';
            panel.appendChild(presetHost);
          }
          for (const member of members.slice(1)) {
            try { member.removeAttribute('data-host-bullet-group'); } catch {}
            panel.appendChild(member);
          }

          let btn = null;
          if (meta.moreEnabled) {
            btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'bullet-point-item__more';
            btn.textContent = meta.moreText;
            btn.setAttribute('aria-expanded', 'false');
            line.appendChild(document.createTextNode(' '));
            line.appendChild(btn);
          }

          item.appendChild(line);
          item.appendChild(panel);

          const setOpen = (open) => {
            const nextOpen = meta.moreEnabled ? !!open : true;
            item.classList.toggle('is-open', nextOpen);
            panel.hidden = !nextOpen;
            if (btn) btn.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
            if (nextOpen) {
              try { markLazyCustomRowsReady(panel); } catch {}
              try { mountInlinePresetInstances(panel); } catch {}
              try { refreshRowLayoutsWithin(panel); } catch {}
              try { primeAllVideoThumbsThenSwapOverrides(panel, { visibleOnly:true }); } catch {}
            }
          };

          if (btn) {
            btn.addEventListener('click', () => {
              if (meta.openAsPopup && openBulletPopup) {
                if (meta.presetSet && typeof openPresetSet === "function") {
                  openPresetSet(meta.presetSet, { openFirstVideo: true });
                  return;
                }
                openBulletPopup(panel, { titleHtml: meta.textHtml });
                return;
              }
              setOpen(!item.classList.contains('is-open'));
            });
          }
          setOpen(meta.startOpen || !meta.moreEnabled);
          return item;
        };

        for (let i = 0; i < children.length; ) {
          const marker = children[i];
          const meta = readBulletDescriptor(marker);
          if (!meta) { i += 1; continue; }

          const list = document.createElement('div');
          list.className = 'bullet-point-list';
          list.style.setProperty('--bullet-list-width-desktop', `${meta.widthDesktopPercent}%`);
          list.style.setProperty('--bullet-list-width-mobile', `${meta.widthMobilePercent}%`);
          list.style.setProperty('--bullet-list-max-width', meta.maxWidthPx > 0 ? `${meta.maxWidthPx}px` : '999999px');
          container.insertBefore(list, marker);

          let j = i;
          while (j < children.length) {
            const itemMarker = children[j];
            const itemMeta = readBulletDescriptor(itemMarker);
            if (!itemMeta) break;
            const members = [itemMarker];
            j += 1;
            while (j < children.length) {
              const next = children[j];
              if (readBulletDescriptor(next)) break;
              if (readBulletHostGroup(next) !== itemMeta.groupKey) break;
              members.push(next);
              j += 1;
            }
            list.appendChild(buildBulletItem(itemMeta, members));
            try { itemMarker.remove(); } catch {}
          }
          i = j;
        }
      }

      function groupBulletRowsRecursive(rootNode) {
        if (!rootNode) return;
        groupBulletRows(rootNode);
        Array.from(rootNode.querySelectorAll('.accordion-row__panel, .row-group__body')).forEach((panel) => {
          groupBulletRows(panel);
        });
      }

      function parseBulletPercent(value, fallback) {
        const raw = String(value || '').trim();
        if (!raw) return fallback;
        const n = Number(raw.replace('%', ''));
        if (!Number.isFinite(n) || n <= 0) return fallback;
        return Math.max(1, Math.min(100, n));
      }

      function applyBulletPointBlockWidths(scope) {
        const rootNode = scope && scope.querySelectorAll ? scope : rowsRoot;
        if (!rootNode) return;
        const containers = [rootNode].concat(Array.from(rootNode.querySelectorAll('.accordion-row__panel, .row-group__body')));
        containers.forEach((container) => {
          const children = Array.from(container.children || []);
          let activeLists = null;
          const flush = () => {
            const lists = activeLists || [];
            activeLists = null;
            if (!lists.length) return;

            let maxLineWidth = 0;
            lists.forEach((list) => {
              list.style.removeProperty('--bullet-list-measured-width');
              list.querySelectorAll('.bullet-point-item__line').forEach((line) => {
                maxLineWidth = Math.max(maxLineWidth, Math.ceil(line.scrollWidth || line.getBoundingClientRect().width || 0));
              });
            });
            if (!maxLineWidth) return;

            const sample = lists[0];
            const parentWidth = Math.max(0, (sample.parentElement && sample.parentElement.clientWidth) || container.clientWidth || root.clientWidth || 0);
            const styles = getComputedStyle(sample);
            const isDesktop = (window.matchMedia && window.matchMedia('(min-width: 769px)').matches) || window.innerWidth >= 769;
            const pct = parseBulletPercent(styles.getPropertyValue(isDesktop ? '--bullet-list-width-desktop' : '--bullet-list-width-mobile'), 100);
            const maxWidthRaw = Number(String(styles.getPropertyValue('--bullet-list-max-width') || '').replace('px', '').trim());
            const configuredMax = Number.isFinite(maxWidthRaw) && maxWidthRaw > 0 ? maxWidthRaw : 999999;
            const cap = Math.max(1, Math.min(parentWidth * pct / 100, configuredMax));
            const measured = Math.min(Math.max(1, maxLineWidth), cap);
            lists.forEach((list) => {
              list.classList.add('is-measured-block');
              list.style.setProperty('--bullet-list-measured-width', measured + 'px');
            });
          };

          children.forEach((child) => {
            if (child.querySelector && child.querySelector('.bullet-points-heading')) {
              flush();
              activeLists = [];
              return;
            }
            const list = child.matches && child.matches('.bullet-point-list')
              ? child
              : child.querySelector ? child.querySelector('.bullet-point-list') : null;
            if (activeLists && list) activeLists.push(list);
          });
          flush();
        });
      }

      function groupCollapseRows(container) {
        if (!container) return;
        const children = Array.from(container.children);

        // Allow Custom liquid rows placed immediately after a collapsed row to remain part
        // of that collapsed content, including inside Accordion panels.
        for (let k = 0; k < children.length; k++) {
          const el = children[k];
          if (!el || !el.classList || !el.classList.contains('video-row')) continue;
          const ownKey = el.dataset ? String(el.dataset.collapseGroup || '').trim() : '';
          if (ownKey) continue;
          const hasCustomOnly = !!el.querySelector('.row-custom-html') && !el.querySelector('.thumb-row');
          if (!hasCustomOnly) continue;
          const prev = children[k - 1];
          const prevKey = prev && prev.dataset ? String(prev.dataset.collapseGroup || '').trim() : '';
          if (!prevKey) continue;
          el.dataset.collapseGroup = prevKey;
          el.dataset.collapseMore = String((prev.dataset && prev.dataset.collapseMore) || 'Show more');
          el.dataset.collapseLess = String((prev.dataset && prev.dataset.collapseLess) || 'Show less');
          el.dataset.rowToggleBorderStyle = String((prev.dataset && prev.dataset.rowToggleBorderStyle) || 'solid');
        }

        let collapseGroupCounter = 0;
        for (let i = 0; i < children.length; ) {
          const first = children[i];
          const groupKey = first && first.dataset ? String(first.dataset.collapseGroup || '').trim() : '';
          if (!groupKey) { i += 1; continue; }

          const members = [];
          let j = i;
          while (j < children.length) {
            const el = children[j];
            const key = el && el.dataset ? String(el.dataset.collapseGroup || '').trim() : '';
            if (key !== groupKey) break;
            members.push(el);
            j += 1;
          }
          if (!members.length) { i = j; continue; }

          const moreText = String(members[0].dataset.collapseMore || 'Show more').trim() || 'Show more';
          const lessText = String(members[0].dataset.collapseLess || 'Show less').trim() || 'Show less';
          const rowToggleBorderStyle = normalizeRowToggleBorderStyle(members[0].dataset.rowToggleBorderStyle || 'solid', 'solid');
          const bodyId = `mg-group-${SET_SAFE}-${collapseGroupCounter++}`;

          const groupWrap = document.createElement('div');
          groupWrap.className = 'row-group is-collapsible is-collapsed';
          groupWrap.setAttribute('data-collapse-group-wrap', groupKey);
          copyAccordionDescriptor(members[0], groupWrap);

          const body = document.createElement('div');
          body.className = 'row-group__body';
          body.id = bodyId;

          const toggleBar = document.createElement('div');
          toggleBar.className = 'row-toggle';

          const toggleBtn = document.createElement('button');
          toggleBtn.type = 'button';
          toggleBtn.className = 'row-toggle-btn';
          toggleBtn.dataset.rowToggleBorderStyle = rowToggleBorderStyle;
          toggleBtn.textContent = moreText;
          toggleBtn.setAttribute('aria-expanded', 'false');
          toggleBtn.setAttribute('aria-controls', bodyId);
          applyRowToggleTheme(toggleBtn);

          toggleBar.appendChild(toggleBtn);
          groupWrap.appendChild(body);
          groupWrap.appendChild(toggleBar);
          container.insertBefore(groupWrap, members[0]);
          for (const member of members) body.appendChild(member);

          toggleBtn.addEventListener('click', () => {
            const collapsedNow = groupWrap.classList.toggle('is-collapsed');
            const expanded = !collapsedNow;
            toggleBtn.textContent = expanded ? lessText : moreText;
            toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            applyRowToggleTheme(toggleBtn);
            if (expanded) refreshRowLayoutsWithin(body);
          });

          i = j;
        }
      }

      function applyAccordionItemVisuals(item) {
        if (!item) return;
        const bg = safeCssColor(item.dataset.accordionBgColor || item.__accordionBgColor, 'rgba(255,255,255,.72)');
        const shadowPct = normalizeAccordionShadowPct(item.dataset.accordionShadowPct || item.__accordionShadowPct, 8);
        const shadowStyle = normalizeAccordionShadowStyle(item.dataset.accordionShadowStyle || item.__accordionShadowStyle, 'raised');
        const shadowCss = buildAccordionShadowCss(shadowStyle, shadowPct);
        const maxWidthPx = normalizePresetButtonMaxWidthPx(item.dataset.accordionMaxWidthPx || item.__accordionMaxWidthPx, 0);
        const panelWidthOverridePercent = normalizeWidthOverridePercent(item.dataset.accordionPanelWidthOverridePercent || item.__accordionPanelWidthOverridePercent, 0);
        const panelMaxWidthOverridePx = normalizePresetButtonMaxWidthPx(item.dataset.accordionPanelMaxWidthOverridePx || item.__accordionPanelMaxWidthOverridePx, 0);
        const hasPanelOverride = panelWidthOverridePercent > 0 || panelMaxWidthOverridePx > 0;
        item.style.setProperty('--accordion-bg', bg);
        item.style.setProperty('--accordion-shadow-alpha', String(Math.max(0, Math.min(1, shadowPct / 100))));
        item.style.setProperty('--accordion-shadow-style', shadowStyle);
        item.style.setProperty('--accordion-shadow', shadowCss);
        item.style.background = bg;
        item.style.backgroundColor = bg;
        item.style.boxShadow = shadowCss;
        item.style.maxWidth = maxWidthPx > 0 ? (maxWidthPx + 'px') : '100%';
        item.style.overflow = hasPanelOverride ? 'visible' : '';
        item.style.marginLeft = 'auto';
        item.style.marginRight = 'auto';
        const btn = item.querySelector('.accordion-row__toggle');
        const panel = item.querySelector('.accordion-row__panel');
        if (btn) {
          btn.style.background = bg;
          btn.style.backgroundColor = bg;
        }
        if (panel) {
          panel.style.background = bg;
          panel.style.backgroundColor = bg;
          panel.dataset.accordionPanelWidthOverridePercent = String(panelWidthOverridePercent || 0);
          panel.dataset.accordionPanelMaxWidthOverridePx = String(panelMaxWidthOverridePx || 0);
          if (hasPanelOverride) {
            const panelWidthCss = panelWidthOverridePercent > 0 ? (`calc(100vw * ${panelWidthOverridePercent} / 100)`) : 'calc(100vw - 24px)';
            panel.style.width = panelMaxWidthOverridePx > 0 ? `min(${panelWidthCss}, ${panelMaxWidthOverridePx}px)` : panelWidthCss;
            panel.style.maxWidth = 'calc(100vw - 24px)';
            panel.style.marginLeft = '50%';
            panel.style.transform = 'translateX(-50%)';
          } else {
            panel.style.width = '';
            panel.style.maxWidth = '';
            panel.style.marginLeft = '';
            panel.style.transform = '';
          }
        }
      }

      function applyAccordionPanelRowOverrides(panel, leaderMeta) {
        if (!panel || !leaderMeta) return;
        const leaderRatioObj = parseImgRatio(leaderMeta.rowImgRatio || '');
        if (!leaderRatioObj) return;

        const rows = Array.from(panel.querySelectorAll('.thumb-row')).filter((rowEl) => {
          try {
            const ownerPanel = rowEl.closest('.accordion-row__panel');
            if (ownerPanel !== panel) return false;
            const rowWrap = rowEl.closest('.video-row');
            if (!rowWrap) return false;
            if (rowWrap.dataset && rowWrap.dataset.presetPassthrough === 'true') return false;
            return true;
          } catch {
            return false;
          }
        });

        for (const rowEl of rows) {
          try {
            const rowWrap = rowEl.closest('.video-row');
            const ownRatioObj = parseImgRatio(
              (rowWrap && rowWrap.dataset ? rowWrap.dataset.accordionRowImgRatio : '') ||
              (rowWrap && rowWrap.__accordionRowImgRatio) ||
              rowEl.dataset.rowImgRatio ||
              rowEl.__rowImgRatio ||
              ''
            );
            const ratioObj = ownRatioObj || leaderRatioObj;
            const wantFitRow = !!leaderMeta.fitRow || !!rowEl.__wantFitRow;
            rowEl.__wantFitRow = wantFitRow;
            rowEl.classList.toggle('is-fit', wantFitRow);
            setRowAspect(rowEl, ratioObj);
            syncRowCardLayout(rowEl, { applyVideoScale: true });
            fitRowIfNeeded(rowEl, wantFitRow);
          } catch {}
        }
      }

      function hydrateAccordionPanel(panel, leaderMeta) {
        if (!panel) return;
        try { markLazyCustomRowsReady(panel); } catch {}
        try { mountInlinePresetInstances(panel); } catch {}
        try { applyAccordionPanelRowOverrides(panel, leaderMeta); } catch {}
        try { refreshRowLayoutsWithin(panel); } catch {}
        try { primeAllVideoThumbsThenSwapOverrides(panel, { visibleOnly:true }); } catch {}
        requestAnimationFrame(() => {
          try { mountInlinePresetInstances(panel); } catch {}
          try { refreshRowLayoutsWithin(panel); } catch {}
          try { primeAllVideoThumbsThenSwapOverrides(panel, { visibleOnly:true }); } catch {}
        });
      }

      function groupAccordionRows(container) {
        if (!container) return;
        const children = Array.from(container.children);
        let accordionCounter = 0;
        for (let i = 0; i < children.length; ) {
          const first = children[i];
          const firstMeta = readAccordionDescriptor(first);
          if (!firstMeta) { i += 1; continue; }

          const run = [];
          let j = i;
          while (j < children.length) {
            const el = children[j];
            if (!hasAccordionAffinity(el)) break;
            run.push(el);
            j += 1;
          }
          if (!run.length) { i = j; continue; }

          const group = document.createElement('div');
          group.className = 'accordion-group';
          if (firstMeta.variant === 'bullet') group.classList.add('bullet-point-list');
          group.setAttribute('data-accordion-group', `acc-${SET_SAFE}-${accordionCounter++}`);
          if (container.closest('.accordion-row__panel')) {
            group.setAttribute('data-mg-nested-accordion-group', 'true');
          }
          container.insertBefore(group, run[0]);

          let exclusiveOpenExists = false;
          for (let k = 0; k < run.length; ) {
            const firstRow = run[k];
            const meta = readAccordionDescriptor(firstRow);
            if (!meta) { k += 1; continue; }

            const members = [firstRow];
            k += 1;
            if (meta.groupKey) {
              while (k < run.length) {
                const nextEl = run[k];
                if (!rowBelongsToAccordionItem(nextEl, meta.groupKey)) break;
                members.push(nextEl);
                k += 1;
              }
            }

            const leaderEl = members.find((el) => !!(el && el.dataset && el.dataset.accordion === 'true')) || firstRow;
            const leaderMeta = readAccordionDescriptor(leaderEl) || meta;

            const item = document.createElement('div');
            item.className = 'accordion-row';
            const isBulletPoint = leaderMeta.variant === 'bullet';
            const bulletHasMore = !isBulletPoint || leaderMeta.moreEnabled !== false;
            if (isBulletPoint) {
              item.classList.add('bullet-point-row');
              item.style.setProperty('--bullet-padding-top', `${Math.max(0, Number(leaderMeta.bulletPaddingTop || 0))}px`);
              item.style.setProperty('--bullet-padding-bottom', `${Math.max(0, Number(leaderMeta.bulletPaddingBottom || 0))}px`);
            }
            if (leaderMeta.openAsPopup) item.dataset.accordionPopup = 'true';
            item.dataset.accordionAlwaysOpen = leaderMeta.alwaysOpen ? 'true' : 'false';
            const accordionOpenSubheadingText = collapseWhitespace(plainTextFromMaybeHtml(leaderMeta.subHtml || ''));
            const collapseHeaderWhenOpen = !!leaderMeta.hideTitleWhenOpen && (!accordionOpenSubheadingText || !!leaderMeta.hideSubheadingWhenOpen);
            item.dataset.hideTitleWhenOpen = leaderMeta.hideTitleWhenOpen ? 'true' : 'false';
            item.dataset.hideSubheadingWhenOpen = leaderMeta.hideSubheadingWhenOpen ? 'true' : 'false';
            item.dataset.hideSubheadingWhenClosed = leaderMeta.hideSubheadingWhenClosed ? 'true' : 'false';
            item.dataset.collapseHeaderWhenOpen = collapseHeaderWhenOpen ? 'true' : 'false';
            const accordionBgColor = safeCssColor(leaderMeta.bgColor, 'rgba(255,255,255,.72)');
            const accordionShadowPct = normalizeAccordionShadowPct(leaderMeta.shadowPct, 8);
            const accordionShadowStyle = normalizeAccordionShadowStyle(leaderMeta.shadowStyle, 'raised');
            const accordionShadowCss = buildAccordionShadowCss(accordionShadowStyle, accordionShadowPct);
            item.dataset.accordionBgColor = accordionBgColor;
            item.dataset.accordionShadowPct = String(accordionShadowPct);
            item.dataset.accordionShadowStyle = accordionShadowStyle;
            item.dataset.accordionMaxWidthPx = String(leaderMeta.maxWidthPx || 0);
            item.dataset.accordionPanelWidthOverridePercent = String(leaderMeta.panelWidthOverridePercent || 0);
            item.dataset.accordionPanelMaxWidthOverridePx = String(leaderMeta.panelMaxWidthOverridePx || 0);
            item.__accordionBgColor = accordionBgColor;
            item.__accordionShadowPct = accordionShadowPct;
            item.__accordionShadowStyle = accordionShadowStyle;
            item.__accordionMaxWidthPx = leaderMeta.maxWidthPx || 0;
            item.__accordionPanelWidthOverridePercent = leaderMeta.panelWidthOverridePercent || 0;
            item.__accordionPanelMaxWidthOverridePx = leaderMeta.panelMaxWidthOverridePx || 0;

            let startOpen = leaderMeta.alwaysOpen || leaderMeta.startOpen;
            if (startOpen && !leaderMeta.allowMultiple && exclusiveOpenExists && !leaderMeta.alwaysOpen) startOpen = false;
            if (startOpen && !leaderMeta.allowMultiple) exclusiveOpenExists = true;

            const btn = document.createElement('button');
            btn.type = 'button';
            let bulletLine = null;
            if (isBulletPoint) {
              bulletLine = document.createElement('div');
              bulletLine.className = 'bullet-point-row__line';
              bulletLine.innerHTML = '<span class="bullet-point-row__text accordion-row__title"></span> ';
              if (bulletHasMore) {
                btn.className = 'bullet-point-row__more';
                btn.textContent = leaderMeta.moreText || 'more';
                bulletLine.appendChild(btn);
              }
            } else {
              btn.className = 'accordion-row__toggle' + (leaderMeta.titleCenter ? ' is-centered' : '');
              btn.innerHTML = `<span class="accordion-row__spacer" aria-hidden="true"></span><span class="accordion-row__copy"><span class="accordion-row__title"></span><span class="accordion-row__subheading"></span></span><span class="accordion-row__chevron" aria-hidden="true"></span>`;
            }
            const titleEl = isBulletPoint ? bulletLine.querySelector('.accordion-row__title') : btn.querySelector('.accordion-row__title');
            const subEl = btn.querySelector('.accordion-row__subheading');
            const resolvedTitleHtml = String(leaderMeta.titleHtml || '').trim() || deriveAccordionTitleHtmlFromElement(leaderEl, isBulletPoint ? 'Bullet point' : 'Accordion Tab');
            if (titleEl) titleEl.innerHTML = sanitizeHtml(resolvedTitleHtml || (isBulletPoint ? 'Bullet point' : 'Accordion Tab'));
            if (subEl) {
              if (leaderMeta.subHtml) subEl.innerHTML = sanitizeHtml(leaderMeta.subHtml);
              else subEl.remove();
            }
            requestAnimationFrame(() => {
              fitSingleLineSqueeze(titleEl, 0.72);
            });

            const panel = document.createElement('div');
            panel.className = 'accordion-row__panel';
            if (leaderMeta.contentHtml) {
              const contentEl = document.createElement('div');
              contentEl.className = 'accordion-row__content rte';
              contentEl.innerHTML = sanitizeHtml(leaderMeta.contentHtml);
              panel.appendChild(contentEl);
            }
            item.__accordionLeaderMeta = leaderMeta;
            panel.__accordionLeaderMeta = leaderMeta;

            const markerMembers = [];
            for (const member of members) {
              const memberMeta = readAccordionDescriptor(member);
              const hostGroup = readAccordionHostGroup(member);
              const removeAsMarker = !!(
                member &&
                member.dataset &&
                member.dataset.accordionMarker === 'true' &&
                memberMeta &&
                memberMeta.groupKey === leaderMeta.groupKey &&
                hostGroup !== leaderMeta.groupKey
              );
              if (removeAsMarker) {
                markerMembers.push(member);
                continue;
              }
              if (hostGroup && leaderMeta.groupKey && hostGroup === leaderMeta.groupKey) {
                try { member.removeAttribute('data-host-accordion-group'); } catch {}
                try { delete member.__accordionHostGroup; } catch {}
              }
              if (
                memberMeta &&
                !memberMeta.isAccordion &&
                leaderMeta.groupKey &&
                memberMeta.groupKey === leaderMeta.groupKey &&
                !hostGroup
              ) {
                try { member.removeAttribute('data-accordion-group'); } catch {}
              }
              panel.appendChild(member);
            }

            item.appendChild(isBulletPoint ? bulletLine : btn);
            item.appendChild(panel);
            group.appendChild(item);
            applyAccordionItemVisuals(item);

            const setOpen = (open) => {
              if (isBulletPoint && leaderMeta.openAsPopup) open = false;
              if (isBulletPoint && !bulletHasMore) open = true;
              const nextOpen = leaderMeta.alwaysOpen ? true : !!open;
              item.classList.toggle('is-open', nextOpen);
              if (bulletHasMore) btn.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
              panel.hidden = !nextOpen;
              if (nextOpen) panel.style.display = '';
              applyAccordionItemVisuals(item);
              if (nextOpen) hydrateAccordionPanel(panel, leaderMeta);
            };

            setOpen(startOpen);
            requestAnimationFrame(() => {
              applyAccordionItemVisuals(item);
              if (item.classList.contains('is-open')) hydrateAccordionPanel(panel, leaderMeta);
            });

            if (bulletHasMore) btn.addEventListener('click', () => {
              if (isBulletPoint && leaderMeta.openAsPopup && openBulletPopup) {
                hydrateAccordionPanel(panel, leaderMeta);
                openBulletPopup(panel, leaderMeta);
                return;
              }
              const currentlyOpen = item.classList.contains('is-open');
              if (currentlyOpen && leaderMeta.alwaysOpen) {
                requestAnimationFrame(() => requestAnimationFrame(() => {
                  scrollAccordionTopIntoView(item, btn);
                }));
                return;
              }
              const willOpen = leaderMeta.alwaysOpen ? true : !currentlyOpen;
              if (willOpen && !leaderMeta.allowMultiple) {
                Array.from(group.querySelectorAll('.accordion-row.is-open')).forEach((other) => {
                  if (other === item) return;
                  if (other.dataset && other.dataset.accordionAlwaysOpen === 'true') return;
                  other.classList.remove('is-open');
                  const otherBtn = other.querySelector('.accordion-row__toggle');
                  const otherPanel = other.querySelector('.accordion-row__panel');
                  if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
                  if (otherPanel) otherPanel.hidden = true;
                });
              }
              setOpen(willOpen);
              if (willOpen) {
                requestAnimationFrame(() => requestAnimationFrame(() => {
                  scrollAccordionTopIntoView(item, btn);
                }));
              }
            });

            for (const marker of markerMembers) {
              try { marker.remove(); } catch {}
            }
          }

          requestAnimationFrame(() => {
            Array.from(group.querySelectorAll('.accordion-row')).forEach((item) => {
              applyAccordionItemVisuals(item);
              if (item.classList.contains('is-open')) {
                const panel = item.querySelector('.accordion-row__panel');
                if (panel) hydrateAccordionPanel(panel, panel.__accordionLeaderMeta || item.__accordionLeaderMeta || readAccordionDescriptor(item));
              }
            });
          });

          i = j;
        }
      }

      function groupAccordionRowsRecursive(rootNode) {
        if (!rootNode) return;
        groupAccordionRows(rootNode);
        const bodies = Array.from(rootNode.querySelectorAll('.row-group__body'));
        for (const body of bodies) {
          const owner = body && body.parentElement ? body.parentElement : null;
          if (owner && readAccordionDescriptor(owner)) continue;
          groupAccordionRows(body);
        }
        const panels = Array.from(rootNode.querySelectorAll('.accordion-row__panel'));
        for (const panel of panels) {
          groupAccordionRows(panel);
        }
      }

      function groupCollapseRowsRecursive(rootNode) {
        if (!rootNode) return;
        groupCollapseRows(rootNode);
        const panels = Array.from(rootNode.querySelectorAll('.accordion-row__panel'));
        for (const panel of panels) {
          groupCollapseRows(panel);
        }
      }

      const cleanupFns = [];
      bindExternalVisibilityRefresh();

      // Collect videos into playlist and render rows
      let globalVideoIndex = 0;
      let rowCounter = 0;
      let lastCollapseCarrierRowWrap = null;

      let renderedRowCount = 0;
      for (const rowSpec of (ROWS || [])) {
        const rowWrap = document.createElement("div");
        rowWrap.className = "video-row";
        const rowMarginTop = Number(rowSpec?.margin_top || 0) || 0;
        const rowMarginBottom = Number(rowSpec?.margin_bottom || 0) || 0;
        rowWrap.style.marginTop = rowMarginTop + "px";
        rowWrap.style.marginBottom = rowMarginBottom + "px";
        rowWrap.style.paddingTop = rowMarginTop + "px";
        rowWrap.style.paddingBottom = rowMarginBottom + "px";

        const collapseGroup = String(rowSpec?.collapse_group || '').trim();
        if (collapseGroup) {
          rowWrap.dataset.collapseGroup = collapseGroup;
          rowWrap.dataset.collapseMore = String(rowSpec?.show_more_text || 'Show more');
          rowWrap.dataset.collapseLess = String(rowSpec?.show_less_text || 'Show less');
        }
        const accordionGroup = String(rowSpec?.accordion_group || '').trim();
        const nestedPresetSet = String(rowSpec?.__nested_preset_set || '').trim();
        if (rowSpec?.accordion_marker) {
          rowWrap.classList.add('is-accordion-marker');
          rowWrap.dataset.accordionMarker = 'true';
          rowWrap.setAttribute('aria-hidden', 'true');
        }
        if (rowSpec?.bullet_marker || rowSpec?.bullet_point) {
          rowWrap.dataset.bulletMarker = rowSpec?.bullet_marker ? 'true' : 'false';
          rowWrap.dataset.bulletPoint = 'true';
          rowWrap.dataset.bulletGroup = String(rowSpec?.bullet_group || '');
          rowWrap.__bulletTextHtml = String(rowSpec?.bullet_text || rowSpec?.accordion_title || '');
          rowWrap.__bulletContentHtml = String(rowSpec?.bullet_content || rowSpec?.accordion_content || '');
          rowWrap.__bulletPresetSet = String(rowSpec?.bullet_preset_set || '');
          rowWrap.__bulletMoreEnabled = rowSpec?.bullet_more_enabled !== false;
          rowWrap.__bulletMoreText = String(rowSpec?.bullet_more_text || 'more');
          rowWrap.__bulletPopup = !!rowSpec?.bullet_popup;
          rowWrap.__bulletPaddingTop = Math.max(0, Number(rowSpec?.bullet_padding_top || 2) || 0);
          rowWrap.__bulletPaddingBottom = Math.max(0, Number(rowSpec?.bullet_padding_bottom || 2) || 0);
          rowWrap.__bulletWidthDesktopPercent = normalizeWidthOverridePercent(rowSpec?.bullet_width_desktop_percent, 100) || 100;
          rowWrap.__bulletWidthMobilePercent = normalizeWidthOverridePercent(rowSpec?.bullet_width_mobile_percent, 100) || 100;
          rowWrap.__bulletMaxWidthPx = normalizePresetButtonMaxWidthPx(rowSpec?.bullet_max_width_px, 0);

          const fallbackList = document.createElement('div');
          fallbackList.className = 'bullet-point-list bullet-point-list--fallback';
          fallbackList.style.setProperty('--bullet-list-width-desktop', `${rowWrap.__bulletWidthDesktopPercent}%`);
          fallbackList.style.setProperty('--bullet-list-width-mobile', `${rowWrap.__bulletWidthMobilePercent}%`);
          fallbackList.style.setProperty('--bullet-list-max-width', rowWrap.__bulletMaxWidthPx > 0 ? `${rowWrap.__bulletMaxWidthPx}px` : '999999px');
          const fallbackItem = document.createElement('div');
          fallbackItem.className = 'bullet-point-item';
          fallbackItem.style.setProperty('--bullet-padding-top', `${rowWrap.__bulletPaddingTop}px`);
          fallbackItem.style.setProperty('--bullet-padding-bottom', `${rowWrap.__bulletPaddingBottom}px`);
          const fallbackLine = document.createElement('div');
          fallbackLine.className = 'bullet-point-item__line';
          const fallbackText = document.createElement('span');
          fallbackText.className = 'bullet-point-item__text rte';
          fallbackText.innerHTML = sanitizeHtml(rowWrap.__bulletTextHtml || 'Bullet point');
          fallbackLine.appendChild(fallbackText);
          if (rowWrap.__bulletMoreEnabled) {
            const fallbackBtn = document.createElement('button');
            fallbackBtn.type = 'button';
            fallbackBtn.className = 'bullet-point-item__more';
            fallbackBtn.textContent = rowWrap.__bulletMoreText || 'more';
            fallbackBtn.setAttribute('aria-expanded', 'false');
            fallbackLine.appendChild(document.createTextNode(' '));
            fallbackLine.appendChild(fallbackBtn);
          }
          fallbackItem.appendChild(fallbackLine);
          if (rowWrap.__bulletPresetSet) {
            const fallbackPanel = document.createElement('div');
            fallbackPanel.className = 'bullet-point-item__panel';
            fallbackPanel.hidden = !rowSpec?.accordion_start_open;
            const fallbackPresetHost = document.createElement('div');
            fallbackPresetHost.className = 'row-inline-preset-host bullet-point-item__preset';
            fallbackPresetHost.setAttribute('data-inline-preset-host', rowWrap.__bulletPresetSet);
            fallbackPresetHost.style.width = '100%';
            fallbackPanel.appendChild(fallbackPresetHost);
            fallbackItem.appendChild(fallbackPanel);
          }
          fallbackList.appendChild(fallbackItem);
          rowWrap.appendChild(fallbackList);
        }
        if (rowSpec?.accordion) rowWrap.dataset.accordion = 'true';
        if (accordionGroup) rowWrap.dataset.accordionGroup = accordionGroup;
        if (rowSpec?.__host_accordion_group) {
          rowWrap.dataset.hostAccordionGroup = String(rowSpec.__host_accordion_group || '').trim();
          rowWrap.__accordionHostGroup = String(rowSpec.__host_accordion_group || '').trim();
        }
        if (rowSpec?.__host_bullet_group) {
          rowWrap.dataset.hostBulletGroup = String(rowSpec.__host_bullet_group || '').trim();
        }
        if (rowSpec?.bullet_points_heading) {
          rowWrap.classList.add('is-bullet-points-heading');
          const headingWrap = document.createElement('div');
          headingWrap.className = 'bullet-points-heading';
          const desktopWidth = normalizeWidthOverridePercent(rowSpec?.bullet_width_desktop_percent, 100) || 100;
          const mobileWidth = normalizeWidthOverridePercent(rowSpec?.bullet_width_mobile_percent, 100) || 100;
          const maxWidth = normalizePresetButtonMaxWidthPx(rowSpec?.bullet_max_width_px, 0);
          headingWrap.style.setProperty('--bullet-list-width-desktop', `${desktopWidth}%`);
          headingWrap.style.setProperty('--bullet-list-width-mobile', `${mobileWidth}%`);
          headingWrap.style.setProperty('--bullet-list-max-width', maxWidth > 0 ? `${maxWidth}px` : '999999px');
          const headingHtml = String(rowSpec?.bullet_heading || '').trim();
          const subheadingHtml = String(rowSpec?.bullet_subheading || '').trim();
          if (headingHtml) {
            const headingEl = document.createElement('div');
            headingEl.className = 'bullet-points-heading__title rte';
            headingEl.innerHTML = sanitizeHtml(headingHtml);
            headingWrap.appendChild(headingEl);
          }
          if (subheadingHtml) {
            const subheadingEl = document.createElement('div');
            subheadingEl.className = 'bullet-points-heading__subheading rte';
            subheadingEl.innerHTML = sanitizeHtml(subheadingHtml);
            headingWrap.appendChild(subheadingEl);
          }
          rowWrap.appendChild(headingWrap);
        }
        if (rowSpec?.accordion || accordionGroup) {
          rowWrap.dataset.accordionStartOpen = rowSpec?.accordion_start_open ? 'true' : 'false';
          rowWrap.dataset.accordionAllowMultiple = rowSpec?.accordion_allow_multiple ? 'true' : 'false';
          rowWrap.dataset.accordionHideTitleWhenOpen = rowSpec?.accordion_hide_title_when_open ? 'true' : 'false';
          rowWrap.dataset.accordionHideSubheadingWhenOpen = rowSpec?.accordion_hide_subheading_when_open ? 'true' : 'false';
          rowWrap.dataset.accordionHideSubheadingWhenClosed = rowSpec?.accordion_hide_subheading_when_closed ? 'true' : 'false';
          rowWrap.dataset.accordionAlwaysOpen = rowSpec?.accordion_always_open ? 'true' : 'false';
          rowWrap.__accordionTitleHtml = String(rowSpec?.accordion_title || '');
          rowWrap.__accordionSubheadingHtml = String(rowSpec?.accordion_subheading || '');
          rowWrap.__accordionContentHtml = String(rowSpec?.accordion_content || '');
          if (rowSpec?.bullet_point) rowWrap.dataset.bulletPoint = 'true';
          rowWrap.__accordionVariant = rowSpec?.bullet_point ? 'bullet' : String(rowSpec?.accordion_variant || '');
          rowWrap.__accordionMoreEnabled = rowSpec?.accordion_more_enabled !== false;
          rowWrap.__accordionMoreText = String(rowSpec?.accordion_more_text || 'more');
          rowWrap.__accordionPopup = !!rowSpec?.accordion_popup;
          rowWrap.__accordionBulletPaddingTop = Math.max(0, Number(rowSpec?.accordion_bullet_padding_top || 2) || 0);
          rowWrap.__accordionBulletPaddingBottom = Math.max(0, Number(rowSpec?.accordion_bullet_padding_bottom || 2) || 0);
          rowWrap.__accordionTitleCenter = !!rowSpec?.accordion_title_center;
          rowWrap.__accordionBgColor = String(rowSpec?.accordion_bg_color || rowSpec?.accordion_bg || '');
          rowWrap.__accordionShadowPct = normalizeAccordionShadowPct(rowSpec?.accordion_shadow_pct, 8);
          rowWrap.__accordionShadowStyle = normalizeAccordionShadowStyle(rowSpec?.accordion_shadow_style || rowSpec?.accordion_shadow_effect, 'raised');
          rowWrap.__accordionMaxWidthPx = normalizePresetButtonMaxWidthPx(rowSpec?.accordion_max_width_px, 0);
          rowWrap.__accordionPanelWidthOverridePercent = normalizeWidthOverridePercent(rowSpec?.accordion_panel_width_override_percent, 0);
          rowWrap.__accordionPanelMaxWidthOverridePx = normalizePresetButtonMaxWidthPx(rowSpec?.accordion_panel_max_width_override_px, 0);
          rowWrap.__accordionRowImgRatio = String(rowSpec?.img_ratio || '');
          rowWrap.__accordionRowHeight = Number(rowSpec?.row_height || 0) || 0;
          rowWrap.__accordionFitRow = !!rowSpec?.fit_row;
          rowWrap.dataset.accordionBgColor = String(rowSpec?.accordion_bg_color || rowSpec?.accordion_bg || '');
          rowWrap.dataset.accordionShadowPct = String(normalizeAccordionShadowPct(rowSpec?.accordion_shadow_pct, 8));
          rowWrap.dataset.accordionShadowStyle = normalizeAccordionShadowStyle(rowSpec?.accordion_shadow_style || rowSpec?.accordion_shadow_effect, 'raised');
          rowWrap.dataset.accordionMaxWidthPx = String(normalizePresetButtonMaxWidthPx(rowSpec?.accordion_max_width_px, 0));
          rowWrap.dataset.accordionPanelWidthOverridePercent = String(normalizeWidthOverridePercent(rowSpec?.accordion_panel_width_override_percent, 0));
          rowWrap.dataset.accordionPanelMaxWidthOverridePx = String(normalizePresetButtonMaxWidthPx(rowSpec?.accordion_panel_max_width_override_px, 0));
          rowWrap.dataset.accordionRowImgRatio = String(rowSpec?.img_ratio || '');
          rowWrap.dataset.accordionRowHeight = String(Number(rowSpec?.row_height || 0) || 0);
          rowWrap.dataset.accordionFitRow = rowSpec?.fit_row ? 'true' : 'false';
          if (rowSpec?.accordion_variant) rowWrap.dataset.accordionVariant = String(rowSpec.accordion_variant);
          if (rowSpec?.accordion_more_enabled === false) rowWrap.dataset.accordionMoreEnabled = 'false';
          if (rowSpec?.accordion_more_text) rowWrap.dataset.accordionMoreText = String(rowSpec.accordion_more_text);
          if (rowSpec?.accordion_popup) rowWrap.dataset.accordionPopup = 'true';
          if (rowSpec?.accordion_bullet_padding_top != null) rowWrap.dataset.accordionBulletPaddingTop = String(rowSpec.accordion_bullet_padding_top);
          if (rowSpec?.accordion_bullet_padding_bottom != null) rowWrap.dataset.accordionBulletPaddingBottom = String(rowSpec.accordion_bullet_padding_bottom);
        }

        if (!rowSpec?.preset_button) {
          const headingNode = buildRowHeading(rowSpec);
          if (headingNode.children.length) rowWrap.appendChild(headingNode);
        }

        // Row-level Custom Liquid / snippet output (server-rendered when available)
        const customTemplateId = String(rowSpec?.custom_template_id || "").trim();
        const customRaw = String(rowSpec?.custom_html || "");
        if (customTemplateId || (customRaw && customRaw.trim())) {
          const customEl = document.createElement("div");
          customEl.className = "row-custom-html";
          customEl.setAttribute("data-custom-liquid", "true");
          queueLazyCustomLiquid(customEl, rowSpec, customTemplateId, customRaw);
          rowWrap.appendChild(customEl);
        }

        if (nestedPresetSet) {
          const host = document.createElement("div");
          host.className = "row-inline-preset-host";
          host.setAttribute("data-inline-preset-host", nestedPresetSet);
          host.style.width = "100%";
          rowWrap.appendChild(host);
          rowsRoot.appendChild(rowWrap);
          lastCollapseCarrierRowWrap = String(rowWrap.dataset.collapseGroup || '').trim() ? rowWrap : null;
          continue;
        }

        const items = normalizeItems(rowSpec?.videos || rowSpec?.items);
        const cardCount = items.length;
        const presetButton = !!rowSpec?.preset_button;
        const presetButtonSet = String(rowSpec?.__preset_button_set || rowSpec?.preset_button_set || rowSpec?.__import_set || '').trim();
        const presetButtonTextHtml = normalizePresetButtonTextHtml(rowSpec?.preset_button_text || rowSpec?.heading || rowSpec?.button_text || '', 'Open Preset');
        const presetButtonTextLabel = plainTextFromMaybeHtml(presetButtonTextHtml) || 'Open Preset';
        const presetButtonStyle = normalizePresetButtonStyle(rowSpec?.preset_button_style || rowSpec?.button_style || 'dropdown_box', 'dropdown_box');
        const presetButtonHintHtml = normalizePresetButtonHintHtml(rowSpec?.preset_button_subtext || rowSpec?.subheading || '', presetButtonStyle === 'dropdown_box' ? 'Select' : '');
        const presetButtonHintLabel = plainTextFromMaybeHtml(presetButtonHintHtml);
        const presetButtonImage = String(rowSpec?.preset_button_image || rowSpec?.preset_button_image_url || rowSpec?.button_image || rowSpec?.button_image_url || '').trim();
        const presetButtonImageAlt = collapseWhitespace(rowSpec?.preset_button_image_alt || rowSpec?.button_image_alt || '');
        const presetButtonWidthPercent = normalizePresetButtonWidthPercent(rowSpec?.preset_button_width_percent ?? rowSpec?.preset_button_width_pct ?? rowSpec?.button_width_percent ?? rowSpec?.button_width_pct, 0);
        const presetButtonMaxWidthPx = normalizePresetButtonMaxWidthPx(rowSpec?.preset_button_max_width_px ?? rowSpec?.button_max_width_px, 0);

        // If there are no cards, still render headings/custom HTML and move on.
        if (!cardCount) {
          const isCollapsible = !!rowSpec?.collapsed;
          const moreText = String(rowSpec?.show_more_text || "Show more").trim() || "Show more";
          const lessText = String(rowSpec?.show_less_text || "Show less").trim() || "Show less";
          const rowId = `mg-row-${SET_SAFE}-${rowCounter++}`;
          const effectiveCollapseGroup = String(rowWrap.dataset.collapseGroup || '').trim() || (isCollapsible ? `row:${SET_SAFE}:${rowId}` : '');
          if (effectiveCollapseGroup) {
            rowWrap.dataset.collapseGroup = effectiveCollapseGroup;
            rowWrap.dataset.collapseMore = moreText;
            rowWrap.dataset.collapseLess = lessText;
          }

          const hasCustomOnly = !presetButton && !!rowWrap.querySelector('.row-custom-html') && !rowWrap.querySelector('.thumb-row');
          if (hasCustomOnly) {
            rowWrap.classList.add('is-custom-liquid-only');
            const explicitBottom = Number(rowSpec?.margin_bottom || 0) || 0;
            if (!explicitBottom) rowWrap.style.marginBottom = '-28px';

            // If a Custom liquid-only row is placed directly after a collapsed row,
            // treat it as part of that collapsed row so the Show more/less button remains.
            if (lastCollapseCarrierRowWrap && lastCollapseCarrierRowWrap.isConnected) {
              const attached = document.createElement('div');
              attached.className = 'row-custom-follow';
              attached.style.marginTop = rowMarginTop + 'px';
              attached.style.marginBottom = rowMarginBottom + 'px';
              attached.style.paddingTop = rowMarginTop + 'px';
              attached.style.paddingBottom = rowMarginBottom + 'px';
              while (rowWrap.firstChild) attached.appendChild(rowWrap.firstChild);
              lastCollapseCarrierRowWrap.appendChild(attached);
              continue;
            }
          }

          if (presetButton) {
            rowWrap.classList.add('is-collapsible', 'is-preset-button');
            rowWrap.dataset.presetButtonStyle = presetButtonStyle;
            const toggleBar = document.createElement('div');
            toggleBar.className = 'row-toggle row-toggle--preset row-toggle--style-' + presetButtonStyle;

            const toggleBtn = document.createElement('button');
            toggleBtn.type = 'button';
            toggleBtn.className = 'row-toggle-btn row-toggle-btn--preset row-toggle-btn--style-' + presetButtonStyle;
            toggleBtn.setAttribute('data-mg-preset-btn-style', presetButtonStyle);
            toggleBtn.setAttribute('aria-haspopup', 'dialog');
            toggleBtn.setAttribute('aria-label', presetButtonTextLabel);
            applyRowToggleBorderStyle(toggleBtn, rowSpec?.row_toggle_border_style || rowSpec?.row_border_style || 'solid');
            toggleBtn.dataset.mgPresetButtonWidthPercent = String(presetButtonWidthPercent || 0);
            toggleBtn.dataset.mgPresetButtonMaxWidthPx = String(presetButtonMaxWidthPx || 0);
            if (presetButtonWidthPercent > 0) toggleBtn.style.setProperty('--mg-preset-btn-width', presetButtonWidthPercent + '%');
            if (presetButtonMaxWidthPx > 0) toggleBtn.style.setProperty('--mg-preset-btn-max-width', presetButtonMaxWidthPx + 'px');
            else toggleBtn.style.setProperty('--mg-preset-btn-max-width', '100%');

            const inner = document.createElement('span');
            inner.className = 'row-toggle-btn__inner';

            if (presetButtonImage) {
              const media = document.createElement('span');
              media.className = 'row-toggle-btn__media';
              const img = document.createElement('img');
              img.className = 'row-toggle-btn__image';
              img.src = presetButtonImage;
              img.alt = presetButtonImageAlt || '';
              img.loading = 'lazy';
              img.decoding = 'async';
              media.appendChild(img);
              inner.appendChild(media);
              toggleBtn.classList.add('has-image');
            }

            const body = document.createElement('span');
            body.className = 'row-toggle-btn__body';

            const label = document.createElement('span');
            label.className = 'row-toggle-btn__label';
            label.innerHTML = presetButtonTextHtml;
            body.appendChild(label);

            if (presetButtonHintLabel || presetButtonStyle === 'dropdown_box') {
              const hint = document.createElement('span');
              hint.className = 'row-toggle-btn__hint';
              hint.innerHTML = presetButtonHintHtml || 'Select';
              body.appendChild(hint);
            }

            inner.appendChild(body);

            if (presetButtonStyle === 'dropdown_box') {
              const chevron = document.createElement('span');
              chevron.className = 'row-toggle-btn__chevron';
              chevron.setAttribute('aria-hidden', 'true');
              inner.appendChild(chevron);
            }

            toggleBtn.appendChild(inner);

            if (presetButtonStyle === 'button') {
              applyThemeShopButtonStyleOnly(toggleBtn, presetButtonTextLabel);
            }

            if (!presetButtonSet || typeof openPresetSet !== 'function') {
              toggleBtn.disabled = true;
              toggleBtn.setAttribute('aria-disabled', 'true');
            } else {
              toggleBtn.addEventListener('click', () => {
                openPresetSet(presetButtonSet);
              });
            }

            toggleBar.appendChild(toggleBtn);
            rowWrap.appendChild(toggleBar);
          }

          rowsRoot.appendChild(rowWrap);
          lastCollapseCarrierRowWrap = String(rowWrap.dataset.collapseGroup || '').trim() ? rowWrap : null;
          continue;
        }

        const rowEl = document.createElement("div");
        rowEl.className = "thumb-row";

        const rowRatio = parseImgRatio(rowSpec?.img_ratio) || { w:1, h:1 };
        rowEl.dataset.rowImgRatio = String(rowSpec?.img_ratio || '');
        rowEl.__rowImgRatio = String(rowSpec?.img_ratio || '');
        const rowAspectCss = setRowAspect(rowEl, rowRatio);

        const wantFitRow = !!rowSpec?.fit_row;
        if (wantFitRow) rowEl.classList.add("is-fit");

        const rowHeight = Number(rowSpec?.row_height || 0) || 0;
        rowEl.__rowHeight = rowHeight;
        applyRowHeight(rowEl, rowHeight);

        // Optional row collapse (Show more / Show less)
        const isCollapsible = !!rowSpec?.collapsed;
        const moreText = String(rowSpec?.show_more_text || "Show more").trim() || "Show more";
        const lessText = String(rowSpec?.show_less_text || "Show less").trim() || "Show less";
        const rowId = `mg-row-${SET_SAFE}-${rowCounter++}`;
        rowEl.id = rowId;
        const effectiveCollapseGroup = String(rowWrap.dataset.collapseGroup || '').trim() || (isCollapsible ? `row:${SET_SAFE}:${rowId}` : '');
        if (effectiveCollapseGroup) {
          rowWrap.dataset.collapseGroup = effectiveCollapseGroup;
          rowWrap.dataset.collapseMore = moreText;
          rowWrap.dataset.collapseLess = lessText;
        }
        rowWrap.dataset.rowToggleBorderStyle = normalizeRowToggleBorderStyle(rowSpec?.row_toggle_border_style || rowSpec?.row_border_style, 'solid');

        // Row ctx for cards
        const rowHideCtaButtons = !!rowSpec?.hide_cta_buttons;
        const rowEndBehaviorOverride = normalizeEndBehavior(rowSpec?.end_behavior_override || rowSpec?.end_of_video_override, "");
        const rowCtx = {
          rowAspectCss,
          rowHeightPx: rowHeight,
          hideCtaButtons: rowHideCtaButtons,
          endBehaviorOverride: rowEndBehaviorOverride
        };
        rowEl.__wantFitRow = wantFitRow;

        if (cardCount === 1) rowEl.classList.add("is-single");
        if (cardCount === 1) {
          const singleMeta = items[0] && typeof items[0] === 'object' ? items[0] : null;
          if (singleMeta && providerOf(singleMeta) === 'video' && isTrueLike(singleMeta?.hero ?? singleMeta?.hero_video ?? singleMeta?.is_hero)) {
            rowEl.classList.add('is-hero-only');
          }
        }

        for (const meta of items) {
          const itemMeta = (meta && typeof meta === "object") ? Object.assign({}, meta) : meta;
          if (itemMeta && typeof itemMeta === "object") {
            itemMeta.__hide_cta_buttons = rowHideCtaButtons;
            itemMeta.__end_behavior_override = rowEndBehaviorOverride;
          }

          const kind = providerOf(itemMeta);
          if (isVideoKind(kind)) {
            itemMeta.__videoIndex = globalVideoIndex++;
            VIDEO_PLAYLIST.push(itemMeta);
          }

          const card = makeCard(itemMeta, rowCtx);
          rowEl.appendChild(card);
        }

        rowWrap.appendChild(rowEl);

        // Collapse UI is unified through groupCollapseRows(), including inside Accordion Tabs.

        rowsRoot.appendChild(rowWrap);
        lastCollapseCarrierRowWrap = String(rowWrap.dataset.collapseGroup || '').trim() ? rowWrap : null;

        // auto thumb height if no row_height set (skip while collapsed)
        if (!rowHeight && !isCollapsible && !rowEl.classList.contains("is-single")) {
          requestAnimationFrame(() => {
            syncRowCardLayout(rowEl, { applyVideoScale: true });
            const h = computeAutoRowHeight(rowEl);
            applyAutoThumbH(rowEl, h);
            syncRowCardLayout(rowEl, { applyVideoScale: true });
          });
        }

        // fit-row observers
        const cleanup = attachRowFitObserver(rowEl, wantFitRow);
        cleanupFns.push(cleanup);
        if (!isCollapsible) requestAnimationFrame(() => fitRowIfNeeded(rowEl, wantFitRow));

        renderedRowCount++;
        if (renderedRowCount <= 4 || renderedRowCount % 2 === 0) {
          await yieldForPaint();
        }
      }

      groupCollapseRows(rowsRoot);
      groupAccordionRowsRecursive(rowsRoot);
      groupBulletRowsRecursive(rowsRoot);
      applyBulletPointBlockWidths(rowsRoot);
      requestAnimationFrame(() => applyBulletPointBlockWidths(rowsRoot));
      setTimeout(() => applyBulletPointBlockWidths(rowsRoot), 500);
      groupCollapseRowsRecursive(rowsRoot);
      mountInlinePresetInstances(rowsRoot);
      startLazyCustomLiquidQueue();

      queueThemeShopButtonRefresh(true);
      setTimeout(() => queueThemeShopButtonRefresh(true), 220);
      setTimeout(() => queueThemeShopButtonRefresh(true), 900);
      setTimeout(() => queueThemeShopButtonRefresh(true), 1600);
      setTimeout(() => queueThemeShopButtonRefresh(true), 2600);

      const onThemeShopButtonSourceMaybeChanged = () => queueThemeShopButtonRefresh(true);
      window.addEventListener("load", onThemeShopButtonSourceMaybeChanged, { once:true });
      cleanupFns.push(() => window.removeEventListener("load", onThemeShopButtonSourceMaybeChanged));

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener("shopify:section:load", onThemeShopButtonSourceMaybeChanged);
        document.addEventListener("shopify:section:reorder", onThemeShopButtonSourceMaybeChanged);
        cleanupFns.push(() => document.removeEventListener("shopify:section:load", onThemeShopButtonSourceMaybeChanged));
        cleanupFns.push(() => document.removeEventListener("shopify:section:reorder", onThemeShopButtonSourceMaybeChanged));
      }

      // iOS: boot the Vimeo iframe early so the first tap can start audio unmuted.
      // (We park it off-screen; opening the modal just docks it and swaps videos via loadVideo.)
      if (IOS_PERSISTENT_PLAYER && !root.hasAttribute("data-mg-mini") && VIDEO_PLAYLIST.length) {
        try { ensurePlayerIframe(VIDEO_PLAYLIST[0]); } catch {}
      }

      // Desktop edge nav updates on resize
      const onResize = () => {
        queueThemeShopButtonRefresh(true);
        updateEdgeNav();
        rowsRoot.querySelectorAll(".thumb-row").forEach((rowEl) => {
          fitRowIfNeeded(rowEl, !!rowEl.__wantFitRow);
        });
        if (isVideoOpen()) {
          updateVideoDescVisibility();
        }
        if (isInfoOpen()) scheduleInfoSizing();
      };
      window.addEventListener("resize", onResize);
      cleanupFns.push(() => window.removeEventListener("resize", onResize));
      window.addEventListener("orientationchange", onResize);
      cleanupFns.push(() => window.removeEventListener("orientationchange", onResize));
      if (window.visualViewport && window.visualViewport.addEventListener) {
        window.visualViewport.addEventListener("resize", onResize);
        cleanupFns.push(() => window.visualViewport.removeEventListener("resize", onResize));
      }

      // Close if route/visibility changes? keep simple
      document.addEventListener("visibilitychange", () => {
        if (document.hidden && isVideoOpen()) closeVideo();
        if (document.hidden && isInfoOpen()) closeInfo();
      });

      /* ------------------------------
         JSON-LD injection (SEO) â€” videos + products + images
      ------------------------------ */
      function injectJsonLd() {
        try {
          const items = [];
          const allRows = ROWS || [];
          for (const r of allRows) {
            const rowItems = normalizeItems(r?.videos || r?.items);
            for (const meta of rowItems) {
              const kind = providerOf(meta);
              if (kind === "video") {
                const u = safeLink(meta?.url);
                if (!u) continue;
                items.push({
                  "@type": "VideoObject",
                  "name": String(meta?.title || "").trim() || undefined,
                  "description": String(meta?.description || "").trim() || undefined,
                  "thumbnailUrl": absUrlMaybe(meta?.thumbnail) || undefined,
                  "contentUrl": u
                });
              } else if (kind === "product") {
                const u = safeLink(meta?.product_url) || safeLink(meta?.cta?.href);
                if (!u) continue;
                const offer = (meta?.price_num != null)
                  ? {
                      "@type": "Offer",
                      "priceCurrency": shopCurrency,
                      "price": Number(meta.price_num),
                      "availability": meta?.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                      "url": u
                    }
                  : undefined;
                items.push({
                  "@type": "Product",
                  "name": String(meta?.title || "").trim() || undefined,
                  "image": absUrlMaybe(meta?.thumbnail) || undefined,
                  "url": u,
                  "offers": offer
                });
              } else if (kind === "image") {
                const u = safeLink(meta?.cta?.href);
                const img = absUrlMaybe(meta?.thumbnail);
                if (!img) continue;
                items.push({
                  "@type": "ImageObject",
                  "name": String(meta?.title || "").trim() || undefined,
                  "contentUrl": img,
                  "url": u || undefined
                });
              }
            }
          }

          if (!items.length) return;

          const ld = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": items.map((it, idx) => ({
              "@type": "ListItem",
              "position": idx + 1,
              "item": it
            }))
          };

          const script = document.createElement("script");
          script.type = "application/ld+json";
          script.textContent = JSON.stringify(ld);
          root.appendChild(script);
        } catch {}
      }
      injectJsonLd();

      // âœ… Thumbnail strategy: download all video thumbs first, then swap to custom overrides
      primeAllVideoThumbsThenSwapOverrides(rowsRoot, { force:true, visibleOnly:true });
      requestAnimationFrame(() => {
        try {
          rowsRoot.querySelectorAll('.accordion-row.is-open .accordion-row__panel').forEach((panel) => {
            primeAllVideoThumbsThenSwapOverrides(panel, { force:true });
          });
        } catch {}
      });

      /* ------------------------------
         Info sizing / CTA placement
      ------------------------------ */
      const infoResizeObserver = new ResizeObserver(() => scheduleInfoSizing());
      infoResizeObserver.observe(infoDescBlock);
      cleanupFns.push(() => { try { infoResizeObserver.disconnect(); } catch {} });

      const ctaResizeObserver = new ResizeObserver(() => scheduleCtaPlacement());
      ctaResizeObserver.observe(frameEl);
      cleanupFns.push(() => { try { ctaResizeObserver.disconnect(); } catch {} });

      // Ensure correct state on mount
      updateEdgeNav();
      scheduleCtaPlacement();

      // Expose close for ESC
      function closeAny() {
        if (isPresetOpen()) closePreset();
        else if (isInfoOpen()) closeInfo();
        else if (isVideoOpen()) closeVideo();
      }

      // Set global esc close handler
      GLOBAL.closeCurrent = closeAny;
      root.removeAttribute("data-mg-rendering");
    }

    // Boot with a short wait/poll so sets defined in custom.js (or via late scripts)
    // don't flash an error. We'll wait up to 60s and also react to sets-ready events.
    let rendered = false;
    let waitMs = 25;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 60000;

    const onSetsReady = () => {
      boot();
    };

    function boot(){
      if (rendered) return;

      const ROWS = getRows();
      if (Array.isArray(ROWS) && ROWS.length) {
        render(ROWS);
        rendered = true;
        document.removeEventListener("vimeo_sets_ready", onSetsReady);
        document.removeEventListener("media_gallery_sets_ready", onSetsReady);
        return;
      }

      if (Date.now() - startedAt > MAX_WAIT_MS) {
        showError();
        document.removeEventListener("vimeo_sets_ready", onSetsReady);
        document.removeEventListener("media_gallery_sets_ready", onSetsReady);
        return;
      }

      waitMs = Math.min(Math.round(waitMs * 1.6), 1000);
      setTimeout(boot, waitMs);
    }

    document.addEventListener("vimeo_sets_ready", onSetsReady);
    document.addEventListener("media_gallery_sets_ready", onSetsReady);
    boot();
  }

  /* ------------------------------
     Public refresh + Shopify editor events
  ------------------------------ */
  // Remove duplicate section instances that can appear in the Shopify Theme Editor preview
  // while a section is being re-rendered (prevents stacking/duplication until Save).
  function dedupeDuplicateSnippetRoots(preferScope){
    try{
      const all = Array.from(document.querySelectorAll(".vimeo-snippet[id]"));
      if(!all.length) return;

      const groups = Object.create(null);
      for(const el of all){
        const id = el.id;
        if(!id) continue;
        (groups[id] || (groups[id] = [])).push(el);
      }

      for(const id in groups){
        const list = groups[id];
        if(!list || list.length < 2) continue;

        // Prefer keeping the instance inside the provided scope (newly rendered section)
        let keep = null;
        if(preferScope && typeof preferScope.contains === "function"){
          // Prefer keeping the LAST instance inside the provided scope (newly rendered section)
          for(let i = list.length - 1; i >= 0; i--){
            if(preferScope.contains(list[i])){
              keep = list[i];
              break;
            }
          }
        }
        if(!keep) keep = list[list.length - 1]; // last in DOM order

        const keepSection = keep && keep.closest ? keep.closest(".shopify-section") : null;

        for(const n of list){
          if(n === keep) continue;

          const sec = n.closest ? n.closest(".shopify-section") : null;

          // If the duplicate lives in a different section wrapper, remove the whole wrapper.
          // If it lives in the same wrapper as the kept instance, only remove the duplicate node.
          if(sec && sec.parentNode && (!keepSection || sec !== keepSection)){
            sec.parentNode.removeChild(sec);
          }else if(n.parentNode){
            n.parentNode.removeChild(n);
          }
        }
      }
    }catch(e){
      // no-op
    }
  }

  R.refresh = function(context){
    const scope = context && context.querySelectorAll ? context : document;
    dedupeDuplicateSnippetRoots(scope);
    buildFromDom(scope);
    initAll(scope);
  };

  ready(() => {
    dedupeDuplicateSnippetRoots(document);
    buildFromDom(document);
    initAll(document);
  });

  // Shopify Theme Editor (dynamic sections)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", (e) => {
      if (!e || !e.target) return;
      R.refresh(e.target);
    });

    document.addEventListener("shopify:section:unload", (e) => {
      if (!e || !e.target) return;

      // Clean up any stale duplicates the editor may have left behind
      dedupeDuplicateSnippetRoots(document);

      // Best-effort teardown hook (if we add one in the future)
      try {
        e.target.querySelectorAll(".vimeo-snippet").forEach((root) => {
          if (root && typeof root.__mgDestroy === "function") root.__mgDestroy();
        });
      } catch (err) {
        // no-op
      }
    });
    document.addEventListener("shopify:section:reorder", (e) => {
      if (!e || !e.target) return;
      R.refresh(e.target);
    });
    document.addEventListener("shopify:section:select", (e) => {
      if (!e || !e.target) return;
      R.refresh(e.target);
    });
    document.addEventListener("shopify:block:select", (e) => {
      if (!e || !e.target) return;
      R.refresh(e.target);
    });
  }
})();

(() => {
  if (!window.Shopify || !window.Shopify.designMode) return;

  /* -------------------------------------------------
     âœ… Export helper
     - Exports the *resolved* set from window.MEDIA_GALLERY_SETS so:
       - Media Gallery Preset blocks are INCLUDED (inlined)
       - Row imports are already applied
     - Strips internal/runtime-only keys (anything starting with "__")
  -------------------------------------------------- */

  function getSetStore(){
    const w = window;

    if (w.MEDIA_GALLERY_SETS && typeof w.MEDIA_GALLERY_SETS === "object") return w.MEDIA_GALLERY_SETS;

    // Back-compat alias
    if (w.VIMEO_VIDEO_SETS && typeof w.VIMEO_VIDEO_SETS === "object") {
      w.MEDIA_GALLERY_SETS = w.VIMEO_VIDEO_SETS;
      return w.MEDIA_GALLERY_SETS;
    }

    w.MEDIA_GALLERY_SETS = {};
    w.VIMEO_VIDEO_SETS = w.MEDIA_GALLERY_SETS;
    return w.MEDIA_GALLERY_SETS;
  }

  const VIMEO_RE = /(?:vimeo\.com|player\.vimeo\.com)/i;
  const YOUTUBE_RE = /(?:youtu\.be|youtube(?:-nocookie)?\.com)/i;
  const FILE_VIDEO_RE = /\.(?:mp4|m4v|webm|ogv|ogg|mov)(?:$|[?#])/i;

  function isSupportedVideoUrl(raw){
    const s = String(raw || "").trim();
    if (!s) return false;
    if (VIMEO_RE.test(s) || YOUTUBE_RE.test(s) || FILE_VIDEO_RE.test(s)) return true;
    try {
      const u = new URL(s, window.location.href);
      return FILE_VIDEO_RE.test(String(u.pathname || ""));
    } catch {
      return false;
    }
  }

  function hasVideoOnlySignals(it){
    if (!it || typeof it !== "object") return false;

    const ctaThumbText = String(it?.cta?.thumb_text || it?.thumb_text || "").trim();
    if (ctaThumbText) return true;

    const markerValues = [
      it?.start_time,
      it?.startTime,
      it?.start_at,
      it?.startAt,
      it?.end_behavior,
      it?.endBehavior,
      it?.end_behavior_override,
      it?.end_of_video_override,
      it?.hero,
      it?.hero_video,
      it?.is_hero,
      it?.hero_autoplay_preview,
      it?.autoplay_video,
      it?.quick_load_video_url,
      it?.quickLoadVideoUrl,
      it?.tiny_video_url,
      it?.tinyVideoUrl,
      it?.prioritize_quick_load_video,
      it?.prioritizeQuickLoadVideo,
      it?.hero_show_play_badge,
      it?.hero_show_play_button,
      it?.show_play_badge,
      it?.show_play_button,
      it?.open_full_video_text,
      it?.title_wrap,
      it?.wrap_title
    ];

    if (markerValues.some((v) => {
      if (v == null) return false;
      if (typeof v === "boolean") return v;
      if (typeof v === "number") return isFinite(v) && v !== 0;
      return String(v).trim() !== "";
    })) return true;

    const poster = String(it?.thumbnail || it?.thumb || "").trim();
    const image = String(it?.image || it?.img || "").trim();
    const productUrl = String(it?.product_url || it?.productUrl || "").trim();
    const endBehavior = String(it?.end_behavior || it?.endBehavior || it?.end_behavior_override || it?.end_of_video_override || "").trim();
    if (poster && !image && !productUrl && endBehavior) return true;

    return false;
  }

  function guessItemKind(it){
    const tRaw = String(it?.type || it?.kind || it?.provider || "").trim().toLowerCase();
    if (tRaw === "image" || tRaw === "product" || tRaw === "video") return tRaw;
    if (tRaw === "hero_video" || tRaw === "hero-video" || tRaw === "hero") return "video";
    if (tRaw === "vimeo" || tRaw === "youtube" || tRaw === "file") return "video";
    if (hasVideoOnlySignals(it)) return "video";

    const productUrl = it?.product_url || it?.productUrl || "";
    if (String(productUrl || "").trim()) return "product";

    const u = String(it?.url || it?.href || "").trim();
    if (u && isSupportedVideoUrl(u)) return "video";

    const thumb = it?.thumbnail || it?.image || it?.img || "";
    if (String(thumb || "").trim()) return "image";

    return null;
  }

  function normalizeEndBehavior(raw, fallback = "next"){
    const s = String(raw || "").trim().toLowerCase();
    if (!s || s === "inherit") return fallback;
    const compact = s.replace(/[\s_-]+/g, "");
    if (compact === "next" || compact === "nextvideo") return "next";
    if (compact === "loop") return "loop";
    if (compact === "default") return "default";
    return fallback;
  }

  function normalizeAccordionShadowPct(raw, fallback = 8){
    const fb = Math.max(0, Math.min(100, Math.round(Number(fallback) || 0)));
    const n = Number(raw);
    if (!isFinite(n)) return fb;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  function normalizeAccordionShadowStyle(raw, fallback = "raised"){
    const base = String(fallback || "raised").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s_-]+/g, "");
    if (compact === "raised" || compact === "dropshadow" || compact === "drop") return "raised";
    if (compact === "pressed" || compact === "innershadow" || compact === "inner") return "pressed";
    if (compact === "layered" || compact === "layeredshadows" || compact === "stacked") return "layered";
    return base;
  }

  function normalizeRowToggleBorderStyle(raw, fallback = "solid"){
    const base = String(fallback || "solid").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s\/_-]+/g, "");
    if (compact === "solid") return "solid";
    if (compact === "inset") return "inset";
    if (compact === "outset") return "outset";
    if (compact === "groove" || compact === "beveled" || compact === "beveledgroove") return "groove";
    if (compact === "ridge") return "ridge";
    if (compact === "double") return "double";
    if (compact === "dasheddotted" || compact === "dashed" || compact === "dotted") return "dashed_dotted";
    return base;
  }

  function normalizePresetButtonStyle(raw, fallback = "dropdown_box"){
    const base = String(fallback || "dropdown_box").trim().toLowerCase();
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return base;
    const compact = s.replace(/[\s\/_-]+/g, "");
    if (compact === "button" || compact === "pill") return "button";
    if (compact === "dropdown" || compact === "dropdownbox" || compact === "selector" || compact === "select") return "dropdown_box";
    if (compact === "imagebutton" || compact === "image" || compact === "imagecard" || compact === "imagefill") return "image_button";
    if (compact === "flatsurface" || compact === "flat" || compact === "surface") return "image_button";
    return base;
  }

  function normalizePresetButtonWidthPercent(raw, fallback = 0){
    const baseNum = Number(fallback);
    const base = Number.isFinite(baseNum) ? Math.max(0, Math.min(100, baseNum)) : 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return base;
    return Math.max(0, Math.min(100, n));
  }

  function normalizePresetButtonMaxWidthPx(raw, fallback = 0){
    const coerce = (value, fallbackValue = NaN) => {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      const s = String(value ?? "").trim();
      if (!s) return fallbackValue;
      const match = s.match(/-?\d+(?:\.\d+)?/);
      if (!match) return fallbackValue;
      const num = Number(match[0]);
      return Number.isFinite(num) ? num : fallbackValue;
    };

    const baseNum = coerce(fallback, 0);
    const base = Number.isFinite(baseNum) ? Math.max(0, Math.min(2400, Math.round(baseNum))) : 0;
    const n = coerce(raw, NaN);
    if (!Number.isFinite(n)) return base;
    return Math.max(0, Math.min(2400, Math.round(n)));
  }

  function plainTextFromMaybeHtml(input){
    return String(input || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }

  function deriveImportedAccordionTitle(rowLike, fallback = "Accordion Tab"){
    const accordionTitle = plainTextFromMaybeHtml(rowLike?.accordion_title || "");
    if (accordionTitle) return accordionTitle;

    const showMore = plainTextFromMaybeHtml(rowLike?.show_more_text || "");
    if (showMore && showMore.toLowerCase() !== "show more") return showMore;

    const headingText = plainTextFromMaybeHtml(rowLike?.heading || rowLike?.title || "");
    if (headingText) return headingText;

    const fb = plainTextFromMaybeHtml(fallback);
    return fb || "Accordion Tab";
  }

  function deriveImportedAccordionSubheading(rowLike, fallback = ""){
    const candidate = String(
      rowLike?.accordion_subheading ||
      rowLike?.subheading ||
      ""
    ).trim();
    return candidate || String(fallback || "");
  }

  function normalizeItem(it){
    if (typeof it === "string") {
      const url = it.trim();
      if (!url || !isSupportedVideoUrl(url)) return null;
      return { type: "video", url };
    }

    if (!it || typeof it !== "object") return null;

    const kind = guessItemKind(it);
    if (!kind) return null;

    const tRaw = String(it.type || it.kind || "").trim().toLowerCase();
    if (!tRaw || tRaw !== kind || tRaw === "vimeo") {
      const copy = Object.assign({}, it);
      copy.type = kind;
      return copy;
    }

    return it;
  }

  function normalizeItems(list){
    const arr = Array.isArray(list) ? list : [];
    const out = [];
    for (const it of arr) {
      const n = normalizeItem(it);
      if (n) out.push(n);
    }
    return out;
  }

  function isRowLike(v){
    return !!(v && typeof v === "object" && (Array.isArray(v.videos) || Array.isArray(v.items) || Array.isArray(v.cards)));
  }

  function rowItems(row){
    return row?.videos || row?.items || row?.cards || [];
  }

  // Minimal coercion to a rows[] shape for export
  function coerceToRows(value){
    if (!value) return [];

    if (Array.isArray(value)) {
      if (value.length && isRowLike(value[0])) {
        return value.map((r) => {
          if (!r || typeof r !== "object") return null;
          const out = Object.assign({}, r);
          out.videos = normalizeItems(Array.isArray(r.videos) ? r.videos : Array.isArray(r.items) ? r.items : Array.isArray(r.cards) ? r.cards : []);
          return out;
        }).filter(Boolean);
      }

      // Flat list of items -> single row
      const items = normalizeItems(value);
      if (!items.length) return [];
      return [{
        heading: "",
        subheading: "",
        img_ratio: "1:1",
        row_height: 0,
        fit_row: false,
        collapsed: false,
        show_more_text: "Show more",
        show_less_text: "Show less",
        videos: items
      }];
    }

    if (typeof value === "object") {
      if (isRowLike(value)) {
        const out = Object.assign({}, value);
        out.videos = normalizeItems(rowItems(value));
        return [out];
      }

      // Single item -> single row
      const item = normalizeItem(value);
      if (!item) return [];
      return [{
        heading: "",
        subheading: "",
        img_ratio: "1:1",
        row_height: 0,
        fit_row: false,
        collapsed: false,
        show_more_text: "Show more",
        show_less_text: "Show less",
        videos: [item]
      }];
    }

    return [];
  }

  function stripInternalsForExport(rows, opts){
    const input = Array.isArray(rows) ? rows : [];
    const keepImportKeys = !!(opts && opts.keepImportKeys);
    const importKeySet = new Set(["__import_set", "__import_mode", "__import_passthrough"]);

    const replacer = (key, value) => {
      // Strip runtime/internal fields (used heavily in the section runtime)
      if (key && key.startsWith("__")) {
        if (keepImportKeys && importKeySet.has(key)) return value;
        return undefined;
      }

      // Prevent weird objects from leaking into JSON (shouldn't happen, but safe)
      if (typeof value === "function") return undefined;
      if (value && typeof value === "object") {
        try {
          if (value === window || value === document) return undefined;
          if (value?.nodeType) return undefined; // DOM nodes
        } catch {}
      }
      return value;
    };

    try {
      return JSON.parse(JSON.stringify(input, replacer));
    } catch {
      // Fallback: shallow clone
      return input.map((r) => Object.assign({}, r));
    }
  }

  function nearest(el, sel){
    while (el && el.nodeType === 1) {
      if (el.matches(sel)) return el;
      el = el.parentElement;
    }
    return null;
  }

  // Resolve imports/presets for export (best-effort).
  // - If referenced sets exist in window.MEDIA_GALLERY_SETS, we inline them.
  // - If a referenced set is missing, we keep the __import_* fields so the export still works once those sets are present.
  function resolveSetValueForExport(val, store, stack, missing){
    const rows = coerceToRows(val);
    return resolveCompositeRowsForExport(rows, store, stack, missing);
  }

  function resolveSetKeyForExport(key, store, stack, missing){
    const k = String(key || "").trim();
    if (!k) return null;

    // Prevent cycles
    if (stack && stack.has(k)) {
      if (missing) missing.add(k);
      return null;
    }

    if (!store || typeof store[k] === "undefined") {
      if (missing) missing.add(k);
      return null;
    }

    if (stack) stack.add(k);
    let out = null;
    try {
      out = resolveSetValueForExport(store[k], store, stack, missing);
    } catch {
      if (missing) missing.add(k);
      out = null;
    }
    if (stack) stack.delete(k);
    return out;
  }

  function resolveCompositeRowsForExport(rows, store, stack, missing){
    const inputRows = coerceToRows(rows);
    const out = [];

    for (const rowSpec of inputRows) {
      const importKey = String(rowSpec?.__import_set || "").trim();
      const importMode = String(rowSpec?.__import_mode || "flatten").trim();
      const passthrough = !!rowSpec?.__import_passthrough;

      const heading = String(rowSpec?.heading || "");
      const subheading = String(rowSpec?.subheading || "");
      const img_ratio = String(rowSpec?.img_ratio || "1:1");
      const row_height = Number(rowSpec?.row_height || 0);
      const fit_row = !!rowSpec?.fit_row;
      const hide_cta_buttons = !!rowSpec?.hide_cta_buttons;
      const end_behavior_override = normalizeEndBehavior(rowSpec?.end_behavior_override || rowSpec?.end_of_video_override, "");
      const collapsed = !!rowSpec?.collapsed;
      const show_more_text = String(rowSpec?.show_more_text || "Show more");
      const show_less_text = String(rowSpec?.show_less_text || "Show less");
      const row_toggle_border_style = normalizeRowToggleBorderStyle(rowSpec?.row_toggle_border_style || rowSpec?.row_border_style, "solid");
      const custom_html = (typeof rowSpec?.custom_html === "string") ? rowSpec.custom_html : "";
      const accordion = !!rowSpec?.accordion;
      const accordion_title = String(rowSpec?.accordion_title || "");
      const accordion_subheading = String(rowSpec?.accordion_subheading || "");
      const accordion_content = String(rowSpec?.accordion_content || "");
      const accordion_hide_title_when_open = !!rowSpec?.accordion_hide_title_when_open;
      const accordion_hide_subheading_when_open = !!rowSpec?.accordion_hide_subheading_when_open;
      const accordion_hide_subheading_when_closed = !!rowSpec?.accordion_hide_subheading_when_closed;
      const accordion_title_center = !!rowSpec?.accordion_title_center;
      const accordion_start_open = !!rowSpec?.accordion_start_open;
      const accordion_allow_multiple = !!rowSpec?.accordion_allow_multiple;
      const accordion_bg_color = String(rowSpec?.accordion_bg_color || rowSpec?.accordion_bg || "");
      const accordion_shadow_pct = normalizeAccordionShadowPct(rowSpec?.accordion_shadow_pct, 8);
      const accordion_shadow_style = normalizeAccordionShadowStyle(rowSpec?.accordion_shadow_style || rowSpec?.accordion_shadow_effect, "raised");
      const accordion_max_width_px = normalizePresetButtonMaxWidthPx(rowSpec?.accordion_max_width_px, 0);
      const accordion_group = String(rowSpec?.accordion_group || "").trim();
      const accordion_always_open = !!rowSpec?.accordion_always_open;
      const accordion_marker = !!rowSpec?.accordion_marker;
      const collapse_group = String(rowSpec?.collapse_group || "");
      const preset_button = !!rowSpec?.preset_button;
      const preset_button_text = String(rowSpec?.preset_button_text || rowSpec?.heading || rowSpec?.button_text || "").trim();
      const preset_button_text_plain = plainTextFromMaybeHtml(preset_button_text);
      const preset_button_set = String(rowSpec?.preset_button_set || rowSpec?.__preset_button_set || rowSpec?.__import_set || "").trim();
      const preset_button_style = normalizePresetButtonStyle(rowSpec?.preset_button_style || rowSpec?.button_style || "dropdown_box", "dropdown_box");
      const preset_button_image = String(rowSpec?.preset_button_image || rowSpec?.preset_button_image_url || rowSpec?.button_image || rowSpec?.button_image_url || "").trim();
      const preset_button_image_alt = String(rowSpec?.preset_button_image_alt || rowSpec?.button_image_alt || "").trim();
      const preset_button_width_percent = normalizePresetButtonWidthPercent(rowSpec?.preset_button_width_percent ?? rowSpec?.preset_button_width_pct ?? rowSpec?.button_width_percent ?? rowSpec?.button_width_pct, 0);
      const preset_button_max_width_px = normalizePresetButtonMaxWidthPx(rowSpec?.preset_button_max_width_px ?? rowSpec?.button_max_width_px, 0);

      const manual = normalizeItems(Array.isArray(rowItems(rowSpec)) ? rowItems(rowSpec) : []);

      if (importKey && importMode === "preserve") {
        const keepRow = Object.assign({}, rowSpec);
        const vids = Array.isArray(keepRow.videos) ? keepRow.videos : (Array.isArray(keepRow.items) ? keepRow.items : []);
        delete keepRow.items;
        keepRow.videos = normalizeItems(vids);
        out.push(keepRow);
        continue;
      }

      if (preset_button) {
        const keepRow = Object.assign({}, rowSpec);
        const vids = Array.isArray(keepRow.videos) ? keepRow.videos : (Array.isArray(keepRow.items) ? keepRow.items : []);
        delete keepRow.items;
        keepRow.videos = normalizeItems(vids);
        keepRow.preset_button = true;
        keepRow.preset_button_text = preset_button_text_plain ? preset_button_text : "Open Preset";
        keepRow.preset_button_set = preset_button_set || importKey;
        keepRow.preset_button_style = preset_button_style || "dropdown_box";
        keepRow.preset_button_width_percent = preset_button_width_percent;
        keepRow.preset_button_max_width_px = preset_button_max_width_px;
        if (preset_button_image) keepRow.preset_button_image = preset_button_image;
        if (preset_button_image_alt) keepRow.preset_button_image_alt = preset_button_image_alt;
        out.push(keepRow);
        continue;
      }

      if (passthrough) {
        const keepRow = Object.assign({}, rowSpec);
        const vids = Array.isArray(keepRow.videos) ? keepRow.videos : (Array.isArray(keepRow.items) ? keepRow.items : []);
        delete keepRow.items;
        keepRow.videos = normalizeItems(vids);
        out.push(keepRow);
        continue;
      }

      if (!importKey) {
        // Keep the row mostly as-is, but normalize to { videos: [] }
        const baseRow = Object.assign({}, rowSpec);
        const vids = Array.isArray(baseRow.videos) ? baseRow.videos : (Array.isArray(baseRow.items) ? baseRow.items : []);
        delete baseRow.items;
        baseRow.videos = normalizeItems(vids);
        out.push(baseRow);
        continue;
      }

      const srcRows = resolveSetKeyForExport(importKey, store, stack, missing);

      if (!srcRows) {
        // Can't inline right now â€” keep as an import row so the export still works.
        const keepRow = Object.assign({}, rowSpec);
        const vids = Array.isArray(keepRow.videos) ? keepRow.videos : (Array.isArray(keepRow.items) ? keepRow.items : []);
        delete keepRow.items;
        keepRow.videos = normalizeItems(vids);
        out.push(keepRow);
        continue;
      }

      if (passthrough) {
        const collapseGroupKey = collapsed ? (`preset:${importKey}:${out.length}`) : "";
        for (const rr of srcRows) {
          const rrRow = Object.assign({}, rr);
          const vids = Array.isArray(rrRow.videos) ? rrRow.videos : (Array.isArray(rrRow.items) ? rrRow.items : []);
          delete rrRow.items;
          rrRow.videos = normalizeItems(vids);

          // Allow the importing row (preset block) to force collapse behavior on the imported group.
          if (collapsed) {
            rrRow.collapsed = false;
            rrRow.collapse_group = collapseGroupKey;
            rrRow.show_more_text = show_more_text;
            rrRow.show_less_text = show_less_text;
          }
          if (hide_cta_buttons) rrRow.hide_cta_buttons = true;
          if (end_behavior_override) rrRow.end_behavior_override = end_behavior_override;
          if (accordion_group) rrRow.__host_accordion_group = accordion_group;
          if (accordion_always_open) rrRow.accordion_always_open = true;
          if (!rrRow.accordion && accordion_title) rrRow.accordion_title = accordion_title;
          if (!rrRow.accordion && accordion_subheading) rrRow.accordion_subheading = accordion_subheading;
          if (!rrRow.accordion && accordion_content) rrRow.accordion_content = accordion_content;
          if (!rrRow.accordion && accordion_title_center) rrRow.accordion_title_center = true;
          if (!rrRow.accordion && accordion_start_open) rrRow.accordion_start_open = true;
          if (!rrRow.accordion && accordion_allow_multiple) rrRow.accordion_allow_multiple = true;
          if (!rrRow.accordion && accordion_bg_color) rrRow.accordion_bg_color = accordion_bg_color;
          if (!rrRow.accordion && accordion_shadow_pct != null) rrRow.accordion_shadow_pct = accordion_shadow_pct;

          out.push(rrRow);
        }

        // If manual items exist, append them to the last row (best-effort).
        if (manual && manual.length) {
          if (out.length) out[out.length - 1].videos = (out[out.length - 1].videos || []).concat(manual);
          else out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: manual });
        }
        continue;
      }

      if (importMode === "preserve" && accordion) {
        const importGroupKey = accordion_group || (`accordion-import:export:${importKey}:${out.length}`);
        const firstImported = srcRows[0] || {};
        out.push({
          heading: "",
          subheading: "",
          img_ratio,
          row_height,
          fit_row,
          hide_cta_buttons,
          end_behavior_override,
          collapsed: false,
          show_more_text,
          show_less_text,
          row_toggle_border_style,
          custom_html: "",
          accordion: true,
          accordion_title: String(accordion_title || deriveImportedAccordionTitle(firstImported, "Accordion Tab")),
          accordion_subheading: String(accordion_subheading || deriveImportedAccordionSubheading(firstImported, "")),
          accordion_content,
          accordion_hide_title_when_open,
          accordion_hide_subheading_when_open,
          accordion_hide_subheading_when_closed,
          accordion_title_center,
          accordion_start_open,
          accordion_allow_multiple,
          accordion_bg_color,
          accordion_shadow_pct,
          accordion_group: importGroupKey,
          accordion_always_open,
          accordion_marker: true,
          collapse_group: "",
          videos: []
        });
        for (const rr of srcRows) {
          const rrRow = Object.assign({}, rr);
          const rrVids = Array.isArray(rrRow.videos) ? rrRow.videos : (Array.isArray(rrRow.items) ? rrRow.items : []);
          delete rrRow.items;
          rrRow.videos = normalizeItems(rrVids);
          rrRow.accordion = false;
          rrRow.accordion_marker = false;
          rrRow.accordion_group = importGroupKey;
          out.push(rrRow);
        }
        if (manual && manual.length) {
          if (out.length) out[out.length - 1].videos = (out[out.length - 1].videos || []).concat(manual);
          else out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: manual });
        }
        continue;
      }

      if ((importMode === "accordion" && accordion) || (importMode === "preserve" && !accordion)) {
        for (const rr of srcRows) {
          const rrVids = Array.isArray(rr?.videos) ? rr.videos : (Array.isArray(rr?.items) ? rr.items : []);
          const rrHtml = (typeof rr?.custom_html === "string") ? rr.custom_html : "";
          out.push({
            heading,
            subheading,
            img_ratio,
            row_height,
            fit_row,
            hide_cta_buttons: hide_cta_buttons || !!rr?.hide_cta_buttons,
            end_behavior_override: end_behavior_override || normalizeEndBehavior(rr?.end_behavior_override || rr?.end_of_video_override, ""),
            collapsed,
            show_more_text,
            show_less_text,
            row_toggle_border_style,
            custom_html: custom_html || rrHtml,
            accordion: accordion || !!rr?.accordion,
            accordion_title: accordion ? deriveImportedAccordionTitle(rr, accordion_title) : String(rr?.accordion_title || ""),
            accordion_subheading: accordion ? deriveImportedAccordionSubheading(rr, accordion_subheading) : String(rr?.accordion_subheading || ""),
            accordion_content: accordion ? accordion_content : String(rr?.accordion_content || ""),
            accordion_hide_title_when_open: accordion ? accordion_hide_title_when_open : !!rr?.accordion_hide_title_when_open,
            accordion_hide_subheading_when_open: accordion ? accordion_hide_subheading_when_open : !!rr?.accordion_hide_subheading_when_open,
            accordion_hide_subheading_when_closed: accordion ? accordion_hide_subheading_when_closed : !!rr?.accordion_hide_subheading_when_closed,
            accordion_title_center: accordion ? accordion_title_center : !!rr?.accordion_title_center,
            accordion_start_open: accordion ? accordion_start_open : !!rr?.accordion_start_open,
            accordion_allow_multiple: accordion ? accordion_allow_multiple : !!rr?.accordion_allow_multiple,
            accordion_bg_color: accordion ? accordion_bg_color : String(rr?.accordion_bg_color || rr?.accordion_bg || ""),
            accordion_shadow_pct: accordion ? accordion_shadow_pct : normalizeAccordionShadowPct(rr?.accordion_shadow_pct, 8),
            accordion_shadow_style: accordion ? accordion_shadow_style : normalizeAccordionShadowStyle(rr?.accordion_shadow_style || rr?.accordion_shadow_effect, "raised"),
            accordion_group: accordion_group || String(rr?.accordion_group || "").trim(),
            accordion_always_open: accordion_always_open || !!rr?.accordion_always_open,
            accordion_marker: !!rr?.accordion_marker,
            collapse_group: String(rr?.collapse_group || ""),
            videos: normalizeItems(rrVids)
          });
        }
        if (manual && manual.length) {
          if (out.length) out[out.length - 1].videos = (out[out.length - 1].videos || []).concat(manual);
          else out.push({ heading, subheading, img_ratio, row_height, fit_row, hide_cta_buttons, end_behavior_override, collapsed, show_more_text, show_less_text, row_toggle_border_style, custom_html, accordion, accordion_title, accordion_subheading, accordion_content, accordion_hide_title_when_open, accordion_hide_subheading_when_open, accordion_hide_subheading_when_closed, accordion_title_center, accordion_start_open, accordion_allow_multiple, accordion_bg_color, accordion_shadow_pct, accordion_shadow_style, accordion_max_width_px, accordion_group, accordion_always_open, accordion_marker, collapse_group, videos: manual });
        }
        continue;
      }

      // flatten
      let flat = [];
      for (const rr of srcRows) {
        const rrVids = Array.isArray(rr?.videos) ? rr.videos : (Array.isArray(rr?.items) ? rr.items : []);
        flat = flat.concat(normalizeItems(rrVids));
      }
      if (manual && manual.length) flat = flat.concat(manual);

      out.push({
        heading,
        subheading,
        img_ratio,
        row_height,
        fit_row,
        collapsed,
        show_more_text,
        show_less_text,
        custom_html,
        accordion,
        accordion_title,
        accordion_subheading,
        accordion_content,
        accordion_hide_title_when_open,
        accordion_hide_subheading_when_open,
        accordion_hide_subheading_when_closed,
        accordion_title_center,
        accordion_start_open,
        accordion_allow_multiple,
        accordion_bg_color,
        accordion_shadow_pct,
        accordion_group,
        accordion_always_open,
        collapse_group,
        hide_cta_buttons: hide_cta_buttons || srcRows.some((rr) => !!rr?.hide_cta_buttons),
        end_behavior_override: end_behavior_override || (() => {
          const vals = srcRows
            .map((rr) => normalizeEndBehavior(rr?.end_behavior_override || rr?.end_of_video_override, ""))
            .filter(Boolean);
          return (vals.length && vals.every((v) => v === vals[0])) ? vals[0] : "";
        })(),
        videos: flat
      });
    }

    return out;
  }

  function exportResolveRows(specRows, store, sourceKey){
    const missingKeys = new Set();
    const stack = new Set();
    if (sourceKey) stack.add(String(sourceKey));
    const resolvedRows = resolveCompositeRowsForExport(specRows, store, stack, missingKeys);
    return { resolvedRows, missingKeys };
  }

  function buildJsonSpec(setName, rows){
    return rows;
  }

  // Export helper: clone rows/items so we never mutate the live runtime/store objects
  const bakeExportSpec = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => {
      const row = Object.assign({}, r);
      const vids = normalizeItems(Array.isArray(row.videos) ? row.videos : []);
      row.videos = vids.map((v) => (v && typeof v === "object") ? Object.assign({}, v) : v);
      return row;
    });
  };

  const buildMediaSetBlock = (setName, rows) => {
    const key = String(setName || "set").trim();
    const baked = bakeExportSpec(rows);
    const json = JSON.stringify(baked, null, 2);
    return [
      `window.MEDIA_GALLERY_SETS[${JSON.stringify(key)}] = ${json};`,
      ``,
    ].join("\n");
  };

  const buildOnceLinesBlock = () => {
    return [
      `// âœ… ONCE-ONLY LINES (copy once per file/snippet â€” skip if you already have these):`,
      `window.MEDIA_GALLERY_SETS = window.MEDIA_GALLERY_SETS || {};`,
      ``,
      ``,
    ].join("\n");
  };

  const buildEventLinesBlock = () => {
    return [
      `// âœ… OPTIONAL: paste these ONCE after adding/updating one or more sets (helps previews refresh):`,
      `document.dispatchEvent(new CustomEvent("media_gallery_sets_ready"));`,
      `document.dispatchEvent(new CustomEvent("vimeo_sets_ready"));`,
      ``,
      ``,
    ].join("\n");
  };

  const buildJsOnly = (setName, rows) => {
    // Back-compat helper
    const key = String(setName || "set").trim();
    const baked = bakeExportSpec(rows);
    const json = JSON.stringify(baked, null, 2);
    return [
      `window.MEDIA_GALLERY_SETS = window.MEDIA_GALLERY_SETS || {};`,
      `window.MEDIA_GALLERY_SETS[${JSON.stringify(key)}] = ${json};`,
      `document.dispatchEvent(new CustomEvent("media_gallery_sets_ready"));`,
      `document.dispatchEvent(new CustomEvent("vimeo_sets_ready"));`,
      ``,
    ].join("\n");
  };

  const buildJsWithLabel = (setName, rows) => {
    const key = String(setName || "set").trim();
    return [
      `// Media Gallery export: ${key}`,
      ``,
      ``,
      buildOnceLinesBlock(),
      buildMediaSetBlock(key, rows),
      buildEventLinesBlock(),
    ].join("\n");
  };


  function isRuntimeOnlySetKey(raw){
    const key = String(raw || "").trim();
    if (!key) return true;
    if (/^mg-product-block-view-/i.test(key)) return true;
    if (/^mg-(?:modal|inline|preview)/i.test(key)) return true;
    if (/^__/.test(key)) return true;
    return false;
  }

  const buildMergedMediaSetsFile = (setName, rows, store, warnPrefix = "") => {
    const targetKey = String(setName || "set").trim() || "set";
    const source = (store && typeof store === "object") ? store : {};
    const merged = {};

    Object.keys(source).sort((a, b) => String(a).localeCompare(String(b))).forEach((key) => {
      const safeKey = String(key || "").trim();
      if (!safeKey || safeKey === targetKey || isRuntimeOnlySetKey(safeKey)) return;
      try {
        const cleaned = stripInternalsForExport(coerceToRows(source[safeKey]), { keepImportKeys: true });
        merged[safeKey] = bakeExportSpec(cleaned);
      } catch {
        merged[safeKey] = bakeExportSpec(coerceToRows(source[safeKey]));
      }
    });

    merged[targetKey] = bakeExportSpec(Array.isArray(rows) ? rows : coerceToRows(rows));

    const ordered = {};
    Object.keys(merged).sort((a, b) => String(a).localeCompare(String(b))).forEach((key) => {
      ordered[key] = merged[key];
    });

    const header = String(warnPrefix || "").trim()
      ? (String(warnPrefix || "").trimEnd() + "\n\n")
      : "";

    return header + [
      `// Paste this into assets/cmg.Gallery-data.js`,
      `window.MEDIA_GALLERY_SETS = ${JSON.stringify(ordered, null, 2)};`,
      `window.VIMEO_VIDEO_SETS = window.MEDIA_GALLERY_SETS;`,
      `document.dispatchEvent(new CustomEvent("media_gallery_sets_ready"));`,
      `document.dispatchEvent(new CustomEvent("vimeo_sets_ready"));`,
      ``,
    ].join("\n");
  };

  function collectRowsFromSection(sectionEl){
    const specScript = sectionEl.querySelector('script[data-vimeo-control-spec][type="application/json"]');
    if (!specScript) return null;

    try {
      const spec = JSON.parse(specScript.textContent || "{}");
      if (!spec || typeof spec !== "object") return null;
      const rows = spec.rows || [];
      return { setName: spec.targetSet || "", rows };
    } catch {
      return null;
    }
  }

  function mountExportUI(exportBox){
    if (exportBox.__mgMounted) return;
    exportBox.__mgMounted = true;

    const setName = (exportBox.getAttribute("data-source-set") || "").trim();

    exportBox.innerHTML = `
      <button class="mg-export__toggle" type="button" data-mg-toggle>Export JSON</button>
      <div class="mg-export__panel" data-mg-panel hidden>
        <div class="mg-export__bar">
          <span class="mg-export__label">Set:</span>
          <input class="mg-export__input" data-mg-set value="${setName ? setName.replace(/"/g,"&quot;") : ""}" />
          <button class="mg-export__btn" type="button" data-mg-copy-all>Copy All</button>
          <button class="mg-export__btn" type="button" data-mg-copy-set>Copy Media Set</button>
          <button class="mg-export__btn" type="button" data-mg-save-sets>Save to Media Gallery Sets</button>
          <button class="mg-export__btn mg-export__btn--close" type="button" data-mg-close aria-label="Close"><span class="mg-close-icon" aria-hidden="true"></span></button>
        </div>
        <textarea class="mg-export__textarea" data-mg-out rows="14" readonly></textarea>
      </div>
    `;

    const toggle = exportBox.querySelector("[data-mg-toggle]");
    const panel  = exportBox.querySelector("[data-mg-panel]");
    const close  = exportBox.querySelector("[data-mg-close]");
    const copyAll = exportBox.querySelector("[data-mg-copy-all]");
    const copySet = exportBox.querySelector("[data-mg-copy-set]");
    const saveSets = exportBox.querySelector("[data-mg-save-sets]");
    const out    = exportBox.querySelector("[data-mg-out]");
    const setInp = exportBox.querySelector("[data-mg-set]");

    let lastCopyAll = "";
    let lastCopySet = "";
    let lastSaveSets = "";

    function refreshOutput(){
      const sectionEl = nearest(exportBox, ".shopify-section") || document;
      const store = getSetStore();

      const readSnapshot = () => {
        const collected = collectRowsFromSection(sectionEl);
        const sourceKey = String(collected?.setName || setName || "").trim();
        const targetKey = String((setInp.value || "").trim() || sourceKey || "set").trim();
        const specRows = (collected && Array.isArray(collected.rows)) ? collected.rows : [];
        const fallbackVal = (sourceKey && typeof store[sourceKey] !== "undefined") ? store[sourceKey] : [];
        const baseRows = coerceToRows(specRows.length ? specRows : fallbackVal);
        return { collected, sourceKey, targetKey, specRows, fallbackVal, baseRows };
      };

      const writeOutput = (targetKey, rows, warn) => {
        const safeTargetKey = String(targetKey || "set").trim() || "set";
        const safeRows = Array.isArray(rows) ? rows : coerceToRows(rows);
        const prefix = String(warn || "");
        lastCopyAll = prefix + buildJsWithLabel(safeTargetKey, safeRows);
        lastCopySet = prefix + buildMediaSetBlock(safeTargetKey, safeRows);
        lastSaveSets = buildMergedMediaSetsFile(safeTargetKey, safeRows, store, prefix);
        out.value = lastCopyAll;
        try { out.textContent = lastCopyAll; } catch {}
      };

      let snapshot = readSnapshot();

      // Only force a runtime refresh if the current section store entry is missing.
      // This avoids editor-side redraw issues while still giving imports a chance to resolve.
      if ((!snapshot.baseRows || !snapshot.baseRows.length) && snapshot.sourceKey) {
        try {
          const RT = window.__VimeoVideoControlRuntime;
          if (RT && typeof RT.refresh === "function") RT.refresh(sectionEl);
          snapshot = readSnapshot();
        } catch {}
      }

      try {
        // Best-effort: inline presets/imports for export (if referenced sets exist).
        const { resolvedRows: inlinedRows, missingKeys } = exportResolveRows(snapshot.baseRows, store, snapshot.sourceKey);
        const keepImportKeys = !!(missingKeys && missingKeys.size);
        const cleanedRows = stripInternalsForExport(coerceToRows(inlinedRows), { keepImportKeys: true });
        const spec = buildJsonSpec(snapshot.targetKey, cleanedRows);

        const warn = keepImportKeys
          ? ("// NOTE: Some presets/sets could not be inlined at export-time. These remain as __import_* references:\n// " + Array.from(missingKeys).join(", ") + "\n\n")
          : "";

        writeOutput(snapshot.targetKey, spec, warn);
        return;
      } catch (err) {
        try { console.warn("[media-gallery] Export resolve failed; using raw rows fallback.", err); } catch {}
      }

      try {
        // Fallback to the raw section/store rows instead of leaving the export textarea blank.
        const rawRows = stripInternalsForExport(snapshot.baseRows, { keepImportKeys: true });
        const spec = buildJsonSpec(snapshot.targetKey, coerceToRows(rawRows));
        writeOutput(snapshot.targetKey, spec, "");
        return;
      } catch (err) {
        try { console.warn("[media-gallery] Export raw rows fallback failed; emitting empty set.", err); } catch {}
      }

      writeOutput(
        snapshot.targetKey || "set",
        [],
        "// NOTE: Export fell back to an empty set because the current rows could not be serialized.\n\n"
      );
    }

    toggle.addEventListener("click", () => {
      panel.hidden = !panel.hidden;
      if (!panel.hidden) refreshOutput();
    });
    close.addEventListener("click", () => { panel.hidden = true; });

    setInp.addEventListener("input", refreshOutput);

    const doCopy = async (text, btn, idleLabel) => {
      const payload = String(text || "");
      if (!payload.trim()) return;

      try {
        await navigator.clipboard.writeText(payload);
        btn.textContent = "Copied!";
        setTimeout(() => (btn.textContent = idleLabel), 900);
      } catch {
        // Fallback: put payload into textarea and execCommand
        try {
          const prev = out.value;
          out.value = payload;
          out.focus();
          out.select();
          document.execCommand("copy");
          btn.textContent = "Copied!";
          setTimeout(() => {
            btn.textContent = idleLabel;
            out.value = prev || lastCopyAll || payload;
          }, 900);
        } catch {
          btn.textContent = "Copy failed";
          setTimeout(() => (btn.textContent = idleLabel), 1100);
        }
      }
    };

    copyAll.addEventListener("click", async () => {
      await doCopy(lastCopyAll || out.value || "", copyAll, "Copy All");
    });

    copySet.addEventListener("click", async () => {
      await doCopy(lastCopySet || "", copySet, "Copy Media Set");
    });

    if (saveSets) {
      saveSets.addEventListener("click", async () => {
        refreshOutput();
        const payload = String(lastSaveSets || "");
        if (!payload.trim()) return;
        out.value = payload;
        try { out.textContent = payload; } catch {}
        await doCopy(payload, saveSets, "Save to Media Gallery Sets");
      });
    }
  }

  function init(){
    document.querySelectorAll("[data-mg-export-box]").forEach(mountExportUI);
  }

  window.__mgMountMediaGalleryExportUI = mountExportUI;

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();


(() => {
  "use strict";

  function refreshMediaGalleryInScope(scope){
    try {
      const RT = window.__VimeoVideoControlRuntime;
      if (!RT || typeof RT.refresh !== "function") return;
      RT.refresh(scope || document);
    } catch (err) {
      // no-op
    }
  }

  function initProductMediaGalleryBlocks(scope){
    const root = scope && scope.querySelectorAll ? scope : document;
    const blocks = root.querySelectorAll(".mg-product-block.vimeo-snippet[data-vimeo-set]");
    if (!blocks.length) return;
    refreshMediaGalleryInScope(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initProductMediaGalleryBlocks(document);
    }, { once:true });
  } else {
    initProductMediaGalleryBlocks(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener("shopify:section:load", (e) => {
      if (!e || !e.target) return;
      initProductMediaGalleryBlocks(e.target);
    });

    document.addEventListener("shopify:section:select", (e) => {
      if (!e || !e.target) return;
      initProductMediaGalleryBlocks(e.target);
    });

    document.addEventListener("shopify:block:select", (e) => {
      if (!e || !e.target) return;
      initProductMediaGalleryBlocks(e.target);
    });

    document.addEventListener("shopify:section:reorder", (e) => {
      if (!e || !e.target) return;
      initProductMediaGalleryBlocks(e.target);
    });
  }
})();

